(function () {
  'use strict';

  var OVERLAY_HISTORY_KEY = 'zellijDesignLab';
  var OVERLAY_HISTORY_LEVEL_LAB = 'lab';
  var OVERLAY_HISTORY_LEVEL_ROOM = 'room';
  var THUMBNAIL_CACHE_DEBOUNCE_MS = 300;
  var SLIDE_EASING = 'cubic-bezier(0.645, 0.045, 0.355, 1)';
  /* Active funnel/PDP lab instance; destroy before setPattern re-init. */
  var activeLabController = null;
  var labInstanceGeneration = 0;
  /*
    Decoded tile cells survive overlay destroy/reopen so a subsequent mount
    can reuse the texture path instead of falling back to flat hex.
  */
  var sharedTextureCellImages = {};
  var sharedTextureReadySlugs = {};

  /*
    Currency-agnostic money formatting for lab prices.
    Prefer active presentment currency (Shopify.currency.active); never hardcode $.
  */
  function formatMoneyCents(cents) {
    var n = Number(cents);
    if (cents == null || isNaN(n)) return '';
    var currency =
      (window.Shopify && Shopify.currency && Shopify.currency.active) ||
      (window.themeVariables &&
        themeVariables.settings &&
        themeVariables.settings.activeCurrency) ||
      null;
    var locale =
      (window.Shopify && Shopify.locale) ||
      (document.documentElement && document.documentElement.lang) ||
      undefined;
    if (currency) {
      try {
        return new Intl.NumberFormat(locale || undefined, {
          style: 'currency',
          currency: currency
        }).format(n / 100);
      } catch (err) {
        /* fall through */
      }
    }
    var settings =
      (window.themeVariables && themeVariables.settings) || {};
    var fmt = settings.currencyCodeEnabled
      ? settings.moneyWithCurrencyFormat
      : settings.moneyFormat;
    if (fmt && window.Shopify && typeof Shopify.formatMoney === 'function') {
      try {
        return Shopify.formatMoney(n, fmt);
      } catch (err2) {
        /* fall through */
      }
    }
    var amount = (n / 100).toFixed(2);
    return currency ? amount + ' ' + currency : amount;
  }

  window.ZellijMoney = {
    formatCents: formatMoneyCents
  };

  /*
    Colour temperature thresholds (tune after reviewing the category table).
    Near-neutrals (Cream/Gazelle) need S up to ~26% plus high lightness.
    True greys use a lower S floor. Greens stay Cool as one family.
  */
  var TEMP_GREY_MAX_S = 0.12;
  var TEMP_NEUTRAL_MAX_S = 0.26;
  var TEMP_NEUTRAL_MIN_L = 0.5;
  var TEMP_WARM_HUE_MAX = 70;
  var TEMP_WARM_HUE_MIN_WRAP = 330;
  var TEMP_GREEN_HUE_MAX = 170;
  var TEMP_GREEN_MIN_S = 0.12;

  /*
    Room cut-outs as fractions of the mockup (asset step).
    targetSheetsAcross: visible tile sheets across the cut-out width.
    cutoutAlphaMax: threshold for placement (0 = strict alpha==0).
    applyRoomToFrame remeasures the loaded CDN photo when CORS allows.
  */
  var ROOM_PRESETS = {
    kitchen: {
      id: 'kitchen',
      file: 'kitchen',
      /* Native kitchen mockup final.png 3000x3000; kitchen-900.webp same ratio */
      aspectRatio: '1 / 1',
      /*
        Near-square slot + cover: media at photo aspect fills block width;
        frame clips; coverShiftY (0=top, 1=bottom) keeps the backsplash in view.
      */
      blockAspectRatio: '1 / 0.9',
      coverShiftY: 0.4,
      cutoutAlphaMax: 0,
      /*
        True alpha==0 (kitchen mockup final.png 3000x3000):
          x 0.000-1.000, y 0.283-0.555, w 1.000, h 0.272
        Live kitchen-900.webp a0 shifts ~0.026 (y 0.309, h 0.246).
      */
      x: 0,
      y: 0.283,
      w: 1,
      h: 0.272333333,
      /* 100% design count. Kitchen mosaic preview uses KITCHEN_MOSAIC_SCALE. */
      targetSheetsAcross: 8
    },
    bathroom: {
      id: 'bathroom',
      file: 'shower-2',
      /* Native shower mockup final 1x1.png 2962x3084; live 900x937 (~same ~0.96 ratio) */
      aspectRatio: '2962 / 3084',
      blockAspectRatio: '1 / 0.9',
      /*
        Prefer top of portrait shower: crop the floor. 0 = top-aligned.
        Keeps shower head clean and mosaic wall in frame.
      */
      coverShiftY: 0,
      cutoutAlphaMax: 128,
      /*
        True alpha==0 (shower mockup final 1x1.png):
          x 0.527, y 0.335, w 0.201, h 0.567 (door glass only)
        See-through bbox a<=128 (frosted wall + glass):
          x 0.273, y 0.052, w 0.472, h 0.851
      */
      x: 0.273126266,
      y: 0.05155642,
      w: 0.472316003,
      h: 0.851167315,
      targetSheetsAcross: 4
    },
    fireplace: {
      id: 'fireplace',
      file: 'fireplace',
      /* Native fire place mockup final@2x.png 2508x2508 */
      aspectRatio: '1 / 1',
      blockAspectRatio: '1 / 0.9',
      coverShiftY: 0.42,
      cutoutAlphaMax: 0,
      borderScene: 'fireplace',
      x: 0,
      y: 0,
      w: 1,
      h: 1
    },
    stairs: {
      id: 'stairs',
      file: 'stairs',
      /* Native stairs mockup@.png 2508x2508 */
      aspectRatio: '1 / 1',
      blockAspectRatio: '1 / 0.9',
      coverShiftY: 0.48,
      cutoutAlphaMax: 0,
      borderScene: 'stairs',
      x: 0,
      y: 0,
      w: 1,
      h: 1
    }
  };

  /*
    Fireplace U-frame zones measured from live Supabase Rooms/fireplace-*.webp.
    Source: fireplace-1800.webp (1800x1800), alpha < 128, Aug 2026.
  */
  var FIREPLACE_BORDER_STRIPS = [
    {
      id: 'left',
      x: 0.181,
      y: 0.4928,
      w: 0.0474,
      h: 0.323,
      rotate: -90
    },
    {
      id: 'right',
      x: 0.7715,
      y: 0.4928,
      w: 0.0478,
      h: 0.323,
      rotate: 90
    },
    {
      id: 'top',
      x: 0.181,
      y: 0.4458,
      w: 0.638,
      h: 0.047,
      rotate: 0
    }
  ];

  var STAIRS_BORDER_RISERS = [
    { x: 0.372807, y: 0.258772, w: 0.273923, h: 0.022329, rotate: 0 },
    { x: 0.366427, y: 0.289075, w: 0.28748, h: 0.023126, rotate: 0 },
    { x: 0.35925, y: 0.320973, w: 0.302632, h: 0.025518, rotate: 0 },
    { x: 0.351276, y: 0.355263, w: 0.318581, h: 0.028708, rotate: 0 },
    { x: 0.343301, y: 0.392743, w: 0.336124, h: 0.033493, rotate: 0 },
    { x: 0.333732, y: 0.435805, w: 0.356061, h: 0.036683, rotate: 0 },
    { x: 0.323365, y: 0.482057, w: 0.378389, h: 0.039872, rotate: 0 },
    { x: 0.310606, y: 0.536284, w: 0.404705, h: 0.04386, rotate: 0 },
    { x: 0.295455, y: 0.599282, w: 0.436603, h: 0.047049, rotate: 0 },
    { x: 0.277911, y: 0.67185, w: 0.473285, h: 0.051834, rotate: 0 },
    { x: 0.257974, y: 0.757974, w: 0.516348, h: 0.058214, rotate: 0 },
    { x: 0.241228, y: 0.86244, w: 0.560207, h: 0.059011, rotate: 0 }
  ];

  /*
    Interim uniform cover scale for Soufiane _3 U-frame until Marianna delivers
    band-adjusted files. Uses max(scaleX, scaleY) * this factor (cover fit).
    Reset toward contain (min) or 1.0 when corrected _3 SVGs land.
  */
  var FIREPLACE_UFRAME_UNIFORM_SCALE = 1;

  var FIREPLACE_UFRAME_ZONE = (function () {
    var minX = 1;
    var minY = 1;
    var maxX = 0;
    var maxY = 0;
    FIREPLACE_BORDER_STRIPS.forEach(function (strip) {
      minX = Math.min(minX, strip.x);
      minY = Math.min(minY, strip.y);
      maxX = Math.max(maxX, strip.x + strip.w);
      maxY = Math.max(maxY, strip.y + strip.h);
    });
    return {
      x: minX,
      y: minY,
      w: maxX - minX,
      h: maxY - minY
    };
  })();

  function isZellijRoomPreviewPortalTarget(target) {
    if (!target || typeof target.closest !== 'function') return false;
    return !!target.closest('.zellij-lab__room-fullscreen--portal');
  }

  function queryRoomPreviewTabButtons(root) {
    var tabs = [];
    function add(tab) {
      if (!tab || tabs.indexOf(tab) !== -1) return;
      tabs.push(tab);
    }
    if (root) {
      root.querySelectorAll('.zellij-lab__room-tab').forEach(add);
    }
    document
      .querySelectorAll('.zellij-lab__room-fullscreen--portal .zellij-lab__room-tab')
      .forEach(add);
    return tabs;
  }

  function syncRoomPreviewTabs(root, patternType, activeRoomId) {
    if (!root) return;
    var isBorder = patternType === 'border';
    var specs = isBorder
      ? [
          { id: 'fireplace', label: zCopy('room_fireplace') },
          { id: 'stairs', label: zCopy('room_stairs') }
        ]
      : [
          { id: 'kitchen', label: zCopy('room_kitchen') },
          { id: 'bathroom', label: zCopy('room_bathroom') }
        ];
    if (!activeRoomId) activeRoomId = defaultRoomIdForPatternType(patternType);
    queryRoomPreviewTabButtons(root).forEach(function (tab, idx) {
      var spec = specs[idx % specs.length];
      var active = spec.id === activeRoomId;
      tab.setAttribute('data-room', spec.id);
      tab.textContent = spec.label;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  function roomPreviewAltForId(roomId) {
    if (roomId === 'bathroom') return zCopy('room_preview_alt_bathroom');
    if (roomId === 'fireplace') return zCopy('room_preview_alt_fireplace');
    if (roomId === 'stairs') return zCopy('room_preview_alt_stairs');
    return zCopy('room_preview_alt_kitchen');
  }

  function defaultRoomIdForPatternType(patternType) {
    return patternType === 'border' ? 'fireplace' : 'kitchen';
  }

  function removeLegacyLightboxScaffolding() {
    [
      'zellij-lab-room-fullscreen-shell',
      'zellij-lab-room-fullscreen-portal',
      'zellij-lab-room-zoom-backdrop',
      'zellij-room-dialog'
    ].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.remove();
    });
  }

  function ensureBorderRoomPreviewMarkup(root, patternType) {
    if (!root || patternType !== 'border') return;
    var previewCol = root.querySelector('.zellij-lab__col--preview');
    if (!previewCol || previewCol.querySelector('.zellij-lab__room-preview')) return;

    var roomPreview = document.createElement('div');
    roomPreview.className = 'zellij-lab__room-preview';
    roomPreview.innerHTML =
      '<div class="zellij-lab__room-preview-frame">' +
      '<div class="zellij-lab__room-preview-media">' +
      '<div class="zellij-lab__room-pattern" aria-hidden="true"></div>' +
      '<img class="zellij-lab__room-photo" alt="" width="900" height="900" decoding="async">' +
      '</div>' +
      '<button type="button" class="zellij-lab__room-expand zellij-lab__btn-see-room" aria-label="' + zCopy('open_room_preview_fullscreen') + '">' +
      '<span class="zellij-lab__room-expand-icon" aria-hidden="true">' +
      '<svg class="zellij-lab__room-expand-svg" viewBox="0 0 20 20" focusable="false">' +
      '<path d="M1 8V1h7"></path><path d="M12 1h7v7"></path>' +
      '<path d="M19 12v7h-7"></path><path d="M8 19H1v-7"></path>' +
      '</svg></span></button></div>' +
      '<div class="zellij-lab__room-tabs" role="tablist" aria-label="' + zCopy('room_preview') + '">' +
      '<button type="button" class="zellij-lab__room-tab is-active" role="tab" aria-selected="true" data-room="fireplace">' + zCopy('room_fireplace') + '</button>' +
      '<button type="button" class="zellij-lab__room-tab" role="tab" aria-selected="false" data-room="stairs">' + zCopy('room_stairs') + '</button>' +
      '</div>';

    var carpetCol = previewCol.querySelector('.zellij-lab__canvas-col--carpet');
    if (carpetCol) {
      previewCol.insertBefore(roomPreview, carpetCol);
    } else {
      previewCol.insertBefore(roomPreview, previewCol.firstChild);
    }

    if (!root.querySelector('.zellij-lab__room-fullscreen')) {
      var roomFs = document.createElement('div');
      roomFs.className = 'zellij-lab__room-fullscreen';
      roomFs.hidden = true;
      roomFs.setAttribute('aria-hidden', 'true');
      roomFs.innerHTML =
        '<header class="zellij-lab__room-fullscreen-chrome">' +
        '<span class="zellij-lab__room-fullscreen-handle" aria-hidden="true"></span>' +
        '<button type="button" class="zellij-lab__room-exit-preview">' +
        '<span class="zellij-lab__room-exit-x" aria-hidden="true">&times;</span>' +
        '<span class="zellij-lab__room-exit-label">' +
        zCopy('exit_preview') +
        '</span></button>' +
        '</header>' +
        '<div class="zellij-lab__room-fullscreen-stage">' +
        '<div class="zellij-lab__room-preview-frame zellij-lab__room-preview-frame--fullscreen">' +
        '<div class="zellij-lab__room-preview-media">' +
        '<div class="zellij-lab__room-pattern" aria-hidden="true"></div>' +
        '<img class="zellij-lab__room-photo" alt="" width="1800" height="1800" decoding="async">' +
        '</div></div></div>' +
        '<div class="zellij-lab__room-tabs zellij-lab__room-tabs--fullscreen" role="tablist" aria-label="' + zCopy('room_preview') + '">' +
        '<button type="button" class="zellij-lab__room-tab is-active" role="tab" aria-selected="true" data-room="fireplace">' + zCopy('room_fireplace') + '</button>' +
        '<button type="button" class="zellij-lab__room-tab" role="tab" aria-selected="false" data-room="stairs">' + zCopy('room_stairs') + '</button>' +
        '</div>';
      root.appendChild(roomFs);
    }
    removeLegacyLightboxScaffolding();
  }

  /* Body scroll lock shared across lab levels / stacked Prestige dialogs */
  var bodyScrollLockDepth = 0;
  var bodyScrollLockY = 0;
  var bodyScrollLockObserver = null;

  function getScrollLockOverlayEl() {
    return document.getElementById('zellij-design-lab-overlay');
  }

  function isOtherPrestigeDialogOpen() {
    return !!document.querySelector(
      'cart-drawer[open], quick-buy-modal[open], header-search[open], ' +
        'header-sidebar[open], pop-in[open], x-popover[open]'
    );
  }

  function shouldDeferBodyScrollTeardown() {
    var overlay = getScrollLockOverlayEl();
    if (overlay && overlay.open) return true;
    if (isOtherPrestigeDialogOpen()) return true;
    return false;
  }

  function readScrollLockState() {
    return {
      depth: bodyScrollLockDepth,
      htmlLock: document.documentElement.classList.contains('lock'),
      overlayOpenClass: document.documentElement.classList.contains(
        'zellij-design-lab-overlay-open'
      ),
      bodyScrollLocked: document.body.classList.contains(
        'is-zellij-lab-scroll-locked'
      ),
      bodyPosition: document.body.style.position || getComputedStyle(document.body).position,
      bodyTop: document.body.style.top,
      savedY: bodyScrollLockY,
      overlayOpen: !!(getScrollLockOverlayEl() && getScrollLockOverlayEl().open),
      otherDialogOpen: isOtherPrestigeDialogOpen()
    };
  }

  function teardownBodyScrollLock() {
    if (bodyScrollLockObserver) {
      bodyScrollLockObserver.disconnect();
      bodyScrollLockObserver = null;
    }
    if (!document.body.classList.contains('is-zellij-lab-scroll-locked')) return;
    document.body.classList.remove('is-zellij-lab-scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, bodyScrollLockY);
  }

  function ensureBodyScrollLockObserver() {
    if (bodyScrollLockObserver) return;
    bodyScrollLockObserver = new MutationObserver(function () {
      if (bodyScrollLockDepth === 0 && !shouldDeferBodyScrollTeardown()) {
        teardownBodyScrollLock();
      }
    });
    bodyScrollLockObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function syncBodyScrollLockRelease() {
    if (bodyScrollLockDepth > 0) return;
    if (shouldDeferBodyScrollTeardown()) {
      ensureBodyScrollLockObserver();
      return;
    }
    teardownBodyScrollLock();
  }

  function acquireBodyScrollLock() {
    if (bodyScrollLockDepth === 0) {
      bodyScrollLockY = window.scrollY || window.pageYOffset || 0;
      document.body.classList.add('is-zellij-lab-scroll-locked');
      document.body.style.top = '-' + bodyScrollLockY + 'px';
    }
    bodyScrollLockDepth += 1;
  }

  function releaseBodyScrollLock() {
    bodyScrollLockDepth = Math.max(0, bodyScrollLockDepth - 1);
    syncBodyScrollLockRelease();
  }

  /*
    Lab overlay closed but counter drifted, or html.lock lingered after Modal
    hide: reset depth and release unless another dialog is still open.
  */
  function resetBodyScrollLockAfterLabClose() {
    var overlay = getScrollLockOverlayEl();
    if (overlay && overlay.open) return;
    bodyScrollLockDepth = 0;
    syncBodyScrollLockRelease();
  }

  window.__zellijLabScrollLock = {
    read: readScrollLockState,
    acquire: acquireBodyScrollLock,
    release: releaseBodyScrollLock,
    reset: resetBodyScrollLockAfterLabClose
  };

  function registerZellijLabOverlay() {
    if (customElements.get('zellij-lab-overlay')) return true;
    var ModalBase = customElements.get('x-modal');
    if (!ModalBase) return false;

    function runSlideAnimation(overlayEl, contentEl, direction) {
      var overlayFrom = direction === 'enter' ? 0 : 1;
      var overlayTo = direction === 'enter' ? 1 : 0;
      var contentFrom =
        direction === 'enter' ? 'translateY(100%)' : 'translateY(0)';
      var contentTo =
        direction === 'enter' ? 'translateY(0)' : 'translateY(100%)';
      var overlayAnim = overlayEl.animate(
        [{ opacity: overlayFrom }, { opacity: overlayTo }],
        { duration: 300, easing: SLIDE_EASING, fill: 'forwards' }
      );
      var contentAnim = contentEl.animate(
        [{ transform: contentFrom }, { transform: contentTo }],
        { duration: 300, easing: SLIDE_EASING, fill: 'forwards' }
      );
      return {
        finished: Promise.all([overlayAnim.finished, contentAnim.finished]),
        finish: function () {
          overlayAnim.finish();
          contentAnim.finish();
        }
      };
    }

    var ZellijLabOverlay = class extends ModalBase {
      createEnterAnimationControls() {
        return runSlideAnimation(
          this.getShadowPartByName('overlay'),
          this.getShadowPartByName('content'),
          'enter'
        );
      }
      createLeaveAnimationControls() {
        return runSlideAnimation(
          this.getShadowPartByName('overlay'),
          this.getShadowPartByName('content'),
          'leave'
        );
      }
      hideForOutsideClickTarget(target) {
        if (isZellijRoomPreviewPortalTarget(target)) return false;
        return !this.contains(target);
      }
      allowOutsideClickForTarget(target) {
        return isZellijRoomPreviewPortalTarget(target);
      }
    };

    customElements.define('zellij-lab-overlay', ZellijLabOverlay);
    return true;
  }

  if (!registerZellijLabOverlay()) {
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('x-modal').then(function () {
        registerZellijLabOverlay();
      });
    }
  }

  /**
   * Custom order SKU config.
   * Color numbers follow palette display order per paletteKey.
   * Format: {BASE}-{c1}-{c2}-...-{cn}  (sample adds SKU_FORMAT.sampleSuffix).
   */
  var PALETTE_COLOR_NUMBER_MAPS = {
    mosaic: {
      'Blanc': 1,
      'Cream': 2,
      'Gazelle': 3,
      'Khaki': 4,
      'Fez Terra Cotta': 5,
      'Caramel': 6,
      'Honeycomb': 7,
      'Sunflower': 8,
      'Los Feliz': 9,
      'Maroon': 10,
      'Fig': 11,
      'Deep Sea': 12,
      'London Sky': 13,
      'Zanzibar': 14,
      'Aspen': 15,
      'Atlas Sage': 16,
      'Forest Green': 17,
      'Titanium': 18
    },
    'hand-painted': {
      'Santorini': 1,
      'Morning Glory': 2,
      'Bay': 3,
      'Ore': 4,
      'Zaffre': 5,
      'Powder': 6,
      'Normandi': 7,
      'Ivy': 8,
      'Mint': 9,
      'Yuzu': 10,
      'Superbloom': 11,
      'Hal': 12,
      'Earth': 13,
      'Roe': 14,
      'Maroon': 15,
      'Cream': 16
    }
  };

  var CUSTOM_SKU_CONFIG = {
    COLOR_NUMBER_MAP: PALETTE_COLOR_NUMBER_MAPS.mosaic,
    SKU_FORMAT: {
      sampleSuffix: '-S'
    }
  };

  function resolveColorNumberMap(paletteKey) {
    return (
      PALETTE_COLOR_NUMBER_MAPS[paletteKey] ||
      PALETTE_COLOR_NUMBER_MAPS.mosaic
    );
  }

  /** Country-aware visible copy from Liquid (window.ZellijCopy). */
  function zCopy(key) {
    var map = window.ZellijCopy || {};
    var value = map[key];
    if (value != null && String(value).length) return String(value);
    return '';
  }

  /** Replace {{ name }} placeholders in a locale string. */
  function zCopyFill(key, vars) {
    var text = zCopy(key);
    if (!vars) return text;
    Object.keys(vars).forEach(function (name) {
      var re = new RegExp('\\{\\{\\s*' + name + '\\s*\\}\\}', 'g');
      text = text.replace(re, String(vars[name] == null ? '' : vars[name]));
    });
    return text;
  }

  /** Incomplete-coloring notice (ordering, spec sheet, sample). */
  function incompleteColoringMsg() {
    return zCopy('please_color_all_areas');
  }

  var ZELLIJ_PENDING_DESIGN_KEY = 'zellij_pending_design';
  var ZELLIJ_DRAFT_STORAGE_KEY = 'zellij-lab-draft';
  var ZELLIJ_DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000; /* 30 days */
  /* Only Lab-owned keys. Theme / Shopify keys must never be swept. */
  var ZELLIJ_LOCAL_STORAGE_ALLOWLIST = [ZELLIJ_DRAFT_STORAGE_KEY];
  var ZELLIJ_SESSION_STORAGE_ALLOWLIST = [
    ZELLIJ_PENDING_DESIGN_KEY,
    'zellij_workshop_signin_done',
    'zellij_workshop_signin_reached',
    'zellij_workshop_trade_banner_dismissed',
    'zellij_workshop_value_banner_dismissed',
    'zellij_workshop_soft_dismissed',
    'zellij_workshop_prompt_variant',
    'zellij_workshop_identified'
  ];

  function isZellijOwnedStorageKey(key) {
    return (
      !!key &&
      (key.indexOf('zellij-lab') === 0 || key.indexOf('zellij_') === 0)
    );
  }

  function sweepZellijStorage() {
    function sweep(store, allowlist) {
      if (!store) return;
      var allowed = {};
      allowlist.forEach(function (k) {
        allowed[k] = true;
      });
      var i;
      for (i = store.length - 1; i >= 0; i--) {
        var key = store.key(i);
        if (!isZellijOwnedStorageKey(key)) continue;
        if (allowed[key]) continue;
        try {
          store.removeItem(key);
        } catch (err) {
          /* ignore */
        }
      }
    }
    try {
      sweep(window.localStorage, ZELLIJ_LOCAL_STORAGE_ALLOWLIST);
    } catch (err) {
      /* ignore */
    }
    try {
      sweep(window.sessionStorage, ZELLIJ_SESSION_STORAGE_ALLOWLIST);
    } catch (err) {
      /* ignore */
    }
  }

  function colorNumberFromHex(hex, palette, colorNumberMap) {
    if (!hex || !palette || !palette.length) return null;
    var normalized = normalizeHex(hex).toLowerCase();
    var found = palette.find(function (c) {
      return normalizeHex(c.hex).toLowerCase() === normalized;
    });
    if (!found) return null;
    return colorNumberMap[found.name] || null;
  }

  function buildCustomSku(baseSku, groupColorHexes, isSample, palette, config) {
    config = config || CUSTOM_SKU_CONFIG;
    var base = (baseSku || '').trim();
    if (!base || !groupColorHexes || !groupColorHexes.length) return null;

    var numbers = groupColorHexes
      .map(function (hex) {
        return colorNumberFromHex(hex, palette, config.COLOR_NUMBER_MAP);
      })
      .filter(function (n) {
        return n != null;
      });

    if (!numbers.length) return null;

    var sku = base + '-' + numbers.join('-');
    if (isSample && config.SKU_FORMAT.sampleSuffix) {
      sku += config.SKU_FORMAT.sampleSuffix;
    }
    return sku;
  }

  /*
    Glaze namespace for asset URLs. Empty keeps legacy Swatches/{slug}.webp /
    Tiles/{slug}/… paths; hand-painted inserts …/hand-painted/… under each root.
  */
  function glazeNamespace(paletteKey, storageFolder) {
    var key = String(paletteKey || '').toLowerCase();
    var folder = String(storageFolder || '');
    if (key === 'hand-painted' || key === 'handpainted' || /hand-painted/i.test(folder)) {
      return 'hand-painted';
    }
    return '';
  }

  function supabaseSwatchUrl(baseUrl, slug, namespace) {
    if (!baseUrl || !slug) return '';
    var ns = namespace ? String(namespace).replace(/^\/+|\/+$/g, '') + '/' : '';
    return String(baseUrl).replace(/\/$/, '') + '/Swatches/' + ns + slug + '.webp';
  }

  function swatchVisualStyle(color, patternSvgBaseUrl, namespace) {
    var preview =
      (color && color.swatchPreviewUrl) ||
      supabaseSwatchUrl(patternSvgBaseUrl, color && color.slug, namespace) ||
      (color && color.swatchImage) ||
      '';
    if (preview) {
      return (
        '--swatch-hex:' +
        color.hex +
        ';background-color:' +
        color.hex +
        ';' +
        "background-image:url('" +
        preview +
        "');background-size:cover;background-position:center;"
      );
    }
    return (
      '--swatch-hex:' +
      color.hex +
      ';background:' +
      color.hex +
      ';' +
      (isLight(color.hex) ? 'box-shadow:inset 0 0 0 1px #ddd;' : '')
    );
  }

  function applySwatchVisual(el, color, patternSvgBaseUrl, namespace) {
    if (!el || !color) return;
    el.style.setProperty('--swatch-hex', color.hex);
    el.style.backgroundColor = color.hex;
    el.style.backgroundSize = 'cover';
    el.style.backgroundPosition = 'center';
    var primary =
      color.swatchPreviewUrl ||
      supabaseSwatchUrl(patternSvgBaseUrl, color.slug, namespace);
    var fallback = color.swatchImage || '';
    if (primary) {
      el.style.backgroundImage = "url('" + primary + "')";
      var probe = new Image();
      probe.onerror = function () {
        if (fallback) {
          el.style.backgroundImage = "url('" + fallback + "')";
        } else {
          el.style.backgroundImage = '';
        }
      };
      probe.src = primary;
    } else if (fallback) {
      el.style.backgroundImage = "url('" + fallback + "')";
    } else {
      el.style.backgroundImage = '';
      el.style.backgroundSize = '';
      el.style.backgroundPosition = '';
    }
    el.style.boxShadow = isLight(color.hex) ? 'inset 0 0 0 1px #ccc' : 'none';
  }

  function getBaseId(id) {
    var s = String(id || '');
    if (!s) return s;
    /*
      Hand-painted Illustrator family cells share one letter. Observed
      forms (all must collapse to that letter):
        A
        A-2 / A_2          classic duplicate suffix
        D_0000_            short collision id
        D_0000006649…_     long UUID-style collision id (RUFUS _3)
      Letter-only early match keeps mosaic ids like Group_12 / Layer_1
      on the classic strip path below (unchanged mosaic behavior).
    */
    var letterFam = s.match(/^([A-Za-z])(?:[-_]\d+_?)?$/);
    if (letterFam) return letterFam[1];
    /*
      Non-letter ids: strip Illustrator digit tails, then classic -N / _N.
    */
    s = s.replace(/_\d{4,}_?$/, '');
    return s.replace(/[-_]\d+$/, '');
  }

  /*
    HP-only: prefer Illustrator data-name when it is the family letter
    (duplicates often keep data-name="A" while id becomes A-2 / A_0000_).
    Mosaic path keeps using getBaseId(id) only.
  */
  function getHandPaintedFamilyBase(el) {
    if (!el || !el.getAttribute) return '';
    var dn = el.getAttribute('data-name');
    if (dn) {
      var d = String(dn).trim();
      var dm = d.match(/^([A-Za-z])(?:[-_]\d+_?)?$/);
      if (dm) return dm[1];
    }
    return getBaseId(el.getAttribute('id'));
  }

  function getShapes(el) {
    return Array.from(
      el.querySelectorAll('path, polygon, polyline, rect, circle, ellipse')
    ).filter(function (shape) {
      return shape.getAttribute('data-zellij-underfill') !== '1';
    });
  }

  function getAllGroupsWithId(svgEl) {
    var groups = Array.from(svgEl.querySelectorAll('g[id]'));

    if (groups.length === 0) {
      var allG = svgEl.getElementsByTagNameNS('http://www.w3.org/2000/svg', 'g');
      groups = Array.from(allG).filter(function (g) {
        return g.getAttribute('id');
      });
    }

    return groups;
  }

  /*
    Hand-painted-only family roots. Some HP sheets put id= on shapes
    (path/polygon/rect) instead of <g>. Mosaic code keeps using
    getAllGroupsWithId — this helper is never called on the mosaic path.
  */
  function isHandPaintedFamilyIgnoredId(id) {
    var s = String(id || '');
    if (!s) return true;
    if (/^calque/i.test(s)) return true;
    if (/^clip/i.test(s)) return true;
    return false;
  }

  function getHandPaintedFamilyElements(svgEl) {
    if (!svgEl || !svgEl.querySelectorAll) return [];
    var nodes = Array.from(svgEl.querySelectorAll('[id]'));
    var out = [];
    var i;
    for (i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el === svgEl) continue;
      var id = el.getAttribute('id');
      if (isHandPaintedFamilyIgnoredId(id)) continue;
      var tag = String(el.tagName || '').toLowerCase();
      if (
        tag === 'svg' ||
        tag === 'defs' ||
        tag === 'clippath' ||
        tag === 'mask' ||
        tag === 'style' ||
        tag === 'lineargradient' ||
        tag === 'radialgradient' ||
        tag === 'pattern' ||
        tag === 'filter' ||
        tag === 'marker' ||
        tag === 'symbol' ||
        tag === 'metadata' ||
        tag === 'title' ||
        tag === 'desc'
      ) {
        continue;
      }
      if (tag === 'g') {
        if (getShapes(el).length === 0) continue;
        out.push(el);
        continue;
      }
      if (
        tag !== 'path' &&
        tag !== 'polygon' &&
        tag !== 'polyline' &&
        tag !== 'rect' &&
        tag !== 'circle' &&
        tag !== 'ellipse'
      ) {
        continue;
      }
      if (el.getAttribute('data-zellij-underfill') === '1') continue;
      /* Nested under a g[id]: parent group owns the family slot (FARID). */
      var parent = el.parentElement;
      var nestedUnderGroup = false;
      while (parent && parent !== svgEl) {
        if (
          parent.tagName &&
          String(parent.tagName).toLowerCase() === 'g' &&
          parent.getAttribute('id') &&
          !isHandPaintedFamilyIgnoredId(parent.getAttribute('id'))
        ) {
          nestedUnderGroup = true;
          break;
        }
        parent = parent.parentElement;
      }
      if (nestedUnderGroup) continue;
      out.push(el);
    }
    return out;
  }

  function getHandPaintedFamilyShapes(el) {
    if (!el) return [];
    if (String(el.tagName || '').toLowerCase() === 'g') {
      return getShapes(el);
    }
    return [el];
  }

  function hpNearNumber(a, b, tol) {
    return Math.abs(a - b) <= tol;
  }

  function hpClusterCenters1D(vals, thr) {
    if (!vals.length) return [];
    var sorted = vals.slice().sort(function (a, b) {
      return a - b;
    });
    var clusters = [[sorted[0]]];
    var i;
    for (i = 1; i < sorted.length; i++) {
      var last = clusters[clusters.length - 1];
      if (sorted[i] - last[last.length - 1] > thr) {
        clusters.push([sorted[i]]);
      } else {
        last.push(sorted[i]);
      }
    }
    return clusters.map(function (c) {
      var sum = 0;
      var j;
      for (j = 0; j < c.length; j++) sum += c[j];
      return sum / c.length;
    });
  }

  /*
    Hand-painted tile-sheet layout. Structural classes:
      - grid: uniform rectangular lattice (ZAHA/FARID 3x3) → crop to origin cell
      - honeycomb: staggered/rosette pack (~7 hexes) → crop to ONE origin hex
        (the physical 4" tile); not the whole multi-tile _2 sheet
      - other: leave full (safer default)
  */
  function analyzeHandPaintedTileSheetLayout(svgEl) {
    var empty = {
      kind: 'other',
      instances: [],
      primary: '',
      thr: 0,
      colMean: 0,
      rowMean: 0,
      squareish: false,
      diagHeavy: 0,
      axisHeavy: 0
    };
    if (!svgEl || !svgEl.getBBox) return empty;
    var els = getHandPaintedFamilyElements(svgEl);
    if (els.length < 4) return empty;
    var byBase = {};
    var i;
    for (i = 0; i < els.length; i++) {
      var el = els[i];
      var id = el.getAttribute('id') || '';
      var base = getHandPaintedFamilyBase(el);
      if (!base) continue;
      var box = null;
      try {
        var b = el.getBBox();
        if (b && (b.width > 0 || b.height > 0)) {
          box = {
            cx: b.x + b.width / 2,
            cy: b.y + b.height / 2,
            w: b.width,
            h: b.height
          };
        }
      } catch (err) {
        /* ignore */
      }
      if (!box) continue;
      if (!byBase[base]) byBase[base] = [];
      byBase[base].push({ id: id, box: box });
    }
    var primary = '';
    var instances = [];
    Object.keys(byBase).forEach(function (base) {
      if (byBase[base].length > instances.length) {
        primary = base;
        instances = byBase[base];
      }
    });
    if (instances.length < 4) return empty;
    var origin =
      instances.filter(function (inst) {
        return inst.id === primary;
      })[0] || instances[0];
    var cellW = origin.box.w;
    var cellH = origin.box.h;
    var thr = Math.max(20, Math.min(cellW || 100, cellH || 100) * 0.35);
    var cxs = instances.map(function (inst) {
      return inst.box.cx;
    });
    var cys = instances.map(function (inst) {
      return inst.box.cy;
    });
    var cols = hpClusterCenters1D(cxs, thr);
    var rows = hpClusterCenters1D(cys, thr);
    var colSteps = [];
    var rowSteps = [];
    for (i = 1; i < cols.length; i++) colSteps.push(cols[i] - cols[i - 1]);
    for (i = 1; i < rows.length; i++) rowSteps.push(rows[i] - rows[i - 1]);
    var colMean = 0;
    var rowMean = 0;
    for (i = 0; i < colSteps.length; i++) colMean += colSteps[i];
    for (i = 0; i < rowSteps.length; i++) rowMean += rowSteps[i];
    if (colSteps.length) colMean /= colSteps.length;
    if (rowSteps.length) rowMean /= rowSteps.length;
    var colReg =
      colSteps.length > 0 &&
      colSteps.every(function (s) {
        return hpNearNumber(s, colMean, colMean * 0.15 + 5);
      });
    var rowReg =
      rowSteps.length > 0 &&
      rowSteps.every(function (s) {
        return hpNearNumber(s, rowMean, rowMean * 0.15 + 5);
      });
    var expected = cols.length * rows.length;
    var countOk =
      cols.length >= 2 &&
      rows.length >= 2 &&
      Math.abs(expected - instances.length) <=
        Math.max(1, Math.floor(instances.length * 0.15));
    var squareish =
      colMean > 0 &&
      rowMean > 0 &&
      hpNearNumber(rowMean, colMean, Math.max(rowMean, colMean) * 0.12 + 5);
    var diagHeavy = 0;
    var axisHeavy = 0;
    var a;
    var c;
    for (a = 0; a < instances.length; a++) {
      for (c = a + 1; c < instances.length; c++) {
        var dx = Math.abs(instances[a].box.cx - instances[c].box.cx);
        var dy = Math.abs(instances[a].box.cy - instances[c].box.cy);
        if (dx > thr && dy > thr) diagHeavy++;
        else if ((dx > thr && dy <= thr) || (dy > thr && dx <= thr)) axisHeavy++;
      }
    }
    var staggered = diagHeavy > axisHeavy * 1.2;
    /* Hex row pitch ≈ col * √3/2 */
    var hexPitch =
      colMean > 0 &&
      rowMean > 0 &&
      hpNearNumber(
        rowMean,
        colMean * 0.866,
        Math.max(rowMean, colMean) * 0.18 + 8
      );
    var kind = 'other';
    if (
      colReg &&
      rowReg &&
      countOk &&
      squareish &&
      !staggered
    ) {
      kind = 'grid';
    } else if (
      instances.length >= 5 &&
      instances.length <= 9 &&
      (staggered || hexPitch || (!squareish && colReg && rowReg))
    ) {
      kind = 'honeycomb';
    }
    return {
      kind: kind,
      instances: instances,
      primary: primary,
      thr: thr,
      colMean: colMean,
      rowMean: rowMean,
      squareish: squareish,
      diagHeavy: diagHeavy,
      axisHeavy: axisHeavy
    };
  }

  function isHandPaintedUniformCellGrid(svgEl) {
    return analyzeHandPaintedTileSheetLayout(svgEl).kind === 'grid';
  }

  function isHandPaintedHoneycombRosette(svgEl) {
    return analyzeHandPaintedTileSheetLayout(svgEl).kind === 'honeycomb';
  }

  /*
    HP tile sheet: show the full `_2` as delivered (no origin/hex/grid crop).
    Still measure the origin-cell width into data-zellij-hp-tile-unit for room
    physical scale. Regular mosaics never call this.
  */
  function cropSvgToHandPaintedOriginCell(svgEl) {
    if (!svgEl) return false;
    var existing = svgEl.getAttribute('data-zellij-hp-tile-layout');
    /* Restore any prior crop so the sheet displays full again. */
    if (existing === 'grid-crop' || existing === 'hex-crop') {
      var restoreVb = svgEl.getAttribute('data-zellij-hp-sheet-vb');
      if (restoreVb) svgEl.setAttribute('viewBox', restoreVb);
      svgEl.removeAttribute('data-zellij-hp-origin-crop');
      existing = '';
    }
    if (
      existing === 'full' &&
      parseFloat(svgEl.getAttribute('data-zellij-hp-tile-unit') || '') > 0
    ) {
      return false;
    }

    var sheetSize = null;
    try {
      if (svgEl.viewBox && svgEl.viewBox.baseVal && svgEl.viewBox.baseVal.width > 0) {
        sheetSize =
          svgEl.viewBox.baseVal.x +
          ' ' +
          svgEl.viewBox.baseVal.y +
          ' ' +
          svgEl.viewBox.baseVal.width +
          ' ' +
          svgEl.viewBox.baseVal.height;
      }
    } catch (errSheet) {
      /* ignore */
    }
    if (!sheetSize) sheetSize = svgEl.getAttribute('viewBox') || '';
    if (sheetSize) svgEl.setAttribute('data-zellij-hp-sheet-vb', sheetSize);

    var els = getHandPaintedFamilyElements(svgEl);
    var origin = els.filter(function (el) {
      var id = el.getAttribute('id') || '';
      return id && id === getBaseId(id);
    });
    var minX = Infinity;
    var maxX = -Infinity;
    var any = false;
    origin.forEach(function (el) {
      if (!el.getBBox) return;
      try {
        var b = el.getBBox();
        if (!b || !(b.width > 0)) return;
        any = true;
        minX = Math.min(minX, b.x);
        maxX = Math.max(maxX, b.x + b.width);
      } catch (err) {
        /* ignore */
      }
    });
    /* Match prior crop pad so tileUnitSvgW (~285.47) and on-wall px stay stable. */
    var pad = 1;
    if (any) {
      var unionW = Math.max(1, maxX - minX);
      /*
        KOS (and similar): bare A+B origins sit side-by-side, so the union span
        is ~2 tile pitches and tilesPerSheet collapses to ~2 (tilePx~54). Prefer
        family-count pitch when it implies a denser sheet matching other HP.
      */
      var unitW = unionW;
      var layout = analyzeHandPaintedTileSheetLayout(svgEl);
      var nFam = layout.instances && layout.instances.length;
      var sheetW = 0;
      try {
        if (svgEl.viewBox && svgEl.viewBox.baseVal && svgEl.viewBox.baseVal.width > 0) {
          sheetW = svgEl.viewBox.baseVal.width;
        }
      } catch (errVb) {
        /* ignore */
      }
      if (!(sheetW > 0) && sheetSize) {
        var vbParts = String(sheetSize)
          .trim()
          .split(/[\s,]+/)
          .map(parseFloat);
        if (vbParts.length >= 4 && vbParts[2] > 0) sheetW = vbParts[2];
      }
      if (sheetW > 0 && nFam >= 4) {
        var countTiles = Math.max(1, Math.round(Math.sqrt(nFam)));
        var fromCount = sheetW / countTiles;
        var unionTiles = sheetW / unionW;
        if (fromCount > 0 && unionTiles < countTiles * 0.85) {
          unitW = fromCount;
        }
      }
      svgEl.setAttribute(
        'data-zellij-hp-tile-unit',
        String(unitW + pad * 2)
      );
    }
    svgEl.setAttribute('data-zellij-hp-tile-layout', 'full');
    svgEl.removeAttribute('data-zellij-hp-origin-crop');
    return false;
  }

  function setFill(shape, hex) {
    if (hex) shape.setAttribute('data-zellij-fill', hex);
    shape.setAttribute('fill', hex);
    shape.style.fill = hex;
  }

  function getShapeHex(shape) {
    if (!shape) return '';
    var stored = shape.getAttribute('data-zellij-fill');
    if (stored) return stored;
    var fill = shape.getAttribute('fill') || shape.style.fill || '';
    if (fill && fill.indexOf('url(') === 0) return '';
    return fill;
  }

  function isLight(hex) {
    var r = parseInt(hex.slice(1, 3), 16);
    var g = parseInt(hex.slice(3, 5), 16);
    var b = parseInt(hex.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.85;
  }

  var CURSOR_COLOR_PLACEHOLDER = '#DYNAMIC_COLOR';

  var BRUSH_CURSOR_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 -100 430 540">' +
    '<path d="M296.003,284.517l-52.542-30.805c-2.865,0.345-5.734,0.913-8.59,1.73c-13.717,3.937-33.557,15.227-50.186,46.668c-13.202,24.961-8.33,46.331-4.031,65.187c4.108,18.023,7.354,32.259-4.718,46.5c-1.187,1.399-1.511,3.336-0.845,5.047c0.419,1.076,1.188,1.952,2.15,2.512c0.571,0.33,1.21,0.551,1.885,0.637c30.535,3.836,76.235-14.17,104.421-62.771c12.802-22.074,18.3-43.299,15.902-61.38C298.825,293.125,297.653,288.672,296.003,284.517zM244.507,287.847c-0.941,2.5-2.8,4.482-5.234,5.584c-17.741,8.03-20.915,22.067-21.042,22.662c-0.702,3.69-3.553,6.751-7.244,7.732c-0.327,0.087-0.659,0.157-0.995,0.212c-1.14,0.182-2.299,0.163-3.441-0.055c-5.418-1.033-8.985-6.277-7.954-11.694c0.19-1.001,4.985-24.655,32.431-37.078c0.506-0.229,1.027-0.414,1.557-0.556c4.743-1.261,9.654,1.07,11.678,5.542C245.361,282.63,245.449,285.347,244.507,287.847z"' +
    ' fill="' +
    CURSOR_COLOR_PLACEHOLDER +
    '" stroke="#1B1F3B" stroke-width="8" stroke-linejoin="round"/>' +
    '<path transform="translate(296,284) scale(1,1.4) translate(-296,-284)"' +
    ' d="M418.939,11.153c-10.538-6.111-24.009-3.033-30.847,7.05c-4.299,6.338-105.41,155.47-118.428,177.916c-6.509,11.224-13.845,29.443-19.947,46.1l52.542,30.805c11.443-13.576,23.674-29.061,30.275-40.443c13.17-22.708,92.25-184.284,95.609-191.151C433.5,30.485,429.48,17.264,418.939,11.153z"' +
    ' fill="#1B1F3B"/>' +
    '</svg>';

  function normalizeHex(hex) {
    var cleaned = (hex || '').replace(/^#/, '').trim();
    if (cleaned.length === 6) return '#' + cleaned.toLowerCase();
    if (cleaned.length === 3) {
      return (
        '#' +
        cleaned[0] +
        cleaned[0] +
        cleaned[1] +
        cleaned[1] +
        cleaned[2] +
        cleaned[2]
      );
    }
    return '#1B1F3B';
  }

  function buildBrushCursor(hex) {
    var fill = normalizeHex(hex);
    var svg = BRUSH_CURSOR_SVG.split(CURSOR_COLOR_PLACEHOLDER).join(fill);
    return "url('data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg) + "') 20 47, crosshair";
  }

  function highlightShapes(shapes) {
    shapes.forEach(function (s) {
      s.setAttribute('data-orig-stroke', s.getAttribute('stroke') || 'none');
      s.setAttribute('data-orig-sw', s.getAttribute('stroke-width') || '0');
      s.setAttribute('data-orig-ve', s.getAttribute('vector-effect') || '');
      s.setAttribute('data-orig-style-stroke', s.style.stroke || '');
      s.setAttribute('stroke', '#555555');
      s.setAttribute('stroke-width', '1.5');
      s.setAttribute('vector-effect', 'non-scaling-stroke');
      s.style.stroke = '#555555';
      s.style.filter = 'brightness(1.25)';
    });
  }

  function restoreShapes(shapes) {
    shapes.forEach(function (s) {
      s.style.filter = '';
      s.style.opacity = '1';
      var origStroke = s.getAttribute('data-orig-stroke');
      var origSW = s.getAttribute('data-orig-sw');
      var origVE = s.getAttribute('data-orig-ve');
      var origStyleStroke = s.getAttribute('data-orig-style-stroke');
      if (origStroke !== null) s.setAttribute('stroke', origStroke);
      if (origSW !== null) s.setAttribute('stroke-width', origSW);
      if (origVE) s.setAttribute('vector-effect', origVE);
      else s.removeAttribute('vector-effect');
      if (origStyleStroke !== null) s.style.stroke = origStyleStroke;
      s.removeAttribute('data-orig-stroke');
      s.removeAttribute('data-orig-sw');
      s.removeAttribute('data-orig-ve');
      s.removeAttribute('data-orig-style-stroke');
    });
  }

  /** Strip diacritics and non-ASCII chars so pattern keys match SVG filenames (e.g. CHÂTEAU → CHATEAU). */
  function toPatternAssetKey(name) {
    if (!name) return '';
    return String(name)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_')
      .replace(/[^A-Za-z0-9_]/g, '')
      .toUpperCase();
  }

  /** Pre-normalization pattern key (accents preserved) for load-design fallback. */
  function toLegacyPatternKey(name) {
    if (!name) return '';
    return String(name).replace(/\s+/g, '_').toUpperCase();
  }

  function legacyKeyFromProductTitle(title) {
    if (!title) return '';
    var t = title.toUpperCase();
    var raw;
    if (t.indexOf(' MOSAIC') >= 0) raw = t.split(' MOSAIC')[0];
    else if (t.indexOf(' BORDER') >= 0) raw = t.split(' BORDER')[0];
    else raw = t.split(' ')[0];
    return raw.replace(/\s+/g, '_');
  }

  /*
    Dimensions come from custom.tile_dimensions on the full-size custom
    colorway product. PATTERN_DIMENSIONS is the per-pattern fallback when
    the metafield is absent; DEFAULT_DIMENSIONS is last resort only.
  */
  var PATTERN_DIMENSIONS = {
    /* Hand-painted: Zellige Spec Sheet 4" x 4" x 1/2" = 10 x 10 x 1.3 cm */
    FARID: { in: '4" x 4" x 1/2"', cm: '10 x 10 x 1.3 cm' },
    FRACTAL: { in: '4" x 4" x 1/2"', cm: '10 x 10 x 1.3 cm' },
    KOS: { in: '4" x 4" x 1/2"', cm: '10 x 10 x 1.3 cm' },
    MAY: { in: '4" x 4" x 1/2"', cm: '10 x 10 x 1.3 cm' },
    MELEHI: { in: '4" x 4" x 1/2"', cm: '10 x 10 x 1.3 cm' },
    OJAI: { in: '4" x 4" x 1/2"', cm: '10 x 10 x 1.3 cm' },
    RUFUS: { in: '4" x 4" x 1/2"', cm: '10 x 10 x 1.3 cm' },
    TESS: { in: '4" x 4" x 1/2"', cm: '10 x 10 x 1.3 cm' },
    WILLIAMS: { in: '4" x 4" x 1/2"', cm: '10 x 10 x 1.3 cm' },
    WRIGHT: { in: '4" x 4" x 1/2"', cm: '10 x 10 x 1.3 cm' },
    YVES: { in: '4" x 4" x 1/2"', cm: '10 x 10 x 1.3 cm' },
    ZAHA: { in: '4" x 4" x 1/2"', cm: '10 x 10 x 1.3 cm' }
  };

  var DEFAULT_DIMENSIONS = {
    in: '11 13/16" x 11 13/16" x 1/2"',
    cm: '30 x 30 x 1.3 cm'
  };

  var SPEC_BRAND_NAVY = [26, 42, 58];
  var SPEC_BRAND_INK = [28, 28, 28];
  var SPEC_SHEET_DOWNLOAD_ICON =
    '<svg class="zellij-lab__spec-sheet-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
    '<path d="M12 3v12m0 0l-4-4m4 4l4-4M4 21h16"/>' +
    '</svg>';

  var ROOM_FULLSCREEN_CLOSE_ICON =
    '<svg class="zellij-lab__room-fullscreen-close-svg" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">' +
    '<path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>' +
    '</svg>';

  var ROOM_FULLSCREEN_ANIM_MS = 180;

  var SPEC_SHEET_LOGO_ASPECT = 311.81 / 226.77;
  var SPEC_SHEET_PAGE = { w: 210, h: 297, margin: 12 };
  var SPEC_SHEET_LOGO_H = 21;
  var SPEC_SHEET_GRID_ROWS = 3;
  var SPEC_SHEET_GRID_ROW_GAP = 4;
  var SPEC_SHEET_GRID_COL_GAP = 3.5;
  var SPEC_SHEET_COLOR_SWATCH_MM = 5;
  var SPEC_SHEET_COLOR_HEADING_H = 4;
  var SPEC_SHEET_COLOR_HEADING_GAP = 2;
  var SPEC_SHEET_COLOR_ROW_GAP = 9;
  var SPEC_SHEET_COLOR_ROW_GAP_TIGHT = 8;
  var SPEC_SHEET_COLOR_SPECS_GAP = 7;
  var SPEC_SHEET_SPEC_LABEL_VALUE_GAP = 5;
  var SPEC_SHEET_SPEC_BLOCK_GAP = 5;
  var SPEC_SHEET_SPEC_VALUE_H = 3.5;
  var SPEC_SHEET_MAIN_BLOCK_TAIL = 3;

  function specSheetColorRowGap(count) {
    return count > 5 ? SPEC_SHEET_COLOR_ROW_GAP_TIGHT : SPEC_SHEET_COLOR_ROW_GAP;
  }

  function measureSpecSheetRightColHeight(doc, colorSwatches, patternLabel, rightColW) {
    var rowGap = specSheetColorRowGap(colorSwatches.length);
    var h = SPEC_SHEET_COLOR_HEADING_H + SPEC_SHEET_COLOR_HEADING_GAP;
    if (colorSwatches.length) {
      h += colorSwatches.length * rowGap;
    } else {
      h += 6;
    }
    h += SPEC_SHEET_COLOR_SPECS_GAP;
    h +=
      SPEC_SHEET_SPEC_LABEL_VALUE_GAP +
      SPEC_SHEET_SPEC_VALUE_H +
      SPEC_SHEET_SPEC_BLOCK_GAP +
      SPEC_SHEET_SPEC_LABEL_VALUE_GAP +
      SPEC_SHEET_SPEC_VALUE_H;
    return h;
  }

  function computeSpecSheetTilePlacement(mainTop, rightColBlockH, tileBox, margin) {
    var mainBlockH = Math.max(rightColBlockH, tileBox.h);
    var tileY = mainTop + Math.max(0, (rightColBlockH - tileBox.h) / 2);
    return {
      tileX: margin,
      tileY: tileY,
      mainBlockH: mainBlockH
    };
  }

  var SPEC_SHEET_ICON_URLS_DEFAULT = {
    waterproof:
      'https://cdn.shopify.com/s/files/1/0774/6279/8370/files/zelligehome-icon-productpage-07.svg?v=1749914983',
    'splash-proof':
      'https://cdn.shopify.com/s/files/1/0774/6279/8370/files/zelligehome-icon-productpage-05.svg?v=1749914983',
    'heat-compatible':
      'https://cdn.shopify.com/s/files/1/0774/6279/8370/files/zelligehome-icon-productpage-04.svg?v=1749914983',
    'freeze-thaw':
      'https://cdn.shopify.com/s/files/1/0774/6279/8370/files/zelligehome-icon-productpage-06.svg?v=1749914983',
    'interior-floor':
      'https://cdn.shopify.com/s/files/1/0774/6279/8370/files/zelligehome-icon-productpage-08.svg?v=1749914983',
    'exterior-floor':
      'https://cdn.shopify.com/s/files/1/0774/6279/8370/files/zelligehome-icon-productpage-01.svg?v=1749914983',
    'interior-walls':
      'https://cdn.shopify.com/s/files/1/0774/6279/8370/files/zelligehome-icon-productpage-02.svg?v=1749914983',
    'exterior-walls':
      'https://cdn.shopify.com/s/files/1/0774/6279/8370/files/zelligehome-icon-productpage-03.svg?v=1749914983'
  };

  var SPEC_PROPERTIES = [
    {
      iconKey: 'waterproof',
      titleKey: 'spec_prop_waterproof_title',
      textKey: 'spec_prop_waterproof_text'
    },
    {
      iconKey: 'splash-proof',
      titleKey: 'spec_prop_splash_proof_title',
      textKey: 'spec_prop_splash_proof_text'
    },
    {
      iconKey: 'heat-compatible',
      titleKey: 'spec_prop_heat_compatible_title',
      textKey: 'spec_prop_heat_compatible_text'
    },
    {
      iconKey: 'freeze-thaw',
      titleKey: 'spec_prop_freeze_thaw_title',
      textKey: 'spec_prop_freeze_thaw_text'
    },
    {
      iconKey: 'interior-floor',
      titleKey: 'spec_prop_interior_floor_title',
      textKey: 'spec_prop_interior_floor_text'
    },
    {
      iconKey: 'exterior-floor',
      titleKey: 'spec_prop_exterior_floor_title',
      textKey: 'spec_prop_exterior_floor_text'
    },
    {
      iconKey: 'interior-walls',
      titleKey: 'spec_prop_interior_walls_title',
      textKey: 'spec_prop_interior_walls_text'
    },
    {
      iconKey: 'exterior-walls',
      titleKey: 'spec_prop_exterior_walls_title',
      textKey: 'spec_prop_exterior_walls_text'
    }
  ];

  function patternLookupKey(name) {
    return String(name || '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  }

  function parseTileDimensionsMetafield(raw) {
    if (!raw) return null;
    if (typeof raw === 'object' && raw.in && raw.cm) {
      return { in: String(raw.in), cm: String(raw.cm) };
    }
    var text = String(raw).trim();
    if (!text) return null;
    if (text.charAt(0) === '{') {
      try {
        var parsed = JSON.parse(text);
        if (parsed && parsed.in && parsed.cm) {
          return { in: String(parsed.in), cm: String(parsed.cm) };
        }
      } catch (err) {
        /* fall through */
      }
    }
    var lines = text.split(/\r?\n/).map(function (line) {
      return line.trim();
    }).filter(Boolean);
    if (lines.length >= 2) {
      return { in: lines[0], cm: lines[1] };
    }
    var pipe = text.split('|');
    if (pipe.length >= 2) {
      return { in: pipe[0].trim(), cm: pipe[1].trim() };
    }
    return null;
  }

  function zellijLabDevMode() {
    try {
      if (window.ZELLIJ_CONFIG && window.ZELLIJ_CONFIG.devMode) return true;
      var host = window.location && window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') return true;
      if (/preview_theme_id=/.test(String(window.location.search || ''))) return true;
    } catch (err) {
      /* ignore */
    }
    return false;
  }

  function getPatternDimensions(name, override) {
    var source =
      override !== undefined
        ? override
        : window.ZELLIJ_CONFIG && window.ZELLIJ_CONFIG.tileDimensions;
    var fromMeta = parseTileDimensionsMetafield(source);
    if (fromMeta) return fromMeta;
    var key = String(name || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_');
    if (key && PATTERN_DIMENSIONS[key]) {
      return PATTERN_DIMENSIONS[key];
    }
    if (zellijLabDevMode()) {
      console.warn(
        '[zellij-lab] tile_dimensions metafield missing for pattern "' +
          (name || '') +
          '"; using DEFAULT_DIMENSIONS.'
      );
    }
    return DEFAULT_DIMENSIONS;
  }

  function stripInchQuotes(value) {
    return String(value || '')
      .trim()
      .replace(/"+$/g, '')
      .replace(/^"+/g, '');
  }

  function formatInchPart(value) {
    var bare = stripInchQuotes(value);
    return bare ? bare + '"' : '';
  }

  function parseDimensionsForPdf(dims) {
    var inMatch = String(dims.in || '').match(/^(.+?)\s*x\s*(.+?)\s*x\s*(.+)$/i);
    var cmMatch = String(dims.cm || '').match(/^(.+?)\s*x\s*(.+?)\s*x\s*(.+?)\s*cm$/i);
    return {
      sizeIn: inMatch
        ? formatInchPart(inMatch[1]) + ' x ' + formatInchPart(inMatch[2])
        : dims.in,
      sizeCm: cmMatch
        ? cmMatch[1].trim() + ' x ' + cmMatch[2].trim() + ' cm'
        : String(dims.cm || '').replace(/\s*x\s*1\.3.*$/, ''),
      thicknessIn: inMatch ? formatInchPart(inMatch[3]) : '1/2"',
      thicknessCm: cmMatch ? cmMatch[3].trim() + ' cm' : '1.3 cm'
    };
  }

  function parsePatternDimensionsForCanvas(name, override) {
    var dims = getPatternDimensions(name, override);
    var cmMatch = String(dims.cm || '').match(/^(.+?)\s*x\s*(.+?)\s*x\s*(.+?)\s*cm$/i);
    var inMatch = String(dims.in || '').match(/^(.+?)\s*x\s*(.+?)\s*x\s*(.+)$/i);
    var widthCm = cmMatch ? cmMatch[1].trim() : '29.21';
    var heightCm = cmMatch ? cmMatch[2].trim() : '29.21';
    var widthIn = inMatch ? formatInchPart(inMatch[1]) : '11 1/2"';
    var heightIn = inMatch ? formatInchPart(inMatch[2]) : '11 1/2"';
    var widthNum = parseFloat(String(widthCm).replace(',', '.')) || 0;
    var heightNum = parseFloat(String(heightCm).replace(',', '.')) || 0;
    var tileAspect = widthNum > 0 && heightNum > 0 ? widthNum / heightNum : 1;
    var heightRatio = widthNum > 0 && heightNum > 0 ? heightNum / widthNum : 1;
    return {
      widthCm: widthCm,
      heightCm: heightCm,
      widthIn: widthIn,
      heightIn: heightIn,
      widthNum: widthNum,
      heightNum: heightNum,
      tileAspect: tileAspect,
      heightRatio: heightRatio,
      widthLabel: widthCm + ' cm / ' + widthIn,
      heightLabel: heightCm + ' cm / ' + heightIn,
      mobileLabel:
        widthCm + ' x ' + heightCm + ' cm  ·  ' + widthIn + ' x ' + heightIn
    };
  }

  function specSheetAspectRatio(name) {
    var parsed = parsePatternDimensionsForCanvas(name);
    var wCm = parseFloat(String(parsed.widthCm).replace(',', '.')) || 1;
    var hCm = parseFloat(String(parsed.heightCm).replace(',', '.')) || 1;
    if (!(wCm > 0 && hCm > 0)) return 1;
    return wCm / hCm;
  }

  function fitSpecTileBox(maxW, maxH, aspect) {
    var ratio = aspect > 0 ? aspect : 1;
    var w = maxW;
    var h = w / ratio;
    if (h > maxH) {
      h = maxH;
      w = h * ratio;
    }
    return { w: w, h: h };
  }

  function specSheetTitle(name, type, displayName) {
    var label = (displayName || '').trim() || String(name || '').replace(/_/g, ' ');
    var typeLabel =
      type === 'border' ? zCopy('spec_type_border') : zCopy('spec_type_mosaic');
    return (label + ' - ' + typeLabel).toUpperCase();
  }

  function getSpecSheetIconUrls() {
    var cfg = window.ZELLIJ_CONFIG && window.ZELLIJ_CONFIG.specSheetIconUrls;
    if (cfg && typeof cfg === 'object') {
      var merged = {};
      Object.keys(SPEC_SHEET_ICON_URLS_DEFAULT).forEach(function (key) {
        merged[key] = cfg[key] || SPEC_SHEET_ICON_URLS_DEFAULT[key];
      });
      return merged;
    }
    return SPEC_SHEET_ICON_URLS_DEFAULT;
  }

  function resolveSpecSheetLogoUrl() {
    if (window.__zellijLogoUrl) return window.__zellijLogoUrl;
    if (window.ZELLIJ_CONFIG && window.ZELLIJ_CONFIG.specSheetLogoUrl) {
      return window.ZELLIJ_CONFIG.specSheetLogoUrl;
    }
    return null;
  }

  function formatSpecDate(date) {
    var d = date || new Date();
    var datePart = d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    var timePart = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    return datePart + ', ' + timePart;
  }

  function hexToRgb(hex) {
    var h = String(hex || '').replace('#', '').trim();
    if (h.length === 3) {
      h = h.charAt(0) + h.charAt(0) + h.charAt(1) + h.charAt(1) + h.charAt(2) + h.charAt(2);
    }
    if (h.length !== 6) return null;
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16)
    };
  }

  function resolveThemeAssetUrl(filename) {
    var cfgUrl = window.ZELLIJ_CONFIG && window.ZELLIJ_CONFIG.jspdfAssetUrl;
    if (cfgUrl) return cfgUrl;
    var scripts = document.querySelectorAll('script[src*="zellij-design-lab"]');
    for (var i = 0; i < scripts.length; i++) {
      var src = scripts[i].getAttribute('src') || '';
      if (src.indexOf('zellij-design-lab') !== -1) {
        return src.replace(/zellij-design-lab[^/?]*(\?.*)?$/, filename);
      }
    }
    return null;
  }

  function loadExternalScript(url) {
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = function () {
        resolve();
      };
      script.onerror = function () {
        reject(new Error('Script load failed: ' + url));
      };
      document.head.appendChild(script);
    });
  }

  function resolveJsPdfConstructor() {
    if (window.jspdf && window.jspdf.jsPDF) return window.jspdf.jsPDF;
    if (window.jsPDF) return window.jsPDF;
    return null;
  }

  function loadJsPdf() {
    var existing = resolveJsPdfConstructor();
    if (existing) return Promise.resolve(existing);

    var urls = [];
    var themeAsset = resolveThemeAssetUrl('jspdf.umd.min.js');
    if (themeAsset) urls.push(themeAsset);
    urls.push('https://unpkg.com/jspdf@2.5.2/dist/jspdf.umd.min.js');
    urls.push('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');

    function tryNext(index) {
      if (index >= urls.length) {
        return Promise.reject(new Error('jsPDF failed to load'));
      }
      return loadExternalScript(urls[index])
        .then(function () {
          var ctor = resolveJsPdfConstructor();
          if (ctor) return ctor;
          return tryNext(index + 1);
        })
        .catch(function () {
          return tryNext(index + 1);
        });
    }

    return tryNext(0);
  }

  function downloadPdfDoc(doc, filename) {
    try {
      var blob = doc.output('blob');
      var url = URL.createObjectURL(blob);
      var link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(function () {
        URL.revokeObjectURL(url);
      }, 60000);
    } catch (err) {
      doc.save(filename);
    }
  }

  function imageElementToDataUrl(img, maxWidth) {
    return new Promise(function (resolve) {
      if (!img || !img.src) {
        resolve(null);
        return;
      }
      var loader = new Image();
      loader.crossOrigin = 'anonymous';
      loader.onload = function () {
        var w = loader.naturalWidth || loader.width;
        var h = loader.naturalHeight || loader.height;
        if (!(w > 0 && h > 0)) {
          resolve(img.src);
          return;
        }
        var scale = maxWidth > 0 && w > maxWidth ? maxWidth / w : 1;
        var canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(w * scale));
        canvas.height = Math.max(1, Math.round(h * scale));
        var ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(img.src);
          return;
        }
        ctx.drawImage(loader, 0, 0, canvas.width, canvas.height);
        try {
          resolve(canvas.toDataURL('image/png'));
        } catch (err) {
          resolve(img.src);
        }
      };
      loader.onerror = function () {
        resolve(img.src);
      };
      loader.src = img.src;
    });
  }

  function svgUrlToPngDataUrl(url, renderW, renderH) {
    if (!url) return Promise.resolve(null);
    return fetch(url, { mode: 'cors', credentials: 'omit' })
      .then(function (response) {
        if (!response.ok) throw new Error('SVG fetch failed');
        return response.text();
      })
      .then(function (svgText) {
        var blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
        var blobUrl = URL.createObjectURL(blob);
        return new Promise(function (resolve) {
          var img = new Image();
          img.onload = function () {
            var canvas = document.createElement('canvas');
            canvas.width = renderW;
            canvas.height = renderH;
            var ctx = canvas.getContext('2d');
            if (!ctx) {
              URL.revokeObjectURL(blobUrl);
              resolve(null);
              return;
            }
            ctx.drawImage(img, 0, 0, renderW, renderH);
            URL.revokeObjectURL(blobUrl);
            try {
              resolve(canvas.toDataURL('image/png'));
            } catch (err) {
              resolve(null);
            }
          };
          img.onerror = function () {
            URL.revokeObjectURL(blobUrl);
            resolve(null);
          };
          img.src = blobUrl;
        });
      })
      .catch(function () {
        return null;
      });
  }

  function fetchSpecSheetLogoDataUrl() {
    var url = resolveSpecSheetLogoUrl();
    if (!url) return Promise.resolve(null);
    var renderW = 620;
    var renderH = Math.round(renderW / SPEC_SHEET_LOGO_ASPECT);
    return svgUrlToPngDataUrl(url, renderW, renderH);
  }

  function preloadSpecSheetPropertyIcons() {
    var urls = getSpecSheetIconUrls();
    return Promise.all(
      SPEC_PROPERTIES.map(function (prop) {
        var iconUrl = urls[prop.iconKey];
        if (!iconUrl) {
          return Promise.resolve({ key: prop.iconKey, dataUrl: null });
        }
        return svgUrlToPngDataUrl(iconUrl, 192, 192).then(function (dataUrl) {
          return { key: prop.iconKey, dataUrl: dataUrl };
        });
      })
    ).then(function (results) {
      var map = {};
      results.forEach(function (entry) {
        map[entry.key] = entry.dataUrl;
      });
      return map;
    });
  }

  function fetchTexturePhotoDataUrl(url) {
    if (!url) return Promise.resolve(null);
    return fetch(url, { mode: 'cors', credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('Texture fetch failed');
        return r.blob();
      })
      .then(function (blob) {
        return new Promise(function (resolve, reject) {
          var reader = new FileReader();
          reader.onload = function () {
            resolve(reader.result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      })
      .catch(function () {
        return null;
      });
  }

  function loadImageFromDataUrl(dataUrl) {
    return new Promise(function (resolve) {
      if (!dataUrl) {
        resolve(null);
        return;
      }
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        resolve(null);
      };
      img.src = dataUrl;
    });
  }

  function buildCircularSwatchFromImage(img, hex, sizePx) {
    var px = sizePx > 0 ? sizePx : 100;
    return new Promise(function (resolve) {
      var canvas = document.createElement('canvas');
      canvas.width = px;
      canvas.height = px;
      var ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      var radius = px / 2;
      ctx.beginPath();
      ctx.arc(radius, radius, radius, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      if (img && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
        var scale = Math.max(px / img.naturalWidth, px / img.naturalHeight);
        var dw = img.naturalWidth * scale;
        var dh = img.naturalHeight * scale;
        ctx.drawImage(img, (px - dw) / 2, (px - dh) / 2, dw, dh);
      } else {
        var rgb = hexToRgb(hex);
        ctx.fillStyle = rgb
          ? 'rgb(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ')'
          : '#cccccc';
        ctx.fillRect(0, 0, px, px);
      }
      try {
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        resolve(null);
      }
    });
  }

  function buildCircularSwatchDataUrl(photoDataUrl, hex, sizePx) {
    var px = sizePx > 0 ? sizePx : 100;
    return loadImageFromDataUrl(photoDataUrl).then(function (img) {
      var canvas = document.createElement('canvas');
      canvas.width = px;
      canvas.height = px;
      var ctx = canvas.getContext('2d');
      if (!ctx) return null;
      var radius = px / 2;
      ctx.beginPath();
      ctx.arc(radius, radius, radius, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      if (img) {
        var iw = img.naturalWidth || img.width;
        var ih = img.naturalHeight || img.height;
        var scale = Math.max(px / iw, px / ih);
        var dw = iw * scale;
        var dh = ih * scale;
        ctx.drawImage(img, (px - dw) / 2, (px - dh) / 2, dw, dh);
      } else {
        var rgb = hexToRgb(hex);
        ctx.fillStyle = rgb
          ? 'rgb(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ')'
          : '#cccccc';
        ctx.fillRect(0, 0, px, px);
      }
      try {
        return canvas.toDataURL('image/png');
      } catch (err) {
        return null;
      }
    });
  }

  function drawSpecInkRule(doc, y, margin, pageW) {
    doc.setDrawColor(SPEC_BRAND_INK[0], SPEC_BRAND_INK[1], SPEC_BRAND_INK[2]);
    doc.setLineWidth(0.35);
    doc.line(margin, y, pageW - margin, y);
  }

  function computeSpecSheetHeaderEnd(margin) {
    return margin + 4 + 4 + SPEC_SHEET_LOGO_H + 4 + 0.35 + 8;
  }

  function measureSpecSheetMinCardHeight(doc, cardW) {
    var layouts = measureSpecPropertyCardLayouts(doc, cardW);
    var minH = 0;
    layouts.forEach(function (layout) {
      minH = Math.max(minH, layout.contentH);
    });
    return minH;
  }

  function planSpecSheetLayout(doc, options) {
    var margin = SPEC_SHEET_PAGE.margin;
    var pageH = SPEC_SHEET_PAGE.h;
    var contentW = SPEC_SHEET_PAGE.w - margin * 2;
    var mainColGap = 8;
    var leftColW = contentW * 0.55;
    var rightColW = contentW - leftColW - mainColGap;
    var cardW = (contentW - SPEC_SHEET_GRID_COL_GAP * 2) / 3;
    var mainTop = computeSpecSheetHeaderEnd(margin);
    var gridBottom = pageH - margin;
    var rightColH = measureSpecSheetRightColHeight(
      doc,
      options.colorSwatches,
      options.patternLabel,
      rightColW
    );
    var minCardH = measureSpecSheetMinCardHeight(doc, cardW);
    var tileMaxW = leftColW;
    var tileMaxH = 92;
    var minTileH = 46;
    var tileAspect = options.tileAspect;
    var plan = null;

    while (tileMaxH >= minTileH) {
      var tileBox = fitSpecTileBox(tileMaxW, tileMaxH, tileAspect);
      var tilePlacement = computeSpecSheetTilePlacement(
        mainTop,
        rightColH,
        tileBox,
        margin
      );
      var mainBlockH = tilePlacement.mainBlockH;
      var ruleY = mainTop + mainBlockH + SPEC_SHEET_MAIN_BLOCK_TAIL;
      var gridTop = ruleY + 0.35 + 5;
      var gridAvailH = gridBottom - gridTop;
      var cardH =
        (gridAvailH - SPEC_SHEET_GRID_ROW_GAP * (SPEC_SHEET_GRID_ROWS - 1)) /
        SPEC_SHEET_GRID_ROWS;

      if (cardH + 0.01 >= minCardH) {
        plan = {
          margin: margin,
          contentW: contentW,
          mainTop: mainTop,
          leftColW: leftColW,
          rightColW: rightColW,
          rightColX: margin + leftColW + mainColGap,
          mainColGap: mainColGap,
          tileBox: tileBox,
          tileX: tilePlacement.tileX,
          tileY: tilePlacement.tileY,
          mainBlockH: mainBlockH,
          rightColBlockH: rightColH,
          ruleY: ruleY,
          gridTop: gridTop,
          cardH: cardH,
          cardW: cardW,
          gridBottom: gridBottom
        };
        break;
      }
      tileMaxH -= 2;
    }

    if (!plan) {
      var fallbackTile = fitSpecTileBox(tileMaxW, minTileH, tileAspect);
      var fallbackPlacement = computeSpecSheetTilePlacement(
        mainTop,
        rightColH,
        fallbackTile,
        margin
      );
      var fallbackMainH = fallbackPlacement.mainBlockH;
      var fallbackRuleY = mainTop + fallbackMainH + SPEC_SHEET_MAIN_BLOCK_TAIL;
      var fallbackGridTop = fallbackRuleY + 0.35 + 5;
      var fallbackAvailH = gridBottom - fallbackGridTop;
      var fallbackCardH =
        (fallbackAvailH - SPEC_SHEET_GRID_ROW_GAP * (SPEC_SHEET_GRID_ROWS - 1)) /
        SPEC_SHEET_GRID_ROWS;
      plan = {
        margin: margin,
        contentW: contentW,
        mainTop: mainTop,
        leftColW: leftColW,
        rightColW: rightColW,
        rightColX: margin + leftColW + mainColGap,
        mainColGap: mainColGap,
        tileBox: fallbackTile,
        tileX: fallbackPlacement.tileX,
        tileY: fallbackPlacement.tileY,
        mainBlockH: fallbackMainH,
        rightColBlockH: rightColH,
        ruleY: fallbackRuleY,
        gridTop: fallbackGridTop,
        cardH: fallbackCardH,
        cardW: cardW,
        gridBottom: gridBottom
      };
    }

    plan.lastRowBottom =
      plan.gridTop +
      SPEC_SHEET_GRID_ROWS * plan.cardH +
      SPEC_SHEET_GRID_ROW_GAP * (SPEC_SHEET_GRID_ROWS - 1);
    if (typeof console !== 'undefined' && console.assert) {
      console.assert(
        plan.lastRowBottom <= plan.gridBottom + 0.05,
        'Spec sheet overflow:',
        plan.lastRowBottom,
        plan.gridBottom
      );
    }
    return plan;
  }

  function measureSpecPropertyCardLayouts(doc, cardW, cardH) {
    var cardPad = 4;
    var iconMm = 10;
    var iconGap = 3;
    var titleGap = 1.5;
    var titleBlockH = 4.2;
    var descFontSize = 8;
    var textW = cardW - cardPad * 2;

    return SPEC_PROPERTIES.map(function (prop) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(descFontSize);
      var descLines = wrapPdfLines(doc, zCopy(prop.textKey), textW);
      var lineH = 3.25;
      if (cardH > 0) {
        var fixedH = cardPad + iconMm + iconGap + titleBlockH + titleGap + cardPad;
        var descAvailH = cardH - fixedH;
        if (descLines.length * lineH > descAvailH && descLines.length > 0) {
          lineH = Math.max(2.9, descAvailH / descLines.length);
        }
      }
      var contentH =
        cardPad +
        iconMm +
        iconGap +
        titleBlockH +
        titleGap +
        descLines.length * lineH +
        cardPad;
      return {
        prop: prop,
        descLines: descLines,
        textW: textW,
        contentH: contentH,
        descLineH: lineH
      };
    });
  }

  function drawSpecPropertyCardGrid(doc, gridTop, margin, contentW, iconMap, cardH) {
    var cols = 3;
    var colGap = SPEC_SHEET_GRID_COL_GAP;
    var rowGap = SPEC_SHEET_GRID_ROW_GAP;
    var cardW = (contentW - colGap * (cols - 1)) / cols;
    var cardPad = 4;
    var iconMm = 10;
    var iconGap = 3;
    var titleGap = 1.5;
    var titleBlockH = 4.2;
    var descFontSize = 8;
    var titleFontSize = 9;
    var cardFill = [245, 243, 238];
    var cardBorder = [229, 225, 216];
    var descColor = [100, 100, 100];
    var titleColor = [40, 40, 40];

    var layouts = measureSpecPropertyCardLayouts(doc, cardW, cardH);
    var rows = Math.ceil(layouts.length / cols);

    layouts.forEach(function (layout, idx) {
      var col = idx % cols;
      var row = Math.floor(idx / cols);
      var x = margin + col * (cardW + colGap);
      var cardY = gridTop + row * (cardH + rowGap);
      var prop = layout.prop;
      var descLineH = layout.descLineH || 3.25;

      doc.setFillColor(cardFill[0], cardFill[1], cardFill[2]);
      doc.setDrawColor(cardBorder[0], cardBorder[1], cardBorder[2]);
      doc.setLineWidth(0.25);
      doc.rect(x, cardY, cardW, cardH, 'FD');

      var iconX = x + cardPad;
      var iconY = cardY + cardPad;
      var iconData = iconMap && iconMap[prop.iconKey];
      if (iconData) {
        try {
          doc.addImage(iconData, 'PNG', iconX, iconY, iconMm, iconMm);
        } catch (iconErr) {
          /* omit failing icon */
        }
      }

      var titleY = iconY + iconMm + iconGap + 3;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(titleFontSize);
      doc.setTextColor(titleColor[0], titleColor[1], titleColor[2]);
      doc.text(zCopy(prop.titleKey), iconX, titleY);

      var descY = titleY + titleGap + 3.2;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(descFontSize);
      doc.setTextColor(descColor[0], descColor[1], descColor[2]);
      layout.descLines.forEach(function (line, lineIdx) {
        doc.text(line, iconX, descY + lineIdx * descLineH);
      });
    });

    if (!rows) return gridTop;
    return gridTop + rows * cardH + (rows - 1) * rowGap;
  }

  function wrapPdfLines(doc, text, maxWidth) {
    return doc.splitTextToSize(String(text || ''), maxWidth);
  }

  function applyPatternShellState(root, patternType, displayName) {
    if (!root) return;
    var isBorder = patternType === 'border';
    root.classList.toggle('zellij-lab--border', isBorder);
    root.classList.toggle('zellij-lab--mosaic', !isBorder);
    root.setAttribute('data-pattern-type', isBorder ? 'border' : 'mosaic');
    var titleEl = root.querySelector('.zellij-lab__pattern-name');
    if (titleEl && displayName) titleEl.textContent = displayName;
    var carpetCol = root.querySelector('.zellij-lab__canvas-col--carpet');
    if (carpetCol) {
      carpetCol.classList.add('zellij-lab__canvas-col--carpet-offscreen');
    }
    var roomPreview = root.querySelector('.zellij-lab__room-preview');
    if (roomPreview) {
      roomPreview.hidden = false;
      roomPreview.setAttribute('aria-hidden', 'false');
    }
    root.querySelectorAll('.zellij-lab__btn-see-room').forEach(function (btn) {
      btn.hidden = false;
      btn.disabled = false;
    });
    syncRoomPreviewTabs(root, patternType);
  }

  function mergeZellijConfig(next) {
    var cfg = window.ZELLIJ_CONFIG || (window.ZELLIJ_CONFIG = {});
    if (!next) return cfg;
    Object.keys(next).forEach(function (key) {
      if (next[key] !== undefined) cfg[key] = next[key];
    });
    if (cfg.palettes && cfg.paletteKey) {
      var list = cfg.palettes[cfg.paletteKey];
      cfg.palette = Array.isArray(list) ? list : cfg.palettes.mosaic || [];
    }
    return cfg;
  }

  function destroyActiveLab() {
    if (activeLabController && typeof activeLabController.destroy === 'function') {
      activeLabController.destroy();
    }
    activeLabController = null;
  }

  /*
    Color yours + card brush: SVG gate lives in zellij-lab-svg-gate.js
    (probe + sessionStorage cache). Re-run here so PDP/related cards are
    covered even if the shared script tagged later.
  */
  function hideColorYoursButton(btn) {
    if (window.ZellijLabSvgGate && typeof window.ZellijLabSvgGate.hideControl === 'function') {
      window.ZellijLabSvgGate.hideControl(btn);
      return;
    }
    if (!btn || !btn.parentNode) return;
    btn.hidden = true;
    btn.removeAttribute('aria-controls');
    btn.setAttribute('data-zellij-svg-unavailable', '1');
    try {
      btn.parentNode.removeChild(btn);
    } catch (err) {
      /* leave hidden */
    }
  }

  function gateColorYoursButtons() {
    if (window.ZellijLabSvgGate && typeof window.ZellijLabSvgGate.gateAll === 'function') {
      window.ZellijLabSvgGate.gateAll();
    }
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', gateColorYoursButtons);
    } else {
      gateColorYoursButtons();
    }
  }

  function workshopTrack(method, arg) {
    try {
      var tracker = window.ZellijWorkshopTracker;
      if (tracker && typeof tracker[method] === 'function') {
        tracker[method](arg);
      }
    } catch (err) {
      /* tracking must never break the lab */
    }
    /* Guest sign-in prompt starts its active-use timer on first paint. */
    if (method === 'onColorApply') {
      try {
        document.dispatchEvent(new CustomEvent('zellij:workshop-color-applied'));
      } catch (err2) {
        /* ignore */
      }
    }
  }

  function workshopPatternInfo(cfg) {
    cfg = cfg || {};
    return {
      handle: cfg.productHandle || '',
      name: cfg.displayName || cfg.patternName || '',
      type: cfg.patternType || '',
      productHandle: cfg.productHandle || '',
      patternName: cfg.patternName || '',
      displayName: cfg.displayName || '',
      patternType: cfg.patternType || ''
    };
  }

  function setPattern(nextConfig) {
    nextConfig = nextConfig || {};
    destroyActiveLab();
    var cfg = mergeZellijConfig(nextConfig);
    if (cfg.designLabAllowed === false) return null;
    var rootId = nextConfig.rootId || cfg.rootId || 'zellij-color-tool';
    var root = document.getElementById(rootId);
    var patternType = nextConfig.patternType || cfg.patternType || 'mosaic';
    var displayName =
      nextConfig.displayName ||
      cfg.displayName ||
      (nextConfig.patternName || cfg.patternName || '').replace(/_/g, ' ');
    if (root && nextConfig.markupTemplateId) {
      var template = document.getElementById(nextConfig.markupTemplateId);
      if (template && template.content && template.content.firstElementChild) {
        var fresh = template.content.firstElementChild.cloneNode(true);
        fresh.id = rootId;
        root.parentNode.replaceChild(fresh, root);
        root = fresh;
      }
    }
    applyPatternShellState(root, patternType, displayName);
    ensureBorderRoomPreviewMarkup(root, patternType);
    var folder =
      nextConfig.patternStorageFolder ||
      cfg.patternStorageFolder ||
      (patternType === 'border' ? 'Borders' : 'Mosaics');
    cfg.patternStorageFolder = folder;
    cfg.patternType = patternType;
    if (displayName) cfg.displayName = displayName;
    var merged = Object.assign({}, cfg, nextConfig, {
      rootId: rootId,
      patternType: patternType,
      patternStorageFolder: folder
    });
    var controller = init(merged);
    workshopTrack('onPattern', workshopPatternInfo(merged));
    return controller;
  }

  function init(config) {
    var cfg          = window.ZELLIJ_CONFIG || {};
    if (cfg.designLabAllowed === false) return null;
    gateColorYoursButtons();
    var CUSTOMER_ID  = cfg.customerId ? String(cfg.customerId) : null;
    var IS_LOGGED_IN = !!CUSTOMER_ID;
    var API_BASE     = typeof window.ZELLIJ_GET_API_BASE === 'function'
      ? window.ZELLIJ_GET_API_BASE()
      : 'https://zellij.vercel.app/api/zelligehome';
    var productHandle = cfg.productHandle || '';
    var productTitle  = cfg.productTitle || '';
    var patternBaseSku = (cfg.patternBaseSku || '').trim();
    var customProductId = cfg.customProductId ? String(cfg.customProductId) : null;
    var customSampleProductId = cfg.customSampleProductId
      ? String(cfg.customSampleProductId)
      : null;
    var customColorwayVariantId = cfg.customColorwayVariantId
      ? String(cfg.customColorwayVariantId)
      : null;
    var customSampleVariantId = cfg.customSampleVariantId
      ? String(cfg.customSampleVariantId)
      : null;
    var artisanSetupVariantId = cfg.artisanSetupVariantId
      ? String(cfg.artisanSetupVariantId)
      : null;
    var artisanPerBoxVariantId = cfg.artisanPerBoxVariantId
      ? String(cfg.artisanPerBoxVariantId)
      : null;
    var customColorwayBoxQuantity = Math.max(
      1,
      parseInt(cfg.customColorwayBoxQuantity, 10) || 1
    );

    var palette = cfg.palette || config.palette || [];
    var paletteKey = cfg.paletteKey || config.paletteKey || 'mosaic';
    CUSTOM_SKU_CONFIG.COLOR_NUMBER_MAP = resolveColorNumberMap(paletteKey);

    var patternName = toPatternAssetKey(config.patternName || '');
    var patternNameLegacy = toLegacyPatternKey(
      config.patternNameLegacy ||
        legacyKeyFromProductTitle(productTitle) ||
        config.patternName ||
        ''
    );
    var patternType = config.patternType || 'mosaic';
    var patternSvgBaseUrl = (cfg.patternSvgBaseUrl || config.patternSvgBaseUrl || '')
      .split('?')[0]
      .replace(/\/$/, '');
    var patternStorageFolder =
      config.patternStorageFolder ||
      cfg.patternStorageFolder ||
      (patternType === 'border' ? 'Borders' : 'Mosaics');
    var glazeNs = glazeNamespace(paletteKey, patternStorageFolder);
    var isHandPaintedLab = glazeNs === 'hand-painted';
    var tileTextureBaseUrl = String(
      cfg.tileTextureBaseUrl || config.tileTextureBaseUrl || ''
    )
      .split('?')[0]
      .replace(/\/$/, '');
    /* Photographic cells live under Tiles/{slug}/{slug}-N.webp (9 per colour). */
    if (tileTextureBaseUrl.indexOf('/Textures') !== -1) {
      tileTextureBaseUrl = tileTextureBaseUrl.replace(/\/Textures\/?$/, '/Tiles');
    }
    if (!tileTextureBaseUrl && patternSvgBaseUrl) {
      tileTextureBaseUrl = patternSvgBaseUrl + '/Tiles';
    }
    if (glazeNs && tileTextureBaseUrl && !/\/hand-painted$/i.test(tileTextureBaseUrl)) {
      tileTextureBaseUrl = tileTextureBaseUrl.replace(/\/$/, '') + '/' + glazeNs;
    }
    var enableTileTexture = cfg.enableTileTexture !== false && cfg.enableTileTexture !== 'false';
    var TEXTURE_POOL_SIZE = 24;
    /* objectBoundingBox patterns cover each shape once; size buckets unused for seams */
    var TEXTURE_BUCKET_RATIO = 1.25;
    var TEXTURE_ROTATIONS = [0, 90, 180, 270];
    var TEXTURE_POOL_KEY = 'obb';
    var texturePreloadPromises = {};
    var textureReadySlugs = {};
    var textureFailedSlugs = {};
    var texturePoolBuilt = {};
    Object.keys(sharedTextureReadySlugs).forEach(function (slug) {
      if (sharedTextureReadySlugs[slug]) {
        textureReadySlugs[slug] = true;
      }
    });

    /*
      Slugs come from the liquid palette (color-asset-map.mjs). Do not derive
      from display names at runtime. Theme swatchImage stays as load fallback.
    */
    palette.forEach(function (color) {
      if (!color) return;
      if (color.slug) {
        color.slug = String(color.slug).toLowerCase();
      } else if (color.swatchImage) {
        var match = String(color.swatchImage).match(/swatch-([a-z0-9-]+)\./i);
        if (match) color.slug = match[1].toLowerCase();
      }
      color.swatchPreviewUrl = supabaseSwatchUrl(
        patternSvgBaseUrl,
        color.slug,
        glazeNs
      );
      color.swatchFallbackUrl = color.swatchImage || '';
    });

    var root = document.getElementById(config.rootId);
    if (!root) return;
    if (root.dataset.zellijLabInit === '1') return;

    var patternTypeEarly = config.patternType || cfg.patternType || 'mosaic';
    var displayNameEarly =
      config.displayName ||
      cfg.displayName ||
      (root.querySelector('.zellij-lab__pattern-name') || {}).textContent ||
      '';
    ensureBorderRoomPreviewMarkup(root, patternTypeEarly);
    applyPatternShellState(root, patternTypeEarly, displayNameEarly);

    labInstanceGeneration += 1;
    var instanceGeneration = labInstanceGeneration;
    var instanceAborted = false;
    var abortCtrl =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    var disposers = [];
    var trackedTimers = [];
    var trackedObservers = [];
    var trackedIdleCallbacks = [];

    /* Cheap hygiene: drop orphan Lab keys + stale drafts before paint. */
    sweepZellijStorage();

    function listen(el, type, fn, opts) {
      if (!el) return fn;
      el.addEventListener(type, fn, opts);
      disposers.push(function () {
        el.removeEventListener(type, fn, opts);
      });
      return fn;
    }

    function trackTimeout(id) {
      if (id != null) trackedTimers.push(id);
      return id;
    }

    function trackIdle(id) {
      if (id != null) trackedIdleCallbacks.push(id);
      return id;
    }

    function trackObserver(observer) {
      if (observer) trackedObservers.push(observer);
      return observer;
    }

    function clearLabHosts() {
      [
        '.zellij-lab__tile-canvas',
        '.zellij-lab__carpet-canvas',
        '.zellij-lab__sample-preview-svg',
        '.zellij-lab__price-card-sample-svg',
        '.zellij-lab__sample-main',
        '.zellij-lab__sample-canvas',
        '.zellij-lab__room-pattern',
        '.zellij-lab__picker',
        '.zellij-lab__used-colors-list'
      ].forEach(function (sel) {
        root.querySelectorAll(sel).forEach(function (el) {
          el.innerHTML = '';
        });
      });
      root.querySelectorAll('.zellij-lab__room-photo').forEach(function (img) {
        img.removeAttribute('src');
      });
    }

    function destroyInstance() {
      if (instanceAborted) return;
      instanceAborted = true;
      /*
        SHOW SAMPLE / carpet toggle use persistent DOM nodes. Clear handlers so
        the next pattern cannot open a prior instance's sampleSvgText (e.g.
        CHATEAU octagon after switching to DIAMAS).
      */
      try {
        root.querySelectorAll('.zellij-lab__btn-show-sample').forEach(function (btn) {
          btn.onclick = null;
          btn.disabled = true;
          btn.setAttribute('aria-pressed', 'false');
          btn.textContent = zCopy('show_sample');
          delete btn.dataset.view;
        });
        var carpetToggle = root.querySelector('.zellij-lab__view-toggle');
        if (carpetToggle) carpetToggle.onclick = null;
      } catch (errHandlers) {
        /* ignore */
      }
      try {
        var sampleMetaKill = root.querySelector('.zellij-lab__sample-meta');
        if (sampleMetaKill) sampleMetaKill.hidden = true;
        var sampleCanvasKill = root.querySelector('.zellij-lab__sample-canvas');
        if (sampleCanvasKill) {
          sampleCanvasKill.hidden = true;
          sampleCanvasKill.setAttribute('aria-hidden', 'true');
          sampleCanvasKill.innerHTML = '';
        }
        if (canvasColEl) canvasColEl.classList.remove('is-sample-view');
      } catch (errSample) {
        /* ignore */
      }
      if (abortCtrl) {
        try {
          abortCtrl.abort();
        } catch (err) {
          /* ignore */
        }
      }
      try {
        if (typeof closeSampleColorPicker === 'function') closeSampleColorPicker();
      } catch (err) {
        /* ignore */
      }
      try {
        if (roomFullscreenEl && !roomFullscreenEl.hidden) {
          closeMobileRoomPreview({ fromUi: false });
        } else {
          releaseRoomPreviewScrollLock();
        }
      } catch (err) {
        /* ignore */
      }
      if (typeof thumbnailCacheTimer !== 'undefined' && thumbnailCacheTimer) {
        clearTimeout(thumbnailCacheTimer);
        thumbnailCacheTimer = null;
      }
      if (typeof roomRecolourTimer !== 'undefined' && roomRecolourTimer) {
        clearTimeout(roomRecolourTimer);
        roomRecolourTimer = null;
      }
      if (typeof previewSlotResizeTimer !== 'undefined' && previewSlotResizeTimer) {
        clearTimeout(previewSlotResizeTimer);
        previewSlotResizeTimer = null;
      }
      if (typeof roomScaleRevealTimer !== 'undefined' && roomScaleRevealTimer) {
        clearTimeout(roomScaleRevealTimer);
        roomScaleRevealTimer = null;
      }
      if (typeof roomScaleForceRevealTimer !== 'undefined' && roomScaleForceRevealTimer) {
        clearTimeout(roomScaleForceRevealTimer);
        roomScaleForceRevealTimer = null;
      }
      trackedTimers.forEach(function (id) {
        clearTimeout(id);
      });
      trackedTimers.length = 0;
      trackedIdleCallbacks.forEach(function (id) {
        if (typeof cancelIdleCallback === 'function') {
          try {
            cancelIdleCallback(id);
          } catch (err) {
            /* ignore */
          }
        }
      });
      trackedIdleCallbacks.length = 0;
      trackedObservers.forEach(function (observer) {
        try {
          observer.disconnect();
        } catch (err) {
          /* ignore */
        }
      });
      trackedObservers.length = 0;
      disposers.slice().forEach(function (dispose) {
        try {
          dispose();
        } catch (err) {
          /* ignore */
        }
      });
      disposers.length = 0;
      clearLabHosts();
      root.querySelectorAll('.zellij-lab__room-pattern').forEach(function (el) {
        delete el.dataset.roomBuildId;
      });
      /* Drop in-flight restore so the next pattern cannot inherit it. */
      if (window._zellijPendingDesign) {
        window._zellijPendingDesign = null;
      }
      if (typeof roomLiveFillCache !== 'undefined' && roomLiveFillCache) {
        roomLiveFillCache.key = null;
      }
      if (typeof roomSceneCache !== 'undefined') {
        roomSceneCache = {};
      }
      if (
        roomFullscreenEl &&
        roomFullscreenEl.classList.contains('zellij-lab__room-fullscreen--portal')
      ) {
        closeMobileRoomPreview({ fromUi: false });
        if (roomFullscreenEl.parentNode) {
          roomFullscreenEl.parentNode.removeChild(roomFullscreenEl);
        }
      }
      if (typeof tileCellImages !== 'undefined') tileCellImages = {};
      texturePreloadPromises = {};
      textureReadySlugs = {};
      textureFailedSlugs = {};
      texturePoolBuilt = {};
      /* Keep sharedTextureCellImages / sharedTextureReadySlugs for reopen. */
      delete root.dataset.zellijLabInit;
      if (
        window.__zellijLabApi &&
        window.__zellijLabApi._generation === instanceGeneration
      ) {
        window.__zellijLabApi = null;
      }
      if (
        activeLabController &&
        activeLabController.generation === instanceGeneration
      ) {
        activeLabController = null;
      }
    }

    activeLabController = {
      generation: instanceGeneration,
      destroy: destroyInstance
    };

    if (!palette.length) {
      var emptyNote = document.createElement('p');
      emptyNote.className = 'zellij-lab__picker-note';
      emptyNote.textContent =
        paletteKey === 'hand-painted'
          ? zCopy('hand_painted_colors_coming')
          : zCopy('color_palette_not_configured');
      var pickerHost = root.querySelector('.zellij-lab__picker');
      if (pickerHost) {
        pickerHost.innerHTML = '';
        pickerHost.appendChild(emptyNote);
      }
      [
        '.zellij-lab__actions',
        '.zellij-lab__used-colors',
        '.zellij-lab__active-indicator',
        '.zellij-lab__canvases'
      ].forEach(function (sel) {
        var el = root.querySelector(sel);
        if (el) el.hidden = true;
      });
      var emptyLoader = root.querySelector('.zellij-lab__loader');
      if (emptyLoader) {
        emptyLoader.classList.add('zellij-lab__loader--hidden');
        emptyLoader.setAttribute('aria-busy', 'false');
      }
      console.warn('[Design Lab] Empty palette for key:', paletteKey);
      activeLabController = null;
      return;
    }
    root.dataset.zellijLabInit = '1';

    var tileCanvas = root.querySelector('.zellij-lab__tile-canvas');
    var carpetCanvas = root.querySelector('.zellij-lab__carpet-canvas');
    var pickerEl = root.querySelector('.zellij-lab__picker');
    var usedListEl = root.querySelector('.zellij-lab__used-colors-list');
    var shuffleBtn = root.querySelector('.zellij-lab__btn-shuffle');
    var resetBtn = root.querySelector('.zellij-lab__btn-reset');
    var activeSwatch = root.querySelector('.zellij-lab__active-color-swatch');
    var activeName = root.querySelector('.zellij-lab__active-color-name');
    var loaderEl = root.querySelector('.zellij-lab__loader');
    var loadState = {
      tileSvg: false,
      carpetSvg: !carpetCanvas,
      swatches: false,
    };

    if (!tileCanvas || !pickerEl) return;

    function maybeHideLoader() {
      if (!loaderEl || loaderEl.classList.contains('zellij-lab__loader--hidden')) return;
      if (loadState.tileSvg && loadState.carpetSvg && loadState.swatches) {
        loaderEl.classList.add('zellij-lab__loader--hidden');
        loaderEl.setAttribute('aria-busy', 'false');
      }
    }

    function preloadSwatchImages() {
      palette.forEach(function (color) {
        var primary = color.swatchPreviewUrl || '';
        var fallback = color.swatchFallbackUrl || color.swatchImage || '';
        if (!primary && !fallback) return;
        var img = new Image();
        img.onerror = function () {
          if (primary && fallback) {
            color.swatchPreviewUrl = fallback;
            var retry = new Image();
            retry.src = fallback;
          }
        };
        img.src = primary || fallback;
      });
    }

    function updateTileCursor() {
      if (!tileCanvas) return;
      tileCanvas.style.cursor = buildBrushCursor(activeColor);
    }

    var defaultColor = palette[0];
    var activeColor = defaultColor.hex;
    var activeColorName = defaultColor.name;
    var hasGroupIds = false;
    var detectedBases = [];
    var colorMap = {};
    var undoStack = [];
    var redoStack = [];
    var tileSvgText = null;
    var carpetSvgText = null;
    var fireplaceSvgText = null;
    var cachedThumbnailUrl = null;
    var cachedThumbnailKey = null;
    var cachedThumbnailDataUrl = null;
    var cachedThumbnailDataKey = null;
    var thumbnailCacheTimer = null;
    var overlayEl = root.closest('zellij-lab-overlay');
    var cachedSampleThumbnailUrl = null;
    var cachedSampleThumbnailKey = null;
    var sampleSvgMetaPromise = null;
    var sampleColorPickerEl = null;
    var sampleColorPickerOutsideHandler = null;
    var sampleSvgText = null;
    var sampleSvgSource = null;
    var samplePreviewBases = [];
    var samplePreviewReady = false;
    var carpetViewMode = 'carpet'; // 'carpet' | 'sample'
    var carpetStage = root.querySelector('.zellij-lab__carpet-stage');
    var samplePreviewEl = root.querySelector('.zellij-lab__sample-preview');
    var samplePreviewSvgHost = root.querySelector('.zellij-lab__sample-preview-svg');
    var priceCardSampleHost = root.querySelector('.zellij-lab__price-card-sample-svg');
    var sampleMainEl = root.querySelector('.zellij-lab__sample-main');
    var sampleCanvasEl = root.querySelector('.zellij-lab__sample-canvas');
    var canvasColEl = root.querySelector('.zellij-lab__col--canvas');
    var carpetViewToggle = root.querySelector('.zellij-lab__view-toggle');
    var showSampleBtns = root.querySelectorAll('.zellij-lab__btn-show-sample');
    var carpetLabelEl = root.querySelector('.zellij-lab__carpet-label');
    var DRAFT_STORAGE_KEY = ZELLIJ_DRAFT_STORAGE_KEY;
    var DRAFT_TTL_MS = ZELLIJ_DRAFT_TTL_MS;
    var reminderBtn = document.querySelector('.zellij-lab-return-reminder');
    var atcGuardEl = document.querySelector('.zellij-lab-atc-guard');
    var lastOrderTrigger = null;
    var colorwayAddInProgress = false;
    var sampleAddInProgress = false;
    var draftDismissedAfterOrder = false;
    var paletteColEl = root.querySelector('.zellij-lab__col--palette');
    var usedLinkBtn = root.querySelector('.zellij-lab__used-link');
    var usedColorsPanel = root.querySelector('.zellij-lab__used-colors');
    var sheetHandleBtn = root.querySelector('.zellij-lab__sheet-handle');
    var SHEET_SNAPS = ['peek', 'half', 'full'];
    var activeFilter = 'all';
    var activeRoomId = defaultRoomIdForPatternType(patternType);
    var roomPreviewReady = true;
    var roomRecolourTimer = null;
    var roomRecolourPendingBases = null;
    var roomPanelPlacement = (function () {
      var raw = cfg.roomPanelPlacement || config.roomPanelPlacement || {};
      return {
        kitchen: Object.assign(
          { x: 0, y: 0.308889, w: 1, h: 0.245556 },
          raw.kitchen || {}
        ),
        bathroom: Object.assign(
          {
            x: 0.22777777777777777,
            y: 0.05155555555555555,
            w: 0.5666666666666667,
            h: 0.8506666666666667,
            stretch: true
          },
          raw.bathroom || {}
        ),
        fireplace: Object.assign({ x: 0, y: 0, w: 1, h: 1 }, raw.fireplace || {}),
        stairs: Object.assign({ x: 0, y: 0, w: 1, h: 1 }, raw.stairs || {})
      };
    })();
    var lastRoomRefreshMs = null;
    /*
      Hold room media invisible until carpet grid, border scene, photo decode,
      and pattern paint are final — then reveal once. Applies to funnel,
      product overlay, and fullscreen (not funnel-only).
    */
    var roomScaleRevealPending = false;
    var roomScaleRevealed = false;
    var roomScaleFrozenColW = 0;
    var roomScaleRevealTimer = null;
    var roomScaleForceRevealTimer = null;
    var ROOM_PREVIEW_REVEAL_TIMEOUT_MS = 1800;
    var overlayEnterSettled = false;
    function isFunnelLabRoot() {
      return !!(
        root &&
        (root.id === 'zellij-funnel-color-tool' ||
          root.closest('.zellij-lab-funnel'))
      );
    }
    function waitForPreviewColStable() {
      return new Promise(function (resolve) {
        var col = root.querySelector('.zellij-lab__col--preview');
        var lastW = -1;
        var lastH = -1;
        var stable = 0;
        var frames = 0;
        var maxFrames = 48;
        function tick() {
          frames += 1;
          var frameEl = root.querySelector(
            '.zellij-lab__room-preview-frame:not(.zellij-lab__room-preview-frame--fullscreen)'
          );
          var box = frameEl || col;
          var w = box ? box.clientWidth : 0;
          var h = box ? box.clientHeight : 0;
          if (w > 8 && h > 8 && w === lastW && h === lastH) {
            stable += 1;
            if (stable >= 2) {
              resolve();
              return;
            }
          } else {
            stable = 0;
            lastW = w;
            lastH = h;
          }
          if (frames >= maxFrames) {
            resolve();
            return;
          }
          requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      });
    }
    function waitForOverlayOpenSettled() {
      return new Promise(function (resolve) {
        function finish() {
          waitForPreviewColStable().then(resolve);
        }
        if (!overlayEl) {
          finish();
          return;
        }
        var anims = [];
        try {
          var overlayPart =
            overlayEl.getShadowPartByName && overlayEl.getShadowPartByName('overlay');
          var contentPart =
            overlayEl.getShadowPartByName && overlayEl.getShadowPartByName('content');
          [overlayPart, contentPart].forEach(function (el) {
            if (el && typeof el.getAnimations === 'function') {
              anims = anims.concat(el.getAnimations());
            }
          });
          if (
            !anims.length &&
            overlayEl.shadowRoot &&
            typeof overlayEl.shadowRoot.getAnimations === 'function'
          ) {
            anims = overlayEl.shadowRoot.getAnimations();
          }
        } catch (err) {
          anims = [];
        }
        if (!anims.length) {
          finish();
          return;
        }
        Promise.all(
          anims.map(function (anim) {
            return anim.finished.catch(function () {});
          })
        ).then(finish);
      });
    }
    function ensureRoomPreviewLoader(hostEl) {
      if (!hostEl) return null;
      var loader = hostEl.querySelector('.zellij-lab__room-preview-loader');
      if (loader) return loader;
      loader = document.createElement('div');
      loader.className = 'zellij-lab__room-preview-loader';
      loader.setAttribute('aria-hidden', 'true');
      loader.innerHTML =
        '<div class="zellij-lab__spinner" role="status" aria-label="' + zCopy('loading_preview_aria') + '"></div>';
      hostEl.appendChild(loader);
      return loader;
    }

    function setRoomPreviewLoadersVisible(visible) {
      forEachRoomPreviewFrame(function (frame) {
        ensureRoomPreviewLoader(frame);
        frame.classList.toggle('is-room-preview-loading', !!visible);
      });
    }

    function clearRoomPreviewRevealTimers() {
      if (roomScaleRevealTimer) {
        clearTimeout(roomScaleRevealTimer);
        roomScaleRevealTimer = null;
      }
      if (roomScaleForceRevealTimer) {
        clearTimeout(roomScaleForceRevealTimer);
        roomScaleForceRevealTimer = null;
      }
    }

    var roomCarpetForceDeferCount = 0;
    function forceRevealRoomPreview() {
      if (!roomPreviewReady || roomScaleRevealed) return;
      /*
        Do not flash the tile-sheet race mount. Keep the hold / previous frame
        until carpet load settles (then normal reveal or genuine tile fallback).
      */
      var tileRaceStillOpen =
        carpetSheetLoadPending() ||
        (carpetSheetAvailable() &&
          queryActiveRoomPatternSlots().some(function (slot) {
            return (
              slot.patternEl &&
              slot.patternEl.dataset &&
              slot.patternEl.dataset.roomSource === 'tile'
            );
          }));
      if (tileRaceStillOpen && roomCarpetForceDeferCount < 50) {
        roomCarpetForceDeferCount += 1;
        if (carpetSheetAvailable()) {
          refreshRoomPreview();
        }
        clearRoomPreviewRevealTimers();
        roomScaleRevealTimer = setTimeout(function () {
          revealRoomPreviewIfStable();
          if (!roomScaleRevealed) {
            forceRevealRoomPreview();
          }
        }, 120);
        return;
      }
      roomCarpetForceDeferCount = 0;
      clearRoomPreviewRevealTimers();
      roomScaleRevealPending = false;
      roomScaleRevealed = true;
      var previewCol = root.querySelector('.zellij-lab__col--preview');
      roomScaleFrozenColW = previewCol ? previewCol.clientWidth : 0;
      root.querySelectorAll('.zellij-lab__room-preview-media').forEach(function (mediaEl) {
        mediaEl.style.setProperty('transition', 'none', 'important');
      });
      if (roomFullscreenEl) {
        roomFullscreenEl.querySelectorAll('.zellij-lab__room-preview-media').forEach(function (mediaEl) {
          mediaEl.style.setProperty('transition', 'none', 'important');
        });
      }
      root.querySelectorAll('.zellij-lab__room-preview').forEach(function (el) {
        el.classList.add('is-room-scale-ready');
        el.classList.remove('is-room-scale-pending');
      });
      if (roomFullscreenEl) {
        roomFullscreenEl.classList.add('is-room-scale-ready');
        roomFullscreenEl.classList.remove('is-room-scale-pending');
      }
      setRoomPreviewLoadersVisible(false);
      requestAnimationFrame(function () {
        root.querySelectorAll('.zellij-lab__room-preview-media').forEach(function (mediaEl) {
          mediaEl.style.removeProperty('transition');
        });
        if (roomFullscreenEl) {
          roomFullscreenEl.querySelectorAll('.zellij-lab__room-preview-media').forEach(function (mediaEl) {
            mediaEl.style.removeProperty('transition');
          });
        }
      });
    }

    function holdRoomPreviewUntilStable() {
      if (!roomPreviewReady) return;
      roomScaleRevealPending = true;
      roomScaleRevealed = false;
      roomScaleFrozenColW = 0;
      roomCarpetForceDeferCount = 0;
      clearRoomPreviewRevealTimers();
      root.querySelectorAll('.zellij-lab__room-preview-media').forEach(function (mediaEl) {
        mediaEl.style.setProperty('transition', 'none', 'important');
      });
      root.querySelectorAll('.zellij-lab__room-preview').forEach(function (el) {
        el.classList.add('is-room-scale-pending');
        el.classList.remove('is-room-scale-ready');
      });
      if (roomFullscreenEl) {
        roomFullscreenEl.classList.add('is-room-scale-pending');
        roomFullscreenEl.classList.remove('is-room-scale-ready');
      }
      setRoomPreviewLoadersVisible(true);
      requestAnimationFrame(function () {
        root.querySelectorAll('.zellij-lab__room-preview-media').forEach(function (mediaEl) {
          mediaEl.style.removeProperty('transition');
        });
      });
      var polls = 0;
      function pollReveal() {
        polls += 1;
        revealRoomPreviewIfStable();
        if (roomScaleRevealed) {
          roomScaleRevealTimer = null;
          return;
        }
        if (polls >= 20) {
          forceRevealRoomPreview();
          return;
        }
        roomScaleRevealTimer = setTimeout(pollReveal, 100);
      }
      roomScaleRevealTimer = setTimeout(pollReveal, 100);
      roomScaleForceRevealTimer = setTimeout(function () {
        forceRevealRoomPreview();
      }, ROOM_PREVIEW_REVEAL_TIMEOUT_MS);
    }
    /*
      Carpet _3 still in flight (mosaic / hand-painted). Room must not composite
      or force-reveal on the tile sheet while this is true.
    */
    function carpetSheetLoadPending() {
      return !!(
        patternType !== 'border' &&
        carpetCanvas &&
        !loadState.carpetSvg
      );
    }

    function carpetSheetAvailable() {
      if (!carpetCanvas) return false;
      var svg = carpetCanvas.querySelector('svg');
      return !!(svg && sheetBelongsToCurrentPatternSafe(svg));
    }

    function sheetBelongsToCurrentPatternSafe(svg) {
      if (!svg) return false;
      var tagged = svg.dataset && svg.dataset.patternName;
      if (tagged) return tagged === String(patternName || '');
      return true;
    }

    function roomPreviewScaleIsFinal(patternEl) {
      if (!patternEl || !patternEl.dataset || !patternEl.dataset.roomBuildId) {
        return false;
      }
      var source = patternEl.dataset.roomSource || '';
      if (patternType === 'border') {
        var rid = patternEl.dataset.roomId || '';
        return source === 'border-scene' && rid === activeRoomId;
      }
      /* Never treat a tile mount as final while _3 is still loading. */
      if (carpetSheetLoadPending()) return false;
      var cols = Number(patternEl.dataset.carpetCols || 0);
      if (source === 'carpet' && cols >= 2) return true;
      /*
        Tile fallback only after carpet load finished and no _3 SVG is present
        (genuine miss). If carpet is now available, keep holding until remount.
      */
      if (source === 'tile' && loadState.carpetSvg && !carpetSheetAvailable()) {
        return true;
      }
      return false;
    }
    function roomPreviewPhotoIsReady(frameEl) {
      if (!frameEl) return true;
      var photoEl = frameEl.querySelector('.zellij-lab__room-photo');
      if (!photoEl) return true;
      var src = photoEl.getAttribute('src');
      if (!src) return false;
      return !!(photoEl.complete && photoEl.naturalWidth > 0);
    }
    function roomPatternFillFingerprint(prefix) {
      return (
        roomColorFingerprint(colorMap) +
        '|' +
        patternName +
        '|live-svg|' +
        (prefix || '')
      );
    }
    function roomSvgTexturedForColorMap(svgEl, map, prefix) {
      if (!svgEl) return false;
      if (!enableTileTexture) return true;
      map = map || {};
      var liveKeys = Object.keys(map).filter(function (base) {
        return map[base] && !isWhiteFill(map[base]);
      });
      if (!liveKeys.length) return true;
      var target = roomPanelSymbolEl(svgEl, prefix) || svgEl;
      var ready = true;
      var anyMappedGroup = false;
      liveKeys.forEach(function (base) {
        var hex = map[base];
        var slug = slugForHex(hex);
        if (!slug || textureFailedSlugs[slug]) return;
        if (!textureReadySlugs[slug]) {
          ready = false;
          return;
        }
        var found = false;
        var textured = false;
        if (isHandPaintedLab) {
          getHandPaintedFamilyElements(target).forEach(function (el) {
            if (roomSymbolBaseId(el.getAttribute('id'), prefix) !== base) return;
            getHandPaintedFamilyShapes(el).forEach(function (s) {
              found = true;
              anyMappedGroup = true;
              var fill = String(s.getAttribute('fill') || s.style.fill || '');
              if (fillReferencesSlugTexture(fill, slug)) textured = true;
            });
          });
        } else {
          getAllGroupsWithId(target).forEach(function (g) {
            if (roomSymbolBaseId(g.getAttribute('id'), prefix) !== base) return;
            getShapes(g).forEach(function (s) {
              found = true;
              anyMappedGroup = true;
              var fill = String(s.getAttribute('fill') || s.style.fill || '');
              if (fillReferencesSlugTexture(fill, slug)) textured = true;
            });
          });
        }
        if (found && !textured) ready = false;
      });
      if (!ready) return false;
      if (!anyMappedGroup) {
        /*
          Fallback: some border scenes prefix groups differently. If the SVG
          already has url(#tex-) fills for a live slug, treat as textured.
        */
        var hasTex = false;
        var fillNodes = svgEl.querySelectorAll('[fill]');
        var fi;
        for (fi = 0; fi < fillNodes.length; fi++) {
          var fillVal = String(fillNodes[fi].getAttribute('fill') || '');
          liveKeys.forEach(function (base) {
            var slug = slugForHex(map[base]);
            if (slug && fillReferencesSlugTexture(fillVal, slug)) {
              hasTex = true;
            }
          });
          if (hasTex) break;
        }
        return hasTex;
      }
      return true;
    }
    function roomPatternCompositeReady(patternEl, frameEl) {
      if (!patternEl) return false;
      if (!roomPreviewScaleIsFinal(patternEl)) return false;
      if (!roomPreviewPhotoIsReady(frameEl)) return false;
      var svgEl = patternEl.querySelector('svg.zellij-lab__room-pattern-svg');
      if (!svgEl) return false;
      var isFull =
        frameEl &&
        frameEl.classList.contains('zellij-lab__room-preview-frame--fullscreen');
      var roomId = patternEl.dataset.roomId || activeRoomId;
      var prefix = roomPatternIdPrefix(roomId, !!isFull);
      var fp = roomPatternFillFingerprint(prefix);
      if (svgEl.dataset.roomFillFp !== fp) return false;
      return roomSvgTexturedForColorMap(svgEl, colorMap, prefix);
    }
    function queryActiveRoomPatternSlots() {
      var slots = [];
      var fullscreenOpen = isRoomFullscreenOpen();
      forEachRoomPreviewFrame(function (frame) {
        var isFull = frame.classList.contains(
          'zellij-lab__room-preview-frame--fullscreen'
        );
        if (isFull && !fullscreenOpen) return;
        if (fullscreenOpen && !isFull) return;
        var patternEl = frame.querySelector('.zellij-lab__room-pattern');
        if (patternEl) slots.push({ frame: frame, patternEl: patternEl });
      });
      return slots;
    }
    function allActiveRoomViewportsReady() {
      var slots = queryActiveRoomPatternSlots();
      if (!slots.length) return false;
      var i;
      for (i = 0; i < slots.length; i++) {
        if (!roomPatternCompositeReady(slots[i].patternEl, slots[i].frame)) {
          return false;
        }
      }
      return true;
    }
    function revealRoomPreviewIfStable() {
      if (!roomPreviewReady) return;
      if (!isFunnelLabRoot() && overlayEl && !overlayEnterSettled) return;
      if (!roomScaleRevealPending || roomScaleRevealed) return;
      if (!allActiveRoomViewportsReady()) return;
      function finishReveal() {
        if (!roomScaleRevealPending || roomScaleRevealed) return;
        if (!isFunnelLabRoot() && overlayEl && !overlayEnterSettled) return;
        if (!allActiveRoomViewportsReady()) return;
        clearRoomPreviewRevealTimers();
        roomScaleRevealPending = false;
        roomScaleRevealed = true;
        var previewCol = root.querySelector('.zellij-lab__col--preview');
        roomScaleFrozenColW = previewCol ? previewCol.clientWidth : 0;
        root.querySelectorAll('.zellij-lab__room-preview-media').forEach(function (mediaEl) {
          mediaEl.style.setProperty('transition', 'none', 'important');
        });
        if (roomFullscreenEl) {
          roomFullscreenEl.querySelectorAll('.zellij-lab__room-preview-media').forEach(function (mediaEl) {
            mediaEl.style.setProperty('transition', 'none', 'important');
          });
        }
        root.querySelectorAll('.zellij-lab__room-preview').forEach(function (el) {
          el.classList.add('is-room-scale-ready');
          el.classList.remove('is-room-scale-pending');
        });
        if (roomFullscreenEl) {
          roomFullscreenEl.classList.add('is-room-scale-ready');
          roomFullscreenEl.classList.remove('is-room-scale-pending');
        }
        setRoomPreviewLoadersVisible(false);
        requestAnimationFrame(function () {
          root.querySelectorAll('.zellij-lab__room-preview-media').forEach(function (mediaEl) {
            mediaEl.style.removeProperty('transition');
          });
          if (roomFullscreenEl) {
            roomFullscreenEl.querySelectorAll('.zellij-lab__room-preview-media').forEach(function (mediaEl) {
              mediaEl.style.removeProperty('transition');
            });
          }
        });
      }
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          finishReveal();
        });
      });
    }
    /* Decoded Tiles/{slug}/{slug}-N.webp images, keyed by slug. */
    var tileCellImages = {};
    Object.keys(sharedTextureCellImages).forEach(function (slug) {
      if (sharedTextureCellImages[slug]) {
        tileCellImages[slug] = sharedTextureCellImages[slug];
        textureReadySlugs[slug] = true;
      }
    });
    /*
      Live room SVG fill fingerprint. Unchanged design skips re-applying
      url(#tex-) fills on the sheet symbol.
    */
    var roomLiveFillCache = {
      key: null
    };
    /*
      Built kitchen/bathroom (or fireplace/stairs) pattern SVGs, keyed by
      pattern + room + desk/full. Tab switch reuses these instead of cloning
      the carpet on every click.
    */
    var roomSceneCache = {};
    /*
      Mosaic grout / letterbox tone. Same hex as
      .zellij-lab__room-preview-frame background in zellij-design-lab.css.
    */
    var MOSAIC_GROUT_HEX = '#f7f5f1';
    /* Vertical bleed (px each edge) so kitchen band covers sub-pixel clip seams. */
    var MOSAIC_BAND_BLEED_Y = 1;
    /* Kitchen mosaic only: 120% tile size vs bathroom / the 100% design count. */
    var KITCHEN_MOSAIC_SCALE = 1.2;
    var TILE_UNPAINTED_GROUT = '#C4BEB4';
    /*
      Border funnel: decoded scene photos keyed by CDN URL so tab switches
      do not flash empty src or a stale overlay before the new photo paints.
    */
    var borderRoomPhotoReady = {};
    var historyApi = null;
    removeLegacyLightboxScaffolding();
    var roomFullscreenEl = root.querySelector('.zellij-lab__room-fullscreen');
    if (roomFullscreenEl) {
      roomFullscreenEl.classList.remove('zellij-lab__room-fullscreen--legacy');
    }
    /*
      Product overlay: Prestige modal keeps a translateY(0) animation fill,
      which traps position:fixed. Move Preview onto document.body.
      Funnel stays put.
    */
    if (!isFunnelLabRoot() && overlayEl && roomFullscreenEl) {
      document.body.appendChild(roomFullscreenEl);
      roomFullscreenEl.classList.add('zellij-lab__room-fullscreen--portal');
    }
    function forEachRoomPreviewFrame(fn) {
      var seen = [];
      function visit(frame) {
        if (!frame || seen.indexOf(frame) !== -1) return;
        seen.push(frame);
        fn(frame);
      }
      root.querySelectorAll('.zellij-lab__room-preview-frame').forEach(visit);
      if (roomFullscreenEl && !root.contains(roomFullscreenEl)) {
        roomFullscreenEl.querySelectorAll('.zellij-lab__room-preview-frame').forEach(visit);
      }
    }
    /* Start held until first stable room paint (all lab roots). */
    if (roomPreviewReady) holdRoomPreviewUntilStable();
    var roomPreviewTriggerEl = null;
    var roomPreviewScrollLockActive = false;

    function queryRoomPreviewFrames() {
      var frames = [];
      root.querySelectorAll('.zellij-lab__room-preview-frame').forEach(function (frame) {
        if (!frame.classList.contains('zellij-lab__room-preview-frame--fullscreen')) {
          frames.push(frame);
        }
      });
      return frames;
    }

    function queryRoomPatternRoots() {
      var nodes = [];
      var seen = [];
      function visit(el) {
        if (!el || seen.indexOf(el) !== -1) return;
        seen.push(el);
        nodes.push(el);
      }
      root.querySelectorAll('.zellij-lab__room-pattern').forEach(visit);
      if (roomFullscreenEl && !root.contains(roomFullscreenEl)) {
        roomFullscreenEl.querySelectorAll('.zellij-lab__room-pattern').forEach(visit);
      }
      return nodes;
    }

    function queryRoomPatternSvgs() {
      var svgs = [];
      queryRoomPatternRoots().forEach(function (patternEl) {
        var svgEl = patternEl.querySelector('svg');
        if (svgEl && svgs.indexOf(svgEl) < 0) svgs.push(svgEl);
      });
      return svgs;
    }
    var labUnavailable = false;
    var LAB_UNAVAILABLE_MSG =
      zCopy('workshop_unavailable') +
      ' ' +
      zCopy('custom_color_options');

    function showLabUnavailable() {
      if (labUnavailable) return;
      labUnavailable = true;
      loadState.tileSvg = true;
      loadState.carpetSvg = true;
      loadState.swatches = true;
      if (tileCanvas) {
        tileCanvas.innerHTML =
          '<div class="zellij-lab__unavailable">' + LAB_UNAVAILABLE_MSG + '</div>';
        tileCanvas.style.paddingBottom = '';
      }
      if (carpetCanvas) carpetCanvas.innerHTML = '';
      [
        '.zellij-lab__actions',
        '.zellij-lab__picker',
        '.zellij-lab__hint-text',
        '.zellij-lab__used-colors',
        '.zellij-lab__active-indicator'
      ].forEach(function (sel) {
        var el = root.querySelector(sel);
        if (el) el.hidden = true;
      });
      /* Missing SVG: drop Color yours so the empty overlay is not reopened. */
      document
        .querySelectorAll('.zellij-color-yours-btn[data-zellij-svg-probe]')
        .forEach(function (btn) {
          hideColorYoursButton(btn);
        });
      if (overlayEl && overlayEl.open && typeof overlayEl.hide === 'function') {
        try {
          overlayEl.hide();
        } catch (err) {
          /* ignore */
        }
      }
      maybeHideLoader();
    }

    function getPatternSvgUrl(suffix) {
      if (!patternSvgBaseUrl) {
        console.error(
          '[Design Lab] patternSvgBaseUrl is not configured. Set Theme settings > Design Lab > Pattern SVG base URL'
        );
        return '';
      }
      return (
        patternSvgBaseUrl +
        '/' +
        patternStorageFolder +
        '/' +
        patternName +
        '/' +
        patternName +
        suffix +
        '.svg'
      );
    }

    function getSvgUrl(idx) {
      return getPatternSvgUrl('_' + idx);
    }

    /*
      Pattern SVGs (canvas + sample) share Supabase with picker thumbs.
      Retry transient delivery failures before treating the pattern as unavailable.
    */
    var PATTERN_SVG_MAX_ATTEMPTS = 3;
    var PATTERN_SVG_BACKOFF_MS = [300, 800, 1500];

    /* Cache-bust only on the last retry attempt (0-based index). */
    function patternSvgCacheBust(url, attempt) {
      if (!url) return url;
      if (!attempt || attempt < PATTERN_SVG_MAX_ATTEMPTS - 1) return url;
      var sep = url.indexOf('?') >= 0 ? '&' : '?';
      return url + sep + 'zellij_retry=' + attempt + '&t=' + Date.now();
    }

    function fetchUrlTextWithRetry(url, fetchOpts) {
      function attempt(n) {
        if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
          return Promise.reject(new Error('aborted'));
        }
        var attemptUrl = patternSvgCacheBust(url, n);
        return fetch(attemptUrl, fetchOpts)
          .then(function (r) {
            if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
              return Promise.reject(new Error('aborted'));
            }
            if (!r.ok) throw new Error('HTTP ' + r.status + ' for ' + url);
            return r.text();
          })
          .catch(function (err) {
            if (
              instanceAborted ||
              (err && (err.name === 'AbortError' || err.message === 'aborted'))
            ) {
              return Promise.reject(err);
            }
            if (n + 1 >= PATTERN_SVG_MAX_ATTEMPTS) return Promise.reject(err);
            return new Promise(function (resolve, reject) {
              setTimeout(function () {
                attempt(n + 1).then(resolve, reject);
              }, PATTERN_SVG_BACKOFF_MS[Math.min(n, PATTERN_SVG_BACKOFF_MS.length - 1)]);
            });
          });
      }
      return attempt(0);
    }

    function injectSvg(container, text) {
      var doc = new DOMParser().parseFromString(text, 'image/svg+xml');
      var svgEl = doc.querySelector('svg');
      if (!svgEl) return null;
      svgEl.removeAttribute('width');
      svgEl.removeAttribute('height');
      if (!svgEl.getAttribute('preserveAspectRatio')) {
        svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      }
      svgEl.style.width = '100%';
      svgEl.style.height = '100%';
      svgEl.style.maxWidth = '100%';
      svgEl.style.maxHeight = '100%';
      svgEl.style.pointerEvents = 'all';
      svgEl.querySelectorAll('[pointer-events]').forEach(function (el) {
        el.removeAttribute('pointer-events');
      });
      if (!svgEl.getAttribute('xmlns:xlink')) {
        svgEl.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
      }
      /* Tag sheet so room preview can reject a stale inject after setPattern. */
      svgEl.dataset.patternName = String(patternName || '');
      container.innerHTML = '';
      container.appendChild(svgEl);
      return svgEl;
    }

    function shapeHash01(shapeId, salt) {
      var str = String(shapeId) + '|' + String(salt || '');
      var h = 2166136261;
      for (var i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return (h >>> 0) / 4294967296;
    }

    function slugForHex(hex) {
      if (!hex) return null;
      var key = String(hex).toLowerCase();
      var found = palette.find(function (c) {
        return c.hex && c.hex.toLowerCase() === key;
      });
      return found && found.slug ? found.slug : null;
    }

    function textureCellUrl(slug, cellIndex) {
      if (!tileTextureBaseUrl || !slug) return '';
      return (
        tileTextureBaseUrl +
        '/' +
        slug +
        '/' +
        slug +
        '-' +
        cellIndex +
        '.webp'
      );
    }

    function measureShapeSize(shape) {
      if (!shape || !shape.getBBox) return 0;
      try {
        var box = shape.getBBox();
        var w = box && box.width ? box.width : 0;
        var h = box && box.height ? box.height : 0;
        return Math.max(w, h);
      } catch (err) {
        return 0;
      }
    }

    function sizeBucketKey(shapeSize) {
      if (!(shapeSize > 0)) return '0';
      var exp = Math.round(
        Math.log(shapeSize) / Math.log(TEXTURE_BUCKET_RATIO)
      );
      return String(exp);
    }

    function ensureSvgDefs(svgEl) {
      var defs = svgEl.querySelector('defs');
      if (!defs) {
        defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        svgEl.insertBefore(defs, svgEl.firstChild);
      }
      return defs;
    }

    function getShapeStableId(shape, fallbackIndex) {
      var id = shape.getAttribute('id');
      if (id) return id;
      var parent = shape.parentElement;
      while (parent && parent.tagName && parent.tagName.toLowerCase() !== 'g') {
        parent = parent.parentElement;
      }
      var groupId =
        parent && parent.getAttribute ? parent.getAttribute('id') : '';
      return (groupId || 'shape') + ':' + fallbackIndex;
    }

    function ensureSvgKey(svgEl) {
      if (!svgEl.getAttribute('data-zellij-svg-key')) {
        svgEl.setAttribute(
          'data-zellij-svg-key',
          'svg-' + Math.floor(shapeHash01(String(Date.now()), 'svg') * 1e9)
        );
      }
      return svgEl.getAttribute('data-zellij-svg-key');
    }

    /*
      Pattern ids must be unique per SVG host. url(#tex-) resolves document-
      wide, so tile canvas + portaled room preview cannot share bare tex-slug
      ids (Preview close left the lab sheet pointing at room defs = flat hex).
    */
    function texturePatternId(svgEl, slug, poolKey, index) {
      return (
        'tex-' +
        ensureSvgKey(svgEl) +
        '-' +
        slug +
        '-' +
        poolKey +
        '-' +
        index
      );
    }

    function fillReferencesSlugTexture(fill, slug) {
      if (!fill || !slug) return false;
      var f = String(fill);
      if (f.indexOf('url(#') !== 0 && f.indexOf('url("#') !== 0) return false;
      return f.indexOf('-' + slug + '-') !== -1;
    }

    function ensureBucketPoolOnSvg(svgEl, slug, bucketKey) {
      if (!svgEl || !slug || !enableTileTexture) return false;
      if (textureFailedSlugs[slug] || !textureReadySlugs[slug]) return false;
      /*
        objectBoundingBox + width/height 1: each shape samples one texture
        region with no pattern repeat inside the shape (avoids white seam grid
        from userSpaceOnUse tile boundaries crossing shape bboxes).
      */
      var poolKey = TEXTURE_POOL_KEY;
      var builtKey = ensureSvgKey(svgEl) + '|' + slug + '|' + poolKey;
      var sampleId = texturePatternId(svgEl, slug, poolKey, 0);
      if (texturePoolBuilt[builtKey]) {
        if (svgEl.getElementById(sampleId)) return true;
        delete texturePoolBuilt[builtKey];
      }

      if (svgEl.getElementById(sampleId)) {
        texturePoolBuilt[builtKey] = true;
        return true;
      }

      var defs = ensureSvgDefs(svgEl);
      var i;
      for (i = 0; i < TEXTURE_POOL_SIZE; i++) {
        var patternId = texturePatternId(svgEl, slug, poolKey, i);
        if (svgEl.getElementById(patternId)) continue;
        var cell = (i % 9) + 1;
        var rotation = TEXTURE_ROTATIONS[i % 4];
        var pattern = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'pattern'
        );
        pattern.setAttribute('id', patternId);
        pattern.setAttribute('patternUnits', 'objectBoundingBox');
        pattern.setAttribute('patternContentUnits', 'objectBoundingBox');
        pattern.setAttribute('width', '1');
        pattern.setAttribute('height', '1');
        var image = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'image'
        );
        var href = textureCellUrl(slug, cell);
        image.setAttribute('href', href);
        image.setAttributeNS('http://www.w3.org/1999/xlink', 'href', href);
        image.setAttribute('x', '0');
        image.setAttribute('y', '0');
        image.setAttribute('width', '1');
        image.setAttribute('height', '1');
        image.setAttribute('preserveAspectRatio', 'xMidYMid slice');
        if (rotation) {
          image.setAttribute(
            'transform',
            'rotate(' + rotation + ' 0.5 0.5)'
          );
        }
        pattern.appendChild(image);
        defs.appendChild(pattern);
      }
      texturePoolBuilt[builtKey] = true;
      return true;
    }

    function removeFlatUnderlay(shape) {
      if (!shape || !shape.parentNode) return;
      var prev = shape.previousElementSibling;
      if (prev && prev.getAttribute('data-zellij-underfill') === '1') {
        prev.parentNode.removeChild(prev);
      }
    }

    function ensureFlatUnderlay(shape, hex) {
      if (!shape || !shape.parentNode) return;
      var fillHex = hex || '#FFFFFF';
      var prev = shape.previousElementSibling;
      if (prev && prev.getAttribute('data-zellij-underfill') === '1') {
        prev.setAttribute('fill', fillHex);
        prev.style.fill = fillHex;
        prev.setAttribute('stroke', 'none');
        prev.style.stroke = 'none';
        prev.style.pointerEvents = 'none';
        return;
      }
      var under = shape.cloneNode(true);
      under.removeAttribute('id');
      under.removeAttribute('data-zellij-fill');
      under.setAttribute('data-zellij-underfill', '1');
      under.setAttribute('fill', fillHex);
      under.style.fill = fillHex;
      under.setAttribute('stroke', 'none');
      under.style.stroke = 'none';
      under.style.pointerEvents = 'none';
      under.style.shapeRendering = 'geometricPrecision';
      shape.parentNode.insertBefore(under, shape);
    }

    function preloadColorTextures(slug) {
      if (!enableTileTexture || !tileTextureBaseUrl || !slug) {
        return Promise.resolve(false);
      }
      if (textureFailedSlugs[slug]) return Promise.resolve(false);
      if (textureReadySlugs[slug] && tileCellImages[slug]) {
        return Promise.resolve(true);
      }
      if (texturePreloadPromises[slug]) return texturePreloadPromises[slug];

      texturePreloadPromises[slug] = Promise.all(
        [1, 2, 3, 4, 5, 6, 7, 8, 9].map(function (cell) {
          return new Promise(function (resolve, reject) {
            var img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = function () {
              if (img.decode) {
                img.decode().then(function () {
                  resolve(img);
                }).catch(function () {
                  resolve(img);
                });
              } else {
                resolve(img);
              }
            };
            img.onerror = function () {
              reject(new Error('texture load failed'));
            };
            img.src = textureCellUrl(slug, cell);
          });
        })
      )
        .then(function (imgs) {
          tileCellImages[slug] = imgs;
          textureReadySlugs[slug] = true;
          sharedTextureCellImages[slug] = imgs;
          sharedTextureReadySlugs[slug] = true;
          refreshInteractiveFillsForSlug(slug);
          return true;
        })
        .catch(function () {
          textureFailedSlugs[slug] = true;
          delete texturePreloadPromises[slug];
          return false;
        });

      return texturePreloadPromises[slug];
    }

    function preloadAllPaletteTextures() {
      if (!enableTileTexture || !tileTextureBaseUrl) return Promise.resolve();
      var slugs = {};
      palette.forEach(function (color) {
        if (color && color.slug) slugs[color.slug] = true;
      });
      var slugList = Object.keys(slugs);
      if (!slugList.length) return Promise.resolve();
      return Promise.all(
        slugList.map(function (slug) {
          return preloadColorTextures(slug);
        })
      );
    }

    var TEXTURE_BG_CONCURRENCY = 2;
    var textureBgQueue = [];
    var textureBgInFlight = 0;
    var textureBgScheduled = {};

    function collectPriorityTextureSlugs() {
      var out = {};
      function add(slug) {
        if (slug) out[String(slug).toLowerCase()] = true;
      }
      Object.keys(colorMap || {}).forEach(function (base) {
        add(slugForHex(colorMap[base]));
      });
      add(slugForHex(activeColor));
      getFilteredPalette().forEach(function (color) {
        if (color && color.slug) add(color.slug);
      });
      return Object.keys(out);
    }

    function pumpBackgroundTextureQueue() {
      while (
        textureBgInFlight < TEXTURE_BG_CONCURRENCY &&
        textureBgQueue.length
      ) {
        (function (slug) {
          textureBgInFlight += 1;
          preloadColorTextures(slug).then(
            function () {
              textureBgInFlight = Math.max(0, textureBgInFlight - 1);
              pumpBackgroundTextureQueue();
            },
            function () {
              textureBgInFlight = Math.max(0, textureBgInFlight - 1);
              pumpBackgroundTextureQueue();
            }
          );
        })(textureBgQueue.shift());
      }
    }

    function enqueueBackgroundTextureSlug(slug) {
      if (!slug || textureBgScheduled[slug]) return;
      if (textureReadySlugs[slug] || textureFailedSlugs[slug]) return;
      textureBgScheduled[slug] = true;
      textureBgQueue.push(slug);
      pumpBackgroundTextureQueue();
    }

    /*
      Priority: used + active + visible swatches (textured first paint).
      Remainder: low concurrency after idle / first paint. Never skip preload.
    */
    function preloadPaletteTexturesPrioritized() {
      if (!enableTileTexture || !tileTextureBaseUrl) return Promise.resolve();
      var priority = collectPriorityTextureSlugs();
      var all = {};
      palette.forEach(function (color) {
        if (color && color.slug) all[String(color.slug).toLowerCase()] = true;
      });
      var rest = Object.keys(all).filter(function (slug) {
        return priority.indexOf(slug) === -1;
      });

      function scheduleRest() {
        if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
          return;
        }
        rest.forEach(enqueueBackgroundTextureSlug);
      }

      return Promise.all(
        priority.map(function (slug) {
          return preloadColorTextures(slug);
        })
      ).then(function () {
        if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
          return;
        }
        if (typeof requestIdleCallback === 'function') {
          requestIdleCallback(scheduleRest, { timeout: 2000 });
        } else {
          setTimeout(scheduleRest, 0);
        }
      });
    }

    function applyInteractiveFill(shape, hex, stableId, svgEl, options) {
      options = options || {};
      if (!shape) return;
      var fillHex = hex || '#FFFFFF';
      shape.setAttribute('data-zellij-fill', fillHex);
      shape.style.shapeRendering = 'geometricPrecision';

      var slug = slugForHex(fillHex);
      var hostSvg = svgEl || shape.ownerSVGElement;
      if (
        !enableTileTexture ||
        !slug ||
        !hostSvg ||
        textureFailedSlugs[slug] ||
        isWhiteFillHex(fillHex)
      ) {
        removeFlatUnderlay(shape);
        shape.setAttribute('fill', fillHex);
        shape.style.fill = fillHex;
        syncTileGroutStroke(shape);
        return;
      }

      if (!textureReadySlugs[slug]) {
        /*
          Hold the prior neutral fill until cells decode. Painting hex first
          caused a visible flat-color flash before url(#tex-) replaced it.
        */
        removeFlatUnderlay(shape);
        preloadColorTextures(slug);
        syncTileGroutStroke(shape);
        return;
      }

      if (!options.skipSizeCheck) {
        var shapeSize = measureShapeSize(shape);
        if (!(shapeSize > 0)) {
          removeFlatUnderlay(shape);
          preloadColorTextures(slug);
          syncTileGroutStroke(shape);
          if (!options._sizeRetry) {
            requestAnimationFrame(function () {
              applyInteractiveFill(shape, fillHex, stableId, hostSvg, {
                skipSizeCheck: false,
                _sizeRetry: true
              });
            });
          }
          return;
        }
      }

      var bucketKey = TEXTURE_POOL_KEY;
      if (!ensureBucketPoolOnSvg(hostSvg, slug, bucketKey)) {
        removeFlatUnderlay(shape);
        preloadColorTextures(slug);
        syncTileGroutStroke(shape);
        return;
      }

      ensureFlatUnderlay(shape, fillHex);

      var poolIndex = Math.floor(
        shapeHash01(stableId || getShapeStableId(shape, 0), 'pool') *
          TEXTURE_POOL_SIZE
      );
      var fill = 'url(#' + texturePatternId(hostSvg, slug, bucketKey, poolIndex) + ')';
      shape.setAttribute('fill', fill);
      shape.style.fill = fill;
      syncTileGroutStroke(shape);
    }

    function isWhiteFillHex(hex) {
      if (!hex) return true;
      var h = String(hex).toLowerCase();
      return h === '#ffffff' || h === '#fff' || h === 'white' || h === 'none';
    }

    function collectTextureStats(svgEl) {
      if (!svgEl) {
        return { buckets: [], bucketCount: 0, patternCount: 0, shapeCount: 0 };
      }
      var bucketSet = {};
      var shapeCount = 0;
      getShapes(svgEl).forEach(function (shape) {
        var hex = getShapeHex(shape);
        if (!hex || isWhiteFillHex(hex) || !slugForHex(hex)) return;
        var size = measureShapeSize(shape);
        if (!(size > 0)) return;
        bucketSet[sizeBucketKey(size)] = true;
        shapeCount += 1;
      });
      var buckets = Object.keys(bucketSet).sort(function (a, b) {
        return parseInt(a, 10) - parseInt(b, 10);
      });
      var patternCount = svgEl.querySelectorAll
        ? svgEl.querySelectorAll('pattern[id^="tex-"]').length
        : 0;
      return {
        buckets: buckets,
        bucketSizes: buckets.map(function (key) {
          return Math.round(Math.pow(TEXTURE_BUCKET_RATIO, parseInt(key, 10)) * 100) / 100;
        }),
        bucketCount: buckets.length,
        patternCount: patternCount,
        shapeCount: shapeCount
      };
    }

    function paintSvgShapesWithMap(svgEl, map, options) {
      options = options || {};
      if (!svgEl || !map) return;
      if (isHandPaintedLab) {
        getHandPaintedFamilyElements(svgEl).forEach(function (el) {
          var base = getHandPaintedFamilyBase(el);
          if (!map[base]) return;
          getHandPaintedFamilyShapes(el).forEach(function (s, idx) {
            if (options.flat) {
              removeFlatUnderlay(s);
              setFill(s, map[base]);
            } else {
              applyInteractiveFill(
                s,
                map[base],
                getShapeStableId(s, base + ':' + idx),
                svgEl,
                { skipSizeCheck: options.skipSizeCheck }
              );
            }
          });
        });
        return;
      }
      getAllGroupsWithId(svgEl).forEach(function (g) {
        var base = getBaseId(g.getAttribute('id'));
        if (!map[base]) return;
        getShapes(g).forEach(function (s, idx) {
          if (options.flat) {
            removeFlatUnderlay(s);
            setFill(s, map[base]);
          } else {
            applyInteractiveFill(
              s,
              map[base],
              getShapeStableId(s, base + ':' + idx),
              svgEl,
              { skipSizeCheck: options.skipSizeCheck }
            );
          }
        });
      });
    }

    function refreshInteractiveFillsForSlug(slug) {
      if (!slug || !textureReadySlugs[slug]) return;
      /* Each SVG host owns scoped url(#tex-{svgKey}-…) pools. */
      function repaintHost(svgEl) {
        if (!svgEl) return;
        getShapes(svgEl).forEach(function (shape, idx) {
          var hex = getShapeHex(shape);
          if (!hex || slugForHex(hex) !== slug) return;
          applyInteractiveFill(
            shape,
            hex,
            getShapeStableId(shape, idx),
            svgEl,
            { skipSizeCheck: true }
          );
        });
      }
      if (!isRoomFullscreenOpen()) {
        if (tileCanvas) repaintHost(tileCanvas.querySelector('svg'));
        if (patternType === 'border' && carpetCanvas) {
          repaintHost(carpetCanvas.querySelector('svg'));
        }
      }
      if (roomPreviewReady) {
        roomLiveFillCache.key = null;
        queryRoomPatternSvgs().forEach(function (roomSvg) {
          var patternEl = roomSvg.parentElement;
          if (!patternEl) return;
          var frame = patternEl.closest('.zellij-lab__room-preview-frame');
          var isFull =
            frame &&
            frame.classList.contains(
              'zellij-lab__room-preview-frame--fullscreen'
            );
          if (isFull && !isRoomFullscreenOpen()) return;
          var roomId = patternEl.dataset.roomId || activeRoomId || 'kitchen';
          if (roomId !== activeRoomId) return;
          if (roomSvg.dataset) delete roomSvg.dataset.roomFillFp;
          var symbol = roomSvg.querySelector('symbol[id$="carpet"]');
          var prefix = symbol
            ? String(symbol.getAttribute('id') || '').replace(/carpet$/, '')
            : '';
          applyColorMapToRoomPanel(roomSvg, colorMap, prefix);
        });
      }
      requestAnimationFrame(function () {
        if (samplePreviewReady) refreshSamplePreview();
      });
    }

    function clearLabCanvasTextureState(svgEl) {
      if (!svgEl) return;
      var svgKey = ensureSvgKey(svgEl);
      Object.keys(texturePoolBuilt).forEach(function (key) {
        if (key.indexOf(svgKey + '|') === 0) delete texturePoolBuilt[key];
      });
      svgEl.querySelectorAll('[data-zellij-underfill="1"]').forEach(function (node) {
        if (node.parentNode) node.parentNode.removeChild(node);
      });
      svgEl.querySelectorAll('pattern[id^="tex-"]').forEach(function (pat) {
        if (pat.parentNode) pat.parentNode.removeChild(pat);
      });
    }

    function repaintLabCanvasesFromColorMap() {
      if (!enableTileTexture) return;
      var tileSvg = tileCanvas ? tileCanvas.querySelector('svg') : null;
      var carpetSvg =
        patternType === 'border' && carpetCanvas
          ? carpetCanvas.querySelector('svg')
          : null;
      if (tileSvg) applyColorMapToSvg(tileSvg, colorMap);
      if (carpetSvg) applyColorMapToSvg(carpetSvg, colorMap);
    }

    function reviveLabTextures() {
      if (!enableTileTexture) return Promise.resolve();
      var tileSvg = tileCanvas ? tileCanvas.querySelector('svg') : null;
      var carpetSvg = carpetCanvas ? carpetCanvas.querySelector('svg') : null;
      clearLabCanvasTextureState(tileSvg);
      clearLabCanvasTextureState(carpetSvg);
      repaintLabCanvasesFromColorMap();
      return preloadAllPaletteTextures().then(function () {
        if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
          return;
        }
        clearLabCanvasTextureState(tileSvg);
        clearLabCanvasTextureState(carpetSvg);
        repaintLabCanvasesFromColorMap();
        roomLiveFillCache.key = null;
        queryRoomPatternSvgs().forEach(function (roomSvg) {
          if (roomSvg.dataset) delete roomSvg.dataset.roomFillFp;
        });
        recolourMountedRoomPanels();
        if (samplePreviewReady) refreshSamplePreview();
      });
    }

    function reviveOverlayTextures() {
      reviveLabTextures();
    }

    function flattenSvgForThumbnail(svgEl) {
      if (!svgEl) return svgEl;
      Array.prototype.slice
        .call(svgEl.querySelectorAll('[data-zellij-underfill="1"]'))
        .forEach(function (node) {
          if (node.parentNode) node.parentNode.removeChild(node);
        });
      getShapes(svgEl).forEach(function (shape) {
        var hex = getShapeHex(shape) || '#FFFFFF';
        shape.setAttribute('fill', hex);
        shape.style.fill = hex;
      });
      var defs = svgEl.querySelector('defs');
      if (defs) {
        Array.prototype.slice
          .call(defs.querySelectorAll('pattern[id^="tex-"]'))
          .forEach(function (node) {
            node.parentNode.removeChild(node);
          });
      }
      return svgEl;
    }

    function setCanvasAspectFromViewBox(canvasEl, svgEl) {
      if (!canvasEl || !svgEl) return;
      var vb = svgEl.getAttribute('viewBox');
      if (!vb) return;
      var parts = vb.trim().split(/[\s,]+/);
      if (parts.length < 4) return;
      var w = parseFloat(parts[2]);
      var h = parseFloat(parts[3]);
      if (w > 0) canvasEl.style.paddingBottom = (h / w) * 100 + '%';
    }

    function measureSvgPaintedBBox(svgEl) {
      if (!svgEl) return null;
      function fromBox(box) {
        if (!box || !(box.width > 0) || !(box.height > 0)) return null;
        return { x: box.x, y: box.y, width: box.width, height: box.height };
      }
      try {
        var live = fromBox(svgEl.getBBox());
        if (live) return live;
      } catch (e) {
        /* not in render tree yet */
      }
      var holder = document.createElement('div');
      holder.setAttribute('aria-hidden', 'true');
      holder.style.cssText =
        'position:fixed;left:-9999px;top:0;width:800px;height:800px;overflow:hidden;visibility:hidden;pointer-events:none';
      var clone = svgEl.cloneNode(true);
      clone.style.width = '800px';
      clone.style.height = '800px';
      clone.removeAttribute('width');
      clone.removeAttribute('height');
      holder.appendChild(clone);
      document.body.appendChild(holder);
      try {
        return fromBox(clone.getBBox());
      } catch (e2) {
        return null;
      } finally {
        if (holder.parentNode) holder.parentNode.removeChild(holder);
      }
    }

    /*
      Border _2 / _SAMPLE files sit on a square 850 artboard with the strip
      occupying only the middle. Crop so the sheet fills the dimension grid.
    */
    function cropSvgViewBoxToPaintedContent(svgEl, padRatio) {
      if (!svgEl) return false;
      var bbox = measureSvgPaintedBBox(svgEl);
      if (!bbox) return false;
      var pad = Math.max(bbox.width, bbox.height) * (padRatio > 0 ? padRatio : 0.02);
      svgEl.setAttribute(
        'viewBox',
        bbox.x - pad + ' ' + (bbox.y - pad) + ' ' +
          (bbox.width + pad * 2) + ' ' + (bbox.height + pad * 2)
      );
      return true;
    }

    function setBorderTileSheetLayout(canvasEl, svgEl) {
      if (!canvasEl || !svgEl) return;
      if (cropSvgViewBoxToPaintedContent(svgEl, 0.02)) {
        setCanvasAspectFromViewBox(canvasEl, svgEl);
        return;
      }
      setCanvasAspectFromViewBox(canvasEl, svgEl);
    }

    function setTileCanvasLayout(canvasEl, svgEl) {
      var isFunnelCanvas =
        canvasEl &&
        root &&
        (root.id === 'zellij-funnel-color-tool' ||
          root.closest('.zellij-lab-funnel'));
      if (
        isHandPaintedLab &&
        patternType !== 'border' &&
        svgEl &&
        !svgEl.getAttribute('data-zellij-hp-tile-layout')
      ) {
        cropSvgToHandPaintedOriginCell(svgEl);
      }
      if (patternType === 'border') {
        setBorderTileSheetLayout(canvasEl, svgEl);
        var dimParsed = parsePatternDimensionsForCanvas(
          patternName,
          cfg.tileDimensions
        );
        if (!isFunnelCanvas && dimParsed.widthNum > 0 && dimParsed.heightNum > 0) {
          canvasEl.style.setProperty(
            '--zellij-lab-tile-aspect',
            String(dimParsed.tileAspect)
          );
          /* Wrap already uses the dimension aspect. Canvas fills that cell. */
          canvasEl.style.removeProperty('aspect-ratio');
          canvasEl.style.paddingBottom = '0';
        }
      } else {
        setCanvasAspectFromViewBox(canvasEl, svgEl);
      }
      /*
        Funnel sizes .zellij-lab__tile-wrap with CSS aspect-ratio and fills
        the canvas at height:100%. Leaving the overlay padding-bottom aspect
        hack in place collapses the absolute SVG to a tiny first paint until
        a later reflow (often the first color click). Clear it for funnel.
      */
      if (isFunnelCanvas) {
        canvasEl.style.paddingBottom = '';
      }
    }

    function svgElFromCachedText(text) {
      if (!text) return null;
      try {
        var doc = new DOMParser().parseFromString(text, 'image/svg+xml');
        return doc.querySelector('svg');
      } catch (e) {
        return null;
      }
    }

    function fetchPatternSvgText(idx, cb) {
      var url = getSvgUrl(idx);
      if (!url) {
        if (cb) cb(null);
        return;
      }
      var fetchOpts = abortCtrl ? { signal: abortCtrl.signal } : undefined;
      fetchUrlTextWithRetry(url, fetchOpts)
        .then(function (text) {
          if (instanceAborted || instanceGeneration !== labInstanceGeneration) return null;
          if (
            !text ||
            (!text.trimStart().startsWith('<?xml') &&
              !text.trimStart().startsWith('<svg'))
          ) {
            throw new Error('Invalid SVG response for ' + url);
          }
          if (idx === 3 && patternType === 'border') fireplaceSvgText = text;
          if (cb) cb(text);
          return text;
        })
        .catch(function (err) {
          if (
            instanceAborted ||
            (err && (err.name === 'AbortError' || err.message === 'aborted'))
          ) {
            return null;
          }
          console.warn('[Design Lab] fetchPatternSvgText', idx, err);
          if (cb) cb(null);
          return null;
        });
    }

    function loadSvg(container, idx, cb) {
      var url = getSvgUrl(idx);
      if (!url) {
        if (idx === 2) {
          loadState.tileSvg = true;
          showLabUnavailable();
          maybeHideLoader();
        } else {
          loadState.carpetSvg = true;
          container.innerHTML =
            '<div class="zellij-lab__unavailable zellij-lab__unavailable--carpet">' +
            LAB_UNAVAILABLE_MSG +
            '</div>';
          maybeHideLoader();
        }
        return;
      }

      container.innerHTML = '<div class="zellij-lab__loading">' + zCopy('loading_ellipsis') + '</div>';
      var fetchOpts = abortCtrl ? { signal: abortCtrl.signal } : undefined;
      fetchUrlTextWithRetry(url, fetchOpts)
        .then(function (text) {
          if (instanceAborted || instanceGeneration !== labInstanceGeneration) return null;
          if (labUnavailable) return null;
          if (!text || (!text.trimStart().startsWith('<?xml') && !text.trimStart().startsWith('<svg'))) {
            throw new Error('Invalid SVG response for ' + url);
          }
          /* Re-check after parse work: a setPattern destroy may have landed. */
          if (instanceAborted || instanceGeneration !== labInstanceGeneration) return null;
          if (idx === 2) tileSvgText = text;
          if (idx === 3 && patternType !== 'border') carpetSvgText = text;
          if (idx === 4 && patternType === 'border') carpetSvgText = text;
          var svgEl = injectSvg(container, text);
          if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
            if (container) container.innerHTML = '';
            return null;
          }
          /*
            Neutralize CDN colorway fills before layout/observers can clone the
            sheet into the room preview.
          */
          if (svgEl) applyWhite(svgEl);
          if (idx === 2 && tileCanvas && svgEl) {
            setTileCanvasLayout(tileCanvas, svgEl);
            /*
              Border getBBox / funnel grid stretch can be wrong on the first
              synchronous paint. Re-run once after layout.
            */
            requestAnimationFrame(function () {
              if (
                instanceAborted ||
                instanceGeneration !== labInstanceGeneration
              ) {
                return;
              }
              if (!tileCanvas.querySelector('svg')) return;
              setTileCanvasLayout(tileCanvas, svgEl);
            });
          }
          if (idx === 3 && carpetCanvas && svgEl) {
            setCanvasAspectFromViewBox(carpetCanvas, svgEl);
            sizeBorderCarpetToSlot();
          }
          if (idx === 4 && carpetCanvas && svgEl) {
            setCanvasAspectFromViewBox(carpetCanvas, svgEl);
            sizeBorderCarpetToSlot();
          }
          if (svgEl && cb) cb(svgEl);
          return svgEl;
        })
        .catch(function (err) {
          if (
            instanceAborted ||
            (err && (err.name === 'AbortError' || err.message === 'aborted'))
          ) {
            return null;
          }
          console.error('[Design Lab] Failed to load pattern SVG:', url, err);
          if (idx === 2) {
            loadState.tileSvg = true;
            showLabUnavailable();
            maybeHideLoader();
            return null;
          }
          loadState.carpetSvg = true;
          container.innerHTML =
            '<div class="zellij-lab__unavailable zellij-lab__unavailable--carpet">' +
            LAB_UNAVAILABLE_MSG +
            '</div>';
          maybeHideLoader();
          return null;
        });
    }

    function detectBases(svgEl) {
      detectedBases = [];
      var seen = {};
      if (isHandPaintedLab) {
        getHandPaintedFamilyElements(svgEl).forEach(function (el) {
          var base = getHandPaintedFamilyBase(el);
          if (!base || seen[base]) return;
          if (getHandPaintedFamilyShapes(el).length === 0) return;
          seen[base] = true;
          detectedBases.push(base);
        });
        hasGroupIds = detectedBases.length > 0;
        return;
      }
      getAllGroupsWithId(svgEl).forEach(function (g) {
        var base = getBaseId(g.getAttribute('id'));
        var shapeCount = getShapes(g).length;
        if (!seen[base] && shapeCount > 0) {
          seen[base] = true;
          detectedBases.push(base);
        }
      });
      hasGroupIds =
        detectedBases.length > 0 &&
        getAllGroupsWithId(svgEl).some(function (g) {
          return getShapes(g).length >= 2;
        });
    }

    function syncTileGroutStroke(shape) {
      if (!shape) return;
      var host = shape.ownerSVGElement;
      var onTile = !!(tileCanvas && host && tileCanvas.contains(host));
      var onSample = !onTile && !!(host && (
        (samplePreviewSvgHost && samplePreviewSvgHost.contains(host)) ||
        (priceCardSampleHost && priceCardSampleHost.contains(host)) ||
        (sampleMainEl && sampleMainEl.contains(host))
      ));
      var fill = (shape.getAttribute('fill') || shape.style.fill || '').trim();
      var unpainted =
        (onTile || onSample) &&
        fill.indexOf('url(') !== 0 &&
        isWhiteFillHex(fill || '#ffffff');
      if (unpainted) {
        shape.setAttribute('stroke', TILE_UNPAINTED_GROUT);
        shape.setAttribute('stroke-width', '1');
        shape.setAttribute('vector-effect', 'non-scaling-stroke');
        shape.style.stroke = TILE_UNPAINTED_GROUT;
      } else {
        shape.setAttribute('stroke', 'none');
        shape.removeAttribute('stroke-width');
        shape.removeAttribute('vector-effect');
        shape.style.stroke = 'none';
      }
    }

    function applyWhite(svgEl) {
      getShapes(svgEl).forEach(function (s) {
        removeFlatUnderlay(s);
        setFill(s, '#FFFFFF');
        syncTileGroutStroke(s);
      });
    }

    function applyColorMapToSvg(svgEl, map, options) {
      options = options || {};
      if (!svgEl || !map) return;
      if (options.flat || !enableTileTexture) {
        if (isHandPaintedLab) {
          getHandPaintedFamilyElements(svgEl).forEach(function (el) {
            var base = getHandPaintedFamilyBase(el);
            if (!map[base]) return;
            getHandPaintedFamilyShapes(el).forEach(function (s) {
              removeFlatUnderlay(s);
              setFill(s, map[base]);
              syncTileGroutStroke(s);
            });
          });
          return;
        }
        getAllGroupsWithId(svgEl).forEach(function (g) {
          var base = getBaseId(g.getAttribute('id'));
          if (!map[base]) return;
          getShapes(g).forEach(function (s) {
            removeFlatUnderlay(s);
            setFill(s, map[base]);
            syncTileGroutStroke(s);
          });
        });
        return;
      }
      paintSvgShapesWithMap(svgEl, map, {
        flat: false,
        skipSizeCheck: true
      });
    }

    function syncColorMapFromTile() {
      var tileSvg = tileCanvas.querySelector('svg');
      if (!tileSvg) return;
      if (isHandPaintedLab) {
        getHandPaintedFamilyElements(tileSvg).forEach(function (el) {
          var base = getHandPaintedFamilyBase(el);
          var shapes = getHandPaintedFamilyShapes(el);
          if (!shapes.length) return;
          var fills = shapes.map(function (s) {
            return String(getShapeHex(s) || '').toLowerCase();
          });
          var first = fills[0];
          var uniform = fills.every(function (f) {
            return f === first;
          });
          if (uniform && first && first !== '#ffffff' && first !== '#fff') {
            colorMap[base] = getShapeHex(shapes[0]);
          } else {
            delete colorMap[base];
          }
        });
        return;
      }
      getAllGroupsWithId(tileSvg).forEach(function (g) {
        var base = getBaseId(g.getAttribute('id'));
        var shapes = getShapes(g);
        if (!shapes.length) return;
        var fills = shapes.map(function (s) {
          return String(getShapeHex(s) || '').toLowerCase();
        });
        var first = fills[0];
        var uniform = fills.every(function (f) {
          return f === first;
        });
        if (uniform && first && first !== '#ffffff' && first !== '#fff') {
          colorMap[base] = getShapeHex(shapes[0]);
        } else {
          delete colorMap[base];
        }
      });
    }

    function captureFillState() {
      var state = {};
      /*
        Mosaic carpet is off-screen and unused for painting; skip its 1k+
        shapes so snapshot/shuffle stay cheap. Borders still need carpet.
      */
      var canvases =
        patternType === 'border'
          ? [tileCanvas, carpetCanvas]
          : [tileCanvas];
      canvases.forEach(function (canvas, ci) {
        if (!canvas) return;
        var svgEl = canvas.querySelector('svg');
        if (!svgEl) return;
        getShapes(svgEl).forEach(function (s, i) {
          state[ci + '-' + i] = getShapeHex(s) || '';
        });
      });
      return state;
    }

    function restoreFillState(state) {
      var canvases =
        patternType === 'border'
          ? [tileCanvas, carpetCanvas]
          : [tileCanvas];
      canvases.forEach(function (canvas, ci) {
        if (!canvas) return;
        var svgEl = canvas.querySelector('svg');
        if (!svgEl) return;
        getShapes(svgEl).forEach(function (s, i) {
          var fill = state[ci + '-' + i];
          if (fill !== undefined && fill !== null && fill !== '') {
            applyInteractiveFill(
              s,
              fill,
              getShapeStableId(s, ci + '-' + i),
              svgEl
            );
          }
        });
      });
    }

    function paintGroupOnCanvas(canvas, base, hex) {
      if (!canvas) return;
      var svgEl = canvas.querySelector('svg');
      if (!svgEl) return;
      if (isHandPaintedLab) {
        getHandPaintedFamilyElements(svgEl).forEach(function (el) {
          if (getHandPaintedFamilyBase(el) !== base) return;
          getHandPaintedFamilyShapes(el).forEach(function (s, idx) {
            applyInteractiveFill(
              s,
              hex,
              getShapeStableId(s, base + ':' + idx),
              svgEl,
              { skipSizeCheck: true }
            );
          });
        });
        return;
      }
      getAllGroupsWithId(svgEl).forEach(function (g) {
        if (getBaseId(g.getAttribute('id')) !== base) return;
        getShapes(g).forEach(function (s, idx) {
          applyInteractiveFill(
            s,
            hex,
            getShapeStableId(s, base + ':' + idx),
            svgEl,
            { skipSizeCheck: true }
          );
        });
      });
    }

    function paintGroupFlatOnCanvas(canvas, base, hex) {
      if (!canvas) return;
      var svgEl = canvas.querySelector('svg');
      if (!svgEl) return;
      if (isHandPaintedLab) {
        getHandPaintedFamilyElements(svgEl).forEach(function (el) {
          if (getHandPaintedFamilyBase(el) !== base) return;
          getHandPaintedFamilyShapes(el).forEach(function (s) {
            removeFlatUnderlay(s);
            setFill(s, hex);
            syncTileGroutStroke(s);
          });
        });
        return;
      }
      getAllGroupsWithId(svgEl).forEach(function (g) {
        if (getBaseId(g.getAttribute('id')) !== base) return;
        getShapes(g).forEach(function (s) {
          removeFlatUnderlay(s);
          setFill(s, hex);
          syncTileGroutStroke(s);
        });
      });
    }

    function paintGroup(base, hex) {
      draftDismissedAfterOrder = false;
      var slug = slugForHex(hex);
      if (slug) preloadColorTextures(slug);
      colorMap[base] = hex;
      /* Tile sheet keeps SVG textures (small, cheap). */
      paintGroupOnCanvas(tileCanvas, base, hex);
      /*
        Carpet is off-screen for mosaics - skip the 2k-shape pass.
        Borders have no room preview; their carpet IS the lab view and must
        stay textured like the mosaic tile sheet (not flat hex).
      */
      var deferredBase = base;
      var deferredHex = hex;
      scheduleRoomPreviewRefresh(deferredBase);
      requestAnimationFrame(function () {
        if (patternType === 'border') {
          paintGroupOnCanvas(carpetCanvas, deferredBase, deferredHex);
        }
        refreshSamplePreview();
      });
      workshopTrack('onColorApply');
    }

    function paintShape(shape, hex) {
      draftDismissedAfterOrder = false;
      var tileSvg = tileCanvas ? tileCanvas.querySelector('svg') : null;
      var idx = 0;
      if (tileSvg) {
        idx = getShapes(tileSvg).indexOf(shape);
        if (idx < 0) idx = 0;
      }
      var slug = slugForHex(hex);
      if (slug) preloadColorTextures(slug);
      applyInteractiveFill(
        shape,
        hex,
        getShapeStableId(shape, idx),
        shape.ownerSVGElement || tileSvg
      );
      workshopTrack('onColorApply');
    }

    function paintOrphanMirror(shapeIndex, hex) {
      if (!carpetCanvas) return;
      var carpetSvg = carpetCanvas.querySelector('svg');
      if (!carpetSvg) return;

      var carpetGroups = getAllGroupsWithId(carpetSvg);
      if (carpetGroups.length === 0) {
        carpetGroups = Array.from(carpetSvg.children).filter(function (el) {
          return el.tagName && el.tagName.toLowerCase() === 'g';
        });
      }

      var tileSvg = tileCanvas.querySelector('svg');
      if (!tileSvg) return;

      var tileGroups = getAllGroupsWithId(tileSvg);
      var targetBase = null;
      var indexWithinGroup = -1;

      for (var i = 0; i < tileGroups.length; i++) {
        var tg = tileGroups[i];
        var tgShapes = getShapes(tg);
        var globalOffset = 0;
        for (var j = 0; j < i; j++) {
          globalOffset += getShapes(tileGroups[j]).length;
        }
        if (shapeIndex >= globalOffset && shapeIndex < globalOffset + tgShapes.length) {
          targetBase = getBaseId(tg.getAttribute('id'));
          indexWithinGroup = shapeIndex - globalOffset;
          break;
        }
      }

      if (targetBase !== null) {
        carpetGroups.forEach(function (cg) {
          if (getBaseId(cg.getAttribute('id') || '') === targetBase) {
            var cgShapes = getShapes(cg);
            if (cgShapes[indexWithinGroup]) {
              removeFlatUnderlay(cgShapes[indexWithinGroup]);
              setFill(cgShapes[indexWithinGroup], hex);
            }
          }
        });
        return;
      }

      if (carpetGroups.length > 0) {
        carpetGroups.forEach(function (group) {
          var shapes = getShapes(group);
          if (shapes[shapeIndex]) {
            removeFlatUnderlay(shapes[shapeIndex]);
            setFill(shapes[shapeIndex], hex);
          }
        });
      } else {
        var carpetShapes = getShapes(carpetSvg);
        if (carpetShapes[shapeIndex]) {
          removeFlatUnderlay(carpetShapes[shapeIndex]);
          setFill(carpetShapes[shapeIndex], hex);
        }
      }
    }

    function snapshot() {
      undoStack.push(captureFillState());
      redoStack = [];
      updateUndoRedo();
    }

    function updateUndoRedo() {}

    function bindGroupMode(svgEl) {
      getAllGroupsWithId(svgEl).forEach(function (group) {
        var base = getBaseId(group.getAttribute('id'));

        group.style.pointerEvents = 'all';
        getShapes(group).forEach(function (s) {
          s.style.pointerEvents = 'all';
        });

        group.addEventListener('mouseenter', function () {
          highlightShapes(getShapes(group));
        });
        group.addEventListener('mouseleave', function () {
          restoreShapes(getShapes(group));
        });
        group.addEventListener('mousedown', function () {
          restoreShapes(getShapes(group));
        });
        group.addEventListener('click', function (e) {
          e.stopPropagation();
          restoreShapes(getShapes(group));
          snapshot();
          paintGroup(base, activeColor);
          renderUsed();
          updateUndoRedo();
          syncStickyBarCustomizingState();
          scheduleThumbnailCache();
        });
      });
    }

    function bindHandPaintedFamilyMode(svgEl) {
      function familyShapesForBase(base) {
        var out = [];
        getHandPaintedFamilyElements(svgEl).forEach(function (node) {
          if (getHandPaintedFamilyBase(node) !== base) return;
          getHandPaintedFamilyShapes(node).forEach(function (s) {
            out.push(s);
          });
        });
        return out;
      }

      getHandPaintedFamilyElements(svgEl).forEach(function (el) {
        var base = getHandPaintedFamilyBase(el);
        var shapes = getHandPaintedFamilyShapes(el);
        if (!shapes.length) return;

        el.style.pointerEvents = 'all';
        shapes.forEach(function (s) {
          s.style.pointerEvents = 'all';
        });

        el.addEventListener('mouseenter', function () {
          highlightShapes(familyShapesForBase(base));
        });
        el.addEventListener('mouseleave', function () {
          restoreShapes(familyShapesForBase(base));
        });
        el.addEventListener('mousedown', function () {
          restoreShapes(familyShapesForBase(base));
        });
        el.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          restoreShapes(familyShapesForBase(base));
          snapshot();
          paintGroup(base, activeColor);
          renderUsed();
          updateUndoRedo();
          syncStickyBarCustomizingState();
          scheduleThumbnailCache();
        });
      });
    }

    function bindOrphanMode(svgEl) {
      var allShapes = getShapes(svgEl);

      allShapes.forEach(function (shape, idx) {
        shape.style.pointerEvents = 'all';

        shape.addEventListener('mouseenter', function () {
          highlightShapes([shape]);
        });
        shape.addEventListener('mouseleave', function () {
          restoreShapes([shape]);
        });
        shape.addEventListener('mousedown', function () {
          restoreShapes([shape]);
        });
        shape.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          restoreShapes([shape]);
          snapshot();
          paintShape(shape, activeColor);
          paintOrphanMirror(idx, activeColor);
          roomLiveFillCache.key = null;
          renderUsed();
          updateUndoRedo();
          syncStickyBarCustomizingState();
          scheduleThumbnailCache();
          refreshSamplePreview();
          scheduleRoomPreviewRefresh();
        });
      });
    }

    function bindTile(svgEl) {
      if (isHandPaintedLab && hasGroupIds) {
        bindHandPaintedFamilyMode(svgEl);
      } else if (hasGroupIds) {
        bindGroupMode(svgEl);
      } else {
        bindOrphanMode(svgEl);
      }
    }

    function rebind(preserveColors) {
      if (!tileSvgText) return;

      var preserve = preserveColors !== false;
      var savedShapeState = preserve ? captureFillState() : null;
      var savedColorMap = {};
      if (preserve) {
        Object.keys(colorMap).forEach(function (k) {
          savedColorMap[k] = colorMap[k];
        });
      }

      if (tileSvgText) {
        var tileSvg = injectSvg(tileCanvas, tileSvgText);
        if (tileSvg) {
          applyWhite(tileSvg);
          setTileCanvasLayout(tileCanvas, tileSvg);
        }
      }
      if (carpetSvgText && carpetCanvas) {
        var carpetSvg = injectSvg(carpetCanvas, carpetSvgText);
        if (carpetSvg) {
          applyWhite(carpetSvg);
          setCanvasAspectFromViewBox(carpetCanvas, carpetSvg);
          sizeBorderCarpetToSlot();
        }
      }

      var tileSvgEl = tileCanvas.querySelector('svg');
      var carpetSvgEl = carpetCanvas ? carpetCanvas.querySelector('svg') : null;

      if (preserve) {
        if (savedShapeState && Object.keys(savedShapeState).length > 0) {
          restoreFillState(savedShapeState);
        } else if (Object.keys(savedColorMap).length > 0) {
          if (tileSvgEl) {
            applyWhite(tileSvgEl);
            applyColorMapToSvg(tileSvgEl, savedColorMap);
          }
          if (carpetSvgEl) {
            applyWhite(carpetSvgEl);
            applyColorMapToSvg(carpetSvgEl, savedColorMap);
          }
        } else {
          if (tileSvgEl) applyWhite(tileSvgEl);
          if (carpetSvgEl) applyWhite(carpetSvgEl);
        }
        colorMap = savedColorMap;
        syncColorMapFromTile();
      } else {
        if (tileSvgEl) applyWhite(tileSvgEl);
        if (carpetSvgEl) applyWhite(carpetSvgEl);
        colorMap = {};
      }

      if (tileSvgEl) bindTile(tileSvgEl);
      updateTileCursor();
      renderUsed();
    }

    function getCurrentColors() {
      var seen = {};
      var tileSvg = tileCanvas.querySelector('svg');
      if (!tileSvg) return [];
      getShapes(tileSvg).forEach(function (el) {
        var hex = getShapeHex(el);
        if (!hex || hex === '#FFFFFF' || hex === '#ffffff' || hex === 'none') return;
        if (String(hex).indexOf('url(') === 0) return;
        seen[hex.toLowerCase()] = hex;
      });
      return Object.values(seen);
    }

    function renderUsed() {
      var colors = getCurrentColors();
      root.querySelectorAll('.zellij-lab__used-count').forEach(function (el) {
        el.textContent = String(colors.length);
      });
      if (!usedListEl) return;
      usedListEl.innerHTML = '';
      colors.forEach(function (hex) {
        var p = palette.find(function (c) {
          return c.hex.toLowerCase() === hex.toLowerCase();
        });
        var item = document.createElement('div');
        item.className = 'zellij-lab__used-color-item';
        item.innerHTML =
          '<div class="zellij-lab__used-swatch" style="' +
          (p && (p.swatchPreviewUrl || p.swatchImage)
            ? swatchVisualStyle(p, patternSvgBaseUrl, glazeNs)
            : 'background:' + hex + ';') +
          (isLight(hex) ? 'box-shadow:inset 0 0 0 1px #ccc;' : '') +
          '"></div>' +
          '<span>' +
          (p ? p.name : hex) +
          '</span>';
        usedListEl.appendChild(item);
      });
    }

    function setActive(hex, name) {
      activeColor = hex;
      activeColorName = name;
      var colorEntry = palette.find(function (c) {
        return c.hex === hex;
      });
      if (activeSwatch) {
        if (colorEntry) {
          applySwatchVisual(activeSwatch, colorEntry, patternSvgBaseUrl, glazeNs);
        } else {
          activeSwatch.style.backgroundImage = '';
          activeSwatch.style.backgroundSize = '';
          activeSwatch.style.backgroundPosition = '';
          activeSwatch.style.backgroundColor = hex;
          activeSwatch.style.boxShadow = isLight(hex) ? 'inset 0 0 0 1px #ccc' : 'none';
        }
      }
      if (activeName) activeName.textContent = name;
      if (pickerEl) {
        pickerEl.querySelectorAll('.zellij-lab__swatch-card').forEach(function (c) {
          var isActive = c.dataset.hex === hex;
          c.classList.toggle('zellij-lab__swatch-card--active', isActive);
          c.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }
      updateTileCursor();
      if (colorEntry && colorEntry.slug) preloadColorTextures(colorEntry.slug);
    }

    function renderPaletteGrid(parentEl) {
      var paletteSection = document.createElement('div');
      paletteSection.className = 'zellij-lab__palette-section';

      var paletteTitle = document.createElement('p');
      paletteTitle.className = 'zellij-lab__palette-heading';
      paletteTitle.textContent = zCopy('select_color');
      paletteSection.appendChild(paletteTitle);

      var grid = document.createElement('div');
      grid.className = 'zellij-lab__palette-grid';
      var visible = getFilteredPalette();
      if (!visible.length) {
        var empty = document.createElement('p');
        empty.className = 'zellij-lab__picker-note';
        empty.textContent = zCopy('no_colors_in_filter');
        paletteSection.appendChild(empty);
        parentEl.appendChild(paletteSection);
        return;
      }
      visible.forEach(function (color) {
        var card = document.createElement('button');
        card.type = 'button';
        card.className =
          'zellij-lab__swatch-card' +
          (activeColor === color.hex ? ' zellij-lab__swatch-card--active' : '');
        card.dataset.hex = color.hex;
        card.dataset.temp = color.tempCategory || '';
        var swatchEl = document.createElement('div');
        swatchEl.className = 'zellij-lab__swatch-color';
        applySwatchVisual(swatchEl, color, patternSvgBaseUrl, glazeNs);
        swatchEl.innerHTML =
          '<span class="zellij-lab__swatch-check" aria-hidden="true"></span>';
        var nameEl = document.createElement('span');
        nameEl.className = 'zellij-lab__swatch-name';
        nameEl.textContent = color.name;
        nameEl.title = color.name;
        card.appendChild(swatchEl);
        card.appendChild(nameEl);
        card.setAttribute(
          'aria-pressed',
          activeColor === color.hex ? 'true' : 'false'
        );
        card.addEventListener('click', function () {
          setActive(color.hex, color.name);
        });
        grid.appendChild(card);
      });
      paletteSection.appendChild(grid);
      parentEl.appendChild(paletteSection);
    }

    function renderPaletteColorsOnly() {
      if (!pickerEl) return;
      pickerEl.innerHTML = '';
      renderPaletteGrid(pickerEl);
      loadState.swatches = true;
      maybeHideLoader();
      syncPaletteSwatchSize();
    }

    function syncPaletteSwatchSize() {
      /* Desktop swatches are fixed 74x49 (Figma). Mobile keeps fluid CSS. */
      if (!root) return;
      if (window.matchMedia && window.matchMedia('(max-width: 1023px)').matches) {
        root.style.removeProperty('--zellij-swatch-size');
        return;
      }
      root.style.setProperty('--zellij-swatch-size', '74px');
    }

    function renderPicker() {
      if (!pickerEl) return;
      pickerEl.innerHTML = '';
      renderPaletteGrid(pickerEl);
    }

    if (shuffleBtn) {
      listen(shuffleBtn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!hasGroupIds) return;

        syncColorMapFromTile();
        var assigned = detectedBases.map(function (base) {
          return colorMap[base] || null;
        });
        var paintedIndexes = [];
        assigned.forEach(function (hex, idx) {
          if (hex && !isWhiteFill(hex)) paintedIndexes.push(idx);
        });

        // Fresh state: don't invent palette colors — ask the user to paint first.
        if (!paintedIndexes.length) {
          showToast(zCopy('paint_areas_first_shuffle'), {
            type: 'info',
            anchor: shuffleBtn
          });
          return;
        }

        var unique = {};
        paintedIndexes.forEach(function (idx) {
          unique[assigned[idx].toLowerCase()] = true;
        });
        if (Object.keys(unique).length < 2) {
          showToast(zCopy('add_more_colors_shuffle'), {
            type: 'info',
            anchor: shuffleBtn
          });
          return;
        }

        // Redistribute only the currently used colors across painted zones.
        var pool = paintedIndexes.map(function (idx) {
          return assigned[idx];
        });
        var attempts = 0;
        var changed = false;
        while (attempts < 8 && !changed) {
          for (var i = pool.length - 1; i > 0; i--) {
            var j = Math.floor(Math.random() * (i + 1));
            var tmp = pool[i];
            pool[i] = pool[j];
            pool[j] = tmp;
          }
          changed = pool.some(function (hex, idx) {
            return (
              hex.toLowerCase() !== assigned[paintedIndexes[idx]].toLowerCase()
            );
          });
          attempts += 1;
        }

        snapshot();
        roomLiveFillCache.key = null;
        paintedIndexes.forEach(function (baseIdx, poolIdx) {
          colorMap[detectedBases[baseIdx]] = pool[poolIdx];
        });
        /*
          Always paint textured fills on the live lab canvas. Never leave flat
          hex on tile / border carpet, even for one frame.
        */
        var tileSvgShuffle = tileCanvas ? tileCanvas.querySelector('svg') : null;
        if (tileSvgShuffle) {
          applyColorMapToSvg(tileSvgShuffle, colorMap);
        }
        if (patternType === 'border' && carpetCanvas) {
          var carpetSvgShuffle = carpetCanvas.querySelector('svg');
          if (carpetSvgShuffle) {
            applyColorMapToSvg(carpetSvgShuffle, colorMap);
          }
        }
        Object.keys(colorMap).forEach(function (base) {
          var slug = slugForHex(colorMap[base]);
          if (slug) preloadColorTextures(slug);
        });
        renderUsed();
        updateUndoRedo();
        syncStickyBarCustomizingState();
        scheduleThumbnailCache();
        recolourMountedRoomPanels();
        refreshSamplePreview();
        var texturePass = function () {
          if (instanceAborted) return;
          var tileSvgLater = tileCanvas ? tileCanvas.querySelector('svg') : null;
          if (tileSvgLater) applyColorMapToSvg(tileSvgLater, colorMap);
          if (patternType === 'border' && carpetCanvas) {
            var carpetSvgLater = carpetCanvas.querySelector('svg');
            if (carpetSvgLater) applyColorMapToSvg(carpetSvgLater, colorMap);
          }
          refreshSamplePreview();
        };
        if (typeof requestIdleCallback === 'function') {
          trackIdle(requestIdleCallback(texturePass, { timeout: 120 }));
        } else {
          trackTimeout(setTimeout(texturePass, 0));
        }
      });
    }

    if (resetBtn) {
      listen(resetBtn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        snapshot();
        rebind(false);
        updateUndoRedo();
        renderUsed();
        syncStickyBarCustomizingState();
        scheduleThumbnailCache();
        refreshSamplePreview();
        scheduleRoomPreviewRefresh();
      });
    }

    function showToast(msg, options) {
      options = options || {};
      var type = options.type || 'default';
      var duration = options.duration || (type === 'error' ? 4500 : 3000);
      var anchor = options.anchor || null;

      root.querySelectorAll('.zellij-lab__toast').forEach(function (node) {
        node.remove();
      });
      document.querySelectorAll('.zellij-lab__toast--anchored').forEach(function (node) {
        node.remove();
      });

      var el = document.createElement('div');
      el.className =
        'zellij-lab__toast' +
        (type === 'error' ? ' zellij-lab__toast--error' : '') +
        (type === 'info' ? ' zellij-lab__toast--info' : '');
      el.textContent = msg;

      if (anchor && anchor.getBoundingClientRect) {
        el.classList.add('zellij-lab__toast--anchored');
        var rect = anchor.getBoundingClientRect();
        var top = rect.bottom + 8;
        var left = rect.left;
        // Keep toast on-screen
        var maxLeft = Math.max(12, window.innerWidth - 320);
        if (left > maxLeft) left = maxLeft;
        if (left < 12) left = 12;
        if (top + 80 > window.innerHeight) {
          top = Math.max(12, rect.top - 72);
        }
        el.style.left = Math.round(left) + 'px';
        el.style.top = Math.round(top) + 'px';
        document.body.appendChild(el);
      } else {
        root.appendChild(el);
      }

      setTimeout(function () {
        if (el.parentNode) el.remove();
      }, duration);
    }

    /*
      Validation notice for ordering with unpainted zones. Centered on screen so
      it cannot be missed, and it sits above the Lab overlay.
    */
    function showCenteredNotice(msg) {
      document
        .querySelectorAll('.zellij-lab__notice, .zellij-lab__notice-backdrop')
        .forEach(function (node) {
          node.remove();
        });

      var backdrop = document.createElement('div');
      backdrop.className = 'zellij-lab__notice-backdrop';

      var card = document.createElement('div');
      card.className = 'zellij-lab__notice';
      card.setAttribute('role', 'alertdialog');
      card.setAttribute('aria-live', 'assertive');

      var closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'zellij-lab__notice-close';
      closeBtn.setAttribute('aria-label', zCopy('close_aria'));
      closeBtn.innerHTML = '&times;';

      var text = document.createElement('p');
      text.className = 'zellij-lab__notice-text';
      text.textContent = msg;

      card.appendChild(closeBtn);
      card.appendChild(text);
      document.body.appendChild(backdrop);
      document.body.appendChild(card);

      var dismissed = false;
      var timer = setTimeout(dismissNotice, 4000);

      function dismissNotice() {
        if (dismissed) return;
        dismissed = true;
        clearTimeout(timer);
        backdrop.classList.remove('is-open');
        card.classList.remove('is-open');
        setTimeout(function () {
          backdrop.remove();
          card.remove();
        }, 200);
      }

      backdrop.addEventListener('click', dismissNotice);
      closeBtn.addEventListener('click', dismissNotice);

      requestAnimationFrame(function () {
        backdrop.classList.add('is-open');
        card.classList.add('is-open');
      });
    }

    function patternStorageKey() {
      return productHandle || patternName || 'design';
    }

    function loginActionRestoreMessage(action) {
      if (action === 'save')
        return zCopy('design_restored_save');
      if (action === 'sample')
        return zCopy('design_restored_sample');
      if (action === 'order')
        return zCopy('design_restored_order_colorway');
      return null;
    }

    function persistDesignForLogin(action) {
      try {
        var payload = {
          patternKey: patternStorageKey(),
          patternName: patternName || '',
          productHandle: productHandle || '',
          patternStorageFolder: patternStorageFolder || '',
          colorMap: Object.assign({}, colorMap),
          action: action || 'order',
          savedAt: Date.now()
        };
        var tileSvg = tileCanvas && tileCanvas.querySelector('svg');
        if (!hasGroupIds && tileSvg) {
          payload.fills = getShapes(tileSvg).map(function (s) {
            return getShapeHex(s) || '#FFFFFF';
          });
        }
        sessionStorage.setItem(ZELLIJ_PENDING_DESIGN_KEY, JSON.stringify(payload));
      } catch (err) {
        /* ignore quota / private mode */
      }
    }

    function loadPendingDesign() {
      try {
        var raw = sessionStorage.getItem(ZELLIJ_PENDING_DESIGN_KEY);
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        if (!parsed) {
          clearPendingDesign();
          return null;
        }
        var key = patternStorageKey();
        /*
          Never restore colors from a different pattern (pattern switch /
          funnel picker). Auth restore still works when patternKey/name match.
        */
        if (parsed.patternName && parsed.patternName !== patternName) return null;
        if (
          parsed.patternKey &&
          parsed.patternKey !== key &&
          !(
            parsed.productHandle &&
            productHandle &&
            parsed.productHandle === productHandle
          )
        ) {
          return null;
        }
        var match =
          parsed.patternKey === key ||
          (parsed.patternName && parsed.patternName === patternName) ||
          (parsed.productHandle &&
            productHandle &&
            parsed.productHandle === productHandle);
        if (!match) return null;
        return parsed;
      } catch (err) {
        clearPendingDesign();
        return null;
      }
    }

    function clearPendingDesign() {
      try {
        sessionStorage.removeItem(ZELLIJ_PENDING_DESIGN_KEY);
      } catch (err) {
        /* ignore */
      }
    }

    /**
     * Open Shopify's native customer account panel (same as header account icon).
     * Theme uses <shopify-account> web component — no public open() API; click
     * the signed-out-avatar button inside its shadow root.
     */
    function clickShopifyAccountTrigger(accountEl) {
      if (!accountEl || !accountEl.shadowRoot) return false;

      var btn =
        accountEl.shadowRoot.querySelector('button[part="signed-out-avatar"]') ||
        accountEl.shadowRoot.querySelector('[part="signed-out-avatar"]') ||
        accountEl.shadowRoot.querySelector('button');

      if (!btn) return false;
      btn.click();
      return true;
    }

    function triggerNativeAccountPanel() {
      var accounts = document.querySelectorAll('shopify-account');
      if (!accounts.length) return false;

      // Prefer header instance, then sidebar; shadow click works even if hidden.
      var ordered = [];
      accounts.forEach(function (el) {
        if (el.closest('.header__account-link')) ordered.unshift(el);
        else ordered.push(el);
      });

      for (var i = 0; i < ordered.length; i++) {
        if (clickShopifyAccountTrigger(ordered[i])) return true;
      }
      return false;
    }

    /**
     * Guests may paint freely. Save / order open Shopify's native account panel.
     * sessionStorage is a backup if the page reloads after sign-in.
     */
    function requireLoginForAction(action) {
      if (IS_LOGGED_IN) return true;
      persistDesignForLogin(action);

      if (!triggerNativeAccountPanel()) {
        showToast(zCopy('please_sign_in_header'), {
          type: 'info',
          duration: 4500
        });
      }
      return false;
    }

    function getActiveColorNames() {
      var names = [];
      var seen = {};

      function add(name) {
        var key = (name || '').toLowerCase();
        if (!key || seen[key]) return;
        seen[key] = true;
        names.push(name);
      }

      if (hasGroupIds && Object.keys(colorMap).length > 0) {
        Object.keys(colorMap).forEach(function (base) {
          var hex = colorMap[base];
          var found = palette.find(function (c) {
            return c.hex.toLowerCase() === hex.toLowerCase();
          });
          add(found ? found.name : null);
        });
      } else {
        var tileSvg = tileCanvas.querySelector('svg');
        if (tileSvg) {
          var fills = {};
          getShapes(tileSvg).forEach(function (s) {
            var f = getShapeHex(s);
            if (f && f.toLowerCase() !== '#ffffff' && f.toLowerCase() !== '#fff') {
              fills[f] = true;
            }
          });
          Object.keys(fills).slice(0, 8).forEach(function (hex) {
            var found = palette.find(function (c) {
              return c.hex.toLowerCase() === hex.toLowerCase();
            });
            add(found ? found.name : hex);
          });
        }
      }
      return names;
    }

    function buildSavedDisplayTitle() {
      var patternLabel = (cfg.displayName || '').trim() || patternName.replace(/_/g, ' ');
      var colors = getActiveColorNames();
      if (colors.length) return patternLabel + ' · ' + colors.join(' + ');
      return patternLabel;
    }

    function buildDesignState() {
      var patternLabel = (cfg.displayName || '').trim() || patternName.replace(/_/g, ' ');
      var colorSummary = getActiveColorNames().join(' + ');
      var state = {
        pattern:       patternName,
        type:          patternType,
        colorMap:      Object.assign({}, colorMap),
        patternLabel:  patternLabel,
        colorSummary:  colorSummary,
      };
      var tileSvg = tileCanvas.querySelector('svg');
      if (!hasGroupIds && tileSvg) {
        state.fills = getShapes(tileSvg).map(function (s) {
          return getShapeHex(s) || '#FFFFFF';
        });
      }
      return state;
    }

    function designFingerprint() {
      return JSON.stringify(buildDesignState());
    }

    function rasterizeSvgElement(svgEl, options) {
      options = options || {};
      return new Promise(function (resolve) {
        if (!svgEl) {
          resolve(null);
          return;
        }

        var outW = options.width > 0 ? options.width : 400;
        var outH = options.height > 0 ? options.height : 400;
        var clone = svgEl.cloneNode(true);
        var svgString = new XMLSerializer().serializeToString(clone);
        var img = new Image();
        var blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        var blobUrl = URL.createObjectURL(blob);

        img.onload = function () {
          URL.revokeObjectURL(blobUrl);
          var canvas = document.createElement('canvas');
          canvas.width = outW;
          canvas.height = outH;
          var ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }

          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, outW, outH);

          var srcW = options.vbW > 0 ? options.vbW : 0;
          var srcH = options.vbH > 0 ? options.vbH : 0;
          if (!(srcW > 0 && srcH > 0)) {
            var vbAttr = svgEl.getAttribute('viewBox');
            srcW = img.width || outW;
            srcH = img.height || outH;
            if (vbAttr) {
              var parts = vbAttr.trim().split(/[\s,]+/);
              if (parts.length >= 4) {
                srcW = parseFloat(parts[2]) || srcW;
                srcH = parseFloat(parts[3]) || srcH;
              }
            } else if (svgEl.viewBox && svgEl.viewBox.baseVal) {
              srcW = svgEl.viewBox.baseVal.width || srcW;
              srcH = svgEl.viewBox.baseVal.height || srcH;
            }
          }
          if (options.stretch) {
            ctx.drawImage(img, 0, 0, outW, outH);
          } else {
            var scale = Math.min(outW / srcW, outH / srcH);
            var drawW = srcW * scale;
            var drawH = srcH * scale;
            ctx.drawImage(
              img,
              (outW - drawW) / 2,
              (outH - drawH) / 2,
              drawW,
              drawH
            );
          }
          resolve(canvas.toDataURL(options.mime || 'image/png', options.quality));
        };

        img.onerror = function () {
          URL.revokeObjectURL(blobUrl);
          resolve(null);
        };

        img.src = blobUrl;
      });
    }

    function buildDesignThumbnailFromCanvas() {
      return new Promise(function (resolve) {
        /*
          Mosaics: paint lives on the tile sheet; carpet _3 stays unpainted
          (off-screen optimization). Borders: carpet is the visible painted view.
          Prefer the painted canvas so cart thumbs are not an empty grid.
        */
        var srcCanvas =
          patternType === 'border'
            ? carpetCanvas || tileCanvas
            : tileCanvas || carpetCanvas;
        var svg = srcCanvas && srcCanvas.querySelector('svg');
        if (!svg) {
          resolve(null);
          return;
        }
        // Cart/checkout thumbnails stay flat hex for order identification.
        var flatClone = flattenSvgForThumbnail(svg.cloneNode(true));
        rasterizeSvgElement(flatClone).then(resolve);
      });
    }

    function buildDesignThumbnail() {
      return buildDesignThumbnailFromCanvas();
    }

    function invalidateUploadedThumbnailCaches() {
      cachedThumbnailUrl = null;
      cachedThumbnailKey = null;
      cachedSampleThumbnailUrl = null;
      cachedSampleThumbnailKey = null;
    }

    function refreshThumbnailCache() {
      var key = designFingerprint();
      return buildDesignThumbnail().then(function (dataUrl) {
        cachedThumbnailDataUrl = dataUrl;
        cachedThumbnailDataKey = key;
        invalidateUploadedThumbnailCaches();
        return dataUrl;
      });
    }

    function scheduleThumbnailCache() {
      if (thumbnailCacheTimer) clearTimeout(thumbnailCacheTimer);
      thumbnailCacheTimer = setTimeout(function () {
        thumbnailCacheTimer = null;
        refreshThumbnailCache();
        persistLocalDraft();
        syncReturnReminder();
      }, THUMBNAIL_CACHE_DEBOUNCE_MS);
    }

    function hexToHsl(hex) {
      var h = String(hex || '').replace('#', '');
      if (h.length === 3) {
        h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      }
      if (h.length !== 6) return { h: 0, s: 0, l: 0 };
      var r = parseInt(h.slice(0, 2), 16) / 255;
      var g = parseInt(h.slice(2, 4), 16) / 255;
      var b = parseInt(h.slice(4, 6), 16) / 255;
      var max = Math.max(r, g, b);
      var min = Math.min(r, g, b);
      var l = (max + min) / 2;
      var s = 0;
      var hue = 0;
      if (max !== min) {
        var d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
        else if (max === g) hue = (b - r) / d + 2;
        else hue = (r - g) / d + 4;
        hue *= 60;
      }
      return { h: hue, s: s, l: l };
    }

    function temperatureCategoryForHex(hex) {
      var hsl = hexToHsl(hex);
      var h = Math.round(hsl.h);
      if (hsl.s < TEMP_GREY_MAX_S) return 'neutral';
      /* Green family stays Cool together even when S is modest */
      if (
        h > TEMP_WARM_HUE_MAX &&
        h < TEMP_GREEN_HUE_MAX &&
        hsl.s >= TEMP_GREEN_MIN_S
      ) {
        return 'cool';
      }
      /* Pale near-neutrals (Cream, Gazelle); dark chromatics keep hue family */
      if (hsl.s < TEMP_NEUTRAL_MAX_S && hsl.l >= TEMP_NEUTRAL_MIN_L) {
        return 'neutral';
      }
      if (h <= TEMP_WARM_HUE_MAX || h >= TEMP_WARM_HUE_MIN_WRAP) return 'warm';
      return 'cool';
    }

    function enrichPaletteTemperatures() {
      palette.forEach(function (color) {
        if (!color) return;
        color.tempCategory = temperatureCategoryForHex(color.hex);
      });
    }

    function getFilteredPalette() {
      if (activeFilter === 'all') return palette.slice();
      return palette.filter(function (c) {
        return c.tempCategory === activeFilter;
      });
    }

    function deliveryTimeframe(line) {
      var parts = String(line || '').split('|');
      return (parts[1] || parts[0] || '').trim();
    }

    function sampleOrderButtonLabel() {
      var sampleCents = parseInt(cfg.customSamplePrice, 10) || 0;
      var sampleFormatted = (
        (sampleCents > 0 ? formatMoneyCents(sampleCents) : '') ||
        cfg.customSamplePriceFormatted ||
        ''
      ).trim();
      if (!sampleFormatted) return zCopy('order_sample');
      return zCopyFill('order_sample_with_price', { price: sampleFormatted });
    }

    function setSampleOrderButtonLabels(label) {
      var text = label || sampleOrderButtonLabel();
      root
        .querySelectorAll(
          '.zellij-lab__btn-add-sample--footer, .zellij-lab__btn-add-sample--sticky'
        )
        .forEach(function (btn) {
          var span = btn.querySelector('.zellij-lab__btn-add-sample-label');
          if (span) span.textContent = text;
          else btn.textContent = text;
          btn.dataset.idleLabel = text;
        });
    }

    function fillPricingUi() {
      /*
        Prefer live ZELLIJ_CONFIG (funnel may hydrate after first paint).
        Number() not parseInt: Shopify cents can arrive as "21570.0".
      */
      var liveCfg = window.ZELLIJ_CONFIG || cfg || {};
      var boxCents = Math.round(Number(liveCfg.customColorwayPrice)) || 0;
      var sampleCents = Math.round(Number(liveCfg.customSamplePrice)) || 0;
      var feet = parseFloat(liveCfg.feetPerBox) || parseFloat(cfg.feetPerBox) || 0;
      var fullEl = root.querySelector('.zellij-lab__custom-price');
      var sampleEl = root.querySelector('.zellij-lab__custom-price--sample');
      var fullMeta = root.querySelector('.zellij-lab__price-card-meta--full');
      var sampleMeta = root.querySelector('.zellij-lab__price-card-meta--sample');
      var stickyVal = root.querySelector('.zellij-lab__sticky-price-value');
      var minBoxesLive =
        parseInt(liveCfg.customColorwayMinBoxes, 10) ||
        parseInt(cfg.customColorwayMinBoxes, 10) ||
        5;
      var fullFormatted =
        (boxCents > 0 ? formatMoneyCents(boxCents) : '') ||
        liveCfg.customColorwayPriceFormatted ||
        cfg.customColorwayPriceFormatted ||
        '';
      var sampleFormatted =
        (sampleCents > 0 ? formatMoneyCents(sampleCents) : '') ||
        liveCfg.customSamplePriceFormatted ||
        cfg.customSamplePriceFormatted ||
        '';
      var perUnit = '';
      if (feet > 0 && boxCents > 0) {
        var units = window.ZellijAreaUnits;
        if (units && typeof units.formatPerUnit === 'function') {
          perUnit = units.formatPerUnit(boxCents, feet, formatMoneyCents) || '';
        } else {
          perUnit = formatMoneyCents(Math.round(boxCents / feet)) + '/FT²';
        }
      }
      if (fullEl) {
        fullEl.textContent = perUnit || fullFormatted || '-';
      }
      if (sampleEl) sampleEl.textContent = sampleFormatted || '-';
      var stickyText = perUnit || fullFormatted || '-';
      if (stickyText && stickyText.indexOf('/FT²') >= 0) {
        stickyText = stickyText.replace('/FT²', '/ft²');
      }
      if (stickyVal) stickyVal.textContent = stickyText;
      setSampleOrderButtonLabels(
        sampleFormatted
          ? zCopyFill('order_sample_with_price', { price: sampleFormatted })
          : zCopy('order_sample')
      );
      if (fullMeta) {
        var bits = [];
        var packLabel =
          window.ZellijAreaUnits && typeof window.ZellijAreaUnits.formatPackSize === 'function'
            ? window.ZellijAreaUnits.formatPackSize(feet)
            : feet > 0
              ? feet + ' FT²'
              : '';
        if (packLabel) bits.push(zCopyFill('per_box_of', { pack: packLabel }));
        else bits.push(zCopy('per_box'));
        bits.push(zCopyFill('minimum_n_boxes', { count: minBoxesLive }));
        var lead = deliveryTimeframe(liveCfg.deliveryFull || cfg.deliveryFull);
        if (lead) bits.push(lead.replace(/^Delivery in\s*/i, zCopy('made_to_order_prefix')));
        fullMeta.textContent = bits.join(' · ');
      }
      var sampleLeadOnly = '';
      if (sampleMeta) {
        var sampleLead = deliveryTimeframe(
          liveCfg.deliverySample || cfg.deliverySample
        );
        var sampleBits = [];
        if (sampleLead) {
          sampleLeadOnly = sampleLead.replace(
            /^Delivery in\s*/i,
            zCopy('ships_in_prefix')
          );
          sampleBits.push(sampleLeadOnly);
        }
        sampleBits.push(zCopy('risk_free_returns'));
        sampleMeta.textContent = sampleBits.join(' · ');
      }
      var sampleMetaPrice = root.querySelector('.zellij-lab__sample-meta-price');
      var sampleMetaLead = root.querySelector('.zellij-lab__sample-meta-lead');
      if (sampleMetaPrice) sampleMetaPrice.textContent = sampleFormatted || '-';
      if (sampleMetaLead) {
        sampleMetaLead.textContent =
          sampleLeadOnly ||
          zCopy('ships_in_prefix') + zCopy('sample_ships').replace(/^in\s+/i, '');
      }
    }

    function fillDimensionLabels() {
      var parsed = parsePatternDimensionsForCanvas(
        patternName,
        cfg.tileDimensions
      );
      /* HP `_2` is a multi-tile sheet; 4"/10cm is one physical tile, not the sheet. */
      var hpEach =
        isHandPaintedLab && patternType !== 'border' ? 'each tile ' : '';

      root.querySelectorAll('.zellij-lab__dim-label--x').forEach(function (el) {
        el.textContent = hpEach + parsed.widthLabel;
      });
      root.querySelectorAll('.zellij-lab__dim-label--y').forEach(function (el) {
        el.textContent = hpEach + parsed.heightLabel;
      });
      root.querySelectorAll('.zellij-lab__dim-label--mobile').forEach(function (el) {
        el.textContent = hpEach + parsed.mobileLabel;
      });

      root.querySelectorAll('.zellij-lab__tile-wrap').forEach(function (el) {
        if (patternType === 'border' && parsed.widthNum > 0 && parsed.heightNum > 0) {
          el.style.setProperty('--zellij-lab-tile-aspect', String(parsed.tileAspect));
          el.style.setProperty('--zellij-lab-dim-height-ratio', String(parsed.heightRatio));
        } else {
          el.style.removeProperty('--zellij-lab-tile-aspect');
          el.style.removeProperty('--zellij-lab-dim-height-ratio');
        }
      });
      root.querySelectorAll('.zellij-lab__tile-canvas').forEach(function (el) {
        if (patternType === 'border' && parsed.widthNum > 0 && parsed.heightNum > 0) {
          el.style.setProperty('--zellij-lab-tile-aspect', String(parsed.tileAspect));
          el.style.removeProperty('aspect-ratio');
          el.style.paddingBottom = '0';
        } else {
          el.style.removeProperty('--zellij-lab-tile-aspect');
          el.style.removeProperty('aspect-ratio');
          el.style.removeProperty('padding-bottom');
        }
      });

      var dimLine = root.querySelector('.zellij-lab__dim-line');
      if (dimLine) {
        dimLine.setAttribute('aria-hidden', 'false');
      }
      root.querySelectorAll('.zellij-lab__dim-rule').forEach(function (el) {
        el.setAttribute('aria-hidden', 'false');
      });
    }

    function roomAssetUrl(fileBase, width) {
      var base = (cfg.patternSvgBaseUrl || '').replace(/\/$/, '');
      return base + '/Rooms/' + fileBase + '-' + width + '.webp';
    }

    function preloadBorderRoomPhoto(roomId, assetWidth) {
      var preset = ROOM_PRESETS[roomId];
      if (!preset) return Promise.resolve();
      var url = roomAssetUrl(preset.file, assetWidth);
      if (borderRoomPhotoReady[url]) return borderRoomPhotoReady[url];
      borderRoomPhotoReady[url] = new Promise(function (resolve) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        function finish() {
          resolve();
        }
        img.onload = function () {
          if (img.decode) {
            img.decode().then(finish).catch(finish);
          } else {
            finish();
          }
        };
        img.onerror = finish;
        img.src = url;
      });
      return borderRoomPhotoReady[url];
    }

    function preloadPairScenePhotos() {
      var ids =
        patternType === 'border'
          ? ['fireplace', 'stairs']
          : ['kitchen', 'bathroom'];
      ids.forEach(function (id) {
        preloadBorderRoomPhoto(id, 900);
        preloadBorderRoomPhoto(id, 1800);
      });
    }

    function preloadBorderScenePhotos() {
      preloadPairScenePhotos();
    }

    /*
      Snap kitchen/bathroom mosaic band to whole pixels plus a small vertical
      bleed so preserveAspectRatio slice never leaves a grout-toned hairline.
    */
    function layoutMosaicPatternBand(patternEl, frameEl, cut) {
      var mediaEl = frameEl.querySelector('.zellij-lab__room-preview-media');
      if (!mediaEl) {
        patternEl.style.left = cut.x * 100 + '%';
        patternEl.style.top = cut.y * 100 + '%';
        patternEl.style.width = cut.w * 100 + '%';
        patternEl.style.height = cut.h * 100 + '%';
        return;
      }
      var mediaW = mediaEl.clientWidth;
      var mediaH = mediaEl.clientHeight;
      if (!(mediaW > 1) || !(mediaH > 1)) {
        patternEl.style.left = cut.x * 100 + '%';
        patternEl.style.top = cut.y * 100 + '%';
        patternEl.style.width = cut.w * 100 + '%';
        patternEl.style.height = cut.h * 100 + '%';
        return;
      }
      var bleedY = MOSAIC_BAND_BLEED_Y;
      var leftPx = Math.round(cut.x * mediaW);
      var topPx = Math.round(cut.y * mediaH) - bleedY;
      var widthPx = Math.round(cut.w * mediaW);
      var heightPx = Math.round(cut.h * mediaH) + bleedY * 2;
      if (topPx < 0) topPx = 0;
      var maxH = mediaH - topPx;
      if (heightPx > maxH) heightPx = Math.max(1, Math.ceil(maxH));
      patternEl.style.left = leftPx + 'px';
      patternEl.style.top = topPx + 'px';
      patternEl.style.width = widthPx + 'px';
      patternEl.style.height = heightPx + 'px';
    }

    function clearBorderPatternMount(patternEl) {
      if (!patternEl) return;
      patternEl.innerHTML = '';
      delete patternEl.dataset.roomBuildId;
      delete patternEl.dataset.roomSource;
    }

    /*
      Carpet _3 packs a grid of tile sheets into one viewBox (usually 3x3;
      ILLUSION is 4x3). Derived per pattern from group counts or layout.
    */
    function countGridClusters(values, threshold) {
      if (!values.length) return 0;
      var sorted = values.slice().sort(function (a, b) {
        return a - b;
      });
      var clusters = 1;
      var thr = threshold > 0 ? threshold : 50;
      var i;
      for (i = 1; i < sorted.length; i++) {
        if (sorted[i] - sorted[i - 1] > thr) clusters++;
      }
      return clusters;
    }

    function measureCarpetGridFromLayout(carpetSvg, tileGroups) {
      if (!carpetSvg || !tileGroups || !tileGroups.length) return null;
      var firstBase = getBaseId(tileGroups[0].getAttribute('id') || '');
      if (!firstBase) return null;
      var originsX = [];
      var originsY = [];
      getAllGroupsWithId(carpetSvg).forEach(function (g) {
        var id = g.getAttribute('id') || '';
        if (getBaseId(id) !== firstBase) return;
        if (!g.getBBox) return;
        try {
          var b = g.getBBox();
          originsX.push(b.x);
          originsY.push(b.y);
        } catch (err) {
          /* ignore */
        }
      });
      if (!originsX.length) return null;
      var cols = countGridClusters(originsX, 50);
      var rows = countGridClusters(originsY, 50);
      if (cols >= 1 && rows >= 1) {
        return { cols: cols, rows: rows };
      }
      return null;
    }

    function deriveCarpetGridDims(carpetSvg, tileSvg) {
      if (!carpetSvg) return { cols: 1, rows: 1 };
      var carpetGroups = getAllGroupsWithId(carpetSvg);
      var tileGroups = tileSvg ? getAllGroupsWithId(tileSvg) : [];
      /*
        Prefer HP family counts when mosaic <g id> lists are empty / incomplete
        (shape-level ids on FRACTAL, etc.).
      */
      if (isHandPaintedLab) {
        var hpTile = tileSvg ? getHandPaintedFamilyElements(tileSvg) : [];
        var hpCarpet = getHandPaintedFamilyElements(carpetSvg);
        if (hpTile.length > 0) tileGroups = hpTile;
        if (hpCarpet.length > 0) carpetGroups = hpCarpet;
      }
      var tileN = tileGroups.length;
      var carpetN = carpetGroups.length;
      if (tileN > 0 && carpetN >= tileN) {
        var cells = carpetN / tileN;
        /*
          Robust cols: round(sqrt(cells)). Exact squares (9→3, 16→4) unchanged.
          Near-squares (64/9≈7.11→√≈2.67→3) no longer fall back to cols=1
          (which made WILLIAMS/YVES rooms ~3x too dense).
        */
        var factor = Math.max(1, Math.round(Math.sqrt(cells)));
        if (Math.abs(cells - 12) < 0.51) {
          return { cols: 4, rows: 3 };
        }
        if (factor >= 1 && factor <= 8 && cells >= 1.5) {
          return { cols: factor, rows: factor };
        }
      }
      /* ViewBox area fallback — uses cropped tile viewBox when present. */
      if (tileSvg) {
        var tileSize = readSvgViewBoxSize(tileSvg);
        var carpetSize = readSvgViewBoxSize(carpetSvg);
        if (tileSize.vbW > 0 && carpetSize.vbW > 0) {
          var areaRatio =
            (carpetSize.vbW * carpetSize.vbH) /
            (tileSize.vbW * tileSize.vbH);
          var areaFactor = Math.max(1, Math.round(Math.sqrt(areaRatio)));
          if (areaFactor >= 1 && areaFactor <= 8 && areaRatio >= 1.5) {
            return { cols: areaFactor, rows: areaFactor };
          }
        }
      }
      var layout = measureCarpetGridFromLayout(carpetSvg, tileGroups);
      if (layout) return layout;
      return { cols: 1, rows: 1 };
    }

    function roomPatternIdPrefix(roomId, isFull) {
      return 'room-' + roomId + '-' + (isFull ? 'full' : 'desk') + '-';
    }

    function roomSymbolBaseId(id, prefix) {
      var raw = String(id || '');
      if (prefix && raw.indexOf(prefix) === 0) raw = raw.slice(prefix.length);
      return getBaseId(raw);
    }

    function uniquifySvgIds(rootEl, prefix) {
      if (!rootEl || !prefix) return;
      var idMap = {};
      rootEl.querySelectorAll('[id]').forEach(function (el) {
        var oldId = el.getAttribute('id');
        if (!oldId) return;
        var newId = prefix + oldId;
        idMap[oldId] = newId;
        el.setAttribute('id', newId);
      });
      function rewriteUrlRef(value) {
        if (!value || value.indexOf('url(') === -1) return value;
        return value.replace(/url\(#([^)]+)\)/g, function (_, id) {
          return idMap[id] ? 'url(#' + idMap[id] + ')' : 'url(#' + id + ')';
        });
      }
      rootEl.querySelectorAll('*').forEach(function (el) {
        ['fill', 'stroke', 'href'].forEach(function (attr) {
          var v = el.getAttribute(attr);
          if (v) el.setAttribute(attr, rewriteUrlRef(v));
        });
        var xlink = el.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
        if (xlink && xlink.charAt(0) === '#' && idMap[xlink.slice(1)]) {
          el.setAttributeNS(
            'http://www.w3.org/1999/xlink',
            'href',
            '#' + idMap[xlink.slice(1)]
          );
        }
        if (el.style) {
          if (el.style.fill) el.style.fill = rewriteUrlRef(el.style.fill);
          if (el.style.stroke) el.style.stroke = rewriteUrlRef(el.style.stroke);
        }
      });
    }

    /*
      Room field: live carpet (_3) symbol with url(#tex-) fills, tiled across
      the wall with a userSpaceOnUse pattern + <use>. Tile sheet (_2) is only
      a fallback when carpet is missing. Recolour swaps fills on the one
      symbol only - no raster, no toDataURL.
    */
    function readSvgViewBoxSize(svgEl) {
      var vbW = 0;
      var vbH = 0;
      if (!svgEl) return { vbW: 850.39, vbH: 850.39 };
      var vb = svgEl.viewBox && svgEl.viewBox.baseVal;
      if (vb && vb.width > 0 && vb.height > 0) {
        vbW = vb.width;
        vbH = vb.height;
      } else {
        var parts = String(svgEl.getAttribute('viewBox') || '')
          .trim()
          .split(/[\s,]+/)
          .map(parseFloat);
        if (parts.length >= 4 && parts[2] > 0 && parts[3] > 0) {
          vbW = parts[2];
          vbH = parts[3];
        }
      }
      if (!(vbW > 0 && vbH > 0)) {
        vbW = 850.39;
        vbH = 850.39;
      }
      return { vbW: vbW, vbH: vbH };
    }

    function roomColorFingerprint(map) {
      var keys = Object.keys(map || {}).sort();
      return keys
        .map(function (k) {
          return k + ':' + String(map[k] || '').toLowerCase();
        })
        .join('|');
    }

    /*
      Clone geometry only. Drop sheet tex pools and underlays so the room SVG
      gets a fresh url(#tex-) pool (objectBoundingBox), matching the tile sheet
      without carrying duplicated defs from the interactive canvas.
    */
    function stripInteractiveTexturesFromClone(rootEl) {
      if (!rootEl) return;
      rootEl.querySelectorAll('[data-zellij-underfill="1"]').forEach(function (el) {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
      rootEl.querySelectorAll('pattern').forEach(function (pat) {
        var id = pat.getAttribute('id') || '';
        if (id.indexOf('tex-') !== -1 && pat.parentNode) {
          pat.parentNode.removeChild(pat);
        }
      });
      rootEl.querySelectorAll('[fill], [stroke]').forEach(function (el) {
        ['fill', 'stroke'].forEach(function (attr) {
          var v = el.getAttribute(attr) || '';
          if (v.indexOf('url(#tex-') !== -1 || v.indexOf('url("#tex-') !== -1) {
            el.removeAttribute(attr);
          }
        });
        if (el.style) {
          if (el.style.fill && String(el.style.fill).indexOf('tex-') !== -1) {
            el.style.fill = '';
          }
          if (el.style.stroke && String(el.style.stroke).indexOf('tex-') !== -1) {
            el.style.stroke = '';
          }
        }
        if (el.hasAttribute('data-zellij-fill')) {
          el.removeAttribute('data-zellij-fill');
        }
      });
    }

    function mosaicTargetSheetsForRoom(roomId, preset) {
      var sheets = (preset && preset.targetSheetsAcross) || 11;
      if (patternType !== 'border' && roomId === 'kitchen') {
        sheets = sheets / KITCHEN_MOSAIC_SCALE;
      }
      return sheets;
    }

    function mosaicRoomScaleTag(roomId) {
      if (patternType !== 'border' && roomId === 'kitchen') return ':k120';
      return '';
    }

    /*
      Physical tile edge length in inches (HP products are 4"). Prefer the
      pattern's declared tile_dimensions width when parseable.
    */
    function physicalTileInchSize() {
      try {
        var parsed = parsePatternDimensionsForCanvas(
          patternName,
          cfg.tileDimensions
        );
        var label = String((parsed && parsed.widthIn) || '');
        var m = label.match(/(\d+(?:\.\d+)?)/);
        if (m) {
          var n = parseFloat(m[1]);
          if (n > 0 && n < 48) return n;
        }
      } catch (err) {
        /* ignore */
      }
      return 4;
    }

    /*
      SVG width of ONE physical tile. Prefer data-zellij-hp-tile-unit (origin
      cell measured on the full `_2`); else origin-family bbox on tile/carpet.
    */
    function measurePhysicalTileUnitSvgW(tileSvg, carpetSvg) {
      if (tileSvg) {
        var stored = parseFloat(tileSvg.getAttribute('data-zellij-hp-tile-unit') || '');
        if (stored > 0) return stored;
      }
      var fromTile = measureOriginFamilySvgWidth(tileSvg);
      if (fromTile > 0) return fromTile;
      return measureOriginFamilySvgWidth(carpetSvg);
    }

    function measureOriginFamilySvgWidth(svgEl) {
      if (!svgEl || !svgEl.getBBox) return 0;
      var els = isHandPaintedLab
        ? getHandPaintedFamilyElements(svgEl)
        : getAllGroupsWithId(svgEl);
      if (!els.length) return 0;
      var origin = els.filter(function (el) {
        var id = el.getAttribute('id') || '';
        return id && id === getBaseId(id);
      });
      if (!origin.length) origin = els.slice(0, 1);
      var minX = Infinity;
      var maxX = -Infinity;
      var any = false;
      var i;
      for (i = 0; i < origin.length; i++) {
        try {
          var b = origin[i].getBBox();
          if (!(b && b.width > 0)) continue;
          any = true;
          minX = Math.min(minX, b.x);
          maxX = Math.max(maxX, b.x + b.width);
        } catch (err) {
          /* ignore */
        }
      }
      return any ? Math.max(1, maxX - minX) : 0;
    }

    /*
      How many physical tiles span the carpet artboard. Prefer measuring a
      cell inside `_3` (packed carpets share viewBox size with `_2` but shrink
      cells). Fallback: viewBox / tile-sheet unit.
    */
    function measureCarpetPhysicalTilesAcross(carpetSvg, tileUnitSvgW) {
      var size = readSvgViewBoxSize(carpetSvg);
      if (!(size.vbW > 0)) return 0;
      var cellW = measureOriginFamilySvgWidth(carpetSvg);
      if (cellW > 0) {
        var across = size.vbW / cellW;
        if (across >= 0.5) return across;
      }
      if (tileUnitSvgW > 0) return size.vbW / tileUnitSvgW;
      return 0;
    }

    function measureTilesPerSheet(tileSvg, tileUnitSvgW) {
      if (!(tileUnitSvgW > 0)) return 1;
      var fromUnit = 0;
      var sheetVb = tileSvg && tileSvg.getAttribute('data-zellij-hp-sheet-vb');
      if (sheetVb) {
        var parts = String(sheetVb)
          .trim()
          .split(/[\s,]+/)
          .map(parseFloat);
        if (parts.length >= 4 && parts[2] > 0) {
          fromUnit = Math.max(1, parts[2] / tileUnitSvgW);
        }
      }
      /* Family count on the sheet ≈ cols×rows for grids; ~7 for honeycomb. */
      var fromFam = 0;
      if (tileSvg && isHandPaintedLab) {
        var fam = getHandPaintedFamilyElements(tileSvg);
        var byBase = {};
        fam.forEach(function (el) {
          var b = getHandPaintedFamilyBase(el);
          if (!b) return;
          byBase[b] = (byBase[b] || 0) + 1;
        });
        var maxN = 0;
        Object.keys(byBase).forEach(function (k) {
          if (byBase[k] > maxN) maxN = byBase[k];
        });
        if (maxN >= 4) {
          fromFam = Math.max(1, Math.round(Math.sqrt(maxN)));
        }
      }
      /*
        Prefer family-count when unit-based pitch under-counts tiles across
        (KOS A∪B origin span ≈ 2 pitches → fromUnit≈2 vs √8≈2.83).
      */
      if (fromFam > 0 && fromUnit > 0 && fromUnit < fromFam * 0.85) {
        return fromFam;
      }
      if (fromUnit > 0) return fromUnit;
      if (fromFam > 0) return fromFam;
      return 1;
    }

    /*
      Physical room scale (HP):
        carpetRealWidthIn = tilesAcrossCarpet * tileInch
        wallWidthIn       = targetSheetsAcross * tilesPerSheet * tileInch
        patternW          = boxW * (carpetRealWidthIn / wallWidthIn)
      Tile size on the wall is then always ~tileInch, independent of whether
      `_3` is 9×9, 8×8, or a honeycomb pack. Mosaic path unchanged (sheet cols).
    */
    function resolveRoomPatternPixelSize(
      carpetSvg,
      tileSvg,
      sheetSvg,
      targetSheets,
      boxW,
      vbW,
      vbH
    ) {
      var sheetsAcross = targetSheets > 0 ? targetSheets : 11;
      var usingCarpet = !!(carpetSvg && sheetSvg === carpetSvg);

      if (isHandPaintedLab) {
        var tileInch = physicalTileInchSize();
        var tileUnitSvgW = measurePhysicalTileUnitSvgW(tileSvg, sheetSvg);
        var tilesAcross = measureCarpetPhysicalTilesAcross(
          sheetSvg,
          tileUnitSvgW
        );
        var tilesPerSheet = measureTilesPerSheet(tileSvg || sheetSvg, tileUnitSvgW);
        if (!(tilesAcross > 0) && tileUnitSvgW > 0 && vbW > 0) {
          tilesAcross = vbW / tileUnitSvgW;
        }
        if (tilesAcross > 0 && tilesPerSheet > 0 && sheetsAcross > 0) {
          var carpetRealWidthIn = tilesAcross * tileInch;
          var carpetRealHeightIn =
            vbW > 0 ? (vbH / vbW) * carpetRealWidthIn : carpetRealWidthIn;
          var wallWidthIn = sheetsAcross * tilesPerSheet * tileInch;
          if (wallWidthIn > 0) {
            var patternW = boxW * (carpetRealWidthIn / wallWidthIn);
            if (patternW > 0) {
              patternW = Math.max(1, Math.round(patternW));
              var patternH = Math.max(
                1,
                Math.round(patternW * (vbH / vbW))
              );
              return {
                patternW: patternW,
                patternH: patternH,
                sheetsAcross: sheetsAcross,
                carpetCols: tilesPerSheet,
                tilesAcross: tilesAcross,
                tilesPerSheet: tilesPerSheet,
                tileUnitSvgW: tileUnitSvgW,
                tileInch: tileInch,
                carpetRealWidthIn: carpetRealWidthIn,
                carpetRealHeightIn: carpetRealHeightIn,
                wallWidthIn: wallWidthIn,
                scaleMode: 'physical-inch'
              };
            }
          }
        }
      }

      /* Mosaic / fallback: sheet-pack cols from cell counts. */
      var grid = deriveCarpetGridDims(carpetSvg || sheetSvg, tileSvg || sheetSvg);
      var carpetCols = grid.cols > 0 ? grid.cols : 1;
      var packsAcross =
        usingCarpet && carpetCols > 0
          ? Math.max(sheetsAcross / carpetCols, 0.25)
          : sheetsAcross;
      var patternW2 = boxW / packsAcross;
      if (!(patternW2 > 0)) return null;
      patternW2 = Math.max(1, Math.round(patternW2));
      return {
        patternW: patternW2,
        patternH: Math.max(1, Math.round(patternW2 * (vbH / vbW))),
        sheetsAcross: sheetsAcross,
        carpetCols: carpetCols,
        scaleMode: 'sheet-cols'
      };
    }

    function buildRoomPatternSvg(
      carpetSvg,
      tileSvg,
      targetSheets,
      boxW,
      boxH,
      idPrefix
    ) {
      /* Prefer carpet view (_3); tile sheet (_2) only if carpet is absent. */
      var sheetSvg = carpetSvg || tileSvg;
      if (!sheetSvg || !(boxW > 0) || !(boxH > 0)) return null;

      var size = readSvgViewBoxSize(sheetSvg);
      var vbW = size.vbW;
      var vbH = size.vbH;
      var resolved = resolveRoomPatternPixelSize(
        carpetSvg,
        tileSvg,
        sheetSvg,
        targetSheets,
        boxW,
        vbW,
        vbH
      );
      if (!resolved) return null;
      var patternW = resolved.patternW;
      var patternH = resolved.patternH;
      var sheetsAcross = resolved.sheetsAcross;
      var carpetCols = resolved.carpetCols;

      var NS = 'http://www.w3.org/2000/svg';
      var XLINK = 'http://www.w3.org/1999/xlink';
      var svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('class', 'zellij-lab__room-pattern-svg');
      svg.setAttribute('xmlns', NS);
      svg.setAttribute('xmlns:xlink', XLINK);
      svg.setAttribute('viewBox', '0 0 ' + boxW + ' ' + boxH);
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      /*
        Uniform scale only. `none` stretches tiles when the pattern element's
        CSS aspect drifts from the viewBox (fullscreen frame CSS fights).
      */
      svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      svg.setAttribute('aria-hidden', 'true');
      svg.style.display = 'block';
      svg.style.shapeRendering = 'geometricPrecision';

      var sheetClone = sheetSvg.cloneNode(true);
      stripInteractiveTexturesFromClone(sheetClone);
      /*
        CDN carpet/tile SVGs ship with colorway fills. The tile sheet is
        whitened on load; the room symbol must start clean too, then receive
        ONLY the live colorMap (empty map => fully white room).
      */
      applyWhite(sheetClone);
      uniquifySvgIds(sheetClone, idPrefix || 'room-');

      var defs = document.createElementNS(NS, 'defs');
      var sheetDefs = sheetClone.querySelector('defs');
      if (sheetDefs) {
        while (sheetDefs.firstChild) {
          defs.appendChild(sheetDefs.firstChild);
        }
        if (sheetDefs.parentNode) sheetDefs.parentNode.removeChild(sheetDefs);
      }

      var symbolId = (idPrefix || 'room-') + 'carpet';
      var symbol = document.createElementNS(NS, 'symbol');
      symbol.setAttribute('id', symbolId);
      symbol.setAttribute('viewBox', '0 0 ' + vbW + ' ' + vbH);
      while (sheetClone.firstChild) {
        symbol.appendChild(sheetClone.firstChild);
      }
      defs.appendChild(symbol);

      var patternId = (idPrefix || 'room-') + 'tile-pattern';
      var pattern = document.createElementNS(NS, 'pattern');
      pattern.setAttribute('id', patternId);
      pattern.setAttribute('patternUnits', 'userSpaceOnUse');
      pattern.setAttribute('width', String(patternW));
      pattern.setAttribute('height', String(patternH));
      var patternUse = document.createElementNS(NS, 'use');
      patternUse.setAttribute('href', '#' + symbolId);
      patternUse.setAttributeNS(XLINK, 'href', '#' + symbolId);
      patternUse.setAttribute('width', String(patternW));
      patternUse.setAttribute('height', String(patternH));
      pattern.appendChild(patternUse);
      defs.appendChild(pattern);
      svg.appendChild(defs);

      var groutBack = document.createElementNS(NS, 'rect');
      groutBack.setAttribute('class', 'zellij-lab__room-grout');
      groutBack.setAttribute('width', String(boxW));
      groutBack.setAttribute('height', String(boxH));
      groutBack.setAttribute('fill', MOSAIC_GROUT_HEX);
      svg.appendChild(groutBack);

      var field = document.createElementNS(NS, 'rect');
      field.setAttribute('class', 'zellij-lab__room-field');
      field.setAttribute('width', String(boxW));
      field.setAttribute('height', String(boxH));
      field.setAttribute('fill', 'url(#' + patternId + ')');
      svg.appendChild(field);

      svg.dataset.sheetFactor = String(carpetCols);
      svg.dataset.targetSheets = String(sheetsAcross);
      svg.dataset.unitPx = String(
        Math.round((patternW / Math.max(carpetCols, 1)) * 100) / 100
      );
      svg.dataset.patternW = String(Math.round(patternW * 100) / 100);
      svg.dataset.patternH = String(Math.round(patternH * 100) / 100);
      svg.dataset.boxW = String(Math.round(boxW * 100) / 100);
      svg.dataset.boxH = String(Math.round(boxH * 100) / 100);
      svg.dataset.carpetCols = String(carpetCols);
      svg.dataset.scaleMode = resolved.scaleMode || '';
      if (resolved.carpetRealWidthIn) {
        svg.dataset.carpetRealWidthIn = String(
          Math.round(resolved.carpetRealWidthIn * 100) / 100
        );
        svg.dataset.wallWidthIn = String(
          Math.round(resolved.wallWidthIn * 100) / 100
        );
        svg.dataset.tilesAcross = String(
          Math.round(resolved.tilesAcross * 100) / 100
        );
        svg.dataset.tileUnitSvgW = String(
          Math.round(resolved.tileUnitSvgW * 100) / 100
        );
        svg.dataset.tileInch = String(resolved.tileInch);
      }
      svg.dataset.carpetRows = String(
        resolved.tilesAcross
          ? Math.round(resolved.tilesAcross * 100) / 100
          : carpetCols
      );
      svg.dataset.roomSource = carpetSvg && sheetSvg === carpetSvg ? 'carpet' : 'tile';
      svg.dataset.roomTexturePath = 'live-svg';
      return svg;
    }

    function appendBorderStripPattern(
      defs,
      NS,
      XLINK,
      symbolId,
      strip,
      boxW,
      boxH,
      vbW,
      vbH,
      idPrefix,
      stripIdx,
      fillEdgeToEdge
    ) {
      var x = strip.x * boxW;
      var y = strip.y * boxH;
      var w = strip.w * boxW;
      var h = strip.h * boxH;
      var rotate = strip.rotate || 0;
      var cornerOverlap = 2;
      var edgeOverlap = fillEdgeToEdge ? 3 : 0;
      if (strip.id === 'top') {
        x -= cornerOverlap;
        w += cornerOverlap * 2;
      } else if (strip.id === 'left' || strip.id === 'right') {
        y -= cornerOverlap;
        h += cornerOverlap;
      } else if (fillEdgeToEdge && rotate === 0) {
        x -= edgeOverlap;
        w += edgeOverlap * 2;
      }
      var clipId = (idPrefix || 'room-') + 'strip-clip-' + stripIdx;
      /*
        Height-driven uniform scale: map full strip viewBox height (vbH) to
        band thickness (tileThick). tileLen preserves aspect at that scale.
        Stairs repeatClip: natural tileLen, tile from left, clip hides overrun.
      */
      var tileThick = rotate === 0 ? h : w;
      var tileLen = tileThick * (vbW / vbH);
      if (!(tileLen > 0) || !(tileThick > 0)) return null;
      var seamOverlap = 1;

      var clip = document.createElementNS(NS, 'clipPath');
      clip.setAttribute('id', clipId);
      clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
      var clipRect = document.createElementNS(NS, 'rect');
      clipRect.setAttribute('x', String(x));
      clipRect.setAttribute('y', String(y));
      clipRect.setAttribute('width', String(w));
      clipRect.setAttribute('height', String(h));
      clip.appendChild(clipRect);
      defs.appendChild(clip);

      var g = document.createElementNS(NS, 'g');
      g.setAttribute('clip-path', 'url(#' + clipId + ')');

      function appendTileUses(parent, originX, originY, runLen, opts) {
        opts = opts || {};
        var centered = !!opts.centered && !opts.repeatClip;
        var repeatClip = !!opts.repeatClip;
        var count;
        var startX = originX;

        if (repeatClip) {
          count = Math.max(1, Math.ceil(runLen / tileLen) + 1);
          startX = originX;
        } else {
          count = Math.max(1, Math.ceil(runLen / tileLen) + 2);
          if (centered) {
            var span = count * tileLen;
            startX = originX + (runLen - span) / 2;
          }
        }

        var i;
        for (i = 0; i < count; i++) {
          var tileUse = document.createElementNS(NS, 'use');
          tileUse.setAttribute('href', '#' + symbolId);
          tileUse.setAttributeNS(XLINK, 'href', '#' + symbolId);
          tileUse.setAttribute('x', String(startX + i * tileLen));
          tileUse.setAttribute('y', String(originY));
          tileUse.setAttribute('width', String(tileLen + seamOverlap));
          tileUse.setAttribute('height', String(tileThick));
          parent.appendChild(tileUse);
        }
      }

      if (rotate === 0) {
        appendTileUses(g, x, y, w, {
          centered: strip.id === 'top',
          repeatClip: !!fillEdgeToEdge
        });
      } else if (rotate === -90) {
        var leftWrap = document.createElementNS(NS, 'g');
        leftWrap.setAttribute(
          'transform',
          'translate(' + x + ' ' + (y + h) + ') rotate(-90)'
        );
        appendTileUses(leftWrap, 0, 0, h);
        g.appendChild(leftWrap);
      } else if (rotate === 90) {
        var rightWrap = document.createElementNS(NS, 'g');
        rightWrap.setAttribute(
          'transform',
          'translate(' + (x + w) + ' ' + y + ') rotate(90)'
        );
        appendTileUses(rightWrap, 0, 0, h);
        g.appendChild(rightWrap);
      } else {
        appendTileUses(g, x, y, w);
      }

      return g;
    }

    /*
      Fireplace only: subtle inner-edge shadow + corner AO so the single border
      row reads as ceramic sitting proud of the opening. Gradient overlays only;
      no extra tile rows, no new assets.
    */
    function appendFireplaceRealismOverlays(svg, defs, NS, boxW, boxH, idPrefix) {
      var pfx = idPrefix || 'room-';
      var cornerOverlap = 2;
      var innerPeak = 0.2;
      var cornerPeak = 0.14;

      function stripBox(strip) {
        var x = strip.x * boxW;
        var y = strip.y * boxH;
        var w = strip.w * boxW;
        var h = strip.h * boxH;
        if (strip.id === 'top') {
          x -= cornerOverlap;
          w += cornerOverlap * 2;
        } else if (strip.id === 'left' || strip.id === 'right') {
          y -= cornerOverlap;
          h += cornerOverlap;
        }
        return { x: x, y: y, w: w, h: h };
      }

      function addLinearGradient(id, x1, y1, x2, y2, startOp, endOp) {
        var g = document.createElementNS(NS, 'linearGradient');
        g.setAttribute('id', id);
        g.setAttribute('gradientUnits', 'userSpaceOnUse');
        g.setAttribute('x1', String(x1));
        g.setAttribute('y1', String(y1));
        g.setAttribute('x2', String(x2));
        g.setAttribute('y2', String(y2));
        var s0 = document.createElementNS(NS, 'stop');
        s0.setAttribute('offset', '0%');
        s0.setAttribute('stop-color', '#000000');
        s0.setAttribute('stop-opacity', String(startOp));
        var s1 = document.createElementNS(NS, 'stop');
        s1.setAttribute('offset', '100%');
        s1.setAttribute('stop-color', '#000000');
        s1.setAttribute('stop-opacity', String(endOp));
        g.appendChild(s0);
        g.appendChild(s1);
        defs.appendChild(g);
      }

      function addRadialGradient(id, cx, cy, r, peak) {
        var g = document.createElementNS(NS, 'radialGradient');
        g.setAttribute('id', id);
        g.setAttribute('gradientUnits', 'userSpaceOnUse');
        g.setAttribute('cx', String(cx));
        g.setAttribute('cy', String(cy));
        g.setAttribute('r', String(r));
        var s0 = document.createElementNS(NS, 'stop');
        s0.setAttribute('offset', '0%');
        s0.setAttribute('stop-color', '#000000');
        s0.setAttribute('stop-opacity', String(peak));
        var s1 = document.createElementNS(NS, 'stop');
        s1.setAttribute('offset', '100%');
        s1.setAttribute('stop-color', '#000000');
        s1.setAttribute('stop-opacity', '0');
        g.appendChild(s0);
        g.appendChild(s1);
        defs.appendChild(g);
      }

      function overlayRect(clipId, gradId, x, y, w, h) {
        var clip = document.createElementNS(NS, 'clipPath');
        clip.setAttribute('id', clipId);
        clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
        var clipRect = document.createElementNS(NS, 'rect');
        clipRect.setAttribute('x', String(x));
        clipRect.setAttribute('y', String(y));
        clipRect.setAttribute('width', String(w));
        clipRect.setAttribute('height', String(h));
        clip.appendChild(clipRect);
        defs.appendChild(clip);

        var g = document.createElementNS(NS, 'g');
        g.setAttribute('clip-path', 'url(#' + clipId + ')');
        var rect = document.createElementNS(NS, 'rect');
        rect.setAttribute('x', String(x));
        rect.setAttribute('y', String(y));
        rect.setAttribute('width', String(w));
        rect.setAttribute('height', String(h));
        rect.setAttribute('fill', 'url(#' + gradId + ')');
        g.appendChild(rect);
        return g;
      }

      var overlayRoot = document.createElementNS(NS, 'g');
      overlayRoot.setAttribute('class', 'zellij-lab__fireplace-realism');
      overlayRoot.setAttribute('pointer-events', 'none');
      overlayRoot.setAttribute('opacity', '0.92');

      var topStrip = null;
      var leftStrip = null;
      var rightStrip = null;
      var i;
      for (i = 0; i < FIREPLACE_BORDER_STRIPS.length; i++) {
        if (FIREPLACE_BORDER_STRIPS[i].id === 'top') topStrip = FIREPLACE_BORDER_STRIPS[i];
        if (FIREPLACE_BORDER_STRIPS[i].id === 'left') leftStrip = FIREPLACE_BORDER_STRIPS[i];
        if (FIREPLACE_BORDER_STRIPS[i].id === 'right') rightStrip = FIREPLACE_BORDER_STRIPS[i];
      }

      if (topStrip) {
        var topBox = stripBox(topStrip);
        var topGrad = pfx + 'fp-inner-top';
        addLinearGradient(
          topGrad,
          topBox.x,
          topBox.y,
          topBox.x,
          topBox.y + topBox.h,
          0,
          innerPeak
        );
        overlayRoot.appendChild(
          overlayRect(
            pfx + 'fp-clip-top',
            topGrad,
            topBox.x,
            topBox.y,
            topBox.w,
            topBox.h
          )
        );
      }

      if (leftStrip) {
        var leftBox = stripBox(leftStrip);
        var leftGrad = pfx + 'fp-inner-left';
        addLinearGradient(
          leftGrad,
          leftBox.x,
          leftBox.y,
          leftBox.x + leftBox.w,
          leftBox.y,
          0,
          innerPeak
        );
        overlayRoot.appendChild(
          overlayRect(
            pfx + 'fp-clip-left',
            leftGrad,
            leftBox.x,
            leftBox.y,
            leftBox.w,
            leftBox.h
          )
        );
      }

      if (rightStrip) {
        var rightBox = stripBox(rightStrip);
        var rightGrad = pfx + 'fp-inner-right';
        addLinearGradient(
          rightGrad,
          rightBox.x,
          rightBox.y,
          rightBox.x + rightBox.w,
          rightBox.y,
          innerPeak,
          0
        );
        overlayRoot.appendChild(
          overlayRect(
            pfx + 'fp-clip-right',
            rightGrad,
            rightBox.x,
            rightBox.y,
            rightBox.w,
            rightBox.h
          )
        );
      }

      if (topStrip && leftStrip) {
        var tlBox = stripBox(leftStrip);
        var ttBox = stripBox(topStrip);
        var tlX = tlBox.x + tlBox.w;
        var tlY = ttBox.y + ttBox.h;
        var tlR = Math.max(3, Math.min(tlBox.w, ttBox.h) * 1.8);
        var tlGrad = pfx + 'fp-ao-tl';
        addRadialGradient(tlGrad, tlX, tlY, tlR, cornerPeak);
        var tlRect = document.createElementNS(NS, 'rect');
        tlRect.setAttribute('x', String(tlX - tlR));
        tlRect.setAttribute('y', String(tlY - tlR));
        tlRect.setAttribute('width', String(tlR * 2));
        tlRect.setAttribute('height', String(tlR * 2));
        tlRect.setAttribute('fill', 'url(#' + tlGrad + ')');
        overlayRoot.appendChild(tlRect);
      }

      if (topStrip && rightStrip) {
        var trBox = stripBox(rightStrip);
        var ttBox2 = stripBox(topStrip);
        var trX = trBox.x;
        var trY = ttBox2.y + ttBox2.h;
        var trR = Math.max(3, Math.min(trBox.w, ttBox2.h) * 1.8);
        var trGrad = pfx + 'fp-ao-tr';
        addRadialGradient(trGrad, trX, trY, trR, cornerPeak);
        var trRect = document.createElementNS(NS, 'rect');
        trRect.setAttribute('x', String(trX - trR));
        trRect.setAttribute('y', String(trY - trR));
        trRect.setAttribute('width', String(trR * 2));
        trRect.setAttribute('height', String(trR * 2));
        trRect.setAttribute('fill', 'url(#' + trGrad + ')');
        overlayRoot.appendChild(trRect);
      }

      svg.appendChild(overlayRoot);
    }

    function buildFireplaceUFramePatternSvg(uFrameSvg, boxW, boxH, idPrefix) {
      if (!uFrameSvg || !(boxW > 0) || !(boxH > 0)) return null;

      var size = readSvgViewBoxSize(uFrameSvg);
      var vbW = size.vbW;
      var vbH = size.vbH;
      if (!(vbW > 0) || !(vbH > 0)) return null;

      var zone = FIREPLACE_UFRAME_ZONE;
      var zx = zone.x * boxW;
      var zy = zone.y * boxH;
      var zw = zone.w * boxW;
      var zh = zone.h * boxH;
      var scaleX = zw / vbW;
      var scaleY = zh / vbH;
      var scale =
        Math.max(scaleX, scaleY) * FIREPLACE_UFRAME_UNIFORM_SCALE;
      var drawW = vbW * scale;
      var drawH = vbH * scale;
      var ox = zx + (zw - drawW) / 2;
      var oy = zy + (zh - drawH) / 2;

      var NS = 'http://www.w3.org/2000/svg';
      var XLINK = 'http://www.w3.org/1999/xlink';
      var svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('class', 'zellij-lab__room-pattern-svg');
      svg.setAttribute('xmlns', NS);
      svg.setAttribute('xmlns:xlink', XLINK);
      svg.setAttribute('viewBox', '0 0 ' + boxW + ' ' + boxH);
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.setAttribute('aria-hidden', 'true');
      svg.style.display = 'block';
      svg.style.shapeRendering = 'geometricPrecision';

      var sheetClone = uFrameSvg.cloneNode(true);
      stripInteractiveTexturesFromClone(sheetClone);
      applyWhite(sheetClone);
      uniquifySvgIds(sheetClone, idPrefix || 'room-');

      var defs = document.createElementNS(NS, 'defs');
      var sheetDefs = sheetClone.querySelector('defs');
      if (sheetDefs) {
        while (sheetDefs.firstChild) {
          defs.appendChild(sheetDefs.firstChild);
        }
        if (sheetDefs.parentNode) sheetDefs.parentNode.removeChild(sheetDefs);
      }

      var clipId = (idPrefix || 'room-') + 'uframe-zone-clip';
      var zoneClip = document.createElementNS(NS, 'clipPath');
      zoneClip.setAttribute('id', clipId);
      zoneClip.setAttribute('clipPathUnits', 'userSpaceOnUse');
      var zoneClipRect = document.createElementNS(NS, 'rect');
      zoneClipRect.setAttribute('x', String(zx));
      zoneClipRect.setAttribute('y', String(zy));
      zoneClipRect.setAttribute('width', String(zw));
      zoneClipRect.setAttribute('height', String(zh));
      zoneClip.appendChild(zoneClipRect);
      defs.appendChild(zoneClip);

      var symbolId = (idPrefix || 'room-') + 'carpet';
      var symbol = document.createElementNS(NS, 'symbol');
      symbol.setAttribute('id', symbolId);
      symbol.setAttribute('viewBox', '0 0 ' + vbW + ' ' + vbH);
      while (sheetClone.firstChild) {
        symbol.appendChild(sheetClone.firstChild);
      }
      defs.appendChild(symbol);

      var frameUse = document.createElementNS(NS, 'use');
      frameUse.setAttribute('href', '#' + symbolId);
      frameUse.setAttributeNS(XLINK, 'href', '#' + symbolId);
      frameUse.setAttribute('x', String(ox));
      frameUse.setAttribute('y', String(oy));
      frameUse.setAttribute('width', String(drawW));
      frameUse.setAttribute('height', String(drawH));

      var frameG = document.createElementNS(NS, 'g');
      frameG.setAttribute('clip-path', 'url(#' + clipId + ')');
      frameG.appendChild(frameUse);

      svg.appendChild(defs);
      svg.appendChild(frameG);
      svg.dataset.roomSource = 'border-scene';
      svg.dataset.borderScene = 'fireplace';
      svg.dataset.borderAsset = 'uframe3cover';
      svg.dataset.uframeScale = String(Math.round(scale * 10000) / 10000);
      svg.dataset.uframeFit = 'cover';
      svg.dataset.boxW = String(Math.round(boxW * 100) / 100);
      svg.dataset.boxH = String(Math.round(boxH * 100) / 100);
      svg.dataset.carpetCols = '1';
      svg.dataset.carpetRows = '1';
      svg.dataset.roomTexturePath = 'live-svg';
      return svg;
    }

    function buildStairsStripPatternSvg(stripSvg, boxW, boxH, idPrefix) {
      var sheetSvg = stripSvg;
      if (!sheetSvg || !(boxW > 0) || !(boxH > 0)) return null;

      var size = readSvgViewBoxSize(sheetSvg);
      var vbW = size.vbW;
      var vbH = size.vbH;
      var strips = STAIRS_BORDER_RISERS;
      if (!strips || !strips.length) return null;

      var NS = 'http://www.w3.org/2000/svg';
      var XLINK = 'http://www.w3.org/1999/xlink';
      var svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('class', 'zellij-lab__room-pattern-svg');
      svg.setAttribute('xmlns', NS);
      svg.setAttribute('xmlns:xlink', XLINK);
      svg.setAttribute('viewBox', '0 0 ' + boxW + ' ' + boxH);
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.setAttribute('aria-hidden', 'true');
      svg.style.display = 'block';
      svg.style.shapeRendering = 'geometricPrecision';

      var sheetClone = sheetSvg.cloneNode(true);
      stripInteractiveTexturesFromClone(sheetClone);
      applyWhite(sheetClone);
      uniquifySvgIds(sheetClone, idPrefix || 'room-');

      var defs = document.createElementNS(NS, 'defs');
      var sheetDefs = sheetClone.querySelector('defs');
      if (sheetDefs) {
        while (sheetDefs.firstChild) {
          defs.appendChild(sheetDefs.firstChild);
        }
        if (sheetDefs.parentNode) sheetDefs.parentNode.removeChild(sheetDefs);
      }

      var symbolId = (idPrefix || 'room-') + 'carpet';
      var symbol = document.createElementNS(NS, 'symbol');
      symbol.setAttribute('id', symbolId);
      symbol.setAttribute('viewBox', '0 0 ' + vbW + ' ' + vbH);
      while (sheetClone.firstChild) {
        symbol.appendChild(sheetClone.firstChild);
      }
      defs.appendChild(symbol);

      var stripGroups = [];
      strips.forEach(function (strip, idx) {
        var stripG = appendBorderStripPattern(
          defs,
          NS,
          XLINK,
          symbolId,
          strip,
          boxW,
          boxH,
          vbW,
          vbH,
          idPrefix,
          idx,
          true
        );
        if (stripG) stripGroups.push(stripG);
      });

      svg.appendChild(defs);
      stripGroups.forEach(function (stripG) {
        svg.appendChild(stripG);
      });
      svg.dataset.roomSource = 'border-scene';
      svg.dataset.borderScene = 'stairs';
      svg.dataset.borderAsset = 'strip4tile';
      svg.dataset.boxW = String(Math.round(boxW * 100) / 100);
      svg.dataset.boxH = String(Math.round(boxH * 100) / 100);
      svg.dataset.carpetCols = '1';
      svg.dataset.carpetRows = '1';
      svg.dataset.roomTexturePath = 'live-svg';
      return svg;
    }

    function buildBorderRoomPatternSvg(sheetSvg, boxW, boxH, sceneId, idPrefix) {
      if (sceneId === 'fireplace') {
        return buildFireplaceUFramePatternSvg(sheetSvg, boxW, boxH, idPrefix);
      }
      return buildStairsStripPatternSvg(sheetSvg, boxW, boxH, idPrefix);
    }

    function roomPanelSymbolEl(svgEl, prefix) {
      if (!svgEl) return null;
      var symbolId = (prefix || '') + 'carpet';
      return (
        svgEl.getElementById(symbolId) ||
        svgEl.querySelector('symbol[id="' + symbolId + '"]') ||
        svgEl
      );
    }

    function renderRoomPanelClean(svgEl, prefix) {
      var target = roomPanelSymbolEl(svgEl, prefix);
      if (!target) return;
      applyWhite(target);
      if (svgEl && svgEl.dataset) {
        svgEl.dataset.roomFillFp = '';
        svgEl.dataset.roomCompositeCache = '0';
        svgEl.dataset.roomTexturePath = 'live-svg';
      }
    }

    function paintRoomSymbolWithMap(svgEl, map, prefix, onlyBase) {
      if (!svgEl || !map) return;
      var symbolId = (prefix || '') + 'carpet';
      var symbol =
        svgEl.getElementById(symbolId) ||
        svgEl.querySelector('symbol[id="' + symbolId + '"]');
      var target = symbol || svgEl;
      if (isHandPaintedLab) {
        getHandPaintedFamilyElements(target).forEach(function (el) {
          var base = roomSymbolBaseId(el.getAttribute('id'), prefix);
          if (onlyBase && base !== onlyBase) return;
          var hex = map[base];
          if (!hex) return;
          if (String(el.tagName || '').toLowerCase() === 'g') {
            el.removeAttribute('fill');
            el.style.fill = '';
          }
          getHandPaintedFamilyShapes(el).forEach(function (s, idx) {
            if (enableTileTexture) {
              applyInteractiveFill(
                s,
                hex,
                getShapeStableId(s, base + ':' + idx),
                svgEl,
                { skipSizeCheck: true }
              );
            } else {
              removeFlatUnderlay(s);
              setFill(s, hex);
            }
          });
        });
        return;
      }
      if (!symbol) {
        getAllGroupsWithId(svgEl).forEach(function (g) {
          var base = roomSymbolBaseId(g.getAttribute('id'), prefix);
          if (onlyBase && base !== onlyBase) return;
          var hex = map[base];
          if (!hex) return;
          getShapes(g).forEach(function (s, idx) {
            if (enableTileTexture) {
              applyInteractiveFill(
                s,
                hex,
                getShapeStableId(s, base + ':' + idx),
                svgEl,
                { skipSizeCheck: true }
              );
            } else {
              removeFlatUnderlay(s);
              setFill(s, hex);
            }
          });
        });
        return;
      }
      getAllGroupsWithId(symbol).forEach(function (g) {
        var base = roomSymbolBaseId(g.getAttribute('id'), prefix);
        if (onlyBase && base !== onlyBase) return;
        var hex = map[base];
        if (!hex) return;
        g.removeAttribute('fill');
        g.style.fill = '';
        getShapes(g).forEach(function (s, idx) {
          if (enableTileTexture) {
            applyInteractiveFill(
              s,
              hex,
              getShapeStableId(s, base + ':' + idx),
              svgEl,
              { skipSizeCheck: true }
            );
          } else {
            removeFlatUnderlay(s);
            setFill(s, hex);
          }
        });
      });
    }

    /*
      Room preview is a VIEW of the live colorMap only. Empty map => clean
      (white) room. Never leave CDN colorway fills visible when tile/USED are
      clean.
    */
    function applyColorMapToRoomPanel(svgEl, map, prefix, onlyBase, onPaintDone) {
      if (!svgEl) {
        if (onPaintDone) onPaintDone();
        return;
      }
      map = map || {};
      var t0 =
        typeof performance !== 'undefined' && performance.now
          ? performance.now()
          : Date.now();
      var liveKeys = Object.keys(map).filter(function (base) {
        return map[base] && !isWhiteFill(map[base]);
      });
      var fp = roomPatternFillFingerprint(prefix);

      function notifyPaintDone() {
        if (!paintTokenCurrent()) return;
        var cb = svgEl._zellijRoomPaintDone;
        svgEl._zellijRoomPaintDone = null;
        if (typeof cb === 'function') cb();
      }

      /*
        Per-SVG paint token: prewarm / alternate scenes must not cancel the
        visible scene's pending texture paint (global gen caused blank rooms).
        Keep the latest mount onPaintDone across re-entrant applies that omit
        a callback (texture refresh), so finishMount still runs.
      */
      var paintToken = (Number(svgEl.dataset.roomPaintGen) || 0) + 1;
      if (svgEl.dataset) svgEl.dataset.roomPaintGen = String(paintToken);
      if (typeof onPaintDone === 'function') {
        svgEl._zellijRoomPaintDone = onPaintDone;
      }
      var paintAttempts = 0;

      function paintTokenCurrent() {
        return (
          !svgEl.dataset ||
          String(svgEl.dataset.roomPaintGen) === String(paintToken)
        );
      }

      if (!onlyBase && !liveKeys.length) {
        renderRoomPanelClean(svgEl, prefix);
        roomLiveFillCache.key = fp;
        if (svgEl.dataset) svgEl.dataset.roomFillFp = fp;
        finishRoomRecolourTiming(svgEl, t0, 'live-svg-clean');
        notifyPaintDone();
        return;
      }

      if (
        svgEl.dataset &&
        svgEl.dataset.roomFillFp === fp &&
        roomSvgTexturedForColorMap(svgEl, map, prefix)
      ) {
        finishRoomRecolourTiming(svgEl, t0, 'live-svg-cache');
        if (svgEl.dataset) svgEl.dataset.roomCompositeCache = '1';
        notifyPaintDone();
        return;
      }

      function paintNow() {
        if (!paintTokenCurrent()) return;
        /*
          Live colorMap is the only source. Unmapped bases go white (clears CDN
          colorways and removed paints). Mapped bases receive map colors.
          Avoid white-all-then-repaint on every shuffle (R02 cost).
        */
        if (!onlyBase) {
          var target = roomPanelSymbolEl(svgEl, prefix);
          if (target) {
            if (isHandPaintedLab) {
              getHandPaintedFamilyElements(target).forEach(function (el) {
                var base = roomSymbolBaseId(el.getAttribute('id'), prefix);
                if (map[base] && !isWhiteFill(map[base])) return;
                if (String(el.tagName || '').toLowerCase() === 'g') {
                  el.removeAttribute('fill');
                  if (el.style) el.style.fill = '';
                }
                getHandPaintedFamilyShapes(el).forEach(function (s) {
                  removeFlatUnderlay(s);
                  setFill(s, '#FFFFFF');
                });
              });
            } else {
              getAllGroupsWithId(target).forEach(function (g) {
                var base = roomSymbolBaseId(g.getAttribute('id'), prefix);
                if (map[base] && !isWhiteFill(map[base])) return;
                g.removeAttribute('fill');
                if (g.style) g.style.fill = '';
                getShapes(g).forEach(function (s) {
                  removeFlatUnderlay(s);
                  setFill(s, '#FFFFFF');
                });
              });
            }
          }
        }
        paintRoomSymbolWithMap(svgEl, map, prefix, onlyBase || null);
        paintAttempts += 1;
        if (
          enableTileTexture &&
          !roomSvgTexturedForColorMap(svgEl, map, prefix)
        ) {
          /*
            Textures were not ready or pool failed: do not stamp roomFillFp
            (cache would skip a blank remount). Retry after preload.
          */
          var retrySlugs = [];
          liveKeys.forEach(function (base) {
            if (onlyBase && base !== onlyBase) return;
            var slug = slugForHex(map[base]);
            if (slug && retrySlugs.indexOf(slug) === -1) retrySlugs.push(slug);
          });
          if (!retrySlugs.length || paintAttempts >= 8) {
            notifyPaintDone();
            if (roomScaleRevealPending) revealRoomPreviewIfStable();
            return;
          }
          Promise.all(
            retrySlugs.map(function (slug) {
              return preloadColorTextures(slug);
            })
          ).then(function () {
            if (!paintTokenCurrent()) return;
            if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
              return;
            }
            paintNow();
          });
          return;
        }
        roomLiveFillCache.key = fp;
        if (svgEl.dataset) {
          svgEl.dataset.roomFillFp = fp;
          svgEl.dataset.roomCompositeCache = '0';
          svgEl.dataset.roomTexturePath = 'live-svg';
        }
        finishRoomRecolourTiming(
          svgEl,
          t0,
          enableTileTexture ? 'live-svg' : 'flat'
        );
        notifyPaintDone();
        if (roomScaleRevealPending) revealRoomPreviewIfStable();
      }

      if (!enableTileTexture) {
        paintNow();
        return;
      }

      var slugList = [];
      liveKeys.forEach(function (base) {
        if (onlyBase && base !== onlyBase) return;
        var slug = slugForHex(map[base]);
        if (slug && slugList.indexOf(slug) === -1) slugList.push(slug);
      });

      var allReady = slugList.every(function (slug) {
        return textureReadySlugs[slug];
      });
      if (allReady || !slugList.length) {
        paintNow();
        return;
      }

      Promise.all(
        slugList.map(function (slug) {
          return preloadColorTextures(slug);
        })
      ).then(function () {
        if (!paintTokenCurrent()) return;
        if (instanceAborted || instanceGeneration !== labInstanceGeneration) return;
        paintNow();
      });
    }


    function parseAspectRatio(value) {
      var parts = String(value || '1 / 1').split('/');
      var a = parseFloat(parts[0]);
      var b = parseFloat(parts[1]);
      if (!(a > 0 && b > 0)) return 1;
      return a / b;
    }

    function fitSizeInSlot(availW, availH, widthOverHeight) {
      var ratio = widthOverHeight > 0 ? widthOverHeight : 1;
      if (!(availW > 1) || !(availH > 1)) {
        return { w: 0, h: 0 };
      }
      var w = availW;
      var h = w / ratio;
      if (h > availH) {
        h = availH;
        w = h * ratio;
      }
      return { w: Math.max(1, Math.floor(w)), h: Math.max(1, Math.floor(h)) };
    }

    function viewBoxWidthOverHeight(svgEl) {
      if (!svgEl) return 1;
      var vb = svgEl.getAttribute('viewBox');
      if (vb) {
        var parts = vb.trim().split(/[\s,]+/);
        if (parts.length >= 4) {
          var w = parseFloat(parts[2]);
          var h = parseFloat(parts[3]);
          if (w > 0 && h > 0) return w / h;
        }
      }
      try {
        var bbox = svgEl.getBBox();
        if (bbox.width > 0 && bbox.height > 0) return bbox.width / bbox.height;
      } catch (e) {}
      return 1;
    }

    function sizeBorderCarpetToSlot() {
      if (patternType !== 'border' || !carpetCanvas) return;
      var stage =
        carpetStage || carpetCanvas.closest('.zellij-lab__carpet-stage');
      if (!stage) return;
      var svgEl = carpetCanvas.querySelector('svg');
      var ratio = viewBoxWidthOverHeight(svgEl);
      var availW = stage.clientWidth;
      var availH = stage.clientHeight;
      var size = fitSizeInSlot(availW, availH, ratio);
      if (!(size.w > 0 && size.h > 0)) return;
      carpetCanvas.style.paddingBottom = '';
      carpetCanvas.style.width = size.w + 'px';
      carpetCanvas.style.height = size.h + 'px';
    }


    function sizeRoomFrameToSlot(frameEl, preset, slotEl) {
      if (!frameEl || !preset) return;
      if (frameEl.closest('.zellij-lab__room-fullscreen') && roomFullscreenEl && roomFullscreenEl.hidden) {
        return;
      }
      /*
        Funnel after reveal: ignore minor column reflows (USED panel / equal-
        height settle) so the kitchen photo does not visibly grow on color.
        Real window resizes (col width delta >= 8px) still remeasure.
      */
      if (
        isFunnelLabRoot() &&
        roomScaleRevealed &&
        roomScaleFrozenColW > 0 &&
        !slotEl
      ) {
        var previewColGate = frameEl.closest('.zellij-lab__col--preview');
        var colWGate = previewColGate ? previewColGate.clientWidth : 0;
        if (colWGate > 0 && Math.abs(colWGate - roomScaleFrozenColW) < 8) {
          return;
        }
        if (colWGate > 0) roomScaleFrozenColW = colWGate;
      }
      var parent = slotEl || frameEl.parentElement;
      if (!parent) return;
      var isFull = frameEl.classList.contains(
        'zellij-lab__room-preview-frame--fullscreen'
      );
      var roomPreview = frameEl.closest('.zellij-lab__room-preview');
      var previewCol = frameEl.closest('.zellij-lab__col--preview');
      var mediaEl = frameEl.querySelector('.zellij-lab__room-preview-media');
      var blockGap = 20;
      var padX = 0;
      var padY = 0;
      /*
        Desktop: size the photo+tabs block (49/47). Fullscreen: size the
        frame alone to the photo aspect.
      */
      var measureEl =
        slotEl ||
        (isFull ? parent : previewCol || roomPreview || parent);
      if (measureEl && typeof getComputedStyle !== 'undefined') {
        var cs = getComputedStyle(measureEl);
        padX =
          (parseFloat(cs.paddingLeft) || 0) +
          (parseFloat(cs.paddingRight) || 0);
        padY =
          (parseFloat(cs.paddingTop) || 0) +
          (parseFloat(cs.paddingBottom) || 0);
        if (previewCol) {
          var colCs = getComputedStyle(previewCol);
          blockGap = parseFloat(colCs.rowGap || colCs.gap) || blockGap;
        }
      }
      var pricing =
        previewCol && previewCol.querySelector('.zellij-lab__pricing-cards');
      var availW = measureEl.clientWidth - padX;
      var availH = measureEl.clientHeight - padY;
      if (!isFull && measureEl === previewCol && pricing) {
        availH -= pricing.offsetHeight + blockGap;
      }
      if (!(availW > 1) || !(availH > 1)) {
        availW = parent.clientWidth || availW;
        availH = parent.clientHeight || availH;
      }
      if (isFull && typeof window !== 'undefined') {
        var capW = Math.floor(Math.min(window.innerWidth * 0.9, 1400));
        var capH = Math.floor(window.innerHeight * 0.8);
        if (!(availW > 64) || !(availH > 64)) {
          availW = capW;
          availH = capH;
        }
      }

      var photoRatio = parseAspectRatio(preset.aspectRatio || '1 / 1');
      var size;
      if (!isFull && roomPreview && measureEl === previewCol) {
        /*
          Fill-with-minimal-crop: block width always equals the left column.
          Height aims near-square (blockAspectRatio) within the column budget.
          Never shrink width via fitSizeInSlot (that left empty side area).
        */
        var fullW = Math.max(1, Math.floor(availW));
        var fullH = Math.max(1, Math.floor(availH));
        var tabsProbe = roomPreview.querySelector('.zellij-lab__room-tabs');
        var tabsH0 = tabsProbe ? tabsProbe.offsetHeight : 52;
        var blockRatio = parseAspectRatio(
          preset.blockAspectRatio || '1 / 0.9'
        );
        var targetBlockH = Math.max(1, Math.round(fullW / blockRatio));
        if (targetBlockH > fullH) targetBlockH = fullH;
        if (targetBlockH - tabsH0 < Math.round(fullW * 0.55)) {
          targetBlockH = Math.min(
            fullH,
            Math.round(fullW * 0.55) + tabsH0
          );
        }
        size = { w: fullW, h: targetBlockH };
      } else {
        var fullBlockRatio =
          !isFull && preset.blockAspectRatio
            ? parseAspectRatio(preset.blockAspectRatio)
            : photoRatio;
        var fitW = availW;
        var fitH = availH;
        if (isFull && typeof window !== 'undefined') {
          /*
            Same visual caps as the old CSS (90vw / 1400 / 80vh), but applied
            uniformly so width and height shrink together — never stretch.
          */
          var capW = Math.floor(Math.min(window.innerWidth * 0.9, 1400));
          var capH = Math.floor(window.innerHeight * 0.8);
          if (capW > 1) fitW = Math.min(fitW, capW);
          if (capH > 1) fitH = Math.min(fitH, capH);
        }
        size = fitSizeInSlot(fitW, fitH, fullBlockRatio);
      }
      if (!(size.w > 0 && size.h > 0)) return;

      if (!isFull && roomPreview) {
        roomPreview.style.flex = '0 0 auto';
        roomPreview.style.alignSelf = 'stretch';
        roomPreview.style.width = '100%';
        roomPreview.style.height = size.h + 'px';
        roomPreview.style.maxWidth = '100%';
        roomPreview.style.aspectRatio = '';
        frameEl.style.width = '100%';
        frameEl.style.height = '';
        frameEl.style.flex = '1 1 auto';
        frameEl.style.minHeight = '0';
        frameEl.style.aspectRatio = '';
        void roomPreview.offsetHeight;
      } else {
        /*
          Fullscreen: letterbox photo-aspect frame in the stage. Never
          align-self:stretch / width:100% — that widens past the photo and
          stretches square tiles. Important beats funnel desk !important.
        */
        frameEl.style.flex = '0 0 auto';
        frameEl.style.alignSelf = 'center';
        frameEl.style.marginInline = 'auto';
        frameEl.style.setProperty('width', size.w + 'px', 'important');
        frameEl.style.setProperty('height', size.h + 'px', 'important');
        frameEl.style.maxWidth = 'none';
        frameEl.style.aspectRatio = '';
      }

      if (mediaEl) {
        var clipW = frameEl.clientWidth || size.w;
        var clipH = frameEl.clientHeight;
        if (!(clipH > 1) && !isFull && roomPreview) {
          var tabsEl = roomPreview.querySelector('.zellij-lab__room-tabs');
          var tabsH = tabsEl ? tabsEl.offsetHeight : 0;
          clipH = Math.max(1, size.h - tabsH);
        }
        if (!(clipH > 1)) clipH = Math.round(clipW / photoRatio);
        /*
          Cover-fill: media at photo aspect, at least as wide/tall as the
          frame. Frame clips. coverShiftY (0=top, 1=bottom) slides crop.
        */
        var mediaW = Math.max(1, Math.round(clipW));
        var mediaH = Math.max(1, Math.round(mediaW / photoRatio));
        if (mediaH < clipH) {
          mediaH = clipH;
          mediaW = Math.max(1, Math.round(mediaH * photoRatio));
        }
        var overflowX = Math.max(0, mediaW - clipW);
        var overflowY = Math.max(0, mediaH - clipH);
        var coverShift =
          typeof preset.coverShiftY === 'number' ? preset.coverShiftY : 0.5;
        /*
          Product overlay borders: the desk slot is shorter than the funnel,
          so center crop cuts the hearth / stair base. Stairs stay bottom-
          pinned. Fireplace sits ~15-20% up from the bottom edge.
          Funnel and mosaics keep preset coverShiftY.
        */
        if (!isFunnelLabRoot() && patternType === 'border' && !isFull) {
          coverShift = preset.id === 'fireplace' ? 0.82 : 1;
        }
        if (coverShift < 0) coverShift = 0;
        if (coverShift > 1) coverShift = 1;
        var shiftX = -Math.round(overflowX * 0.5);
        var shiftY = -Math.round(overflowY * coverShift);
        mediaEl.style.position = 'absolute';
        mediaEl.style.left = '0';
        mediaEl.style.top = '0';
        mediaEl.style.width = mediaW + 'px';
        mediaEl.style.height = mediaH + 'px';
        mediaEl.style.maxWidth = 'none';
        mediaEl.style.transform =
          'translate(' + shiftX + 'px, ' + shiftY + 'px)';
        mediaEl.style.aspectRatio = '';
        frameEl.style.removeProperty('--zellij-room-shift-y');
        frameEl.dataset.shiftYPx = String(shiftY);
        frameEl.dataset.shiftXPx = String(shiftX);
        frameEl.dataset.coverCropY = String(overflowY);
        frameEl.dataset.coverCropX = String(overflowX);
        var photoEl = frameEl.querySelector('.zellij-lab__room-photo');
        if (
          photoEl &&
          !isFunnelLabRoot() &&
          patternType === 'border' &&
          !isFull
        ) {
          photoEl.style.objectPosition =
            preset.id === 'fireplace' ? 'center 82%' : 'bottom';
        }
      }
    }

    function syncRoomPhotoDimensions(photoEl, preset, assetWidth) {
      if (!photoEl || !preset) return;
      var ratio = parseAspectRatio(preset.aspectRatio || '1 / 1');
      var w = assetWidth > 0 ? assetWidth : 900;
      var h = Math.max(1, Math.round(w / ratio));
      photoEl.width = w;
      photoEl.height = h;
    }

    function panelPlacementForRoom(roomId) {
      return (
        roomPanelPlacement[roomId] ||
        roomPanelPlacement.kitchen || { x: 0, y: 0, w: 1, h: 1 }
      );
    }

    function finishRoomRecolourTiming(svgEl, t0, path) {
      var ms = Math.round(
        (typeof performance !== 'undefined' && performance.now
          ? performance.now()
          : Date.now()) - t0
      );
      if (svgEl && svgEl.dataset) {
        svgEl.dataset.roomRecolourMs = String(ms);
        svgEl.dataset.roomTexturePath = path;
      }
      if (root.dataset) root.dataset.roomRecolourMs = String(ms);
      var patternEl = svgEl && svgEl.parentElement;
      if (patternEl && patternEl.classList.contains('zellij-lab__room-pattern')) {
        patternEl.dataset.roomNodeCount = String(countRoomPatternNodes(svgEl));
      }
    }

    function recolourMountedRoomPanels(onlyBase) {
      var slugList = [];
      Object.keys(colorMap || {}).forEach(function (base) {
        if (onlyBase && base !== onlyBase) return;
        var slug = slugForHex(colorMap[base]);
        if (slug && slugList.indexOf(slug) === -1) slugList.push(slug);
      });

      function applyAll() {
        if (instanceAborted || instanceGeneration !== labInstanceGeneration) return;
        queryRoomPatternSvgs().forEach(function (svgEl) {
          var patternEl = svgEl.parentElement;
          if (!patternEl) return;
          var frame = patternEl.closest('.zellij-lab__room-preview-frame');
          var isFull =
            frame &&
            frame.classList.contains('zellij-lab__room-preview-frame--fullscreen');
          if (isFull && !isRoomFullscreenOpen()) return;
          var roomId = patternEl.dataset.roomId || activeRoomId || 'kitchen';
          if (roomId !== activeRoomId) return;
          var prefix = roomPatternIdPrefix(roomId, !!isFull);
          applyColorMapToRoomPanel(svgEl, colorMap, prefix, onlyBase || null);
        });
      }

      if (!slugList.length) {
        applyAll();
        return;
      }
      Promise.all(
        slugList.map(function (slug) {
          return preloadColorTextures(slug);
        })
      ).then(applyAll);
    }

    function countRoomPatternNodes(svgEl) {
      if (!svgEl) return 0;
      return svgEl.querySelectorAll('*').length;
    }

    function isRoomFullscreenOpen() {
      return !!(
        roomFullscreenEl &&
        !roomFullscreenEl.hidden &&
        roomFullscreenEl.getAttribute('aria-hidden') !== 'true'
      );
    }

    function pairRoomIds() {
      return patternType === 'border'
        ? ['fireplace', 'stairs']
        : ['kitchen', 'bathroom'];
    }

    function sceneCacheKey(roomId, isFull) {
      return (
        String(patternName || '') +
        '|' +
        String(roomId || '') +
        '|' +
        (isFull ? 'full' : 'desk')
      );
    }

    function collectPatternMeta(patternEl) {
      if (!patternEl || !patternEl.dataset) return {};
      var d = patternEl.dataset;
      return {
        roomBuildId: d.roomBuildId || '',
        sheetFactor: d.sheetFactor || '',
        targetSheets: d.targetSheets || '',
        roomPath: d.roomPath || '',
        roomSource: d.roomSource || '',
        unitPx: d.unitPx || '',
        boxW: d.boxW || '',
        boxH: d.boxH || '',
        patternW: d.patternW || '',
        patternH: d.patternH || '',
        carpetCols: d.carpetCols || '',
        carpetRows: d.carpetRows || '',
        visibleSheets: d.visibleSheets || '',
        roomNodeCount: d.roomNodeCount || '',
        roomId: d.roomId || '',
        cutX: d.cutX || '',
        cutY: d.cutY || '',
        cutW: d.cutW || '',
        cutH: d.cutH || ''
      };
    }

    function applyPatternMeta(patternEl, meta) {
      if (!patternEl || !meta) return;
      Object.keys(meta).forEach(function (key) {
        if (meta[key] != null && meta[key] !== '') {
          patternEl.dataset[key] = meta[key];
        }
      });
    }

    function stashRoomScene(patternEl, isFull, roomIdOverride) {
      if (!patternEl) return;
      var svg = patternEl.querySelector('svg.zellij-lab__room-pattern-svg');
      var rid =
        roomIdOverride ||
        (svg && svg.dataset && svg.dataset.roomSceneId) ||
        patternEl.dataset.roomId;
      if (!svg || !rid || !patternEl.dataset.roomBuildId) return;
      if (svg.dataset) svg.dataset.roomSceneId = rid;
      roomSceneCache[sceneCacheKey(rid, isFull)] = {
        svg: svg,
        buildId: patternEl.dataset.roomBuildId,
        meta: collectPatternMeta(patternEl)
      };
    }

    function sceneBoxMatches(meta, boxW, boxH) {
      if (!meta) return false;
      var w = parseFloat(meta.boxW);
      var h = parseFloat(meta.boxH);
      if (!(w > 0) || !(h > 0)) return false;
      return Math.abs(w - boxW) <= 8 && Math.abs(h - boxH) <= 8;
    }

    function prewarmRoomScene(frameEl, roomId, isFull) {
      if (!frameEl || !roomId || instanceAborted) return;
      var key = sceneCacheKey(roomId, isFull);
      if (roomSceneCache[key] && roomSceneCache[key].svg) return;
      var preset =
        ROOM_PRESETS[roomId] ||
        ROOM_PRESETS[defaultRoomIdForPatternType(patternType)];
      if (!preset) return;
      var isBorderRoom = patternType === 'border';
      var cut = isBorderRoom
        ? { x: 0, y: 0, w: 1, h: 1 }
        : panelPlacementForRoom(roomId);
      var mediaEl = frameEl.querySelector('.zellij-lab__room-preview-media');
      var boxW = 0;
      var boxH = 0;
      if (mediaEl && mediaEl.clientWidth > 1 && mediaEl.clientHeight > 1) {
        if (isBorderRoom) {
          boxW = mediaEl.clientWidth;
          boxH = mediaEl.clientHeight;
        } else {
          var bleedY = MOSAIC_BAND_BLEED_Y;
          boxW = Math.max(1, Math.round(cut.w * mediaEl.clientWidth));
          boxH = Math.max(1, Math.round(cut.h * mediaEl.clientHeight) + bleedY * 2);
        }
      }
      if (!(boxW > 1) || !(boxH > 1)) return;

      var tileSvg = tileCanvas ? tileCanvas.querySelector('svg') : null;
      var carpetSvg = carpetCanvas ? carpetCanvas.querySelector('svg') : null;
      var idPrefix = roomPatternIdPrefix(roomId, isFull);
      var sceneId = (preset && preset.borderScene) || roomId;
      var targetSheets = mosaicTargetSheetsForRoom(roomId, preset);
      var roomSvg = null;
      if (isBorderRoom) {
        var borderSheetSvg =
          sceneId === 'fireplace'
            ? svgElFromCachedText(fireplaceSvgText)
            : carpetSvg;
        if (!borderSheetSvg) return;
        roomSvg = buildBorderRoomPatternSvg(
          borderSheetSvg,
          boxW,
          boxH,
          sceneId,
          idPrefix
        );
      } else {
        if (!(carpetSvg || tileSvg)) return;
        roomSvg = buildRoomPatternSvg(
          carpetSvg,
          tileSvg,
          targetSheets,
          boxW,
          boxH,
          idPrefix
        );
      }
      if (!roomSvg) return;
      var gridProbe = isBorderRoom
        ? { cols: 1, rows: 1 }
        : deriveCarpetGridDims(carpetSvg, tileSvg);
      var borderBuildTag =
        sceneId === 'fireplace' ? ':uframe3cover' : ':strip4tile';
      var roomBuildId =
        roomId +
        ':' +
        (isFull ? 'full' : 'desk') +
        ':' +
        String(patternName || '') +
        ':' +
        (isBorderRoom ? 'border-scene:' + sceneId : '') +
        (isBorderRoom
          ? ':v17' +
            borderBuildTag +
            ':' +
            Math.round(boxW) +
            'x' +
            Math.round(boxH)
          : (carpetSvg ? 'c' : 't') +
            String((gridProbe && gridProbe.cols) || 1) +
            'x' +
            String((gridProbe && gridProbe.rows) || 1) +
            ':v1' +
            mosaicRoomScaleTag(roomId) +
            ':' +
            Math.round(boxW) +
            'x' +
            Math.round(boxH));
      roomSvg.dataset.boxW = String(Math.round(boxW * 100) / 100);
      roomSvg.dataset.boxH = String(Math.round(boxH * 100) / 100);
      roomSvg.dataset.roomSceneId = roomId;
      applyColorMapToRoomPanel(roomSvg, colorMap, idPrefix, null);
      roomSceneCache[key] = {
        svg: roomSvg,
        buildId: roomBuildId,
        meta: {
          roomBuildId: roomBuildId,
          sheetFactor: roomSvg.dataset.sheetFactor || '1',
          targetSheets: String(targetSheets),
          roomPath: isBorderRoom ? 'border-scene' : 'tiled',
          roomSource: roomSvg.dataset.roomSource || '',
          unitPx: roomSvg.dataset.unitPx || '',
          boxW: roomSvg.dataset.boxW,
          boxH: roomSvg.dataset.boxH,
          patternW: roomSvg.dataset.patternW || '',
          patternH: roomSvg.dataset.patternH || '',
          carpetCols: roomSvg.dataset.carpetCols || '',
          carpetRows: roomSvg.dataset.carpetRows || '',
          visibleSheets: roomSvg.dataset.unitPx
            ? String(
                Math.round((boxW / parseFloat(roomSvg.dataset.unitPx)) * 100) /
                  100
              )
            : '',
          roomNodeCount: String(countRoomPatternNodes(roomSvg)),
          roomId: roomId,
          cutX: String(cut.x),
          cutY: String(cut.y),
          cutW: String(cut.w),
          cutH: String(cut.h)
        }
      };
    }

    function schedulePrewarmAlternateScene(frameEl, isFull) {
      if (!frameEl || !roomPreviewReady) return;
      var ids = pairRoomIds();
      var other = ids[0] === activeRoomId ? ids[1] : ids[0];
      if (!other || roomSceneCache[sceneCacheKey(other, isFull)]) return;
      function run() {
        if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
          return;
        }
        prewarmRoomScene(frameEl, other, isFull);
      }
      if (typeof requestIdleCallback === 'function') {
        trackIdle(requestIdleCallback(run, { timeout: 1200 }));
      } else {
        trackTimeout(setTimeout(run, 250));
      }
    }

    function schedulePrewarmPairScenes(frameEl, isFull) {
      if (!frameEl || !roomPreviewReady) return;
      var ids = pairRoomIds();
      function run() {
        if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
          return;
        }
        ids.forEach(function (roomId) {
          prewarmRoomScene(frameEl, roomId, isFull);
        });
      }
      if (typeof requestIdleCallback === 'function') {
        trackIdle(requestIdleCallback(run, { timeout: 1500 }));
      } else {
        trackTimeout(setTimeout(run, 300));
      }
    }

    function applyRoomToFrame(frameEl, roomId) {
      if (!frameEl) return;
      frameEl._roomApplyGen = (frameEl._roomApplyGen || 0) + 1;
      var applyGen = frameEl._roomApplyGen;

      function applyFrameStale() {
        return applyGen !== frameEl._roomApplyGen || roomId !== activeRoomId;
      }

      var preset =
        ROOM_PRESETS[roomId] ||
        ROOM_PRESETS[defaultRoomIdForPatternType(patternType)];
      var patternEl = frameEl.querySelector('.zellij-lab__room-pattern');
      var photoEl = frameEl.querySelector('.zellij-lab__room-photo');
      var isFull = frameEl.classList.contains(
        'zellij-lab__room-preview-frame--fullscreen'
      );
      var width = isFull ? 1800 : 900;
      var nextSrc = photoEl ? roomAssetUrl(preset.file, width) : '';
      if (!isFull) {
        sizeRoomFrameToSlot(frameEl, preset);
      } else {
        var stage = frameEl.closest('.zellij-lab__room-fullscreen-stage');
        sizeRoomFrameToSlot(frameEl, preset, stage || frameEl.parentElement);
      }
      var cachedScene = roomSceneCache[sceneCacheKey(roomId, isFull)];

      function commitRoomPhoto() {
        if (!photoEl || !nextSrc) return;
        photoEl.alt = roomPreviewAltForId(roomId);
        if (photoEl.crossOrigin !== 'anonymous') {
          photoEl.crossOrigin = 'anonymous';
        }
        if (!(cachedScene && cachedScene.svg)) {
          syncRoomPhotoDimensions(photoEl, preset, width);
        }
        if (photoEl.getAttribute('src') !== nextSrc) {
          photoEl.onload = function () {
            photoEl.onload = null;
            photoEl.onerror = null;
            if (applyFrameStale()) return;
            revealRoomPreviewIfStable();
          };
          photoEl.onerror = function () {
            photoEl.onerror = null;
            photoEl.onload = null;
            if (applyFrameStale()) return;
            forceRevealRoomPreview();
          };
          photoEl.src = nextSrc;
        } else if (photoEl.complete && photoEl.naturalWidth > 0) {
          /* already decoded */
        } else {
          photoEl.onload = function () {
            photoEl.onload = null;
            photoEl.onerror = null;
            if (applyFrameStale()) return;
            revealRoomPreviewIfStable();
          };
          photoEl.onerror = function () {
            photoEl.onerror = null;
            photoEl.onload = null;
            if (applyFrameStale()) return;
            forceRevealRoomPreview();
          };
        }
      }

      function placePattern() {
        if (instanceAborted || instanceGeneration !== labInstanceGeneration) return;
        if (applyFrameStale()) return;
        if (!patternEl) return;
        var isBorderRoom = patternType === 'border';
        var cut = isBorderRoom
          ? { x: 0, y: 0, w: 1, h: 1 }
          : panelPlacementForRoom(roomId);
        if (isBorderRoom) {
          patternEl.style.left = cut.x * 100 + '%';
          patternEl.style.top = cut.y * 100 + '%';
          patternEl.style.width = cut.w * 100 + '%';
          patternEl.style.height = cut.h * 100 + '%';
        } else {
          layoutMosaicPatternBand(patternEl, frameEl, cut);
        }
        patternEl.style.backgroundImage = '';
        patternEl.style.backgroundSize = '';
        patternEl.style.backgroundRepeat = '';
        patternEl.style.backgroundColor = isBorderRoom ? 'transparent' : MOSAIC_GROUT_HEX;
        patternEl.dataset.cutX = String(cut.x);
        patternEl.dataset.cutY = String(cut.y);
        patternEl.dataset.cutW = String(cut.w);
        patternEl.dataset.cutH = String(cut.h);
        patternEl.dataset.roomPath = isBorderRoom ? 'border-scene' : 'tiled';
        patternEl.dataset.roomId = roomId;

        var tileSvg = tileCanvas ? tileCanvas.querySelector('svg') : null;
        var carpetSvg = carpetCanvas ? carpetCanvas.querySelector('svg') : null;
        if (carpetSvg && !sheetBelongsToCurrentPatternSafe(carpetSvg)) {
          carpetSvg = null;
        }
        if (tileSvg && !sheetBelongsToCurrentPatternSafe(tileSvg)) {
          tileSvg = null;
        }
        /*
          Mosaic / HP room must use carpet _3. Await carpet load in every
          context (funnel + overlay). Tile _2 is only for genuine _3 failure
          after loadState.carpetSvg is true — never as a race fallback.
        */
        if (carpetSheetLoadPending()) {
          return;
        }
        /*
          Mosaic carpet packs (usually 3x3 / 4x3) need the tile sheet to
          derive carpetCols. Carpet can finish fetching before tile; without
          tile, deriveCarpetGridDims falls back to cols:1 and the first room
          paint is too dense (small tiles). Wait for tile when carpet is
          already present so the first visible paint uses the correct scale.
        */
        if (
          patternType !== 'border' &&
          carpetCanvas &&
          carpetSvg &&
          !tileSvg
        ) {
          return;
        }
        var targetSheets = mosaicTargetSheetsForRoom(roomId, preset);

        var boxW = patternEl.clientWidth;
        var boxH = patternEl.clientHeight;
        if (!(boxW > 0) || !(boxH > 0)) {
          var fr = frameEl.getBoundingClientRect();
          boxW = fr.width * cut.w;
          boxH = fr.height * cut.h;
        }
        /*
          Layout may still be settling (overlay open). Fall back to a stable
          pixel box from the frame's target width so the live SVG can mount.
        */
        if (!(boxW > 1) || !(boxH > 1)) {
          var fallbackW = isFull ? 1800 : 900;
          boxW = Math.max(1, Math.round(fallbackW * cut.w));
          boxH = Math.max(1, Math.round(boxW * (cut.h / Math.max(cut.w, 0.0001))));
        }
        if (isBorderRoom) {
          var mediaProbe = frameEl.querySelector('.zellij-lab__room-preview-media');
          if (mediaProbe && mediaProbe.clientWidth > 1 && mediaProbe.clientHeight > 1) {
            boxW = mediaProbe.clientWidth;
            boxH = mediaProbe.clientHeight;
          }
        }
        if (!(boxW > 1) || !(boxH > 1)) {
          if (isFull) {
            requestAnimationFrame(function () {
              if (applyFrameStale()) return;
              applyRoomToFrame(frameEl, roomId);
            });
          }
          return;
        }
        if (!isBorderRoom) {
          boxW = Math.max(1, Math.round(boxW));
          boxH = Math.max(1, Math.round(boxH));
        }
        var idPrefix = roomPatternIdPrefix(roomId, isFull);
        var sceneId =
          (ROOM_PRESETS[roomId] && ROOM_PRESETS[roomId].borderScene) || roomId;
        if (isBorderRoom) {
          if (sceneId === 'fireplace' && !fireplaceSvgText) return;
          if (sceneId === 'stairs' && !carpetSvg) return;
        } else if (!(carpetSvg || tileSvg)) return;

        function revealBorderCompositeWhenReady() {
          if (isBorderRoom) {
            revealRoomPreviewIfStable();
          }
        }

        function mountBorderPattern() {
          if (instanceAborted || instanceGeneration !== labInstanceGeneration) return;
          if (applyFrameStale()) return;

          var cacheKey = sceneCacheKey(roomId, isFull);
          var cached = roomSceneCache[cacheKey];
          var existingSvg = patternEl.querySelector('svg.zellij-lab__room-pattern-svg');

          function finishMount(svgEl) {
            commitRoomPhoto();
            requestAnimationFrame(function () {
              revealBorderCompositeWhenReady();
              if (!isBorderRoom) revealRoomPreviewIfStable();
            });
            schedulePrewarmPairScenes(frameEl, isFull);
          }

          if (cached && cached.svg) {
            var cachedSource =
              (cached.meta && cached.meta.roomSource) ||
              (cached.svg.dataset && cached.svg.dataset.roomSource) ||
              '';
            /*
              Drop a race-built tile cache once carpet _3 is available so the
              room remounts from the carpet instead of recolouring _2.
            */
            if (
              !isBorderRoom &&
              cachedSource === 'tile' &&
              carpetSvg
            ) {
              delete roomSceneCache[cacheKey];
              cached = null;
              if (existingSvg && existingSvg.parentNode === patternEl) {
                patternEl.removeChild(existingSvg);
                existingSvg = null;
              }
            }
          }

          if (cached && cached.svg) {
            if (existingSvg && existingSvg !== cached.svg) {
              var prevSceneId =
                (existingSvg.dataset && existingSvg.dataset.roomSceneId) ||
                patternEl.dataset.roomId;
              stashRoomScene(patternEl, isFull, prevSceneId);
              if (existingSvg.parentNode === patternEl) {
                patternEl.removeChild(existingSvg);
              }
            }
            /*
              colorMap is the single source of truth. Reuse a still-valid
              textured cache hit; otherwise attach and re-paint on a live SVG.
            */
            if (cached.svg.parentNode !== patternEl) {
              patternEl.appendChild(cached.svg);
            }
            if (cached.svg.dataset) cached.svg.dataset.roomSceneId = roomId;
            applyPatternMeta(patternEl, cached.meta);
            patternEl.dataset.roomId = roomId;
            if (cached.buildId) {
              patternEl.dataset.roomBuildId = cached.buildId;
            }
            var cachedFp = roomPatternFillFingerprint(idPrefix);
            var cacheStillValid =
              cached.svg.dataset &&
              cached.svg.dataset.roomFillFp === cachedFp &&
              roomSvgTexturedForColorMap(cached.svg, colorMap, idPrefix);
            if (cacheStillValid) {
              finishMount(cached.svg);
              return;
            }
            if (cached.svg.dataset) delete cached.svg.dataset.roomFillFp;
            applyColorMapToRoomPanel(
              cached.svg,
              colorMap,
              idPrefix,
              null,
              function () {
                if (applyFrameStale()) return;
                finishMount(cached.svg);
              }
            );
            return;
          }

          if (
            existingSvg &&
            patternEl.dataset.roomId === roomId &&
            !(
              !isBorderRoom &&
              patternEl.dataset.roomSource === 'tile' &&
              carpetSvg
            )
          ) {
            if (existingSvg.dataset) delete existingSvg.dataset.roomFillFp;
            applyColorMapToRoomPanel(
              existingSvg,
              colorMap,
              idPrefix,
              null,
              function () {
                finishMount(existingSvg);
              }
            );
            return;
          }

          if (
            existingSvg &&
            !isBorderRoom &&
            patternEl.dataset.roomSource === 'tile' &&
            carpetSvg
          ) {
            var prevTileSceneId =
              (existingSvg.dataset && existingSvg.dataset.roomSceneId) ||
              patternEl.dataset.roomId;
            stashRoomScene(patternEl, isFull, prevTileSceneId);
            if (existingSvg.parentNode === patternEl) {
              patternEl.removeChild(existingSvg);
            }
            existingSvg = null;
            delete roomSceneCache[cacheKey];
          }

          var borderBuildTag =
            sceneId === 'fireplace' ? ':uframe3cover' : ':strip4tile';
          var roomBuildId =
            roomId +
            ':' +
            (isFull ? 'full' : 'desk') +
            ':' +
            String(patternName || '') +
            ':' +
            (isBorderRoom ? 'border-scene:' + sceneId : '') +
            (isBorderRoom
              ? ':v17' +
                borderBuildTag +
                ':' +
                Math.round(boxW) +
                'x' +
                Math.round(boxH)
              : (function () {
                  var gridProbe = deriveCarpetGridDims(carpetSvg, tileSvg);
                  return (
                    (carpetSvg ? 'c' : 't') +
                    String(gridProbe.cols || 1) +
                    'x' +
                    String(gridProbe.rows || 1) +
                    ':v1' +
                    mosaicRoomScaleTag(roomId) +
                    ':' +
                    Math.round(boxW) +
                    'x' +
                    Math.round(boxH)
                  );
                })());

          if (existingSvg) {
            var prevBuiltId =
              (existingSvg.dataset && existingSvg.dataset.roomSceneId) ||
              patternEl.dataset.roomId;
            stashRoomScene(patternEl, isFull, prevBuiltId);
            if (existingSvg.parentNode === patternEl) {
              patternEl.removeChild(existingSvg);
            }
          }

          var borderSheetSvg =
            sceneId === 'fireplace'
              ? svgElFromCachedText(fireplaceSvgText)
              : carpetSvg;
          if (isBorderRoom && !borderSheetSvg) return;

          var roomSvg = isBorderRoom
            ? buildBorderRoomPatternSvg(
                borderSheetSvg,
                boxW,
                boxH,
                sceneId,
                idPrefix
              )
            : buildRoomPatternSvg(
                carpetSvg,
                tileSvg,
                targetSheets,
                boxW,
                boxH,
                idPrefix
              );
          if (!roomSvg) return;
          if (roomSvg.dataset) roomSvg.dataset.roomSceneId = roomId;
          patternEl.appendChild(roomSvg);
          patternEl.dataset.roomBuildId = roomBuildId;
          patternEl.dataset.sheetFactor = roomSvg.dataset.sheetFactor || '1';
          patternEl.dataset.targetSheets = String(targetSheets);
          patternEl.dataset.roomPath = isBorderRoom ? 'border-scene' : 'tiled';
          patternEl.dataset.roomSource = roomSvg.dataset.roomSource || '';
          patternEl.dataset.unitPx = roomSvg.dataset.unitPx;
          patternEl.dataset.boxW = roomSvg.dataset.boxW;
          patternEl.dataset.boxH = roomSvg.dataset.boxH;
          patternEl.dataset.patternW = roomSvg.dataset.patternW;
          patternEl.dataset.patternH = roomSvg.dataset.patternH;
          patternEl.dataset.carpetCols = roomSvg.dataset.carpetCols;
          patternEl.dataset.carpetRows = roomSvg.dataset.carpetRows;
          patternEl.dataset.visibleSheets = String(
            Math.round((boxW / parseFloat(roomSvg.dataset.unitPx)) * 100) / 100
          );
          patternEl.dataset.roomNodeCount = String(countRoomPatternNodes(roomSvg));
          patternEl.dataset.roomId = roomId;
          roomSceneCache[cacheKey] = {
            svg: roomSvg,
            buildId: roomBuildId,
            meta: collectPatternMeta(patternEl)
          };
          applyColorMapToRoomPanel(
            roomSvg,
            colorMap,
            idPrefix,
            null,
            function () {
              commitRoomPhoto();
              requestAnimationFrame(function () {
                if (isBorderRoom) {
                  revealBorderCompositeWhenReady();
                } else {
                  revealRoomPreviewIfStable();
                }
              });
              schedulePrewarmPairScenes(frameEl, isFull);
            }
          );
        }

        mountBorderPattern();
      }

      if (photoEl) {
        function afterPhotoReady() {
          if (applyFrameStale()) return;
          placePattern();
        }
        if (photoEl.onload !== undefined) {
          photoEl.onload = null;
          photoEl.onerror = null;
        }
        preloadBorderRoomPhoto(roomId, width).then(function () {
          if (applyFrameStale()) return;
          afterPhotoReady();
        });
      } else {
        placePattern();
      }
    }

    function refreshRoomPreview() {
      if (!roomPreviewReady) return;
      function applyAllFrames() {
        var t0 =
          typeof performance !== 'undefined' && performance.now
            ? performance.now()
            : Date.now();
        var fullscreenOpen = isRoomFullscreenOpen();
        forEachRoomPreviewFrame(function (frame) {
          var isFull = frame.classList.contains(
            'zellij-lab__room-preview-frame--fullscreen'
          );
          if (isFull && !fullscreenOpen) return;
          applyRoomToFrame(frame, activeRoomId);
        });
        var t1 =
          typeof performance !== 'undefined' && performance.now
            ? performance.now()
            : Date.now();
        lastRoomRefreshMs = Math.round(t1 - t0);
        if (root.dataset) {
          root.dataset.roomRefreshMs = String(lastRoomRefreshMs);
          root.dataset.roomPathKitchen = 'tiled';
          root.dataset.roomPathBathroom = 'tiled';
        }
      }
      applyAllFrames();
    }

    function scheduleRoomPreviewRefresh(onlyBase) {
      if (!roomPreviewReady) return;
      /*
        Colour / shuffle / reset: recolour carpet symbol in place only.
        Full remount only on room tab or refreshRoomPreview.
        Coalesce rapid clicks: one base stays onlyBase; mixed bases go full.
      */
      if (!onlyBase) {
        roomRecolourPendingBases = '*';
      } else if (roomRecolourPendingBases !== '*') {
        if (!roomRecolourPendingBases) roomRecolourPendingBases = {};
        roomRecolourPendingBases[onlyBase] = true;
      }
      if (roomRecolourTimer) clearTimeout(roomRecolourTimer);
      roomRecolourTimer = setTimeout(function () {
        roomRecolourTimer = null;
        var pending = roomRecolourPendingBases;
        roomRecolourPendingBases = null;
        if (!pending || pending === '*') {
          recolourMountedRoomPanels();
          return;
        }
        var bases = Object.keys(pending);
        if (bases.length === 1) {
          recolourMountedRoomPanels(bases[0]);
        } else {
          recolourMountedRoomPanels();
        }
      }, 32);
    }

    function roomSceneCachedReady(roomId, isFull) {
      var key = sceneCacheKey(roomId, isFull);
      var cached = roomSceneCache[key];
      if (!cached || !cached.svg) return false;
      var preset = ROOM_PRESETS[roomId];
      if (preset) {
        var assetWidth = isFull ? 1800 : 900;
        var photoUrl = roomAssetUrl(preset.file, assetWidth);
        if (photoUrl && !borderRoomPhotoReady[photoUrl]) {
          preloadBorderRoomPhoto(roomId, assetWidth);
          return false;
        }
      }
      var prefix = roomPatternIdPrefix(roomId, !!isFull);
      var fp = roomPatternFillFingerprint(prefix);
      if (cached.svg.dataset.roomFillFp !== fp) return false;
      return roomSvgTexturedForColorMap(cached.svg, colorMap, prefix);
    }

    function setActiveRoom(roomId, options) {
      options = options || {};
      if (!ROOM_PRESETS[roomId]) {
        roomId = defaultRoomIdForPatternType(patternType);
      }
      activeRoomId = roomId;
      queryRoomPreviewTabButtons(root).forEach(function (tab) {
        var on = tab.getAttribute('data-room') === roomId;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (!isFunnelLabRoot() && patternType === 'border') {
        var deskPreview = root.querySelector(
          '.zellij-lab__col--preview .zellij-lab__room-preview'
        );
        if (deskPreview) deskPreview.setAttribute('data-scene', roomId);
      }
      if (roomPreviewReady) {
        var isFull = isRoomFullscreenOpen();
        if (roomSceneCachedReady(roomId, isFull)) {
          refreshRoomPreview();
          revealRoomPreviewIfStable();
        } else {
          holdRoomPreviewUntilStable();
          refreshRoomPreview();
          revealRoomPreviewIfStable();
        }
      } else {
        refreshRoomPreview();
      }
      if (!options.skipHistory) {
        /* tab switch stays on current history level */
      }
    }

    function getRoomFullscreenFocusables() {
      if (!roomFullscreenEl || !isRoomFullscreenOpen()) return [];
      return Array.prototype.slice
        .call(
          roomFullscreenEl.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        )
        .filter(function (el) {
          return !el.disabled && el.offsetParent !== null && !el.hidden;
        });
    }

    function handleRoomFullscreenTabKey(event) {
      if (!isRoomFullscreenOpen()) return;
      if (event.key !== 'Tab') return;
      var focusables = getRoomFullscreenFocusables();
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function handleRoomPreviewWheel(event) {
      if (!isRoomFullscreenOpen()) return;
      if (roomFullscreenEl && roomFullscreenEl.contains(event.target)) return;
      event.preventDefault();
    }

    function acquireRoomPreviewScrollLock() {
      if (roomPreviewScrollLockActive) return;
      if (window.__zellijLabScrollLock && window.__zellijLabScrollLock.acquire) {
        window.__zellijLabScrollLock.acquire();
      }
      document.documentElement.classList.add('is-zellij-room-preview-open');
      roomPreviewScrollLockActive = true;
    }

    function releaseRoomPreviewScrollLock() {
      if (!roomPreviewScrollLockActive) return;
      if (window.__zellijLabScrollLock && window.__zellijLabScrollLock.release) {
        window.__zellijLabScrollLock.release();
      }
      document.documentElement.classList.remove('is-zellij-room-preview-open');
      roomPreviewScrollLockActive = false;
    }

    function openMobileRoomPreview() {
      if (!roomPreviewReady || !roomFullscreenEl) return;
      if (carpetViewMode === 'sample') setCarpetViewMode('carpet');
      roomFullscreenEl.hidden = false;
      roomFullscreenEl.setAttribute('aria-hidden', 'false');
      root.classList.add('is-room-preview');
      acquireRoomPreviewScrollLock();
      showSampleBtns.forEach(function (btn) {
        btn.setAttribute('aria-pressed', 'false');
      });
      var roomModeBtn = root.querySelector('.zellij-lab__btn-see-room');
      if (roomModeBtn) roomModeBtn.setAttribute('aria-pressed', 'true');
      if (roomSceneCachedReady(activeRoomId, true)) {
        refreshRoomPreview();
        revealRoomPreviewIfStable();
      } else {
        holdRoomPreviewUntilStable();
        refreshRoomPreview();
        revealRoomPreviewIfStable();
      }
      requestAnimationFrame(function () {
        revealRoomPreviewIfStable();
        requestAnimationFrame(function () {
          revealRoomPreviewIfStable();
        });
      });
      syncRoomPreviewTabs(root, patternType, activeRoomId);
      if (historyApi && !historyApi.hasLevel(OVERLAY_HISTORY_LEVEL_ROOM)) {
        historyApi.pushHistoryLevel(OVERLAY_HISTORY_LEVEL_ROOM);
      }
    }

    function closeMobileRoomPreview(options) {
      options = options || {};
      if (!roomFullscreenEl) return;
      var wasOpen = !roomFullscreenEl.hidden;
      roomFullscreenEl.hidden = true;
      roomFullscreenEl.setAttribute('aria-hidden', 'true');
      root.classList.remove('is-room-preview');
      releaseRoomPreviewScrollLock();
      var roomModeBtn = root.querySelector('.zellij-lab__btn-see-room');
      if (roomModeBtn) roomModeBtn.setAttribute('aria-pressed', 'false');
      if (
        wasOpen &&
        options.fromUi &&
        historyApi &&
        historyApi.hasLevel(OVERLAY_HISTORY_LEVEL_ROOM)
      ) {
        historyApi.popHistoryLevelFromUi();
      }
      var trigger = roomPreviewTriggerEl;
      roomPreviewTriggerEl = null;
      if (trigger && typeof trigger.focus === 'function') {
        trigger.focus();
      }
      if (wasOpen) {
        reviveLabTextures().then(function () {
          if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
            return;
          }
          requestAnimationFrame(function () {
            if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
              return;
            }
            reviveLabTextures();
          });
        });
      }
    }

    function handleRoomFullscreenBackdropClick(event) {
      if (!roomFullscreenEl || roomFullscreenEl.hidden) return;
      if (event.target === roomFullscreenEl) {
        closeMobileRoomPreview({ fromUi: true });
        return;
      }
      var stage = roomFullscreenEl.querySelector('.zellij-lab__room-fullscreen-stage');
      if (stage && event.target === stage) {
        closeMobileRoomPreview({ fromUi: true });
      }
    }

    function handleRoomFullscreenEscape(event) {
      if (!roomFullscreenEl || roomFullscreenEl.hidden) return;
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      event.stopPropagation();
      closeMobileRoomPreview({ fromUi: true });
    }

    function setColourFilter(filterId) {
      activeFilter = filterId || 'all';
      root.querySelectorAll('.zellij-lab__filter-pill').forEach(function (pill) {
        var id = pill.getAttribute('data-filter');
        var on = id === activeFilter;
        pill.classList.toggle('is-active', on);
      });
      renderPaletteColorsOnly();
      setActive(activeColor, activeColorName);
    }

    function readLocalDraft() {
      try {
        var raw = localStorage.getItem(DRAFT_STORAGE_KEY);
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
          localStorage.removeItem(DRAFT_STORAGE_KEY);
          return null;
        }
        var savedAt = Number(parsed.savedAt || parsed.updatedAt || 0);
        if (!savedAt || Date.now() - savedAt > DRAFT_TTL_MS) {
          localStorage.removeItem(DRAFT_STORAGE_KEY);
          return null;
        }
        return parsed;
      } catch (err) {
        try {
          localStorage.removeItem(DRAFT_STORAGE_KEY);
        } catch (e2) {
          /* ignore */
        }
        return null;
      }
    }

    function persistLocalDraft() {
      if (draftDismissedAfterOrder) {
        clearLocalDraftIfCurrentProduct();
        syncReturnReminder();
        return;
      }
      if (!hasCustomColors()) {
        clearLocalDraftIfCurrentProduct();
        return;
      }
      var payload = {
        productHandle: productHandle,
        patternName: patternName,
        patternType: patternType,
        colorMap: Object.assign({}, colorMap),
        fills: null,
        savedAt: Date.now(),
        updatedAt: Date.now()
      };
      var tileSvg = tileCanvas && tileCanvas.querySelector('svg');
      if (tileSvg && !hasGroupIds) {
        var fills = {};
        getShapes(tileSvg).forEach(function (s, i) {
          var hex = getShapeHex(s);
          if (hex && !isWhiteFill(hex)) fills[i] = hex;
        });
        if (Object.keys(fills).length) payload.fills = fills;
      }
      var serialized;
      try {
        serialized = JSON.stringify(payload);
      } catch (err) {
        return;
      }
      function writeOnce() {
        localStorage.setItem(DRAFT_STORAGE_KEY, serialized);
      }
      try {
        writeOnce();
      } catch (err) {
        /* QuotaExceeded or private mode: clear our draft and retry once. */
        try {
          localStorage.removeItem(DRAFT_STORAGE_KEY);
          writeOnce();
        } catch (err2) {
          /* ignore — never break the lab for storage failure */
        }
      }
    }

    function clearLocalDraftIfCurrentProduct() {
      var draft = readLocalDraft();
      if (!draft) return;
      if (draft.productHandle && draft.productHandle !== productHandle) return;
      try {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      } catch (err) {
        /* ignore */
      }
    }

    function syncReturnReminder() {
      if (!reminderBtn) return;
      var draft = readLocalDraft();
      var show =
        !!draft &&
        draft.productHandle === productHandle &&
        draft.patternName === patternName &&
        !!draft.colorMap &&
        Object.keys(draft.colorMap).length > 0;
      reminderBtn.hidden = !show;
    }

    function restoreLocalDraftIfMatching() {
      var draft = readLocalDraft();
      if (!draft || !draft.colorMap) return null;
      if (draft.productHandle !== productHandle) return null;
      if (draft.patternName !== patternName) return null;
      return draft;
    }

    function uploadDesignThumbnail(dataUrl) {
      if (!dataUrl) return Promise.resolve(null);

      return fetch(API_BASE + '/upload-design-thumbnail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageDataUrl: dataUrl,
          productHandle: productHandle,
          customerId: CUSTOMER_ID
        })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) { return data.thumbnailUrl || null; })
        .catch(function () { return null; });
    }

    function ensureThumbnailUrl() {
      var key = designFingerprint();
      if (cachedThumbnailUrl && cachedThumbnailKey === key) {
        return Promise.resolve(cachedThumbnailUrl);
      }

      function uploadCached(dataUrl) {
        if (!dataUrl) return Promise.resolve(null);
        return uploadDesignThumbnail(dataUrl).then(function (url) {
          if (url) {
            cachedThumbnailUrl = url;
            cachedThumbnailKey = key;
          }
          return url;
        });
      }

      if (cachedThumbnailDataUrl && cachedThumbnailDataKey === key) {
        return uploadCached(cachedThumbnailDataUrl);
      }

      // No custom paint: order/save use the static neutral fallback (null URL).
      if (!hasCustomColors()) {
        return Promise.resolve(null);
      }

      // Overlay is normally open when ordering a painted design; live rasterize.
      return buildDesignThumbnail().then(uploadCached);
    }

    function detectSampleSvgGroups(svgEl) {
      var bases = [];
      var seen = {};
      if (isHandPaintedLab) {
        getHandPaintedFamilyElements(svgEl).forEach(function (el) {
          var base = getHandPaintedFamilyBase(el);
          if (!seen[base] && getHandPaintedFamilyShapes(el).length > 0) {
            seen[base] = true;
            bases.push(base);
          }
        });
        return { bases: bases, hasGroups: bases.length > 0 };
      }
      getAllGroupsWithId(svgEl).forEach(function (g) {
        var base = getBaseId(g.getAttribute('id'));
        if (!seen[base] && getShapes(g).length > 0) {
          seen[base] = true;
          bases.push(base);
        }
      });
      var hasGroups =
        bases.length > 0 &&
        getAllGroupsWithId(svgEl).some(function (g) {
          return getShapes(g).length >= 2;
        });
      return { bases: bases, hasGroups: hasGroups };
    }

    /**
     * Picker when sample is a single-zone (0–1 groups) but the tile has more zones.
     * Matched and partial-match samples (CRAVAT/TASMIM) skip the picker.
     */
    function needsSampleColorPicker(sampleBases) {
      var sampleCount = sampleBases ? sampleBases.length : 0;
      var tileCount = detectedBases.length;
      return sampleCount < tileCount && sampleCount <= 1;
    }

    function probeSampleSvgMeta() {
      if (sampleSvgMetaPromise) return sampleSvgMetaPromise;

      var url = getPatternSvgUrl('_SAMPLE');
      if (!url) {
        sampleSvgMetaPromise = resolveBorderSampleFallback({ bases: [], text: null });
        return sampleSvgMetaPromise;
      }

      sampleSvgMetaPromise = fetch(url)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status + ' for ' + url);
          return r.text();
        })
        .then(function (text) {
          if (
            !text ||
            (!text.trimStart().startsWith('<?xml') &&
              !text.trimStart().startsWith('<svg'))
          ) {
            throw new Error('Invalid _SAMPLE.svg response');
          }
          var doc = new DOMParser().parseFromString(text, 'image/svg+xml');
          var svgEl = doc.querySelector('svg');
          var detection = svgEl
            ? detectSampleSvgGroups(svgEl)
            : { bases: [], hasGroups: false };
          return {
            bases: detection.bases.slice(),
            text: text,
            source: url
          };
        })
        .catch(function (err) {
          console.warn('[sample-thumbnail] probe failed:', err);
          return resolveBorderSampleFallback({ bases: [], text: null });
        })
        .then(function (meta) {
          if (!meta || !meta.text) {
            sampleSvgMetaPromise = null;
          }
          return meta || { bases: [], text: null };
        });

      return sampleSvgMetaPromise;
    }

    function resolveBorderSampleFallback(fallback) {
      if (patternType !== 'border' || !carpetSvgText) {
        return Promise.resolve(fallback);
      }
      try {
        var doc = new DOMParser().parseFromString(carpetSvgText, 'image/svg+xml');
        var svgEl = doc.querySelector('svg');
        var detection = svgEl
          ? detectSampleSvgGroups(svgEl)
          : { bases: [], hasGroups: false };
        return {
          bases: detection.bases.slice(),
          text: carpetSvgText,
          source: 'carpet-repeat-unit'
        };
      } catch (e) {
        return fallback;
      }
    }

    function measureSvgPaintedAspect(svgEl) {
      var bbox = measureSvgPaintedBBox(svgEl);
      if (bbox && bbox.height > 0) return bbox.width / bbox.height;
      return viewBoxWidthOverHeight(svgEl);
    }

    function syncPriceCardSampleHostLayout(svgEl, host) {
      if (!host || !svgEl) return;
      var ratio = measureSvgPaintedAspect(svgEl);
      if (!(ratio > 0)) ratio = 1;

      if (patternType === 'border') {
        host.style.setProperty('--zellij-price-sample-aspect', String(ratio));
        host.style.width = '';
        host.style.maxWidth = '';
        host.style.removeProperty('aspect-ratio');
        host.style.maxHeight = '';
        host.dataset.sampleAspect = ratio.toFixed(3);
      } else {
        host.style.removeProperty('--zellij-price-sample-aspect');
        host.style.width = 'min(100%, 6.75rem)';
        host.style.maxWidth = '';
        host.style.aspectRatio = '1';
        host.style.maxHeight = '';
        delete host.dataset.sampleAspect;
      }
    }

    function applySingleFillToSvg(svgEl, hex, options) {
      options = options || {};
      if (!svgEl || !hex) return;
      getShapes(svgEl).forEach(function (s, idx) {
        if (options.flat || !enableTileTexture) {
          removeFlatUnderlay(s);
          setFill(s, hex);
          syncTileGroutStroke(s);
        } else {
          applyInteractiveFill(
            s,
            hex,
            getShapeStableId(s, idx),
            svgEl
          );
        }
      });
    }

    function hideSamplePreviewUi() {
      samplePreviewReady = false;
      if (samplePreviewEl) {
        samplePreviewEl.hidden = true;
        samplePreviewEl.setAttribute('aria-hidden', 'true');
      }
      if (sampleMainEl) {
        sampleMainEl.hidden = true;
        sampleMainEl.setAttribute('aria-hidden', 'true');
      }
      if (carpetViewToggle) carpetViewToggle.hidden = true;
      showSampleBtns.forEach(function (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-pressed', 'false');
        btn.textContent = zCopy('show_sample');
      });
      if (carpetStage) carpetStage.classList.remove('zellij-lab__carpet-stage--sample-view');
      carpetViewMode = 'carpet';
      if (carpetLabelEl) carpetLabelEl.textContent = zCopy('carpet_view');
      if (carpetCanvas) carpetCanvas.style.opacity = '';
      syncSampleModeChrome();
    }

    function colorSampleSvgElement(svgEl) {
      if (!svgEl) return;
      svgEl.style.pointerEvents = 'none';
      svgEl.querySelectorAll('*').forEach(function (el) {
        el.style.pointerEvents = 'none';
      });
      applyWhite(svgEl);
      syncColorMapFromTile();

      /*
        Sample motif (price card + preview chip) uses the same url(#tex-)
        pool as the tile sheet. Flat hex here was a silent regression.
      */
      if (samplePreviewBases.length > 0) {
        applyColorMapToSvg(svgEl, colorMap);
        return;
      }

      // Ungrouped sample: fill with first used design color if any
      var used = getOrderedColorEntries();
      if (used.length && used[0].hex) {
        applySingleFillToSvg(svgEl, used[0].hex);
      }
    }

    function paintSampleIntoHost(host) {
      if (!host || !sampleSvgText) return null;
      var svgEl = injectSvg(host, sampleSvgText);
      if (!svgEl) return null;
      if (patternType === 'border') {
        cropSvgViewBoxToPaintedContent(svgEl, 0.04);
      }
      colorSampleSvgElement(svgEl);
      if (host === sampleCanvasEl) {
        svgEl.style.display = 'block';
        svgEl.style.width = 'auto';
        svgEl.style.height = 'auto';
        svgEl.style.maxWidth = '100%';
        svgEl.style.maxHeight = '100%';
        svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      }
      if (host === priceCardSampleHost) {
        syncPriceCardSampleHostLayout(svgEl, host);
      }
      return svgEl;
    }

    function clearMobileSampleFitStyles() {
      if (!sampleCanvasEl) return;
      sampleCanvasEl.style.flex = '';
      sampleCanvasEl.style.height = '';
      sampleCanvasEl.style.maxHeight = '';
      sampleCanvasEl.style.marginTop = '';
      sampleCanvasEl.style.width = '';
      var svgEl = sampleCanvasEl.querySelector('svg');
      if (svgEl) {
        svgEl.style.width = '';
        svgEl.style.height = '';
        svgEl.style.maxWidth = '';
        svgEl.style.maxHeight = '';
      }
    }

    /*
      Sample layer visible slot is meta.bottom → sheet.top.
      Do NOT use sampleCanvas.clientHeight: the sheet is position:absolute and
      overlays the flex host, so clientHeight includes the covered region and
      the SVG is sized too tall (octagon mid-screen, diamond under the sheet).
    */
    function syncMobileSampleFit() {
      var mq =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 1023px)').matches;
      if (!mq || carpetViewMode !== 'sample' || !sampleCanvasEl || sampleCanvasEl.hidden) {
        clearMobileSampleFitStyles();
        return;
      }
      var svgEl = sampleCanvasEl.querySelector('svg');
      var metaEl = root.querySelector('.zellij-lab__sample-meta');
      var sheetEl = paletteColEl;
      if (!svgEl || !metaEl || metaEl.hidden || !sheetEl) return;

      var pad = 16;
      var metaBottom = metaEl.getBoundingClientRect().bottom;
      var sheetTop = sheetEl.getBoundingClientRect().top;
      var colRect = (canvasColEl || sampleCanvasEl).getBoundingClientRect();
      var availH = Math.floor(sheetTop - metaBottom - pad * 2);
      var availW = Math.floor(colRect.width - pad * 2);
      if (availH < 40) availH = 40;
      if (availW < 40) availW = 40;

      sampleCanvasEl.style.flex = '0 0 auto';
      sampleCanvasEl.style.marginTop = pad + 'px';
      sampleCanvasEl.style.height = availH + 'px';
      sampleCanvasEl.style.maxHeight = availH + 'px';
      sampleCanvasEl.style.width = '100%';
      sampleCanvasEl.style.display = 'flex';
      sampleCanvasEl.style.alignItems = 'center';
      sampleCanvasEl.style.justifyContent = 'center';
      sampleCanvasEl.style.overflow = 'hidden';

      var vb = svgEl.viewBox && svgEl.viewBox.baseVal;
      var aspect = 1;
      if (vb && vb.width > 0 && vb.height > 0) {
        aspect = vb.width / vb.height;
      } else {
        try {
          var bbox = svgEl.getBBox();
          if (bbox && bbox.width > 0 && bbox.height > 0) {
            aspect = bbox.width / bbox.height;
          }
        } catch (err) {
          /* getBBox can throw if not rendered */
        }
      }
      var fitW = availW;
      var fitH = Math.floor(fitW / aspect);
      if (fitH > availH) {
        fitH = availH;
        fitW = Math.floor(fitH * aspect);
      }
      if (fitW < 1) fitW = 1;
      if (fitH < 1) fitH = 1;
      svgEl.style.width = fitW + 'px';
      svgEl.style.height = fitH + 'px';
      svgEl.style.maxWidth = availW + 'px';
      svgEl.style.maxHeight = availH + 'px';
      svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    }

    function refreshSamplePreview() {
      if (!samplePreviewReady || !sampleSvgText) return;
      if (samplePreviewSvgHost && patternType !== 'border') {
        paintSampleIntoHost(samplePreviewSvgHost);
      }
      if (priceCardSampleHost) {
        paintSampleIntoHost(priceCardSampleHost);
      }
      if (carpetViewMode === 'sample' && sampleMainEl) {
        paintSampleIntoHost(sampleMainEl);
      }
      if (
        carpetViewMode === 'sample' &&
        sampleCanvasEl &&
        !sampleCanvasEl.hidden
      ) {
        paintSampleIntoHost(sampleCanvasEl);
      }
    }

    function syncSampleModeChrome() {
      var isSample = carpetViewMode === 'sample';
      if (canvasColEl) {
        canvasColEl.classList.toggle('is-sample-view', isSample);
      }
      var sampleMetaEl = root.querySelector('.zellij-lab__sample-meta');
      if (sampleMetaEl) {
        sampleMetaEl.hidden = !(isSample && samplePreviewReady);
      }
      if (sampleCanvasEl) {
        if (isSample && samplePreviewReady) {
          sampleCanvasEl.hidden = false;
          sampleCanvasEl.setAttribute('aria-hidden', 'false');
          paintSampleIntoHost(sampleCanvasEl);
        } else {
          clearMobileSampleFitStyles();
          sampleCanvasEl.hidden = true;
          sampleCanvasEl.setAttribute('aria-hidden', 'true');
          sampleCanvasEl.innerHTML = '';
        }
      }
      showSampleBtns.forEach(function (btn) {
        btn.textContent = isSample ? zCopy('show_design_canvas') : zCopy('show_sample');
        btn.dataset.view = isSample ? 'sample' : 'carpet';
        btn.setAttribute('aria-pressed', isSample ? 'true' : 'false');
      });
      fillPricingUi();
      syncMobileEditorCanvasFit();
      requestAnimationFrame(function () {
        syncMobileSampleFit();
        requestAnimationFrame(syncMobileSampleFit);
      });
    }

    function setCarpetViewMode(mode) {
      if (!samplePreviewReady) return;
      carpetViewMode = mode === 'sample' ? 'sample' : 'carpet';

      if (carpetStage) {
        carpetStage.classList.toggle(
          'zellij-lab__carpet-stage--sample-view',
          carpetViewMode === 'sample'
        );
      }

      if (carpetViewMode === 'sample') {
        if (sampleMainEl) {
          sampleMainEl.hidden = false;
          sampleMainEl.setAttribute('aria-hidden', 'false');
          paintSampleIntoHost(sampleMainEl);
        }
        if (samplePreviewEl) {
          samplePreviewEl.hidden = true;
          samplePreviewEl.setAttribute('aria-hidden', 'true');
        }
        if (carpetLabelEl) carpetLabelEl.textContent = zCopy('sample_view');
        if (carpetViewToggle) {
          carpetViewToggle.textContent = zCopy('show_carpet_view');
          carpetViewToggle.dataset.view = 'sample';
          carpetViewToggle.setAttribute('aria-pressed', 'true');
        }
      } else {
        if (sampleMainEl) {
          sampleMainEl.hidden = true;
          sampleMainEl.setAttribute('aria-hidden', 'true');
        }
        if (samplePreviewEl) {
          if (patternType === 'border') {
            samplePreviewEl.hidden = true;
            samplePreviewEl.setAttribute('aria-hidden', 'true');
          } else {
            samplePreviewEl.hidden = false;
            samplePreviewEl.setAttribute('aria-hidden', 'false');
          }
        }
        if (carpetLabelEl) carpetLabelEl.textContent = zCopy('carpet_view');
        if (carpetViewToggle) {
          carpetViewToggle.textContent = zCopy('show_sample');
          carpetViewToggle.dataset.view = 'carpet';
          carpetViewToggle.setAttribute('aria-pressed', 'false');
        }
        refreshSamplePreview();
      }
      syncSampleModeChrome();
    }

    function initSamplePreview() {
      return probeSampleSvgMeta().then(function (meta) {
        if (!meta || !meta.text) {
          hideSamplePreviewUi();
          return false;
        }

        sampleSvgText = meta.text;
        sampleSvgSource = meta.source || null;
        samplePreviewBases = meta.bases ? meta.bases.slice() : [];
        samplePreviewReady = true;

        if (samplePreviewEl && samplePreviewSvgHost && patternType !== 'border') {
          paintSampleIntoHost(samplePreviewSvgHost);
          samplePreviewEl.hidden = false;
          samplePreviewEl.setAttribute('aria-hidden', 'false');
        } else if (samplePreviewEl) {
          samplePreviewEl.hidden = true;
          samplePreviewEl.setAttribute('aria-hidden', 'true');
          if (samplePreviewSvgHost) samplePreviewSvgHost.innerHTML = '';
        }
        if (priceCardSampleHost) {
          paintSampleIntoHost(priceCardSampleHost);
          if (sampleSvgSource) {
            priceCardSampleHost.dataset.sampleSource = sampleSvgSource;
          }
        }

        if (carpetViewToggle) {
          if (patternType === 'border') {
            carpetViewToggle.hidden = true;
            carpetViewToggle.onclick = null;
          } else {
            carpetViewToggle.hidden = false;
            carpetViewToggle.onclick = function () {
              if (instanceAborted) return;
              setCarpetViewMode(carpetViewMode === 'carpet' ? 'sample' : 'carpet');
            };
          }
        }

        /*
          Borders and mosaics share SHOW SAMPLE. Always rebind onclick so a
          prior mosaic instance cannot leave CHATEAU_SAMPLE on the buttons.
          Mobile sample layer uses the same sampleSvgText + colorSampleSvgElement
          path as the desktop price/order card (_SAMPLE.svg via probeSampleSvgMeta).
        */
        showSampleBtns.forEach(function (btn) {
          btn.hidden = false;
          btn.disabled = false;
          btn.onclick = function () {
            if (instanceAborted) return;
            if (root.classList.contains('is-room-preview')) {
              closeMobileRoomPreview({ fromUi: true });
            }
            setCarpetViewMode(carpetViewMode === 'carpet' ? 'sample' : 'carpet');
          };
        });

        setCarpetViewMode('carpet');
        return true;
      });
    }

    function buildSampleThumbnailFromSampleSvg(overrideHex) {
      return probeSampleSvgMeta().then(function (meta) {
        if (!meta.text) return null;

        var holder = document.createElement('div');
        holder.setAttribute('aria-hidden', 'true');
        holder.style.cssText =
          'position:fixed;left:-9999px;width:0;height:0;overflow:hidden;pointer-events:none';
        document.body.appendChild(holder);

        var svgEl = injectSvg(holder, meta.text);
        if (!svgEl) {
          document.body.removeChild(holder);
          return null;
        }

        syncColorMapFromTile();

        if (overrideHex) {
          applySingleFillToSvg(svgEl, overrideHex, { flat: true });
          console.log('[sample-thumbnail] single-color');
          return rasterizeSvgElement(flattenSvgForThumbnail(svgEl)).then(function (dataUrl) {
            document.body.removeChild(holder);
            return dataUrl;
          });
        }

        var detection = detectSampleSvgGroups(svgEl);
        // Accept any detected group base (including single-shape groups like CORA).
        if (!detection.bases.length) {
          document.body.removeChild(holder);
          console.log('[sample-thumbnail] fallback (no groups in _SAMPLE.svg)');
          return null;
        }

        applyColorMapToSvg(svgEl, colorMap, { flat: true });
        console.log('[sample-thumbnail] grouped');
        return rasterizeSvgElement(flattenSvgForThumbnail(svgEl)).then(function (dataUrl) {
          document.body.removeChild(holder);
          return dataUrl;
        });
      });
    }

    function ensureSampleThumbnailUrl(options) {
      options = options || {};
      var overrideHex = options.overrideHex || null;
      var key =
        designFingerprint() +
        (overrideHex ? '|sampleFill:' + String(overrideHex).toLowerCase() : '');
      if (cachedSampleThumbnailUrl && cachedSampleThumbnailKey === key) {
        return Promise.resolve(cachedSampleThumbnailUrl);
      }

      return buildSampleThumbnailFromSampleSvg(overrideHex)
        .then(function (dataUrl) {
          if (dataUrl) return dataUrl;
          if (
            cachedThumbnailDataUrl &&
            cachedThumbnailDataKey === designFingerprint()
          ) {
            return cachedThumbnailDataUrl;
          }
          return buildDesignThumbnailFromCanvas();
        })
        .then(function (dataUrl) {
          if (!dataUrl) {
            throw new Error('Sample thumbnail rasterization failed');
          }
          return uploadDesignThumbnail(dataUrl);
        })
        .then(function (url) {
          if (!url) {
            throw new Error('Sample thumbnail upload failed');
          }
          cachedSampleThumbnailUrl = url;
          cachedSampleThumbnailKey = key;
          return url;
        });
    }

    function getUsedSampleColorOptions() {
      var seen = {};
      var options = [];
      getOrderedColorEntries().forEach(function (entry) {
        var key = String(entry.hex || '').toLowerCase();
        if (!key || seen[key]) return;
        seen[key] = true;
        var found = palette.find(function (c) {
          return c.hex.toLowerCase() === key;
        });
        options.push({
          hex: entry.hex,
          name: entry.name,
          swatchImage: found ? found.swatchImage : null
        });
      });
      return options;
    }

    function closeSampleColorPicker() {
      if (sampleColorPickerEl) {
        sampleColorPickerEl.hidden = true;
        sampleColorPickerEl.setAttribute('aria-hidden', 'true');
      }
      if (sampleColorPickerOutsideHandler) {
        document.removeEventListener('mousedown', sampleColorPickerOutsideHandler, true);
        document.removeEventListener('keydown', sampleColorPickerOutsideHandler, true);
        sampleColorPickerOutsideHandler = null;
      }
    }

    function sampleOrderConfirmLabel(count) {
      if (count === 1) return zCopy('order_1_sample');
      return zCopyFill('order_n_samples', { count: count });
    }

    function ensureSampleColorPickerEl() {
      if (sampleColorPickerEl) return sampleColorPickerEl;
      var btn = root.querySelector('.zellij-lab__btn-add-sample');
      if (!btn || !btn.parentNode) return null;

      var panel = document.createElement('div');
      panel.className = 'zellij-lab__sample-color-picker';
      panel.hidden = true;
      panel.setAttribute('role', 'dialog');
      panel.setAttribute(
        'aria-label',
        zCopy('choose_sample_colors_aria')
      );
      panel.setAttribute('aria-hidden', 'true');
      panel.innerHTML =
        '<p class="zellij-lab__sample-color-picker-label">' +
        zCopy('choose_sample_colors_label') +
        '</p>' +
        '<div class="zellij-lab__sample-color-picker-swatches"></div>' +
        '<button type="button" class="zellij-lab__action-btn zellij-lab__action-btn--primary zellij-lab__sample-color-picker-confirm" disabled>' +
        zCopy('order_1_sample') +
        '</button>' +
        '<button type="button" class="zellij-lab__action-btn zellij-lab__sample-color-picker-cancel">' +
        zCopy('cancel') +
        '</button>';
      btn.parentNode.insertBefore(panel, btn.nextSibling);
      sampleColorPickerEl = panel;
      return panel;
    }

    function openSampleColorPicker(colorOptions, onConfirm, onCancel) {
      var panel = ensureSampleColorPickerEl();
      if (!panel) {
        onCancel();
        return;
      }

      closeSampleColorPicker();

      var swatchHost = panel.querySelector('.zellij-lab__sample-color-picker-swatches');
      var confirmBtn = panel.querySelector('.zellij-lab__sample-color-picker-confirm');
      var cancelBtn = panel.querySelector('.zellij-lab__sample-color-picker-cancel');
      var selectedByHex = {};
      swatchHost.innerHTML = '';

      function getSelectedColors() {
        return colorOptions.filter(function (opt) {
          return selectedByHex[String(opt.hex).toLowerCase()];
        });
      }

      function syncConfirmState() {
        var selected = getSelectedColors();
        var count = selected.length;
        confirmBtn.disabled = count < 1;
        confirmBtn.textContent = sampleOrderConfirmLabel(Math.max(count, 1));
      }

      colorOptions.forEach(function (opt) {
        var hexKey = String(opt.hex).toLowerCase();
        var card = document.createElement('button');
        card.type = 'button';
        card.className = 'zellij-lab__sample-color-option';
        card.setAttribute('aria-pressed', 'false');
        card.setAttribute('aria-label', opt.name || opt.hex);
        card.innerHTML =
          '<span class="zellij-lab__sample-color-option-check" aria-hidden="true"></span>' +
          '<span class="zellij-lab__sample-color-option-swatch" style="' +
          swatchVisualStyle(opt, patternSvgBaseUrl, glazeNs) +
          (isLight(opt.hex) ? 'box-shadow:inset 0 0 0 1px #ddd;' : '') +
          '"></span>' +
          '<span class="zellij-lab__sample-color-option-name">' +
          (opt.name || opt.hex) +
          '</span>';
        card.addEventListener('click', function () {
          if (selectedByHex[hexKey]) {
            delete selectedByHex[hexKey];
            card.classList.remove('zellij-lab__sample-color-option--selected');
            card.setAttribute('aria-pressed', 'false');
          } else {
            selectedByHex[hexKey] = true;
            card.classList.add('zellij-lab__sample-color-option--selected');
            card.setAttribute('aria-pressed', 'true');
          }
          syncConfirmState();
        });
        swatchHost.appendChild(card);
      });

      confirmBtn.onclick = function () {
        var selected = getSelectedColors();
        if (!selected.length) return;
        closeSampleColorPicker();
        onConfirm(selected);
      };

      cancelBtn.onclick = function () {
        closeSampleColorPicker();
        onCancel();
      };

      syncConfirmState();
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');

      sampleColorPickerOutsideHandler = function (evt) {
        if (evt.type === 'keydown' && evt.key === 'Escape') {
          closeSampleColorPicker();
          onCancel();
          return;
        }
        if (evt.type === 'mousedown') {
          if (panel.contains(evt.target)) return;
          var sampleBtn = root.querySelector('.zellij-lab__btn-add-sample');
          if (sampleBtn && sampleBtn.contains(evt.target)) return;
          closeSampleColorPicker();
          onCancel();
        }
      };
      document.addEventListener('mousedown', sampleColorPickerOutsideHandler, true);
      document.addEventListener('keydown', sampleColorPickerOutsideHandler, true);
    }

    function applyDesignState(state) {
      if (state.colorMap && Object.keys(state.colorMap).length > 0) {
        colorMap = state.colorMap;
        roomLiveFillCache.key = null;
        var slugList = [];
        Object.keys(colorMap).forEach(function (base) {
          var slug = slugForHex(colorMap[base]);
          if (slug && slugList.indexOf(slug) === -1) slugList.push(slug);
        });

        function paintRestoredTextures() {
          if (instanceAborted || instanceGeneration !== labInstanceGeneration) return;
          var tileSvg = tileCanvas ? tileCanvas.querySelector('svg') : null;
          var carpetSvg = carpetCanvas ? carpetCanvas.querySelector('svg') : null;
          /*
            Restore through the same textured path as live paint: SVG textures
            on the tile sheet; mosaic carpet stays flat (room uses composite);
            border carpet is the lab view and must be textured. Never leave a
            flat-hex first frame when cells are already preloaded.
          */
          if (tileSvg) applyColorMapToSvg(tileSvg, colorMap);
          if (carpetSvg) {
            applyColorMapToSvg(
              carpetSvg,
              colorMap,
              patternType === 'border' ? undefined : { flat: true }
            );
          }
          renderUsed();
          updateUndoRedo();
          syncStickyBarCustomizingState();
          scheduleThumbnailCache();
          refreshSamplePreview();
          recolourMountedRoomPanels();
        }

        if (!slugList.length || !enableTileTexture) {
          paintRestoredTextures();
        } else {
          Promise.all(
            slugList.map(function (slug) {
              return preloadColorTextures(slug);
            })
          ).then(paintRestoredTextures);
        }
        return;
      }
      if (state.fills) {
        var tileSvgFills = tileCanvas.querySelector('svg');
        if (tileSvgFills) {
          getShapes(tileSvgFills).forEach(function (s, i) {
            if (state.fills[i]) {
              applyInteractiveFill(
                s,
                state.fills[i],
                getShapeStableId(s, i),
                tileSvgFills
              );
            }
          });
        }
      }
      renderUsed();
      updateUndoRedo();
      syncStickyBarCustomizingState();
      scheduleThumbnailCache();
      refreshSamplePreview();
      scheduleRoomPreviewRefresh();
    }

    function isWhiteFill(hex) {
      if (!hex) return true;
      var h = hex.toLowerCase();
      return h === '#ffffff' || h === '#fff' || h === 'white' || h === 'none';
    }

    function isColoringComplete() {
      if (hasGroupIds && detectedBases.length > 0) {
        return detectedBases.every(function (base) {
          return colorMap[base] && !isWhiteFill(colorMap[base]);
        });
      }

      var tileSvg = tileCanvas.querySelector('svg');
      if (!tileSvg) return false;
      var shapes = getShapes(tileSvg);
      if (!shapes.length) return false;
      return shapes.every(function (s) {
        var fill = getShapeHex(s);
        return !isWhiteFill(fill);
      });
    }

    function hasCustomColors() {
      var key;
      for (key in colorMap) {
        if (Object.prototype.hasOwnProperty.call(colorMap, key) && !isWhiteFill(colorMap[key])) {
          return true;
        }
      }

      var tileSvg = tileCanvas && tileCanvas.querySelector('svg');
      if (!tileSvg) return false;

      return getShapes(tileSvg).some(function (s) {
        var fill = getShapeHex(s);
        return !isWhiteFill(fill);
      });
    }

    function syncStickyBarCustomizingState() {
      document.documentElement.classList.toggle(
        'zellij-design-lab-customizing',
        hasCustomColors()
      );
    }

    function setSaveButtonsBusy(busy) {
      root.querySelectorAll('.zellij-lab__btn-save').forEach(function (saveBtn) {
        saveBtn.disabled = !!busy;
        var full = saveBtn.querySelector('.zellij-lab__chrome-save-full');
        var short = saveBtn.querySelector('.zellij-lab__chrome-save-short');
        if (full) full.textContent = busy ? zCopy('saving') : zCopy('save_design');
        if (short) short.textContent = busy ? '...' : zCopy('save');
        if (!full && !short) {
          saveBtn.textContent = busy ? zCopy('saving') : zCopy('save_design');
        }
      });
    }

    function saveDesign() {
      if (!requireLoginForAction('save')) return;
      var state = buildDesignState();
      setSaveButtonsBusy(true);

      ensureThumbnailUrl()
        .then(function (thumbnailUrl) {
          var payload = {
            customerId: CUSTOMER_ID,
            design: state
          };
          if (thumbnailUrl) payload.thumbnailUrl = thumbnailUrl;
          payload.productTitle = buildSavedDisplayTitle();

          return fetch(API_BASE + '/save-design', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          showToast(
            data.ok
              ? zCopy('design_saved')
              : zCopyFill('error_with_message', {
                  message: data.error || zCopy('error_unknown')
                })
          );
        })
        .catch(function () { showToast(zCopy('could_not_save_design')); })
        .finally(function () {
          setSaveButtonsBusy(false);
        });
    }

    function fetchSavedDesign() {
      function fetchByPattern(key) {
        return fetch(API_BASE + '/load-design?customerId=' + CUSTOMER_ID +
          '&pattern=' + encodeURIComponent(key))
          .then(function (r) { return r.json(); });
      }

      return fetchByPattern(patternName).then(function (data) {
        if (data.designs && data.designs.length) return data;
        if (patternNameLegacy && patternNameLegacy !== patternName) {
          return fetchByPattern(patternNameLegacy);
        }
        return data;
      });
    }

    function setButtonLabel(btn, text) {
      if (!btn) return;
      var sampleSpan = btn.querySelector('.zellij-lab__btn-add-sample-label');
      if (sampleSpan) {
        sampleSpan.textContent = text;
        return;
      }
      var funnelSampleLabel = btn.querySelector('[data-order-sample-label]');
      if (funnelSampleLabel) {
        funnelSampleLabel.textContent = text;
        return;
      }
      if (btn.hasAttribute('data-order-full-btn')) {
        var funnelFullLabel = btn.querySelector(
          'span:not(.zellij-funnel-order__cta-arrow)'
        );
        if (funnelFullLabel) {
          funnelFullLabel.textContent = text;
          return;
        }
      }
      var full = btn.querySelector('.zellij-lab__chrome-save-full');
      var short = btn.querySelector('.zellij-lab__chrome-save-short');
      if (full || short) {
        if (full) full.textContent = text;
        if (short) short.textContent = text;
        return;
      }
      btn.textContent = text;
    }

    function captureOrderButtonIdleLabel(btn) {
      if (!btn || btn.dataset.idleLabel) return;
      var sampleSpan = btn.querySelector('.zellij-lab__btn-add-sample-label');
      if (sampleSpan) {
        btn.dataset.idleLabel = sampleSpan.textContent.trim();
        return;
      }
      var funnelSampleLabel = btn.querySelector('[data-order-sample-label]');
      if (funnelSampleLabel) {
        btn.dataset.idleLabel = funnelSampleLabel.textContent.trim();
        return;
      }
      if (btn.hasAttribute('data-order-full-btn')) {
        var funnelFullLabel = btn.querySelector(
          'span:not(.zellij-funnel-order__cta-arrow)'
        );
        if (funnelFullLabel) {
          btn.dataset.idleLabel = funnelFullLabel.textContent.trim();
          return;
        }
      }
      btn.dataset.idleLabel = (btn.textContent || '').trim();
    }

    function getColorwayOrderButtons() {
      var buttons = [];
      root.querySelectorAll('.zellij-lab__btn-add-to-cart').forEach(function (btn) {
        buttons.push(btn);
      });
      document.querySelectorAll('[data-order-full-btn]').forEach(function (btn) {
        if (buttons.indexOf(btn) === -1) buttons.push(btn);
      });
      return buttons;
    }

    function getSampleOrderButtons() {
      var buttons = [];
      root.querySelectorAll('.zellij-lab__btn-add-sample').forEach(function (btn) {
        buttons.push(btn);
      });
      document.querySelectorAll('[data-order-sample-btn]').forEach(function (btn) {
        if (buttons.indexOf(btn) === -1) buttons.push(btn);
      });
      return buttons;
    }

    function setOrderButtonsBusy(buttons, busy, busyLabel) {
      (buttons || []).forEach(function (btn) {
        captureOrderButtonIdleLabel(btn);
        if (busy) {
          btn.dataset.orderWasDisabled = btn.disabled ? '1' : '0';
          btn.dataset.orderAddBusy = '1';
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
          if (busyLabel) setButtonLabel(btn, busyLabel);
        } else {
          btn.setAttribute('aria-busy', 'false');
          if (btn.dataset.idleLabel) setButtonLabel(btn, btn.dataset.idleLabel);
          btn.disabled = btn.dataset.orderWasDisabled === '1';
          delete btn.dataset.orderWasDisabled;
          delete btn.dataset.orderAddBusy;
        }
      });
    }

    function beginColorwayAdd() {
      if (colorwayAddInProgress || sampleAddInProgress) return false;
      colorwayAddInProgress = true;
      setOrderButtonsBusy(getColorwayOrderButtons(), true, zCopy('adding'));
      setOrderButtonsBusy(getSampleOrderButtons(), true, null);
      return true;
    }

    function endColorwayAdd() {
      colorwayAddInProgress = false;
      setOrderButtonsBusy(getColorwayOrderButtons(), false);
      setOrderButtonsBusy(getSampleOrderButtons(), false);
    }

    function beginSampleAdd() {
      if (colorwayAddInProgress || sampleAddInProgress) return false;
      sampleAddInProgress = true;
      setOrderButtonsBusy(getSampleOrderButtons(), true, zCopy('adding_sample'));
      setOrderButtonsBusy(getColorwayOrderButtons(), true, null);
      return true;
    }

    function endSampleAdd() {
      sampleAddInProgress = false;
      setOrderButtonsBusy(getSampleOrderButtons(), false);
      setOrderButtonsBusy(getColorwayOrderButtons(), false);
    }

    function bindBaseProductAtcGuard() {
      /*
        Guard removed by request: the product's native Add to cart always adds
        the plain product with no interception. The modal markup ships hidden and
        is never shown, so nothing intercepts the native submit.
      */
      if (atcGuardEl) atcGuardEl.hidden = true;
    }

    function getSheetSnap() {
      if (!paletteColEl) return 'peek';
      return paletteColEl.getAttribute('data-sheet') || 'peek';
    }

    function setUsedColorsOpen(open) {
      if (!usedColorsPanel) return;
      var on = !!open;
      usedColorsPanel.classList.toggle('is-open', on);
      if (on) {
        usedColorsPanel.hidden = false;
        usedColorsPanel.removeAttribute('hidden');
      } else {
        usedColorsPanel.hidden = true;
        usedColorsPanel.setAttribute('hidden', '');
      }
      if (usedLinkBtn) {
        usedLinkBtn.setAttribute('aria-expanded', on ? 'true' : 'false');
        usedLinkBtn.classList.toggle('is-open', on);
      }
    }

    function setSheetSnap(snap) {
      if (!paletteColEl) return;
      var next = SHEET_SNAPS.indexOf(snap) >= 0 ? snap : 'peek';
      paletteColEl.setAttribute('data-sheet', next);
      if (sheetHandleBtn) {
        sheetHandleBtn.setAttribute('aria-expanded', next === 'peek' ? 'false' : 'true');
      }
      setUsedColorsOpen(false);
      syncMobileEditorCanvasFit();
      requestAnimationFrame(syncMobileSampleFit);
    }

    function syncMobileEditorCanvasFit() {
      var mq =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 1023px)').matches;
      if (!mq) {
        root.style.removeProperty('--zellij-editor-canvas-max');
        root.style.removeProperty('--zellij-editor-sheet-h');
        return;
      }
      var vv = window.visualViewport;
      var vh = Math.floor((vv && vv.height) || window.innerHeight || 0);
      if (vh < 1) return;
      var chrome = root.querySelector('.zellij-lab__chrome');
      var sticky = root.querySelector('.zellij-lab__sticky-bar');
      var toolbar = root.querySelector('.zellij-lab__canvas-toolbar');
      var dimLine = root.querySelector('.zellij-lab__dim-line');
      var chromeH = chrome ? Math.ceil(chrome.getBoundingClientRect().height) : 0;
      var stickyH = 0;
      if (sticky) {
        var stickyCs = window.getComputedStyle(sticky);
        if (stickyCs.display !== 'none') {
          stickyH = Math.ceil(sticky.getBoundingClientRect().height);
        }
      }
      if (!stickyH) {
        stickyH = Math.round(
          parseFloat(
            (window.getComputedStyle(root).getPropertyValue('--zellij-sticky-bar-h') ||
              '62')
              .toString()
              .replace(/[^\d.]/g, '')
          ) || 62
        );
      }
      var sheetH = 0;
      if (paletteColEl) {
        var pcs = window.getComputedStyle(paletteColEl);
        if (pcs.display !== 'none') {
          sheetH = Math.ceil(paletteColEl.getBoundingClientRect().height);
        }
      }
      if (!sheetH) sheetH = Math.round(vh * 0.4);
      var toolbarH = toolbar ? Math.ceil(toolbar.getBoundingClientRect().height) : 0;
      var dimH = 0;
      if (dimLine) {
        var dcs = window.getComputedStyle(dimLine);
        if (dcs.display !== 'none') {
          dimH = Math.ceil(dimLine.getBoundingClientRect().height) + 8;
        } else {
          dimH = 22;
        }
      }
      var pad = 20;
      var avail = Math.floor(vh - chromeH - stickyH - sheetH - toolbarH - dimH - pad);
      if (avail < 140) avail = 140;
      root.style.setProperty('--zellij-editor-canvas-max', avail + 'px');
      /*
        Borders center in the free gap above the sheet. Pad the stage by the
        actual stage-bottom → sheet-top overlap (palette height alone under-
        shoots when sticky / preview chrome eats the bottom).
      */
      var overlapPad = sheetH;
      var stageEl = root.querySelector('.zellij-lab__canvas-stage');
      if (stageEl && paletteColEl && sheetH) {
        var stageBottom = stageEl.getBoundingClientRect().bottom;
        var sheetTop = paletteColEl.getBoundingClientRect().top;
        overlapPad = Math.max(0, Math.ceil(stageBottom - sheetTop));
      }
      root.style.setProperty('--zellij-editor-sheet-h', overlapPad + 'px');
    }

    function cycleSheetSnap() {
      var cur = getSheetSnap();
      var idx = SHEET_SNAPS.indexOf(cur);
      if (idx < 0) idx = 0;
      setSheetSnap(SHEET_SNAPS[(idx + 1) % SHEET_SNAPS.length]);
    }

    function bindMobileSheetHandle() {
      if (!sheetHandleBtn || !paletteColEl) return;
      setSheetSnap(getSheetSnap());

      var drag = null;
      var DRAG_THRESHOLD = 16;

      listen(sheetHandleBtn, 'click', function (event) {
        if (sheetHandleBtn.dataset.dragMoved === '1') {
          sheetHandleBtn.dataset.dragMoved = '0';
          event.preventDefault();
          return;
        }
        cycleSheetSnap();
      });

      function onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        drag = {
          startY: event.clientY,
          startSnap: getSheetSnap(),
          pointerId: event.pointerId
        };
        sheetHandleBtn.dataset.dragMoved = '0';
        try {
          sheetHandleBtn.setPointerCapture(event.pointerId);
        } catch (err) {
          /* ignore */
        }
      }

      function onPointerUp(event) {
        if (!drag || event.pointerId !== drag.pointerId) return;
        var dy = event.clientY - drag.startY;
        if (Math.abs(dy) >= DRAG_THRESHOLD) {
          sheetHandleBtn.dataset.dragMoved = '1';
          var idx = SHEET_SNAPS.indexOf(drag.startSnap);
          if (idx < 0) idx = 0;
          var steps = Math.abs(dy) > 90 ? 2 : 1;
          if (dy < 0) {
            setSheetSnap(
              SHEET_SNAPS[Math.min(SHEET_SNAPS.length - 1, idx + steps)]
            );
          } else {
            setSheetSnap(SHEET_SNAPS[Math.max(0, idx - steps)]);
          }
        }
        drag = null;
      }

      listen(sheetHandleBtn, 'pointerdown', onPointerDown);
      listen(sheetHandleBtn, 'pointerup', onPointerUp);
      listen(sheetHandleBtn, 'pointercancel', onPointerUp);
    }

    /*
      After a successful add the painted design stays on screen and the Lab stays
      open, so only the saved draft and the return reminder are settled here.
      Thumbnail caches are keyed by design fingerprint, so they stay valid.
    */
    function finalizeAfterSuccessfulOrder() {
      draftDismissedAfterOrder = true;
      clearLocalDraftIfCurrentProduct();
      syncReturnReminder();
      try {
        updateUndoRedo();
        renderUsed();
        syncStickyBarCustomizingState();
        refreshSamplePreview();
        scheduleRoomPreviewRefresh();
      } catch (err) {
        /* ignore UI refresh errors after cart success */
      }
    }

    function nativeAddedToCartText(fallback) {
      try {
        var strings = window.themeVariables && window.themeVariables.strings;
        if (strings && strings.addedToCart) return strings.addedToCart;
      } catch (err) {
        /* theme strings are optional */
      }
      return fallback || zCopy('added_to_cart');
    }

    function isNodeVisible(el) {
      if (!el) return false;
      if (el.closest('[hidden]')) return false;
      var rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }

    function viewportVisibleArea(el) {
      if (!isNodeVisible(el)) return 0;
      var rect = el.getBoundingClientRect();
      var top = Math.max(0, rect.top);
      var left = Math.max(0, rect.left);
      var bottom = Math.min(window.innerHeight, rect.bottom);
      var right = Math.min(window.innerWidth, rect.right);
      var w = right - left;
      var h = bottom - top;
      if (w <= 0 || h <= 0) return 0;
      return w * h;
    }

    function sameActionOrderButtons(btn) {
      if (!btn) return [];
      if (
        btn.classList.contains('zellij-lab__btn-add-to-cart') ||
        btn.hasAttribute('data-order-full-btn')
      ) {
        return getColorwayOrderButtons();
      }
      if (
        btn.classList.contains('zellij-lab__btn-add-sample') ||
        btn.hasAttribute('data-order-sample-btn')
      ) {
        return getSampleOrderButtons();
      }
      return [btn];
    }

    /*
      Prefer the clicked trigger when it is on-screen; otherwise the most
      viewport-visible same-action twin (sticky vs footer).
    */
    function cartBannerAnchor(btn) {
      var preferred = btn || lastOrderTrigger;
      if (preferred && viewportVisibleArea(preferred) > 0) return preferred;

      var twins = sameActionOrderButtons(preferred);
      var best = null;
      var bestArea = 0;
      twins.forEach(function (candidate) {
        var area = viewportVisibleArea(candidate);
        if (area > bestArea) {
          best = candidate;
          bestArea = area;
        }
      });
      if (best) return best;

      var fallbacks = [
        preferred,
        lastOrderTrigger,
        root.querySelector('.zellij-lab__btn-add-to-cart'),
        root.querySelector('.zellij-lab__btn-add-sample'),
        document.querySelector('[data-order-full-btn]'),
        document.querySelector('[data-order-sample-btn]')
      ];
      for (var i = 0; i < fallbacks.length; i++) {
        if (isNodeVisible(fallbacks[i])) return fallbacks[i];
      }
      return null;
    }

    /*
      Prestige buy-buttons prepends .banner.banner--success above ATC (message
      cart type, 2.5s). Drawer cart type only announces. Lab reuses the same
      markup/classes and pins the banner above the clicked CTA with its right
      edge flush to the button's right edge.
    */
    function showCartSuccessBanner(message, btn) {
      document.querySelectorAll('.zellij-lab__cart-banner').forEach(function (node) {
        node.remove();
      });

      var banner = document.createElement('div');
      banner.className = 'banner banner--success zellij-lab__cart-banner';
      banner.setAttribute('role', 'status');
      banner.setAttribute('aria-live', 'polite');
      banner.textContent = message;
      document.body.appendChild(banner);

      var anchor = cartBannerAnchor(btn);
      if (anchor) {
        var rect = anchor.getBoundingClientRect();
        var gap = 8;
        var edgePad = 12;
        banner.style.width = 'max-content';
        banner.style.maxWidth = Math.min(320, window.innerWidth - edgePad * 2) + 'px';
        banner.style.transform = 'none';
        /* Right edges of banner and button must coincide. */
        banner.style.left = Math.round(rect.right - banner.offsetWidth) + 'px';
        var top = rect.top - banner.offsetHeight - gap;
        if (top < edgePad) top = rect.bottom + gap;
        banner.style.top = Math.round(top) + 'px';

        var bannerRect = banner.getBoundingClientRect();
        if (bannerRect.left < edgePad) {
          banner.style.left = edgePad + 'px';
        } else if (bannerRect.right > window.innerWidth - edgePad) {
          banner.style.left =
            Math.round(window.innerWidth - edgePad - bannerRect.width) + 'px';
        }
      } else {
        banner.classList.add('zellij-lab__cart-banner--floating');
      }

      setTimeout(function () {
        if (banner.parentNode) banner.remove();
      }, 2500);
    }

    function finishAddToCart(data, btn, successMessage) {
      var added = data && (data.id || (data.items && data.items.length));
      if (added) {
        showCartSuccessBanner(
          successMessage || nativeAddedToCartText(zCopy('added_to_cart')),
          btn
        );
        /* Keep the paint and the open Lab; only settle the saved draft. */
        finalizeAfterSuccessfulOrder();

        var afterFeeSync = function () {
          document.dispatchEvent(new CustomEvent('cart:refresh'));

          fetch('/?sections=cart-drawer')
            .then(function (r) { return r.json(); })
            .then(function (sections) {
              var drawerEl = document.querySelector('#cart-drawer');
              if (drawerEl && sections['cart-drawer']) {
                var tmp = document.createElement('div');
                tmp.innerHTML = sections['cart-drawer'];
                var newContent = tmp.querySelector('#cart-drawer');
                if (newContent) drawerEl.innerHTML = newContent.innerHTML;
              }
            });

          document.dispatchEvent(new CustomEvent('cart:open'));
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        };

        if (window.ZellijCartFeeSync && typeof window.ZellijCartFeeSync.syncFromServer === 'function') {
          window.ZellijCartFeeSync.syncFromServer().then(afterFeeSync);
        } else {
          afterFeeSync();
        }
      } else {
        showToast(data.description || zCopy('could_not_add_to_cart'));
      }
    }

    function getOrderBoxQuantity() {
      var qtyInput =
        document.querySelector('form[action*="/cart/add"] input[name="quantity"]') ||
        document.querySelector('.product-form input[name="quantity"]') ||
        document.querySelector('input[name="quantity"]');
      if (qtyInput) {
        var parsed = parseInt(qtyInput.value, 10);
        if (!isNaN(parsed) && parsed > 0) {
          return parsed;
        }
      }
      return customColorwayBoxQuantity;
    }

    function buildColorwayCartItems(props, hasSetupInCart) {
      var boxQty = getOrderBoxQuantity();
      var items = [{
        id: parseInt(customColorwayVariantId, 10),
        quantity: boxQty,
        properties: props
      }];

      if (artisanPerBoxVariantId) {
        items.push({
          id: parseInt(artisanPerBoxVariantId, 10),
          quantity: boxQty,
          properties: {
            _fee_type: 'per-box'
          }
        });
      }

      if (artisanSetupVariantId && !hasSetupInCart) {
        items.push({
          id: parseInt(artisanSetupVariantId, 10),
          quantity: 1,
          properties: {
            _fee_type: 'setup'
          }
        });
      }

      return items;
    }

    function cartHasSetupFee(cart) {
      if (!cart || !Array.isArray(cart.items)) return false;
      return cart.items.some(function (item) {
        if (!item || !item.properties) return false;
        if (item.properties._fee_type === 'setup') return true;
        var title = (item.product_title || item.title || '').toLowerCase();
        return title.indexOf('artisan commission') !== -1 &&
          title.indexOf('setup') !== -1;
      });
    }

    function postColorwayCartAdd(props) {
      return fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var hasSetup = cartHasSetupFee(cart);
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: buildColorwayCartItems(props, hasSetup) })
          }).then(function (r) { return r.json(); });
        });
    }

    function addToCart() {
      var variantId = customColorwayVariantId;
      if (!variantId) {
        showToast(
          zCopy('custom_colorway_ordering_unavailable')
        );
        return;
      }
      if (!isColoringComplete()) {
        showCenteredNotice(incompleteColoringMsg());
        return;
      }
      if (!requireLoginForAction('order')) return;
      if (!beginColorwayAdd()) return;

      ensureThumbnailUrl()
        .then(function (thumbnailUrl) {
          var props = buildColorProperties();
          applyCustomSkuToProps(props, false);
          if (thumbnailUrl) {
            props['_design_thumbnail_url'] = thumbnailUrl;
          }
          return postColorwayCartAdd(props);
        })
        .catch(function () {
          var props = buildColorProperties();
          applyCustomSkuToProps(props, false);
          return postColorwayCartAdd(props);
        })
        .then(function (data) {
          finishAddToCart(data, lastOrderTrigger);
          workshopTrack('onOrder');
        })
        .catch(function () { showToast(zCopy('could_not_add_to_cart')); })
        .finally(function () {
          endColorwayAdd();
        });
    }

    function addSampleToCart() {
      var variantId = customSampleVariantId;
      if (!variantId) {
        showToast(
          zCopy('custom_colorway_sample_ordering_unavailable')
        );
        return;
      }
      if (!isColoringComplete()) {
        showCenteredNotice(incompleteColoringMsg());
        return;
      }
      if (!requireLoginForAction('sample')) return;

      closeSampleColorPicker();

      probeSampleSvgMeta().then(function (meta) {
        var usedColors = getUsedSampleColorOptions();

        if (needsSampleColorPicker(meta.bases) && usedColors.length > 1) {
          openSampleColorPicker(
            usedColors,
            function (selectedColors) {
              completeSampleAddToCart(variantId, selectedColors);
            },
            function () {
              /* cancelled - nothing added; busy state stays off until fetch */
            }
          );
          return;
        }

        var autoChosen =
          needsSampleColorPicker(meta.bases) && usedColors.length === 1
            ? [usedColors[0]]
            : null;
        completeSampleAddToCart(variantId, autoChosen);
      });
    }

    function buildSampleCartItem(variantId, chosenColor) {
      var overrideHex = chosenColor ? chosenColor.hex : null;
      return ensureSampleThumbnailUrl({ overrideHex: overrideHex }).then(function (thumbnailUrl) {
        var props = buildColorProperties(
          patternName + ' - Custom Colorway Sample',
          chosenColor
        );
        applyCustomSkuToProps(props, true, overrideHex);
        props['_design_thumbnail_url'] = thumbnailUrl;
        return {
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: props
        };
      });
    }

    function completeSampleAddToCart(variantId, chosenColors) {
      if (!beginSampleAdd()) return;

      var colors = Array.isArray(chosenColors)
        ? chosenColors
        : chosenColors
          ? [chosenColors]
          : null;

      var itemsPromise = colors && colors.length
        ? Promise.all(
            colors.map(function (color) {
              return buildSampleCartItem(variantId, color);
            })
          )
        : buildSampleCartItem(variantId, null).then(function (item) {
            return [item];
          });

      itemsPromise
        .then(function (items) {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items })
          }).then(function (r) {
            return r.json().then(function (data) {
              if (!r.ok) {
                throw new Error(
                  (data && (data.description || data.message)) ||
                    'Cart add failed'
                );
              }
              return { data: data, count: items.length };
            });
          });
        })
        .then(function (result) {
          finishAddToCart(result.data, lastOrderTrigger);
          workshopTrack('onSample');
        })
        .catch(function (err) {
          console.error('SAMPLE THUMBNAIL FAILED:', err);
          showToast(zCopy('could_not_add_sample_preview_failed'));
        })
        .finally(function () {
          endSampleAdd();
        });
    }

    function buildColorProperties(designLine, chosenSampleColor) {
      var props = {};
      props._Design = designLine || patternName + ' - Custom Colorway';

      // Single-zone sample: production needs the physical sample color only.
      if (chosenSampleColor && chosenSampleColor.name) {
        props.Colors = chosenSampleColor.name;
        return props;
      }

      var colorNames = getOrderedColorEntries().map(function (entry) {
        return entry.name;
      }).filter(function (name) {
        return !!name;
      });

      if (colorNames.length) {
        props.Colors = colorNames.join(' + ');
      }

      return props;
    }

    /**
     * Canonical color order for cart properties and SKU segments.
     * Grouped patterns: detectedBases (SVG group order). Individual: tile shape order.
     */
    function getOrderedColorEntries() {
      syncColorMapFromTile();

      if (hasGroupIds && detectedBases.length) {
        return detectedBases
          .map(function (base) {
            var hex = colorMap[base];
            if (!hex || isWhiteFill(hex)) return null;
            var found = palette.find(function (c) {
              return c.hex.toLowerCase() === hex.toLowerCase();
            });
            return { base: base, hex: hex, name: found ? found.name : hex };
          })
          .filter(function (entry) {
            return entry != null;
          });
      }

      var tileSvg = tileCanvas && tileCanvas.querySelector('svg');
      if (!tileSvg) return [];

      var entries = [];
      var seen = {};
      getShapes(tileSvg).forEach(function (s) {
        var hex = getShapeHex(s);
        var key = (hex || '').toLowerCase();
        if (!hex || isWhiteFill(hex) || seen[key]) return;
        seen[key] = true;
        var found = palette.find(function (c) {
          return c.hex.toLowerCase() === hex.toLowerCase();
        });
        entries.push({ base: null, hex: hex, name: found ? found.name : hex });
      });
      return entries;
    }

    function getGroupColorsInOrder() {
      return getOrderedColorEntries().map(function (entry) {
        return entry.hex;
      });
    }

    function logCustomSkuPreview(isSample, overrideHex) {
      var groupColors = overrideHex ? [overrideHex] : getGroupColorsInOrder();
      var sku = buildCustomSku(
        patternBaseSku,
        groupColors,
        isSample,
        palette,
        CUSTOM_SKU_CONFIG
      );

      console.log('[custom-sku]', {
        flow: isSample ? 'sample' : 'full-size',
        sku: sku,
        baseSku: patternBaseSku || null,
        groupColors: groupColors,
        detectedBases: detectedBases.slice(),
        colorMap: Object.assign({}, colorMap)
      });

      return sku;
    }

    function applyCustomSkuToProps(props, isSample, overrideHex) {
      var sku = logCustomSkuPreview(isSample, overrideHex);
      if (sku) {
        props._SKU = sku;
      }
      return props;
    }

    function loadFromUrl() {
      var params = new URLSearchParams(window.location.search);
      var design = params.get('design');
      if (!design) return;
      try {
        design.split(',').forEach(function (pair) {
          var parts = pair.split(':');
          if (parts.length === 2 && parts[0] && parts[1]) {
            colorMap[parts[0]] = '#' + parts[1];
          }
        });
      } catch (e) {}
    }

    function loadTextureImageForSwatch(slug, cellIndex) {
      var url = textureCellUrl(slug, cellIndex);
      if (!url) return Promise.resolve(null);
      return new Promise(function (resolve) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () {
          resolve(img);
        };
        img.onerror = function () {
          resolve(null);
        };
        img.src = url;
      });
    }

    function resolveSpecSheetSwatch(entry) {
      var slug = slugForHex(entry.hex);

      function finish(swatchDataUrl) {
        return {
          entry: entry,
          swatchDataUrl: swatchDataUrl
        };
      }

      function fromImage(img) {
        return buildCircularSwatchFromImage(img, entry.hex, 120).then(function (dataUrl) {
          if (dataUrl) return finish(dataUrl);
          return buildCircularSwatchDataUrl(null, entry.hex, 120).then(finish);
        });
      }

      if (!slug || !enableTileTexture || !tileTextureBaseUrl) {
        return buildCircularSwatchDataUrl(null, entry.hex, 120).then(finish);
      }

      var cached = tileCellImages[slug];
      if (cached && cached[0] && cached[0].complete && cached[0].naturalWidth > 0) {
        return fromImage(cached[0]);
      }

      return preloadColorTextures(slug)
        .then(function () {
          var imgs = tileCellImages[slug];
          if (imgs && imgs[0] && imgs[0].complete && imgs[0].naturalWidth > 0) {
            return fromImage(imgs[0]);
          }
          return loadTextureImageForSwatch(slug, 1);
        })
        .then(function (img) {
          if (img && img.complete && img.naturalWidth > 0) {
            return fromImage(img);
          }
          var url = textureCellUrl(slug, 1);
          return fetchTexturePhotoDataUrl(url).then(function (photoDataUrl) {
            return buildCircularSwatchDataUrl(photoDataUrl, entry.hex, 120);
          }).then(finish);
        })
        .catch(function () {
          return buildCircularSwatchDataUrl(null, entry.hex, 120).then(finish);
        });
    }

    function buildSpecSheetColorSwatches() {
      var colors = getOrderedColorEntries();
      return Promise.all(
        colors.map(function (entry) {
          return resolveSpecSheetSwatch(entry);
        })
      );
    }

    function textureImageToPngDataUrl(img) {
      if (!img || !img.complete || !(img.naturalWidth > 0)) return null;
      try {
        var canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        var ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(img, 0, 0);
        return canvas.toDataURL('image/png');
      } catch (err) {
        return null;
      }
    }

    function slugFromTextureHref(href) {
      if (!href || !tileTextureBaseUrl) return null;
      var base = String(tileTextureBaseUrl).replace(/\/$/, '');
      if (String(href).indexOf(base) !== 0) return null;
      var rest = String(href).slice(base.length + 1);
      var slash = rest.indexOf('/');
      if (slash < 0) return null;
      return rest.slice(0, slash);
    }

    function cellIndexFromTextureHref(href) {
      var match = String(href || '').match(/-(\d+)\.webp(?:\?.*)?$/i);
      return match ? parseInt(match[1], 10) : 1;
    }

    function preloadTexturesForSpecSheet() {
      var slugs = {};
      getOrderedColorEntries().forEach(function (entry) {
        var slug = slugForHex(entry.hex);
        if (slug) slugs[slug] = true;
      });
      var list = Object.keys(slugs);
      if (!list.length) return Promise.resolve();
      return Promise.all(
        list.map(function (slug) {
          return preloadColorTextures(slug);
        })
      );
    }

    function prepareTexturedSvgClone(svgEl) {
      var clone = svgEl.cloneNode(true);
      clone.querySelectorAll('pattern[id^="tex-"] image').forEach(function (imageEl) {
        var href =
          imageEl.getAttribute('href') ||
          imageEl.getAttributeNS('http://www.w3.org/1999/xlink', 'href') ||
          '';
        var slug = slugFromTextureHref(href);
        if (!slug) return;
        var cell = cellIndexFromTextureHref(href);
        var imgs = tileCellImages[slug];
        var img = imgs && imgs[cell - 1];
        var dataUrl = img ? textureImageToPngDataUrl(img) : null;
        if (dataUrl) {
          imageEl.setAttribute('href', dataUrl);
          imageEl.setAttributeNS('http://www.w3.org/1999/xlink', 'href', dataUrl);
        }
      });
      return clone;
    }

    function rasterizeTexturedTileSheet(svgEl, options) {
      if (!svgEl || !enableTileTexture) return Promise.resolve(null);
      var clone = prepareTexturedSvgClone(svgEl);
      var hasInlined = false;
      clone.querySelectorAll('pattern[id^="tex-"] image').forEach(function (imageEl) {
        var href =
          imageEl.getAttribute('href') ||
          imageEl.getAttributeNS('http://www.w3.org/1999/xlink', 'href') ||
          '';
        if (href.indexOf('data:') === 0) hasInlined = true;
      });
      if (!hasInlined) return Promise.resolve(null);
      return rasterizeSvgElement(clone, options);
    }

    function buildSpecSheetTileImage() {
      return new Promise(function (resolve) {
        var svg = tileCanvas && tileCanvas.querySelector('svg');
        if (!svg) {
          resolve(null);
          return;
        }
        syncColorMapFromTile();

        var aspect = specSheetAspectRatio(patternName);
        var maxPx = 1000;
        var outW = maxPx;
        var outH = Math.round(maxPx / aspect);
        if (outH > maxPx) {
          outH = maxPx;
          outW = Math.round(maxPx * aspect);
        }
        outW = Math.max(1, outW);
        outH = Math.max(1, outH);
        var rasterOpts = { width: outW, height: outH };

        preloadTexturesForSpecSheet()
          .then(function () {
            return rasterizeTexturedTileSheet(svg, rasterOpts);
          })
          .then(function (dataUrl) {
            if (dataUrl) return dataUrl;
            var flatClone = flattenSvgForThumbnail(svg.cloneNode(true));
            return rasterizeSvgElement(flatClone, rasterOpts);
          })
          .then(function (dataUrl) {
            if (!dataUrl) {
              resolve(null);
              return;
            }
            resolve({ dataUrl: dataUrl, aspectRatio: aspect });
          })
          .catch(function () {
            var flatClone = flattenSvgForThumbnail(svg.cloneNode(true));
            rasterizeSvgElement(flatClone, rasterOpts).then(function (dataUrl) {
              if (!dataUrl) {
                resolve(null);
                return;
              }
              resolve({ dataUrl: dataUrl, aspectRatio: aspect });
            });
          });
      });
    }

    function setSpecSheetButtonsBusy(busy) {
      root.querySelectorAll('.zellij-lab__btn-spec-sheet').forEach(function (btn) {
        btn.disabled = !!busy;
        btn.classList.toggle('is-busy', !!busy);
        btn.setAttribute('aria-busy', busy ? 'true' : 'false');
      });
    }

    function createSpecSheetButton() {
      var specBtn = document.createElement('button');
      specBtn.type = 'button';
      specBtn.className = 'zellij-lab__action-btn zellij-lab__btn-spec-sheet';
      specBtn.innerHTML =
        SPEC_SHEET_DOWNLOAD_ICON +
        '<span class="zellij-lab__spec-sheet-spinner" aria-hidden="true"></span>' +
        '<span class="zellij-lab__spec-sheet-full">' +
        zCopy('spec_sheet') +
        '</span>' +
        '<span class="zellij-lab__spec-sheet-short">' +
        zCopy('spec_sheet_short') +
        '</span>';
      return specBtn;
    }

    function injectSpecSheetButtons() {
      root.querySelectorAll('.zellij-lab__btn-save').forEach(function (saveBtn) {
        if (
          saveBtn.parentNode &&
          saveBtn.parentNode.querySelector('.zellij-lab__btn-spec-sheet')
        ) {
          return;
        }
        var specBtn = createSpecSheetButton();
        if (saveBtn.classList.contains('zellij-lab__chrome-save')) {
          var group = saveBtn.parentNode.querySelector('.zellij-lab__chrome-actions');
          if (!group) {
            group = document.createElement('div');
            group.className = 'zellij-lab__chrome-actions';
            saveBtn.parentNode.insertBefore(group, saveBtn);
            group.appendChild(saveBtn);
          }
          group.appendChild(specBtn);
          return;
        }
        saveBtn.parentNode.insertBefore(specBtn, saveBtn.nextSibling);
      });
    }

    function buildSpecSheetPdf(jsPDF, tileImage, logoDataUrl, colorSwatches, propertyIcons) {
      var doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      var pageW = SPEC_SHEET_PAGE.w;
      var titleText = specSheetTitle(
        patternName,
        patternType,
        (cfg.displayName || '').trim()
      );
      var dims = parseDimensionsForPdf(
        getPatternDimensions(patternName, cfg.tileDimensions)
      );
      var patternLabel =
        ((cfg.displayName || '').trim() || patternName.replace(/_/g, ' ')).toUpperCase();
      colorSwatches = colorSwatches || [];
      propertyIcons = propertyIcons || {};
      var tileAspect =
        tileImage && tileImage.aspectRatio > 0
          ? tileImage.aspectRatio
          : specSheetAspectRatio(patternName);
      var layout = planSpecSheetLayout(doc, {
        colorSwatches: colorSwatches,
        patternLabel: patternLabel,
        tileAspect: tileAspect
      });
      var margin = layout.margin;
      var y = margin;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(formatSpecDate(), margin, y);
      doc.text('www.zelligehome.com', pageW - margin, y, { align: 'right' });
      y += 4;
      doc.text('support@zelligehome.com', pageW - margin, y, { align: 'right' });
      y += 4;

      var logoH = SPEC_SHEET_LOGO_H;
      var logoW = logoH * SPEC_SHEET_LOGO_ASPECT;
      var headerRowY = y;
      var logoRendered = false;

      if (logoDataUrl) {
        try {
          doc.addImage(logoDataUrl, 'PNG', margin, headerRowY, logoW, logoH);
          logoRendered = true;
        } catch (err) {
          logoRendered = false;
        }
      }

      if (!logoRendered) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(SPEC_BRAND_NAVY[0], SPEC_BRAND_NAVY[1], SPEC_BRAND_NAVY[2]);
        doc.text('ZELLIGE HOME', margin, headerRowY + logoH * 0.55);
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(SPEC_BRAND_NAVY[0], SPEC_BRAND_NAVY[1], SPEC_BRAND_NAVY[2]);
      doc.text(titleText, pageW / 2, headerRowY + logoH / 2 + 1, { align: 'center' });
      y = headerRowY + logoH + 4;
      drawSpecInkRule(doc, y, margin, pageW);

      var tileBox = layout.tileBox;
      var leftColW = layout.leftColW;
      var rightColX = layout.rightColX;
      var rightColW = layout.rightColW;
      var tileX = layout.tileX;
      var tileY = layout.tileY;
      var tileDataUrl =
        tileImage && tileImage.dataUrl
          ? tileImage.dataUrl
          : typeof tileImage === 'string'
            ? tileImage
            : null;

      if (tileDataUrl) {
        try {
          doc.addImage(tileDataUrl, 'PNG', tileX, tileY, tileBox.w, tileBox.h);
        } catch (err2) {
          doc.setDrawColor(210, 210, 210);
          doc.rect(tileX, tileY, tileBox.w, tileBox.h);
        }
      } else {
        doc.setDrawColor(210, 210, 210);
        doc.rect(tileX, tileY, tileBox.w, tileBox.h);
      }

      var rightY = layout.mainTop;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(SPEC_BRAND_NAVY[0], SPEC_BRAND_NAVY[1], SPEC_BRAND_NAVY[2]);
      doc.text(zCopy('spec_used_colors'), rightColX, rightY + 2);
      rightY += SPEC_SHEET_COLOR_HEADING_H + SPEC_SHEET_COLOR_HEADING_GAP;

      var swatchMm = SPEC_SHEET_COLOR_SWATCH_MM;
      var colorRowGap = specSheetColorRowGap(colorSwatches.length);
      colorSwatches.forEach(function (swatch) {
        var entry = swatch.entry;
        var swatchTop = rightY;
        var swatchCenterY = swatchTop + swatchMm / 2;
        var textY = swatchCenterY + 0.8;
        if (swatch.swatchDataUrl) {
          try {
            doc.addImage(
              swatch.swatchDataUrl,
              'PNG',
              rightColX,
              swatchTop,
              swatchMm,
              swatchMm
            );
          } catch (err3) {
            var rgb = hexToRgb(entry.hex);
            if (rgb) {
              doc.setFillColor(rgb.r, rgb.g, rgb.b);
              doc.circle(rightColX + swatchMm / 2, swatchCenterY, swatchMm / 2, 'F');
            }
          }
        } else {
          var fallbackRgb = hexToRgb(entry.hex);
          if (fallbackRgb) {
            doc.setFillColor(fallbackRgb.r, fallbackRgb.g, fallbackRgb.b);
            doc.circle(rightColX + swatchMm / 2, swatchCenterY, swatchMm / 2, 'F');
          }
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(40, 40, 40);
        doc.text(
          String(entry.name || entry.hex).toUpperCase(),
          rightColX + swatchMm + 2.5,
          textY
        );
        rightY += colorRowGap;
      });

      if (!colorSwatches.length) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(10);
        doc.setTextColor(120, 120, 120);
        doc.text(
          zCopy('no_colors_applied_yet'),
          rightColX,
          rightY
        );
        rightY += 6;
      }

      rightY += SPEC_SHEET_COLOR_SPECS_GAP;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(SPEC_BRAND_NAVY[0], SPEC_BRAND_NAVY[1], SPEC_BRAND_NAVY[2]);
      doc.text(zCopy('spec_size_label'), rightColX, rightY);
      rightY += SPEC_SHEET_SPEC_LABEL_VALUE_GAP;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 40);
      doc.text(dims.sizeIn + ' = ' + dims.sizeCm, rightColX, rightY);
      rightY += SPEC_SHEET_SPEC_VALUE_H + SPEC_SHEET_SPEC_BLOCK_GAP;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(SPEC_BRAND_NAVY[0], SPEC_BRAND_NAVY[1], SPEC_BRAND_NAVY[2]);
      doc.text(zCopy('spec_thickness_label'), rightColX, rightY);
      rightY += SPEC_SHEET_SPEC_LABEL_VALUE_GAP;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 40);
      doc.text(dims.thicknessIn + ' (' + dims.thicknessCm + ')', rightColX, rightY);

      drawSpecInkRule(doc, layout.ruleY, margin, pageW);

      drawSpecPropertyCardGrid(
        doc,
        layout.gridTop,
        margin,
        layout.contentW,
        propertyIcons,
        layout.cardH
      );

      return doc;
    }

    function specSheetFilename() {
      var slug = patternLookupKey(patternName).replace(/\s+/g, '-');
      if (patternType === 'border') {
        slug = slug.replace(/^BORDER-/i, '');
      }
      var typeSlug = patternType === 'border' ? 'border' : 'mosaic';
      return slug + '-' + typeSlug + '-spec-sheet.pdf';
    }

    function generateSpecSheet() {
      if (!isColoringComplete()) {
        showCenteredNotice(incompleteColoringMsg());
        return;
      }
      setSpecSheetButtonsBusy(true);
      Promise.all([
        loadJsPdf(),
        buildSpecSheetTileImage(),
        fetchSpecSheetLogoDataUrl(),
        buildSpecSheetColorSwatches(),
        preloadSpecSheetPropertyIcons()
      ])
        .then(function (parts) {
          var jsPDF = parts[0];
          var tileImage = parts[1];
          var logoDataUrl = parts[2];
          var colorSwatches = parts[3];
          var propertyIcons = parts[4];
          if (!jsPDF) {
            throw new Error('jsPDF constructor missing');
          }
          var doc = buildSpecSheetPdf(
            jsPDF,
            tileImage,
            logoDataUrl,
            colorSwatches,
            propertyIcons
          );
          downloadPdfDoc(doc, specSheetFilename());
          workshopTrack('onPdf');
        })
        .catch(function (err) {
          console.error('[spec-sheet]', err);
          showToast(zCopy('could_not_generate_spec_sheet'));
        })
        .finally(function () {
          setSpecSheetButtonsBusy(false);
        });
    }

    injectSpecSheetButtons();

    root.querySelectorAll('.zellij-lab__btn-save').forEach(function (btn) {
      listen(btn, 'click', saveDesign);
    });
    root.querySelectorAll('.zellij-lab__btn-spec-sheet').forEach(function (btn) {
      listen(btn, 'click', generateSpecSheet);
    });
    root.querySelectorAll('.zellij-lab__btn-add-to-cart').forEach(function (btn) {
      listen(btn, 'click', function () {
        lastOrderTrigger = btn;
        addToCart();
      });
    });
    root.querySelectorAll('.zellij-lab__btn-add-sample').forEach(function (btn) {
      listen(btn, 'click', function () {
        lastOrderTrigger = btn;
        addSampleToCart();
      });
    });

    /*
      Exit uses aria-controls on the overlay (Prestige DialogElement toggle).
      Do not add a second click handler that calls hide() - it runs before the
      delegate toggle and re-opens the overlay (scroll lock stuck at depth 1).
    */

    function bindRoomPreviewTab(tab) {
      listen(tab, 'click', function () {
        setActiveRoom(
          tab.getAttribute('data-room') || defaultRoomIdForPatternType(patternType)
        );
      });
    }

    queryRoomPreviewTabButtons(root).forEach(bindRoomPreviewTab);

    syncRoomPreviewTabs(root, patternType, activeRoomId);
    if (!isFunnelLabRoot() && patternType === 'border') {
      var deskPreviewInit = root.querySelector(
        '.zellij-lab__col--preview .zellij-lab__room-preview'
      );
      if (deskPreviewInit) {
        deskPreviewInit.setAttribute(
          'data-scene',
          activeRoomId || 'fireplace'
        );
      }
    }
    var roomPreviewInit = root.querySelector('.zellij-lab__room-preview');
    if (roomPreviewInit) {
      roomPreviewInit.hidden = false;
      roomPreviewInit.setAttribute('aria-hidden', 'false');
    }

    root.querySelectorAll('.zellij-lab__btn-see-room').forEach(function (seeRoomBtn) {
      if (!roomPreviewReady) return;
      seeRoomBtn.disabled = false;
      seeRoomBtn.hidden = false;
      listen(seeRoomBtn, 'click', function () {
        roomPreviewTriggerEl = seeRoomBtn;
        openMobileRoomPreview();
      });
    });

    var exitPreviewBtn =
      (roomFullscreenEl &&
        roomFullscreenEl.querySelector('.zellij-lab__room-exit-preview')) ||
      root.querySelector('.zellij-lab__room-exit-preview');
    if (exitPreviewBtn) {
      listen(exitPreviewBtn, 'click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        closeMobileRoomPreview({ fromUi: true });
      });
    }
    if (roomFullscreenEl) {
      listen(roomFullscreenEl, 'click', handleRoomFullscreenBackdropClick);
      listen(document, 'keydown', handleRoomFullscreenEscape, true);
    }
    listen(document, 'keydown', handleRoomFullscreenTabKey);
    listen(document, 'wheel', handleRoomPreviewWheel, { passive: false });
    listen(document, 'touchmove', handleRoomPreviewWheel, { passive: false });

    root.querySelectorAll('.zellij-lab__filter-pill').forEach(function (pill) {
      listen(pill, 'click', function () {
        if (pill.disabled) return;
        setColourFilter(pill.getAttribute('data-filter') || 'all');
      });
    });

    if (usedLinkBtn && usedColorsPanel) {
      setUsedColorsOpen(false);
      listen(usedLinkBtn, 'click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        setUsedColorsOpen(!usedColorsPanel.classList.contains('is-open'));
      });
      listen(document, 'click', function (event) {
        if (!usedColorsPanel.classList.contains('is-open')) return;
        if (usedColorsPanel.contains(event.target)) return;
        if (usedLinkBtn.contains(event.target)) return;
        setUsedColorsOpen(false);
      });
      listen(document, 'keydown', function (event) {
        if (event.key !== 'Escape') return;
        if (!usedColorsPanel.classList.contains('is-open')) return;
        setUsedColorsOpen(false);
      });
    }

    if (reminderBtn && overlayEl) {
      listen(reminderBtn, 'click', function () {
        if (typeof overlayEl.show === 'function') overlayEl.show();
      });
    }

    bindMobileSheetHandle();
    bindBaseProductAtcGuard();
    syncMobileEditorCanvasFit();
    listen(window, 'resize', function () {
      syncMobileEditorCanvasFit();
      syncMobileSampleFit();
    });
    if (window.visualViewport) {
      listen(window.visualViewport, 'resize', function () {
        syncMobileEditorCanvasFit();
        syncMobileSampleFit();
      });
    }

    var previewSlotResizeTimer = null;
    function onPreviewSlotResize() {
      if (
        roomScaleRevealed &&
        roomScaleFrozenColW > 0 &&
        previewColForResize &&
        Math.abs(previewColForResize.clientWidth - roomScaleFrozenColW) < 8
      ) {
        return;
      }
      if (previewSlotResizeTimer) clearTimeout(previewSlotResizeTimer);
      previewSlotResizeTimer = setTimeout(function () {
        if (roomPreviewReady) refreshRoomPreview();
        sizeBorderCarpetToSlot();
        syncPaletteSwatchSize();
      }, 80);
    }
    var previewColForResize = root.querySelector('.zellij-lab__col--preview');
    var paletteColForResize = root.querySelector('.zellij-lab__col--palette');
    if (typeof ResizeObserver !== 'undefined') {
      if (previewColForResize) {
        trackObserver(new ResizeObserver(onPreviewSlotResize)).observe(
          previewColForResize
        );
      }
      if (paletteColForResize) {
        trackObserver(
          new ResizeObserver(function () {
            syncPaletteSwatchSize();
          })
        ).observe(paletteColForResize);
      }
    } else {
      listen(window, 'resize', onPreviewSlotResize);
    }

    var stickyPriceInfo = root.querySelector('.zellij-lab__sticky-price-info');
    if (stickyPriceInfo) {
      listen(stickyPriceInfo, 'click', function () {
        var meta = root.querySelector('.zellij-lab__price-card-meta--full');
        var msg = meta && meta.textContent ? meta.textContent.trim() : '';
        if (msg) showToast(msg, { type: 'info', duration: 4000 });
      });
    }

    if (overlayEl) {
      listen(overlayEl, 'dialog:after-hide', function () {
        persistLocalDraft();
        syncReturnReminder();
        setSheetSnap('peek');
      });
    }

    enrichPaletteTemperatures();
    fillPricingUi();
    fillDimensionLabels();

    loadFromUrl();

    // Restore design after login / intentional hydrate only.
    // Do NOT silently paint from remote saved designs on every setPattern —
    // that leaves room/tile/USED out of sync and bleeds colors across fresh
    // pattern picks. Auth sessionStorage key is separate.
    // Matching zellij-lab-draft is applied after SVG load (same pattern only)
    // so a reload after sign-in / paint keeps colours; cross-pattern drafts
    // are ignored by restoreLocalDraftIfMatching().
    var pendingDesign = loadPendingDesign();
    var pendingRestoreMessage = null;
    if (pendingDesign && pendingDesign.colorMap) {
      var intentionalRestore =
        pendingDesign.action === 'save' ||
        pendingDesign.action === 'sample' ||
        pendingDesign.action === 'order' ||
        pendingDesign.action === 'signin' ||
        pendingDesign.action === 'reinit';
      if (intentionalRestore) {
        window._zellijPendingDesign = pendingDesign;
        if (IS_LOGGED_IN) {
          clearPendingDesign();
          pendingRestoreMessage = loginActionRestoreMessage(pendingDesign.action);
        }
      }
    }

    syncReturnReminder();

    renderPaletteColorsOnly();
    setActive(activeColor, activeColorName);
    preloadSwatchImages();
    preloadPaletteTexturesPrioritized();
    preloadPairScenePhotos();
    syncReturnReminder();

    /* Remote saved designs load via My Designs / explicit user action only. */

    loadSvg(tileCanvas, 2, function (svgEl) {
      if (instanceAborted || instanceGeneration !== labInstanceGeneration) return;
      loadState.tileSvg = true;
      if (labUnavailable || !svgEl) {
        maybeHideLoader();
        return;
      }
      detectBases(svgEl);
      applyWhite(svgEl);
      bindTile(svgEl);
      if (window._zellijPendingDesign) {
        var appliedPending = window._zellijPendingDesign;
        applyDesignState(appliedPending);
        window._zellijPendingDesign = null;
        /* Always consume after restore so pending cannot linger/reapply. */
        clearPendingDesign();
        /*
          Write draft immediately (do not wait on thumbnail debounce) so a
          reload after sign-in restore still has zellij-lab-draft colours.
        */
        persistLocalDraft();
        syncReturnReminder();
        if (pendingRestoreMessage) {
          showToast(pendingRestoreMessage, { type: 'info', duration: 5000 });
          pendingRestoreMessage = null;
        }
      } else {
        var matchingDraft = restoreLocalDraftIfMatching();
        if (matchingDraft && matchingDraft.colorMap) {
          applyDesignState(matchingDraft);
        } else if (Object.keys(colorMap).length > 0) {
          applyDesignState({ colorMap: colorMap });
        }
      }
      updateTileCursor();
      renderPicker();
      setActive(activeColor, activeColorName);
      renderUsed();
      updateUndoRedo();
      syncStickyBarCustomizingState();

      if (shuffleBtn) shuffleBtn.style.display = hasGroupIds ? '' : 'none';
      /*
        Carpet often resolves before tile. Remount room now that tile exists so
        carpetCols is correct on first visible paint (no small-then-big jump).
      */
      if (
        roomPreviewReady &&
        carpetCanvas &&
        carpetCanvas.querySelector('svg')
      ) {
        refreshRoomPreview();
        requestAnimationFrame(function () {
          if (
            instanceAborted ||
            instanceGeneration !== labInstanceGeneration
          ) {
            return;
          }
          refreshRoomPreview();
        });
      }
      maybeHideLoader();
    });

    if (carpetCanvas) {
      var carpetSvgIdx = patternType === 'border' ? 4 : 3;
      if (patternType === 'border') {
        preloadBorderScenePhotos();
        fetchPatternSvgText(3, function () {
          if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
            return;
          }
          preloadBorderScenePhotos();
          refreshRoomPreview();
        });
      }
      loadSvg(carpetCanvas, carpetSvgIdx, function (svgEl) {
        if (instanceAborted || instanceGeneration !== labInstanceGeneration) return;
        loadState.carpetSvg = true;
        if (patternType === 'border') {
          preloadBorderScenePhotos();
        }
        if (svgEl) {
          applyWhite(svgEl);
          if (Object.keys(colorMap).length > 0) {
            applyColorMapToSvg(svgEl, colorMap);
          }
          sizeBorderCarpetToSlot();
          requestAnimationFrame(function () {
            if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
              return;
            }
            sizeBorderCarpetToSlot();
            refreshRoomPreview();
          });
        }
        refreshRoomPreview();
        initSamplePreview().finally(function () {
          maybeHideLoader();
        });
      });
    } else {
      initSamplePreview();
    }

    if (overlayEl) {
      holdRoomPreviewUntilStable();
    }

    historyApi = setupOverlayShell(overlayEl, {
      listen: listen,
      onOpening: function () {
        overlayEnterSettled = false;
        holdRoomPreviewUntilStable();
      },
      onOpened: function () {
        workshopTrack(
          'onOpen',
          workshopPatternInfo({
            productHandle: productHandle,
            patternName: patternName,
            displayName: displayNameEarly || cfg.displayName || '',
            patternType: patternType
          })
        );
        if (hasCustomColors()) {
          refreshThumbnailCache();
        }
        sizeBorderCarpetToSlot();
        waitForOverlayOpenSettled().then(function () {
          if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
            return;
          }
          overlayEnterSettled = true;
          sizeBorderCarpetToSlot();
          reviveOverlayTextures();
          refreshRoomPreview();
          revealRoomPreviewIfStable();
        });
      },
      onLevelExit: function (exitedLevel) {
        if (exitedLevel === OVERLAY_HISTORY_LEVEL_ROOM) {
          closeMobileRoomPreview({ fromUi: false });
        }
      },
      onClosed: function () {
        overlayEnterSettled = false;
        closeMobileRoomPreview({ fromUi: false });
        holdRoomPreviewUntilStable();
      }
    });

    if (overlayEl && overlayEl.open) {
      waitForOverlayOpenSettled().then(function () {
        if (instanceAborted || instanceGeneration !== labInstanceGeneration) {
          return;
        }
        overlayEnterSettled = true;
        sizeBorderCarpetToSlot();
        reviveOverlayTextures();
        refreshRoomPreview();
        revealRoomPreviewIfStable();
      });
    }

    window.__zellijLabApi = {
      _generation: instanceGeneration,
      destroy: destroyInstance,
      /* Funnel passes its own CTA so the banner sits above that button. */
      orderFullSize: function (trigger) {
        lastOrderTrigger = trigger || null;
        addToCart();
      },
      orderSample: function (trigger) {
        lastOrderTrigger = trigger || null;
        addSampleToCart();
      },
      getActivePattern: function () {
        return {
          patternName: patternName,
          patternType: patternType,
          patternStorageFolder: patternStorageFolder,
          productHandle: productHandle,
          displayName: (cfg.displayName || '').trim() || patternName.replace(/_/g, ' '),
          customColorwayVariantId: customColorwayVariantId,
          customSampleVariantId: customSampleVariantId,
          colorMap: Object.assign({}, colorMap)
        };
      },
      getCachedThumbnailDataUrl: function () {
        return cachedThumbnailDataUrl;
      },
      getCachedThumbnailDataKey: function () {
        return cachedThumbnailDataKey;
      },
      refreshThumbnailCache: refreshThumbnailCache,
      generateSpecSheet: generateSpecSheet,
      /*
        Lets the funnel order section paint its own _SAMPLE.svg through the
        same url(#tex-) path as the tile sheet instead of flat hex.
      */
      paintSampleSvgElement: function (svgEl) {
        if (!svgEl) return false;
        colorSampleSvgElement(svgEl);
        getShapes(svgEl).forEach(function (s) {
          var fill = (s.getAttribute('fill') || s.style.fill || '').trim();
          var white =
            fill.indexOf('url(') !== 0 && isWhiteFillHex(fill || '#ffffff');
          if (white) {
            s.setAttribute('stroke', TILE_UNPAINTED_GROUT);
            s.setAttribute('stroke-width', '1');
            s.setAttribute('vector-effect', 'non-scaling-stroke');
            s.style.stroke = TILE_UNPAINTED_GROUT;
          }
        });
        return true;
      },
      refreshRoomPreview: refreshRoomPreview,
      getLastRoomRefreshMs: function () {
        return lastRoomRefreshMs;
      },
      getRoomReport: function () {
        var patternEl = root.querySelector('.zellij-lab__room-pattern');
        var roomSvg = patternEl
          ? patternEl.querySelector('svg.zellij-lab__room-pattern-svg')
          : null;
        var tileSvg = tileCanvas ? tileCanvas.querySelector('svg') : null;
        var carpetSvg = carpetCanvas ? carpetCanvas.querySelector('svg') : null;
        var grid = carpetSvg
          ? deriveCarpetGridDims(carpetSvg, tileSvg)
          : { cols: 0, rows: 0 };
        var roomStats = roomSvg ? collectTextureStats(roomSvg) : null;
        var symbolTex = roomSvg
          ? roomSvg.querySelectorAll(
              'symbol [fill*="tex-"], symbol [style*="tex-"]'
            ).length
          : 0;
        var liveTexPatterns = roomSvg
          ? roomSvg.querySelectorAll('pattern[id^="tex-"]').length
          : 0;
        return {
          roomPath: patternEl ? patternEl.dataset.roomPath : null,
          roomId: patternEl ? patternEl.dataset.roomId : null,
          targetSheets: patternEl ? patternEl.dataset.targetSheets : null,
          visibleSheets: patternEl ? patternEl.dataset.visibleSheets : null,
          unitPx: patternEl ? patternEl.dataset.unitPx : null,
          patternW: patternEl ? patternEl.dataset.patternW : null,
          patternH: patternEl ? patternEl.dataset.patternH : null,
          carpetCols: String(grid.cols),
          carpetRows: String(grid.rows),
          roomNodeCount: roomSvg ? countRoomPatternNodes(roomSvg) : 0,
          roomTexturePath: roomSvg ? roomSvg.dataset.roomTexturePath : null,
          roomPatternCount: roomStats ? roomStats.patternCount : 0,
          roomRasterPxW: null,
          roomRasterPxH: null,
          roomRasterDpr: null,
          roomCompositeCache: roomSvg ? roomSvg.dataset.roomCompositeCache : null,
          patternImageCount: roomSvg
            ? roomSvg.querySelectorAll('pattern[id^="tex-"] image').length
            : 0,
          symbolTexFills: symbolTex,
          liveTexPatterns: liveTexPatterns,
          roomRecolourMs: root.dataset ? root.dataset.roomRecolourMs : null,
          roomRefreshMs: root.dataset ? root.dataset.roomRefreshMs : null,
          tileSheetSizeCm: cfg.tileSheetSizeCm
        };
      },
      getTextureReport: function () {
        var tileSvg = tileCanvas ? tileCanvas.querySelector('svg') : null;
        var carpetSvg = carpetCanvas ? carpetCanvas.querySelector('svg') : null;
        var tileStats = collectTextureStats(tileSvg);
        var carpetStats = collectTextureStats(carpetSvg);
        var ready = Object.keys(textureReadySlugs).filter(function (s) {
          return textureReadySlugs[s];
        });
        return {
          baseUrl: tileTextureBaseUrl,
          poolSize: TEXTURE_POOL_SIZE,
          readySlugs: ready,
          tilePatterns: tileStats.patternCount,
          carpetPatterns: carpetStats.patternCount,
          tileBuckets: tileStats.bucketCount,
          carpetBuckets: carpetStats.bucketCount,
          cellsPerColour: 9,
          payloadFiles: ready.length * 9
        };
      },
      getTemperatureTable: function () {
        return palette.map(function (c) {
          var hsl = hexToHsl(c.hex);
          return {
            name: c.name,
            hex: c.hex,
            slug: c.slug,
            hue: Math.round(hsl.h),
            saturation: Math.round(hsl.s * 1000) / 10,
            lightness: Math.round(hsl.l * 1000) / 10,
            category: c.tempCategory
          };
        });
      },
      openOverlay: function () {
        if (overlayEl && typeof overlayEl.show === 'function') overlayEl.show();
      },
      closeOverlay: function () {
        if (overlayEl && typeof overlayEl.hide === 'function') overlayEl.hide();
      }
    };
  }

  function setupOverlayShell(overlay, options) {
    if (!overlay) return null;
    options = options || {};
    var onOpened = options.onOpened;
    var onOpening = options.onOpening;
    var onClosed = options.onClosed;
    var onLevelExit = options.onLevelExit;
    var listenFn =
      typeof options.listen === 'function'
        ? options.listen
        : function (el, type, fn, opts) {
            el.addEventListener(type, fn, opts);
          };

    /* History levels: lab, optional nested room preview. */
    var historyStack = [];
    var closingFromPopstate = false;

    function setOverlayOpenClass(isOpen) {
      document.documentElement.classList.toggle(
        'zellij-design-lab-overlay-open',
        !!isOpen
      );
    }

    function pushHistoryLevel(level) {
      if (historyStack.indexOf(level) !== -1) return;
      try {
        var nextState = Object.assign({}, history.state || {});
        var nextStack = historyStack.concat([level]);
        nextState[OVERLAY_HISTORY_KEY] = nextStack.slice();
        history.pushState(nextState, '');
        historyStack = nextStack;
      } catch (err) {
        /* ignore */
      }
    }

    function popHistoryLevelFromUi() {
      if (!historyStack.length) return;
      try {
        history.back();
      } catch (err) {
        historyStack = [];
      }
    }

    function handleHistoryPop() {
      if (!historyStack.length) return;
      var exited = historyStack[historyStack.length - 1];
      historyStack = historyStack.slice(0, -1);
      if (typeof onLevelExit === 'function') {
        onLevelExit(exited, historyStack.slice());
      }
      if (historyStack.length === 0) {
        if (overlay.open) {
          closingFromPopstate = true;
          overlay.hide();
        }
      }
    }

    listenFn(overlay, 'dialog:before-show', function () {
      acquireBodyScrollLock();
      if (typeof onOpening === 'function') onOpening();
    });

    listenFn(overlay, 'dialog:after-show', function () {
      setOverlayOpenClass(true);
      pushHistoryLevel(OVERLAY_HISTORY_LEVEL_LAB);
      if (typeof onOpened === 'function') onOpened();
    });

    listenFn(overlay, 'dialog:after-hide', function () {
      setOverlayOpenClass(false);
      releaseBodyScrollLock();
      resetBodyScrollLockAfterLabClose();
      if (typeof onClosed === 'function') onClosed();
      if (typeof onLevelExit === 'function' && historyStack.indexOf(OVERLAY_HISTORY_LEVEL_ROOM) !== -1) {
        onLevelExit(OVERLAY_HISTORY_LEVEL_ROOM, []);
      }
      if (historyStack.length && !closingFromPopstate) {
        historyStack = [];
        popHistoryLevelFromUi();
      } else {
        historyStack = [];
      }
      closingFromPopstate = false;
    });

    listenFn(window, 'popstate', function () {
      if (!overlay.open && !historyStack.length) return;
      if (!historyStack.length) return;
      handleHistoryPop();
    });

    if (
      window.location.hash === '#zellij-color-tool' ||
      window.location.hash === '#zellij-design-lab-overlay'
    ) {
      var gateCfg = window.ZELLIJ_CONFIG || {};
      if (gateCfg.designLabAllowed !== false) {
        requestAnimationFrame(function () {
          overlay.show();
        });
      }
    }

    return {
      pushHistoryLevel: pushHistoryLevel,
      popHistoryLevelFromUi: popHistoryLevelFromUi,
      hasLevel: function (level) {
        return historyStack.indexOf(level) !== -1;
      }
    };
  }

  var PREVIEW_TEXTURE_POOL_SIZE = 24;
  var PREVIEW_TEXTURE_ROTATIONS = [0, 90, 180, 270];
  var PREVIEW_TEXTURE_POOL_KEY = 'obb';
  var previewTextureReady = {};
  var previewTextureFailed = {};
  var previewTexturePromises = {};
  var previewPoolBuilt = {};

  function previewShapeHash01(shapeId, salt) {
    var str = String(shapeId) + '|' + String(salt || '');
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return (h >>> 0) / 4294967296;
  }

  function previewEnsureDefs(svgEl) {
    var defs = svgEl.querySelector('defs');
    if (!defs) {
      defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      svgEl.insertBefore(defs, svgEl.firstChild);
    }
    return defs;
  }

  function previewStableId(shape, fallbackIndex) {
    var id = shape.getAttribute('id');
    if (id) return id;
    var parent = shape.parentElement;
    while (parent && parent.tagName && parent.tagName.toLowerCase() !== 'g') {
      parent = parent.parentElement;
    }
    var groupId = parent && parent.getAttribute ? parent.getAttribute('id') : '';
    return (groupId || 'shape') + ':' + fallbackIndex;
  }

  function previewSvgKey(svgEl) {
    if (!svgEl.getAttribute('data-zellij-svg-key')) {
      svgEl.setAttribute(
        'data-zellij-svg-key',
        'svg-' + Math.floor(previewShapeHash01(String(Date.now()), 'svg') * 1e9)
      );
    }
    return svgEl.getAttribute('data-zellij-svg-key');
  }

  function previewTextureCellUrl(baseUrl, slug, cellIndex) {
    return baseUrl + '/' + slug + '/' + slug + '-' + cellIndex + '.webp';
  }

  function previewSlugForHex(palette, hex) {
    if (!hex || !palette || !palette.length) return null;
    var key = String(hex).toLowerCase();
    for (var i = 0; i < palette.length; i++) {
      var c = palette[i];
      if (c && c.hex && String(c.hex).toLowerCase() === key && c.slug) {
        return String(c.slug).toLowerCase();
      }
    }
    return null;
  }

  function previewPreloadSlug(baseUrl, slug) {
    if (!baseUrl || !slug) return Promise.resolve(false);
    if (previewTextureFailed[slug]) return Promise.resolve(false);
    if (previewTextureReady[slug]) return Promise.resolve(true);
    if (previewTexturePromises[slug]) return previewTexturePromises[slug];
    previewTexturePromises[slug] = Promise.all(
      [1, 2, 3, 4, 5, 6, 7, 8, 9].map(function (cell) {
        return new Promise(function (resolve, reject) {
          var img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = function () {
            resolve(img);
          };
          img.onerror = function () {
            reject(new Error('texture load failed'));
          };
          img.src = previewTextureCellUrl(baseUrl, slug, cell);
        });
      })
    )
      .then(function () {
        previewTextureReady[slug] = true;
        return true;
      })
      .catch(function () {
        previewTextureFailed[slug] = true;
        delete previewTexturePromises[slug];
        return false;
      });
    return previewTexturePromises[slug];
  }

  function previewEnsurePool(svgEl, baseUrl, slug) {
    if (!svgEl || !slug || !previewTextureReady[slug]) return false;
    var builtKey = previewSvgKey(svgEl) + '|' + slug + '|' + PREVIEW_TEXTURE_POOL_KEY;
    if (previewPoolBuilt[builtKey]) return true;
    var sampleId = 'tex-' + slug + '-' + PREVIEW_TEXTURE_POOL_KEY + '-0';
    if (svgEl.getElementById(sampleId)) {
      previewPoolBuilt[builtKey] = true;
      return true;
    }
    var defs = previewEnsureDefs(svgEl);
    var i;
    for (i = 0; i < PREVIEW_TEXTURE_POOL_SIZE; i++) {
      var patternId = 'tex-' + slug + '-' + PREVIEW_TEXTURE_POOL_KEY + '-' + i;
      if (svgEl.getElementById(patternId)) continue;
      var cell = (i % 9) + 1;
      var rotation = PREVIEW_TEXTURE_ROTATIONS[i % 4];
      var pattern = document.createElementNS('http://www.w3.org/2000/svg', 'pattern');
      pattern.setAttribute('id', patternId);
      pattern.setAttribute('patternUnits', 'objectBoundingBox');
      pattern.setAttribute('patternContentUnits', 'objectBoundingBox');
      pattern.setAttribute('width', '1');
      pattern.setAttribute('height', '1');
      var image = document.createElementNS('http://www.w3.org/2000/svg', 'image');
      var href = previewTextureCellUrl(baseUrl, slug, cell);
      image.setAttribute('href', href);
      image.setAttributeNS('http://www.w3.org/1999/xlink', 'href', href);
      image.setAttribute('x', '0');
      image.setAttribute('y', '0');
      image.setAttribute('width', '1');
      image.setAttribute('height', '1');
      image.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      if (rotation) {
        image.setAttribute('transform', 'rotate(' + rotation + ' 0.5 0.5)');
      }
      pattern.appendChild(image);
      defs.appendChild(pattern);
    }
    previewPoolBuilt[builtKey] = true;
    return true;
  }

  function previewRemoveUnderlay(shape) {
    if (!shape || !shape.parentNode) return;
    var prev = shape.previousElementSibling;
    if (prev && prev.getAttribute('data-zellij-underfill') === '1') {
      prev.parentNode.removeChild(prev);
    }
  }

  function previewEnsureUnderlay(shape, hex) {
    if (!shape || !shape.parentNode) return;
    var fillHex = hex || '#FFFFFF';
    var prev = shape.previousElementSibling;
    if (prev && prev.getAttribute('data-zellij-underfill') === '1') {
      prev.setAttribute('fill', fillHex);
      prev.style.fill = fillHex;
      return;
    }
    var under = shape.cloneNode(true);
    under.removeAttribute('id');
    under.removeAttribute('data-zellij-fill');
    under.setAttribute('data-zellij-underfill', '1');
    under.setAttribute('fill', fillHex);
    under.style.fill = fillHex;
    under.setAttribute('stroke', 'none');
    under.style.stroke = 'none';
    under.style.pointerEvents = 'none';
    shape.parentNode.insertBefore(under, shape);
  }

  function previewApplyFill(shape, hex, stableId, svgEl, opts) {
    opts = opts || {};
    var fillHex = hex || '#FFFFFF';
    shape.setAttribute('data-zellij-fill', fillHex);
    shape.style.shapeRendering = 'geometricPrecision';
    var slug = previewSlugForHex(opts.palette, fillHex);
    var textured =
      opts.enableTileTexture !== false &&
      slug &&
      previewTextureReady[slug] &&
      previewEnsurePool(svgEl, opts.tileTextureBaseUrl, slug);
    if (!textured) {
      previewRemoveUnderlay(shape);
      shape.setAttribute('fill', fillHex);
      shape.style.fill = fillHex;
      return;
    }
    previewEnsureUnderlay(shape, fillHex);
    var poolIndex = Math.floor(
      previewShapeHash01(stableId || previewStableId(shape, 0), 'pool') *
        PREVIEW_TEXTURE_POOL_SIZE
    );
    var fill =
      'url(#tex-' + slug + '-' + PREVIEW_TEXTURE_POOL_KEY + '-' + poolIndex + ')';
    shape.setAttribute('fill', fill);
    shape.style.fill = fill;
  }

  function previewPaintMap(svgEl, colorMap, opts) {
    if (!svgEl || !colorMap) return;
    opts = opts || {};
    var hpPreview =
      String(opts.paletteKey || '').toLowerCase() === 'hand-painted' ||
      String(opts.paletteKey || '').toLowerCase() === 'handpainted' ||
      /hand-painted/i.test(
        String(opts.storageFolder || opts.patternStorageFolder || '')
      );
    if (hpPreview) {
      getHandPaintedFamilyElements(svgEl).forEach(function (el) {
        var base = getHandPaintedFamilyBase(el);
        if (!colorMap[base]) return;
        getHandPaintedFamilyShapes(el).forEach(function (s, idx) {
          previewApplyFill(
            s,
            colorMap[base],
            previewStableId(s, base + ':' + idx),
            svgEl,
            opts
          );
        });
      });
      return;
    }
    getAllGroupsWithId(svgEl).forEach(function (g) {
      var base = getBaseId(g.getAttribute('id'));
      if (!colorMap[base]) return;
      getShapes(g).forEach(function (s, idx) {
        previewApplyFill(
          s,
          colorMap[base],
          previewStableId(s, base + ':' + idx),
          svgEl,
          opts
        );
      });
    });
  }

  function patternSvgUrlForPreview(options) {
    var base = String(options.patternSvgBaseUrl || '')
      .split('?')[0]
      .replace(/\/$/, '');
    var pattern = String(options.pattern || '').trim();
    if (!base || !pattern) return '';
    var folder =
      options.storageFolder ||
      (options.type === 'border' ? 'Borders' : 'Mosaics');
    return base + '/' + folder + '/' + pattern + '/' + pattern + '_2.svg';
  }

  function renderTexturedPreview(host, options) {
    options = options || {};
    if (!host) return Promise.resolve(null);
    var colorMap = options.colorMap || null;
    var svgUrl = options.svgUrl || patternSvgUrlForPreview(options);
    if (!svgUrl || !colorMap) {
      return Promise.resolve(null);
    }

    var tileTextureBaseUrl = String(
      options.tileTextureBaseUrl || ''
    )
      .split('?')[0]
      .replace(/\/$/, '');
    if (tileTextureBaseUrl.indexOf('/Textures') !== -1) {
      tileTextureBaseUrl = tileTextureBaseUrl.replace(/\/Textures\/?$/, '/Tiles');
    }
    var previewNs = glazeNamespace(
      options.paletteKey,
      options.storageFolder || options.patternStorageFolder
    );
    if (previewNs && tileTextureBaseUrl && !/\/hand-painted$/i.test(tileTextureBaseUrl)) {
      tileTextureBaseUrl = tileTextureBaseUrl.replace(/\/$/, '') + '/' + previewNs;
    }
    var enableTileTexture = options.enableTileTexture !== false;
    var palette = Array.isArray(options.palette) ? options.palette : [];
    var paintOpts = {
      tileTextureBaseUrl: tileTextureBaseUrl,
      enableTileTexture: enableTileTexture,
      palette: palette
    };

    host.innerHTML = '';
    host.classList.add('zellij-textured-preview');

    return fetch(svgUrl)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (text) {
        var wrap = document.createElement('div');
        wrap.innerHTML = text.trim();
        var svgEl = wrap.querySelector('svg');
        if (!svgEl) throw new Error('No SVG');
        svgEl.removeAttribute('width');
        svgEl.removeAttribute('height');
        svgEl.setAttribute('width', '100%');
        svgEl.setAttribute('height', '100%');
        svgEl.style.display = 'block';
        svgEl.style.width = '100%';
        svgEl.style.height = '100%';
        host.appendChild(svgEl);

        var slugs = {};
        Object.keys(colorMap).forEach(function (base) {
          var slug = previewSlugForHex(palette, colorMap[base]);
          if (slug) slugs[slug] = true;
        });
        var preload = Object.keys(slugs).map(function (slug) {
          return previewPreloadSlug(tileTextureBaseUrl, slug);
        });
        return Promise.all(preload).then(function () {
          previewPaintMap(svgEl, colorMap, paintOpts);
          return svgEl;
        });
      })
      .catch(function () {
        return null;
      });
  }

  window.ZellijDesignLab = {
    init: init,
    setPattern: setPattern,
    destroy: destroyActiveLab,
    gateColorYoursButtons: gateColorYoursButtons,
    renderTexturedPreview: renderTexturedPreview,
    getInstanceApi: function () {
      return window.__zellijLabApi || null;
    }
  };
})();
